import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "../ThemeContext";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import { collection, query, where, getDocs, addDoc, updateDoc, doc, getDoc, deleteDoc, onSnapshot, setDoc, orderBy, increment, serverTimestamp, Timestamp, deleteField } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../firebaseConfig";
import * as XLSX from 'xlsx';
import { EMPRESAS_VOUCHER, EMPRESAS_MENU_PRINCIPAL } from "../constants/empresas";
import ReporteUnidades from './ReporteUnidades';
import WhatsAppWeb from './WhatsAppWeb';
import ReporteTotalGeneral from './ReporteTotalGeneral';
import { cabeceraSesion } from '../utils/sesionPanel';

const ECUADOR_TIMEZONE = 'America/Guayaquil';
// Ecuador continental es UTC-5 (sin DST). Para construir rangos "del día" en Ecuador,
// convertimos medianoche Ecuador a UTC sumando 5 horas.
const ECUADOR_UTC_OFFSET_HOURS = 5;

// Centro base del mapa: Sangolquí (Ecuador)
const MAP_CENTER_SANGOLQUI = [-0.3301412, -78.4499915];
const MAP_ZOOM_DEFAULT = 14;

function ymdEcuador(date = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: ECUADOR_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);
    const y = parts.find(p => p.type === 'year')?.value;
    const m = parts.find(p => p.type === 'month')?.value;
    const d = parts.find(p => p.type === 'day')?.value;
    if (!y || !m || !d) return new Date().toISOString().split('T')[0];
    return `${y}-${m}-${d}`;
  } catch {
    return new Date().toISOString().split('T')[0];
  }
}

function rangoDiaEcuadorDesdeYmd(ymd) {
  if (!ymd || typeof ymd !== 'string') return { inicio: null, fin: null };
  const [year, month, day] = ymd.split('-').map(Number);
  if (!year || !month || !day) return { inicio: null, fin: null };
  const inicioUTCms = Date.UTC(year, month - 1, day, ECUADOR_UTC_OFFSET_HOURS, 0, 0, 0);
  const siguienteInicioUTCms = Date.UTC(year, month - 1, day + 1, ECUADOR_UTC_OFFSET_HOURS, 0, 0, 0);
  return { inicio: new Date(inicioUTCms), fin: new Date(siguienteInicioUTCms - 1) };
}

// "2026-08-09" -> "09-08-2026" (id del documento de fecha en la colección "viajes")
function ymdADdMmYyyy(ymd) {
  if (!ymd || typeof ymd !== 'string') return '';
  const [year, month, day] = ymd.split('-');
  if (!year || !month || !day) return '';
  return `${day}-${month}-${year}`;
}

// import axios from 'axios'; // Comentado porque no se usa

/**
 * Funcionalidad de tokens de conductores:
 * - Cuando se asigna manualmente una unidad/conductor, se incluye el token FCM del conductor
 * - El token se obtiene de los campos: tokenFCM, tokenConductor, etc.
 * - Se valida que el token tenga al menos 100 caracteres para considerarlo válido
 * - Se muestra un indicador visual en la gestión de conductores
 * - Los mensajes de confirmación incluyen el estado del token
 * 
 * Funcionalidad de NotificaciOnenCurso:
 * - Se crea un duplicado automático en la colección "NotificaciOnenCurso" 
 * - Incluye todos los datos del pedido más campos específicos para notificaciones
 * - Mantiene el mismo ID del documento original para referencia
 * - Agrega fechaNotificacion y estadoNotificacion para seguimiento
 */

// Configuración de Google Maps
const GOOGLE_MAPS_API_KEY = "AIzaSyBWqJ5_eaGfM6epbuChtkq0W5eqv2Ew37c";
const DEFAULT_CITY_CENTER = { lat: -0.180653, lng: -78.467838 }; // Quito

// Animación de carga para estado "Buscando"
function BuscandoAnimado() {
  const [puntos, setPuntos] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setPuntos((p) => (p + 1) % 4);
    }, 400);
    return () => clearInterval(id);
  }, []);
  return <span>Buscando{'.'.repeat(puntos)}</span>;
}

// Componente del Mapa de Google con Places API
function GoogleMapComponent({ onCoordinatesSelect, onAddressSelect, onSectorSelect, onSolicitarApp, onSaveLocation, coordenadas, direccionFormulario, center, height }) {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const markerRef = useRef(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const autocompleteInputRef = useRef(null);
  // Refs para mantener las callbacks actualizadas sin reinicializar el mapa
  const onCoordinatesSelectRef = useRef(onCoordinatesSelect);
  const onAddressSelectRef = useRef(onAddressSelect);
  const onSectorSelectRef = useRef(onSectorSelect);
  const skipNextReverseGeocoding = useRef(false);

  useEffect(() => {
    onCoordinatesSelectRef.current = onCoordinatesSelect;
  }, [onCoordinatesSelect]);

  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
  }, [onAddressSelect]);

  useEffect(() => {
    onSectorSelectRef.current = onSectorSelect;
  }, [onSectorSelect]);

  const handleCoordinatesSelect = useCallback((coords) => {
    if (onCoordinatesSelectRef.current) onCoordinatesSelectRef.current(coords);
  }, []);

  const handleAddressSelect = useCallback((address) => {
    if (onAddressSelectRef.current) onAddressSelectRef.current(address);
  }, []);

  const handleSectorSelect = useCallback((sector) => {
    if (onSectorSelectRef.current) onSectorSelectRef.current(sector);
  }, []);

  // Inicializar mapa y geocoder
  useEffect(() => {
    if (mapRef.current && !map && window.google && window.google.maps) {
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: center || DEFAULT_CITY_CENTER,
        zoom: 13,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
      });
      setMap(newMap);
      setGeocoder(new window.google.maps.Geocoder());

      // NOTA: Ya no usamos markerRef.current porque usamos un overlay CSS fijo en el centro

      // Centrar mapa al hacer click
      newMap.addListener('click', (event) => {
        // Al hacer click, paneamos el mapa para que el punto clicado quede en el centro
        newMap.panTo(event.latLng);
        // El evento idle se encargará de actualizar coordenadas
      });

      // Actualizar coordenadas y dirección al terminar de mover el mapa (idle)
      newMap.addListener('idle', () => {
        const centerPos = newMap.getCenter();
        if (!centerPos) return;
        const lat = centerPos.lat();
        const lng = centerPos.lng();
        const nuevasCoordenadas = `${lat.toFixed(6)},${lng.toFixed(6)}`;

        // Actualizamos siempre las coordenadas al centro
        handleCoordinatesSelect(nuevasCoordenadas);

        // Geocodificación inversa
        if (skipNextReverseGeocoding.current) {
          skipNextReverseGeocoding.current = false;
          return;
        }

        const geocoderInstance = new window.google.maps.Geocoder();
        geocoderInstance.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results.length > 0) {
            // Priorizar direcciones de calle o intersecciones sobre Plus Codes
            const bestResult = results.find(r => !r.types.includes('plus_code') && r.types.includes('intersection')) ||
              results.find(r => !r.types.includes('plus_code') && r.types.includes('route')) ||
              results.find(r => !r.types.includes('plus_code') && r.types.includes('street_address')) ||
              results.find(r => !r.types.includes('plus_code') && r.types.includes('premise')) ||
              results[0];

            let address = bestResult.formatted_address;

            // Limpiezas de texto dirección...
            address = address.replace(/, Cuenca \d+, Ecuador/i, '')
              .replace(/, Cuenca, Ecuador/i, '')
              .replace(/, Ecuador/i, '')
              .replace(/, Cuenca/i, '')
              .trim();

            if (address.endsWith(',')) address = address.slice(0, -1);

            handleAddressSelect(address);

            // Sector logic
            const sectorSource = bestResult.address_components ? bestResult : results[0];
            const components = sectorSource.address_components || [];
            const neighborhood = components.find(c => c.types.includes('neighborhood'));
            const sublocality = components.find(c => c.types.includes('sublocality'));
            const sector = neighborhood?.long_name || sublocality?.long_name || '';
            if (sector) handleSectorSelect(sector);

            if (autocompleteInputRef.current) {
              if (document.activeElement !== autocompleteInputRef.current) {
                autocompleteInputRef.current.value = address;
              }
            }
          }
        });
      });
    }

    // Cleanup function
    return () => {
      if (map) {
        window.google?.maps?.event?.clearInstanceListeners(map);
      }
    };
  }, [map, center, handleCoordinatesSelect, handleAddressSelect, handleSectorSelect]);

  // Inicializar Autocomplete restringido a Cuenca
  useEffect(() => {
    if (map && autocompleteInputRef.current && !autocomplete && window.google?.maps?.places) {
      // Definir límites de Cuenca
      const cuencaBounds = new window.google.maps.LatLngBounds(
        { lat: -3.10, lng: -79.20 }, // Suroeste
        { lat: -2.70, lng: -78.80 }  // Noreste
      );

      const newAutocomplete = new window.google.maps.places.Autocomplete(autocompleteInputRef.current, {
        bounds: cuencaBounds,
        strictBounds: true, // Forzar resultados dentro de Cuenca
        componentRestrictions: { country: 'ec' },
        fields: ['formatted_address', 'geometry', 'name', 'address_components'] // Sin restricción de 'types' para ver todo
      });

      newAutocomplete.addListener('place_changed', () => {
        const place = newAutocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
          skipNextReverseGeocoding.current = true;
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const nuevasCoordenadas = `${lat.toFixed(6)},${lng.toFixed(6)}`;
          const address = place.formatted_address || place.name;
          handleCoordinatesSelect(nuevasCoordenadas);
          handleAddressSelect(address);

          // Extract Sector
          const components = place.address_components || [];
          const neighborhood = components.find(c => c.types.includes('neighborhood'));
          const sublocality = components.find(c => c.types.includes('sublocality'));
          const sector = neighborhood?.long_name || sublocality?.long_name || '';
          if (sector) handleSectorSelect(sector);

          map.setCenter({ lat, lng });
          map.setZoom(18);
        }
      });
      setAutocomplete(newAutocomplete);
    }
  }, [map, autocomplete, handleCoordinatesSelect, handleAddressSelect, handleSectorSelect]);

  // Sincronizar centro del mapa cuando cambian coordenadas desde props (externamente)
  useEffect(() => {
    if (map && coordenadas && coordenadas.includes(',')) {
      const [lat, lng] = coordenadas.split(',').map(Number);
      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        const currentCenter = map.getCenter();
        const pos = { lat, lng };

        // Solo mover centro si la distancia es significativa
        if (!currentCenter || Math.abs(currentCenter.lat() - lat) > 0.0001 || Math.abs(currentCenter.lng() - lng) > 0.0001) {
          skipNextReverseGeocoding.current = true;
          map.setCenter(pos);
        }
      }
    }
  }, [map, coordenadas]);

  // Sincronizar input de búsqueda con la dirección del formulario
  useEffect(() => {
    if (autocompleteInputRef.current && direccionFormulario && autocompleteInputRef.current.value !== direccionFormulario) {
      autocompleteInputRef.current.value = direccionFormulario;
    }
  }, [direccionFormulario]);

  // Auto-búsqueda: Cuando carga el mapa, si hay dirección pero no coordenadas, buscar la dirección priorizando Cuenca
  useEffect(() => {
    if (map && geocoder && direccionFormulario && (!coordenadas || !coordenadas.includes(','))) {
      let searchAddress = direccionFormulario;
      // Agregar contexto de Cuenca si no está presente para mejorar la búsqueda
      // Solo si la dirección parece ser una calle (tiene cierta longitud) y no tiene ciudad
      if (searchAddress.length > 3 && !searchAddress.toLowerCase().includes('cuenca') && !searchAddress.toLowerCase().includes('ecuador')) {
        searchAddress += ', Cuenca, Ecuador';
      }

      console.log('🔍 Buscando dirección inicial en el mapa (optimizado):', searchAddress);

      geocoder.geocode({ address: searchAddress, componentRestrictions: { country: 'EC' } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          map.setCenter(location);
          map.setZoom(17);
          // Opcional: Extraer sector si no existe
          const components = results[0].address_components || [];
          const neighborhood = components.find(c => c.types.includes('neighborhood'));
          const sublocality = components.find(c => c.types.includes('sublocality'));
          const sector = neighborhood?.long_name || sublocality?.long_name || '';
          if (sector) handleSectorSelect(sector);
        } else {
          // Fallback: Intentar búsqueda original si falla la optimizada (y si era diferente)
          if (searchAddress !== direccionFormulario) {
            console.log('⚠️ Búsqueda optimizada falló, intentando original...');
            geocoder.geocode({ address: direccionFormulario, componentRestrictions: { country: 'EC' } }, (res2, stat2) => {
              if (stat2 === 'OK' && res2[0]) {
                const location = res2[0].geometry.location;
                map.setCenter(location);
                map.setZoom(17);
              }
            });
          }
        }
      });
    }
    // Ejecutar solo cuando el mapa se inicializa
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, geocoder]); // Solo al montar/cargar mapa

  const handleBuscarDireccion = () => {
    const address = autocompleteInputRef.current?.value;
    if (!address || !geocoder) return;
    geocoder.geocode({ address, componentRestrictions: { country: 'EC' } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        skipNextReverseGeocoding.current = true;
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        const nuevasCoordenadas = `${lat.toFixed(6)},${lng.toFixed(6)}`;

        handleCoordinatesSelect(nuevasCoordenadas);
        handleAddressSelect(results[0].formatted_address);

        // Sector logic...
        const components = results[0].address_components || [];
        const neighborhood = components.find(c => c.types.includes('neighborhood'));
        const sublocality = components.find(c => c.types.includes('sublocality'));
        const sector = neighborhood?.long_name || sublocality?.long_name || '';
        if (sector) handleSectorSelect(sector);

        if (map) {
          map.setCenter({ lat, lng });
          map.setZoom(18);
        }
      }
    });
  };

  return (
    <div style={{ padding: 15 }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
        <input
          ref={autocompleteInputRef}
          type="text"
          placeholder="Buscar dirección..."
          style={{ flex: 1, padding: '8px 12px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleBuscarDireccion();
            }
          }}
        />
        <button
          type="button"
          onClick={handleBuscarDireccion}
          style={{ padding: '8px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4, fontSize: 14, cursor: 'pointer' }}
        >
          🔍 Buscar
        </button>
        <button
          type="button"
          onClick={onSolicitarApp}
          style={{ padding: '8px 12px', background: '#10b981', color: 'white', border: 'none', borderRadius: 4, fontSize: 14, cursor: 'pointer', marginLeft: 'auto' }}
        >
          📱 Solicitar a la APP
        </button>
        <button
          type="button"
          onClick={async () => {
            if (onSaveLocation) {
              const center = map.getCenter();
              const coords = `${center.lat().toFixed(6)},${center.lng().toFixed(6)}`;
              onSaveLocation(coords);
            } else if (onCoordinatesSelect) {
              const center = map.getCenter();
              const coords = `${center.lat().toFixed(6)},${center.lng().toFixed(6)}`;
              onCoordinatesSelect(coords);
              alert('✅ Ubicación seleccionada y lista para guardar');
            }
          }}
          style={{ padding: '8px 12px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: 4, fontSize: 14, cursor: 'pointer' }}
        >
          💾 Guardar Ubicación
        </button>
      </div>
      <div style={{ position: 'relative', width: '100%', height: height || 720 }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%', border: '2px solid #ddd', borderRadius: 8, background: '#f8f9fa' }} />
        {/* Marcador fijo en el centro - ESTILO UBER/TAXI */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0px 4px 6px rgba(0,0,0,0.5))' }}>
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#ea4335" />
          </svg>
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
        💡 Escribe una dirección, haz clic en el mapa o arrastra el marcador para seleccionar ubicación
      </div>
    </div>
  );
}

const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry'];

// Selector con Wrapper y controles de visibilidad/coordenadas
function MapaSelector({ mapaVisible, setMapaVisible, onCoordinatesSelect, onAddressSelect, onSectorSelect, onSolicitarApp, onSaveLocation, coordenadas, direccionFormulario, center, height }) {
  const [coordenadasTemp, setCoordenadasTemp] = useState(coordenadas || '');

  useEffect(() => {
    setCoordenadasTemp(coordenadas || '');
  }, [coordenadas]);

  const render = useCallback((status) => {
    switch (status) {
      case Status.LOADING:
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, background: '#f3f4f6', borderRadius: 8, border: '2px solid #d1d5db' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, marginBottom: 10 }}>🗺️</div>
              <div>Cargando Google Maps...</div>
            </div>
          </div>
        );
      case Status.FAILURE:
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, background: '#fee2e2', borderRadius: 8, border: '2px solid #fecaca', color: '#dc2626' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, marginBottom: 10 }}>❌</div>
              <div>Error al cargar Google Maps</div>
              <div style={{ fontSize: 12, marginTop: 5 }}>Verifique la conexión a internet y la API key</div>
            </div>
          </div>
        );
      case Status.SUCCESS:
        return (
          <GoogleMapComponent
            onCoordinatesSelect={onCoordinatesSelect}
            onAddressSelect={onAddressSelect}
            onSectorSelect={onSectorSelect}
            onSolicitarApp={onSolicitarApp}
            onSaveLocation={onSaveLocation}
            coordenadas={coordenadas}
            direccionFormulario={direccionFormulario}
            center={center}
            height={height}
          />
        );
      default:
        return null;
    }
  }, [onCoordinatesSelect, onAddressSelect, onSectorSelect, onSolicitarApp, onSaveLocation, coordenadas, direccionFormulario, center, height]);

  if (!mapaVisible) return null;

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ border: '2px solid #ccc', borderRadius: 8, background: '#f8f9fa', overflow: 'hidden' }}>

        <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={render} libraries={GOOGLE_MAPS_LIBRARIES} />

        {/* Controles de coordenadas manuales */}
        <div style={{ padding: 15, borderTop: '1px solid #ddd', background: 'white' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 14, fontWeight: 'bold' }}>Coordenadas:</span>
            <input
              type="text"
              value={coordenadasTemp}
              onChange={(e) => setCoordenadasTemp(e.target.value)}
              onBlur={(e) => onCoordinatesSelect(e.target.value)}
              placeholder="Lat,Lng (ej: -0.2298500,-78.5249500)"
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
            />
            <button
              type="button"
              onClick={async () => {
                if (onSaveLocation) {
                  await onSaveLocation(coordenadasTemp);
                } else {
                  await onCoordinatesSelect(coordenadasTemp);
                  setMapaVisible(false);
                }
              }}
              style={{ padding: '8px 12px', background: '#10b981', color: 'white', border: 'none', borderRadius: 4, fontSize: 14, cursor: 'pointer' }}
            >
              💾 Guardar
            </button>
          </div>
        </div>
      </div>
    </div>

  );
}

// Componente de Mapa en Tiempo Real (reutilizable)
function RealTimeLocationMap({ viajesAsignados = [], alertaActiva = null, focusPedidoId = null }) {
  const [ubicaciones, setUbicaciones] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [connectionStatus, setConnectionStatus] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [leafletLoaded, setLeafletLoaded] = useState(false);
  const mapRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const REFRESH_INTERVAL = 5000;

  // Estados para el ciclo de auto-enfoque
  const [focusIndex, setFocusIndex] = useState(0);
  const [isAutoCycling, setIsAutoCycling] = useState(false);
  const [orderMarkers, setOrderMarkers] = useState({});
  const autoCycleTimerRef = useRef(null);
  const zoomOutTimeoutRef = useRef(null);
  const autoCycleStopTimerRef = useRef(null);
  const prevPedidosCountRef = useRef(0);
  const alertMarkerRef = useRef(null);
  const pendingAlertUnitRef = useRef(null);

  // Ajustes del ciclo (más lento y con auto-stop)
  const AUTO_CYCLE_ZOOM_IN = 17;
  const AUTO_CYCLE_ZOOM_OUT = 14;
  const AUTO_CYCLE_ZOOM_OUT_AFTER_MS = 5000; // antes 2500ms
  const AUTO_CYCLE_NEXT_MS = 12000; // antes 6000ms
  const AUTO_CYCLE_TOTAL_MS = 60000; // correr 1 minuto y detenerse

  const API_URL = '/api/locacion-location-plaza';
  const [busquedaUnidad, setBusquedaUnidad] = useState('');

  const getUbicacionKey = (loc) => {
    if (!loc) return '';
    return String(loc._id || loc.unidad || loc.email || '');
  };

  const sanitizeUbicacion = (loc) => {
    const lat = Number(loc?.latitud);
    const lng = Number(loc?.longitud);
    return {
      _id: loc?._id ?? null,
      unidad: loc?.unidad ?? null,
      estado: loc?.estado ?? null,
      hora: loc?.hora ?? null,
      fecha: loc?.fecha ?? null,
      timestamp: loc?.timestamp ?? null,
      createdAt: loc?.createdAt ?? null,
      updatedAt: loc?.updatedAt ?? null,
      latitud: Number.isFinite(lat) ? lat : null,
      longitud: Number.isFinite(lng) ? lng : null,
    };
  };

  useEffect(() => {
    if (window.L) {
      setLeafletLoaded(true);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    link.crossOrigin = '';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
    script.crossOrigin = '';
    script.onload = () => {
      setLeafletLoaded(true);
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 500);
    };
    script.onerror = () => {
      console.error('Error al cargar Leaflet');
      setError('Error al cargar la librería de mapas');
    };
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const fetchUbicaciones = async () => {
    try {
      setError(null);
      setConnectionStatus(true);

      let response;
      try {
        response = await fetch(API_URL, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });
      } catch (proxyError) {
        try {
          response = await fetch('https://cab.my.id/api/locacion-location-plaza/', {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Accept': 'application/json',
            },
          });
        } catch (directError) {
          throw new Error('No se pudo conectar con la API.');
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setUbicaciones(Array.isArray(result.data) ? result.data.map(sanitizeUbicacion) : []);

        setLastUpdate(new Date());
        setConnectionStatus(true);
      }
    } catch (error) {
      console.error('❌ Error al obtener ubicaciones:', error);
      setConnectionStatus(false);

      setError(error.message);
    }
  };

  const getTimeAgo = (date) => {
    if (!date) return 'Nunca';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'hace unos segundos';
    if (seconds < 3600) return `hace ${Math.floor(seconds / 60)} minutos`;
    if (seconds < 86400) return `hace ${Math.floor(seconds / 3600)} horas`;
    return `hace ${Math.floor(seconds / 86400)} días`;
  };

  const createDriverIcon = (isActive, unidad) => {
    if (!window.L) return null;

    const taxiYellow = '#FFC107';
    const mainColor = isActive ? taxiYellow : '#9ca3af';
    const unitText = unidad || '--';
    const circleTextColor = isActive ? '#111827' : 'white';
    const plateBg = isActive ? '#111827' : '#4b5563';
    const plateText = isActive ? taxiYellow : 'white';

    return window.L.divIcon({
      className: 'modern-driver-marker',
      html: `
        <div style="display: flex; flex-direction: column; align-items: center; position: relative; filter: drop-shadow(0 4px 6px rgba(0,0,0,0.2));">
          <!-- Badge CV -->
          <div style="
            position: absolute;
            top: -6px;
            right: -6px;
            width: 18px;
            height: 18px;
            background: #111827;
            color: ${taxiYellow};
            border-radius: 999px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            font-weight: 900;
            border: 2px solid white;
            box-shadow: 0 2px 6px rgba(0,0,0,0.35);
            z-index: 5;
          ">CV</div>
          <!-- Círculo principal -->
          <div style="
            width: 42px; 
            height: 42px; 
            background: ${mainColor}; 
            border: 3px solid white; 
            border-radius: 50%; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            font-size: 22px;
            z-index: 2;
            transition: all 0.3s ease;
            color: ${circleTextColor};
          ">
            🚕
          </div>
          
          <!-- Badge de Unidad (Cuadrado inferior) -->
          <div style="
            background: ${plateBg};
            color: ${plateText};
            padding: 2px 8px;
            border-radius: 4px;
            font-weight: 800;
            font-size: 15px;
            margin-top: -8px;
            border: 1.5px solid white;
            z-index: 3;
            min-width: 35px;
            text-align: center;
          ">
            ${unitText}
          </div>
        </div>
      `,
      iconSize: [50, 60],
      iconAnchor: [25, 55],
      popupAnchor: [0, -55]
    });
  };

  // Icono para pedidos disponibles
  const createOrderIcon = (p) => {
    if (!window.L) return null;
    return window.L.divIcon({
      className: 'custom-order-marker',
      html: `
        <div style="position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 40px; height: 55px;">
          <div style="
            position: absolute;
            width: 35px;
            height: 35px;
            background: #ef4444;
            border-radius: 50%;
            opacity: 0.3;
            animation: order-pulse 1.5s infinite ease-out;
            z-index: -1;
          "></div>
          
          <div style="
            width: 30px;
            height: 30px;
            background: #ef4444;
            border: 2px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            font-size: 16px;
            z-index: 2;
          ">
            📍
          </div>
          
          <div style="
            background: #ef4444;
            color: white;
            padding: 1px 6px;
            border-radius: 3px;
            font-weight: bold;
            font-size: 10px;
            margin-top: -5px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.3);
            border: 1px solid white;
            z-index: 3;
            text-align: center;
          ">
            APP
          </div>
          
          <style>
            @keyframes order-pulse {
              0% { transform: scale(0.8); opacity: 0.8; }
              100% { transform: scale(2.5); opacity: 0; }
            }
          </style>
        </div>
      `,
      iconSize: [40, 55],
      iconAnchor: [20, 50],
      popupAnchor: [0, -50]
    });
  };

  useEffect(() => {
    if (!map || !ubicaciones.length || !window.L) return;

    Object.values(markers).forEach(marker => {
      if (marker && map) {
        map.removeLayer(marker);
      }
    });

    const newMarkers = {};
    const bounds = [];

    ubicaciones.forEach(location => {
      const { latitud, longitud, estado, unidad } = location;
      const ubicacionKey = getUbicacionKey(location);

      if (!latitud || !longitud || isNaN(latitud) || isNaN(longitud)) {
        return;
      }

      const isActive = estado === 'activo' || estado === 'true';
      const icon = createDriverIcon(isActive, unidad);

      const marker = window.L.marker([latitud, longitud], { icon })
        .addTo(map)
        .bindPopup(`
          <div style="min-width: 200px; padding: 10px;">
            <h3 style="margin: 0 0 10px 0; font-size: 16px;">Unidad: ${unidad || '--'}</h3>
            <div style="margin: 5px 0;"><strong>Estado:</strong> ${isActive ? 'Activo' : 'Inactivo'}</div>
            ${unidad ? `<div style="margin: 5px 0;"><strong>Unidad:</strong> ${unidad}</div>` : ''}
          </div>
        `);

      if (ubicacionKey) newMarkers[ubicacionKey] = marker;
      bounds.push([latitud, longitud]);
    });

    setMarkers(newMarkers);

    // Modificación: No ajustar bounds automáticamente para mantener el mapa fijo en Cuenca
    // if (bounds.length > 0 && Object.keys(newMarkers).length > 0) {
    //   try {
    //     const group = new window.L.featureGroup(Object.values(newMarkers));
    //     const layers = group.getLayers();
    //     if (layers.length > 0) {
    //       map.fitBounds(group.getBounds().pad(0.1));
    //     }
    //   } catch (err) {
    //     console.warn('⚠️ Error al ajustar vista del mapa:', err);
    //   }
    // }
  }, [map, ubicaciones]); // Removed 'markers' from dependencies

  // Efecto para manejar marcadores de pedidos disponibles
  useEffect(() => {
    if (!map || !window.L) return;

    // Limpiar marcadores antiguos
    Object.values(orderMarkers).forEach(m => map.removeLayer(m));

    const newOrderMarkers = {};
    viajesAsignados.forEach(p => {
      let lat = parseFloat(p.latitud);
      let lng = parseFloat(p.longitud);

      if (isNaN(lat) || isNaN(lng)) {
        if (p.coordenadas && typeof p.coordenadas === 'string') {
          const parts = p.coordenadas.split(',');
          if (parts.length === 2) {
            lat = parseFloat(parts[0]);
            lng = parseFloat(parts[1]);
          }
        }
      }

      if (!isNaN(lat) && !isNaN(lng)) {
        const marker = window.L.marker([lat, lng], { icon: createOrderIcon(p) })
          .addTo(map)
          .bindPopup(`
            <div style="min-width: 150px; padding: 5px;">
              <div style="font-weight: bold; color: #ef4444; margin-bottom: 5px;">Pedido Aplicación</div>
              <div><strong>Cliente:</strong> ${p.nombreCliente || p.codigo || 'S/N'}</div>
              <div><strong>Dirección:</strong> ${p.direccion || 'S/D'}</div>
              ${(p.notificados && p.notificados.length > 0) ? `
              <div style="margin-top: 5px; border-top: 1px solid #eee; padding-top: 5px;">
                <strong>Notificados:</strong> ${Array.isArray(p.notificados) ? p.notificados.map(n => typeof n === 'object' ? (n.unidad || n.id || JSON.stringify(n)) : n).join(', ') : p.notificados}
              </div>` : ''}
              ${(p.conductoresNotificados && p.conductoresNotificados.length > 0) ? `
              <div style="margin-top: 5px; border-top: 1px solid #eee; padding-top: 5px;">
                <strong>Notificados:</strong> ${Array.isArray(p.conductoresNotificados) ? p.conductoresNotificados.map(n => typeof n === 'object' ? (n.unidad || n.id || JSON.stringify(n)) : n).join(', ') : p.conductoresNotificados}
              </div>` : ''}
              ${(p.rechazados && p.rechazados.length > 0) ? `
              <div style="margin-top: 5px; border-top: 1px solid #eee; padding-top: 5px;">
                <strong>Rechazados:</strong> ${Array.isArray(p.rechazados) ? p.rechazados.map(n => typeof n === 'object' ? (n.unidad || n.id || JSON.stringify(n)) : n).join(', ') : p.rechazados}
              </div>` : ''}
            </div>
          `);
        newOrderMarkers[p.id] = marker;
      }
    });

    setOrderMarkers(newOrderMarkers);
  }, [map, viajesAsignados]);

  // Enfocar un pedido específico (desde botón en tabla)
  useEffect(() => {
    if (!map || !focusPedidoId) return;
    const m = orderMarkers?.[focusPedidoId];
    if (m && m.getLatLng) {
      const ll = m.getLatLng();
      map.setView([ll.lat, ll.lng], 18);
      m.openPopup?.();
      return;
    }
    // Fallback: intentar centrar por coords del pedido si no hay marcador
    const p = (viajesAsignados || []).find(x => x?.id === focusPedidoId);
    if (!p) return;
    let lat = parseFloat(p.latitud);
    let lng = parseFloat(p.longitud);
    if (isNaN(lat) || isNaN(lng)) {
      if (p.coordenadas && typeof p.coordenadas === 'string') {
        const parts = p.coordenadas.split(',');
        if (parts.length === 2) {
          lat = parseFloat(parts[0]);
          lng = parseFloat(parts[1]);
        }
      }
    }
    if (!isNaN(lat) && !isNaN(lng)) {
      map.setView([lat, lng], 18);
    }
  }, [focusPedidoId, map, orderMarkers, viajesAsignados]);

  // Lógica de Ciclo de Auto-enfoque
  useEffect(() => {
    if (!map || !isAutoCycling) return;

    // Filtrar pedidos que tienen coordenadas válidas
    const pedidosConCoordenadas = viajesAsignados.filter(p => {
      let lat = parseFloat(p.latitud);
      let lng = parseFloat(p.longitud);

      if (isNaN(lat) || isNaN(lng)) {
        if (p.coordenadas && typeof p.coordenadas === 'string') {
          const parts = p.coordenadas.split(',');
          if (parts.length === 2) {
            lat = parseFloat(parts[0]);
            lng = parseFloat(parts[1]);
          }
        }
      }
      return !isNaN(lat) && !isNaN(lng);
    });

    if (pedidosConCoordenadas.length === 0) return;

    // Reiniciar focusIndex si supera el tamaño de la lista
    if (focusIndex >= pedidosConCoordenadas.length) {
      setFocusIndex(0);
    }

    const cycleOrder = () => {
      const currentOrder = pedidosConCoordenadas[focusIndex];
      if (!currentOrder) return;

      let lat = parseFloat(currentOrder.latitud);
      let lng = parseFloat(currentOrder.longitud);

      if (isNaN(lat) || isNaN(lng)) {
        if (currentOrder.coordenadas && typeof currentOrder.coordenadas === 'string') {
          const parts = currentOrder.coordenadas.split(',');
          if (parts.length === 2) {
            lat = parseFloat(parts[0]);
            lng = parseFloat(parts[1]);
          }
        }
      }

      if (!isNaN(lat) && !isNaN(lng)) {
        console.log(`🎯 Enfocando pedido: ${currentOrder.id} (${focusIndex + 1}/${pedidosConCoordenadas.length})`);

        // 1. Centrar y zoom cercano
        map.setView([lat, lng], AUTO_CYCLE_ZOOM_IN, { animate: true });

        // 2. Después de un tiempo, alejar para ver conductores cercanos
        if (zoomOutTimeoutRef.current) clearTimeout(zoomOutTimeoutRef.current);
        zoomOutTimeoutRef.current = setTimeout(() => {
          map.setZoom(AUTO_CYCLE_ZOOM_OUT, { animate: true });
        }, AUTO_CYCLE_ZOOM_OUT_AFTER_MS);
      }

      // Programar siguiente cambio (más lento)
      if (autoCycleTimerRef.current) clearTimeout(autoCycleTimerRef.current);
      autoCycleTimerRef.current = setTimeout(() => {
        setFocusIndex(prev => (prev + 1) % pedidosConCoordenadas.length);
      }, AUTO_CYCLE_NEXT_MS);
    };

    cycleOrder();

    return () => {
      if (autoCycleTimerRef.current) clearTimeout(autoCycleTimerRef.current);
      if (zoomOutTimeoutRef.current) clearTimeout(zoomOutTimeoutRef.current);
    };
  }, [map, viajesAsignados, focusIndex, isAutoCycling]);

  // Auto-detener el ciclo luego de un tiempo (para que el mapa quede estable)
  useEffect(() => {
    if (!map) return;
    if (!isAutoCycling) {
      if (autoCycleStopTimerRef.current) clearTimeout(autoCycleStopTimerRef.current);
      autoCycleStopTimerRef.current = null;
      return;
    }
    if (autoCycleStopTimerRef.current) clearTimeout(autoCycleStopTimerRef.current);
    autoCycleStopTimerRef.current = setTimeout(() => {
      setIsAutoCycling(false);
      setFocusIndex(0);
      try {
        map.setZoom(AUTO_CYCLE_ZOOM_OUT, { animate: true });
      } catch (_) { }
    }, AUTO_CYCLE_TOTAL_MS);
    return () => {
      if (autoCycleStopTimerRef.current) clearTimeout(autoCycleStopTimerRef.current);
    };
  }, [isAutoCycling, map]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUbicaciones();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      refreshIntervalRef.current = setInterval(() => {
        fetchUbicaciones();
      }, REFRESH_INTERVAL);
    } else {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh]);

  useEffect(() => {
    if (!leafletLoaded || !mapRef.current || map) return;

    if (window.L && mapRef.current && !map) {
      // Small delay to ensure container has size
      const timer = setTimeout(() => {
        if (!mapRef.current || map) return;

        try {
          const newMap = window.L.map(mapRef.current).setView(MAP_CENTER_SANGOLQUI, MAP_ZOOM_DEFAULT);

          window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors',
            maxZoom: 19
          }).addTo(newMap);

          setMap(newMap);

          // Force resize calculation
          setTimeout(() => {
            newMap.invalidateSize();
          }, 200);
        } catch (error) {
          console.error("Error initializing map:", error);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [leafletLoaded, map]);

  // eslint-disable-next-line no-unused-vars
  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const centerMap = () => {
    if (map) {
      map.setView(MAP_CENTER_SANGOLQUI, MAP_ZOOM_DEFAULT);
    }
  };

  const encuadrarUnidades = useCallback(() => {
    if (!map || !window.L) return;
    try {
      const layers = Object.values(markers || {}).filter(Boolean);
      if (!layers.length) {
        centerMap();
        return;
      }
      const group = new window.L.featureGroup(layers);
      const b = group.getBounds?.();
      if (b && b.isValid && b.isValid()) {
        map.fitBounds(b.pad(0.15), { animate: true });
      } else {
        centerMap();
      }
    } catch (e) {
      centerMap();
    }
  }, [map, markers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cuando ya no haya pedidos, volver a encuadrar unidades
  useEffect(() => {
    if (!map) return;
    const count = Array.isArray(viajesAsignados) ? viajesAsignados.length : 0;
    const prev = prevPedidosCountRef.current || 0;
    prevPedidosCountRef.current = count;

    // transición: de tener pedidos -> 0 pedidos
    if (prev > 0 && count === 0) {
      if (isAutoCycling) setIsAutoCycling(false);
      setFocusIndex(0);
      setTimeout(() => encuadrarUnidades(), 300);
    }
  }, [viajesAsignados, map, encuadrarUnidades, isAutoCycling]);

  const handleBuscarUnidad = (valor) => {
    setBusquedaUnidad(valor);
    if (!valor || !map) return;

    // Buscar la unidad en el listado de ubicaciones
    const unit = ubicaciones.find(loc => loc.unidad === valor.trim() || loc.unidad === `0${valor.trim()}` || loc.unidad === `00${valor.trim()}`);
    if (unit && unit.latitud && unit.longitud) {
      map.setView([unit.latitud, unit.longitud], 18);
      // Abrir popup si existe el marcador
      const unitKey = getUbicacionKey(unit);
      if (unitKey && markers[unitKey]) markers[unitKey].openPopup();
    }
  };

  // Enfocar mapa y marcar alerta (si trae lat/lng) + buscar unidad (si viene)
  useEffect(() => {
    if (!map || !window.L) return;

    // Si ya no hay alerta activa, no mover el mapa a (0,0). Solo limpiar marcador.
    if (!alertaActiva) {
      try {
        if (alertMarkerRef.current) {
          map.removeLayer(alertMarkerRef.current);
          alertMarkerRef.current = null;
        }
      } catch (_) { }
      pendingAlertUnitRef.current = null;
      return;
    }

    const unidad = (alertaActiva?.unidadConductor ?? alertaActiva?.unit ?? '').toString().trim();
    if (unidad) {
      pendingAlertUnitRef.current = unidad;
      // Intento inmediato (si ya hay ubicaciones)
      handleBuscarUnidad(unidad);
    }

    const latRaw = (alertaActiva?.latitude ?? alertaActiva?.latitud ?? '').toString().trim();
    const lngRaw = (alertaActiva?.longitude ?? alertaActiva?.longitud ?? '').toString().trim();
    // Evitar Number('') => 0 cuando no hay coords reales
    if (!latRaw || !lngRaw) return;
    const lat = Number(latRaw);
    const lng = Number(lngRaw);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    // Limpiar marcador previo
    try {
      if (alertMarkerRef.current) {
        map.removeLayer(alertMarkerRef.current);
        alertMarkerRef.current = null;
      }
    } catch (_) { }

    const tipo = ((alertaActiva?.TipoAlerta || alertaActiva?.type || '') + '').toUpperCase().trim();
    const color = tipo === 'NARANJA' ? '#f97316' : (tipo === 'AMARILLA' ? '#f59e0b' : '#ef4444');
    const icon = window.L.divIcon({
      className: '',
      html: `<div style="width:34px;height:34px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 6px 14px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;font-weight:900;color:white;font-family:Arial;">!</div>`,
      iconSize: [34, 34],
      iconAnchor: [17, 34],
      popupAnchor: [0, -34],
    });

    const m = window.L.marker([lat, lng], { icon }).addTo(map);
    const unidadTxt = (alertaActiva?.unidadConductor ?? '').toString().trim();
    const nombreTxt = (alertaActiva?.nombreConductor ?? '').toString().trim();
    m.bindPopup(
      `<div style="font-weight:bold;margin-bottom:6px;">ALERTA ${tipo || ''}</div>` +
      (unidadTxt ? `<div>Unidad: <b>${unidadTxt}</b></div>` : '') +
      (nombreTxt ? `<div>Conductor: <b>${nombreTxt}</b></div>` : '')
    );
    m.openPopup();
    alertMarkerRef.current = m;
    map.setView([lat, lng], 18);
  }, [alertaActiva, map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reintentar enfoque por unidad cuando carguen ubicaciones
  useEffect(() => {
    const unidad = (pendingAlertUnitRef.current || '').toString().trim();
    if (!unidad || !map) return;
    const unit = ubicaciones.find(loc => loc.unidad === unidad || loc.unidad === `0${unidad}` || loc.unidad === `00${unidad}`);
    if (unit && unit.latitud && unit.longitud) {
      map.setView([unit.latitud, unit.longitud], 18);
      const unitKey = getUbicacionKey(unit);
      if (unitKey && markers[unitKey]) markers[unitKey].openPopup();
      pendingAlertUnitRef.current = null;
    }
  }, [ubicaciones, markers, map]);

  const activeCount = ubicaciones.filter(loc => loc.estado === 'activo' || loc.estado === 'true').length;

  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Listado horizontal de conductores en la parte superior */}
      <div style={{
        background: '#fff',
        padding: '12px 20px',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: 15,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        zIndex: 10
      }}>
        {/* Contenedor de Controles Principales */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={centerMap}
            style={{
              background: 'white',
              color: '#10b981',
              border: '2px solid #10b981',
              borderRadius: 8,
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#10b981';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#10b981';
            }}
          >
            📍 Centrar
          </button>

          <button
            onClick={() => {
              setIsAutoCycling(!isAutoCycling);
              if (!isAutoCycling) setFocusIndex(0);
            }}
            style={{
              background: isAutoCycling ? '#ef4444' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            {isAutoCycling ? '⏹️ Detener Ciclo' : '▶️ Iniciar Ciclo'}
          </button>
        </div>

        {/* Separador */}
        <div style={{ width: '1px', height: '30px', background: '#e2e8f0', margin: '0 5px' }} />

        {/* Buscador de Unidad */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flex: 1
        }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: '300px' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>🔍</span>
            <input
              type="text"
              placeholder="Buscar por unidad (ej: 0778)..."
              value={busquedaUnidad}
              onChange={(e) => handleBuscarUnidad(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 15px 10px 35px',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                fontSize: 14,
                outline: 'none',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
              }}
              onFocus={(e) => e.target.style.border = '1px solid #3b82f6'}
              onBlur={(e) => e.target.style.border = '1px solid #e2e8f0'}
            />
          </div>

          {busquedaUnidad && (
            <button
              onClick={() => setBusquedaUnidad('')}
              style={{
                background: '#f1f5f9',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                color: '#64748b',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 'bold'
              }}
            >
              Limpiar
            </button>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              padding: '6px 12px',
              background: '#f0fdf4',
              color: '#166534',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 'bold',
              border: '1px solid #bbf7d0'
            }}>
              🟢 {activeCount} Activas
            </div>
            {lastUpdate && (
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                Actualizado: {lastUpdate.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{
        flex: 1,
        position: 'relative',
        minHeight: '200px' // Ensure minimum height to prevent collapse
      }}>
        {/* El mapa ocupa todo el espacio restante */}
        {!leafletLoaded ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            fontSize: 16,
            color: '#666'
          }}>
            Cargando mapa...
          </div>
        ) : (
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        )}
      </div>
    </div>
  );
}

// Formulario principal de Taxi
// Formulario principal de Taxi
function TaxiForm({ alertaActiva = null, operadorAutenticado, setOperadorAutenticado, reporteDiario, setReporteDiario, authTrigger, setAuthTrigger, setIsCollapsed }) {
  const { colors, isDarkMode } = useTheme();
  // Estados para autenticación de operadores
  const [mostrarModalOperador, setMostrarModalOperador] = useState(false);
  const [codigoOperador, setCodigoOperador] = useState('');
  const [errorAutenticacion, setErrorAutenticacion] = useState('');
  const [cargandoAutenticacion, setCargandoAutenticacion] = useState(false);
  const [siguienteNumeroAutorizacion, setSiguienteNumeroAutorizacion] = useState(100);

  // Estado separado para actualizar la UI de autorización (sin abrir modal de autenticación)
  const [uiUpdateTrigger, setUiUpdateTrigger] = useState(0);
  const lastAuthTrigger = useRef(authTrigger || 0);

  // Nueva función para obtener y actualizar la secuencia de autorización
  const obtenerSiguienteAutorizacion = async () => {
    try {
      const secuenciaRef = doc(db, 'autorizacionSecuencia', 'contador');
      const secuenciaDoc = await getDoc(secuenciaRef);

      let siguienteNumero = 200; // Número inicial como especificaste

      if (secuenciaDoc.exists()) {
        const data = secuenciaDoc.data();
        siguienteNumero = (data.numero || 199) + 1;
      }

      // Actualizar el contador con el siguiente número
      await setDoc(secuenciaRef, {
        numero: siguienteNumero,
        ultimaActualizacion: new Date()
      }, { merge: true });

      console.log(`✅ Nueva autorización generada: ${siguienteNumero}`);
      return siguienteNumero;
    } catch (error) {
      console.error('❌ Error al obtener siguiente autorización:', error);
      // Fallback al método anterior si hay error
      return await obtenerSiguienteAutorizacionFallback();
    }
  };

  // Función de respaldo que mantiene la lógica anterior
  const obtenerSiguienteAutorizacionFallback = async () => {
    try {
      const vouchersRef = collection(db, 'voucherCorporativos');
      const querySnapshot = await getDocs(vouchersRef);
      let maxNumero = 199; // Empezar desde 199 para que el siguiente sea 200

      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const numAuth = data.numeroAutorizacion;
        if (typeof numAuth === 'number' && numAuth >= 200) {
          maxNumero = Math.max(maxNumero, numAuth);
        }
      });

      return maxNumero + 1;
    } catch (error) {
      console.error('Error en fallback:', error);
      return 200;
    }
  };

  // Función auxiliar para consultar todas las colecciones y obtener números de autorización existentes
  const obtenerNumerosAutorizacionExistentes = async (logPrefix = '') => {
    const numerosExistentes = new Set();

    try {
      // 1. Consultar voucherCorporativos
      console.log(`${logPrefix}📋 Consultando voucherCorporativos`);
      const vouchersRef = collection(db, 'voucherCorporativos');
      const vouchersSnapshot = await getDocs(vouchersRef);

      vouchersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const numAuth = data.numeroAutorizacion;
        // Convertir a número si es string o ya es número
        const numAuthNumber = typeof numAuth === 'string' ? parseInt(numAuth) : numAuth;
        if (typeof numAuthNumber === 'number' && !isNaN(numAuthNumber) && numAuthNumber >= 100) {
          numerosExistentes.add(numAuthNumber);
          console.log(`${logPrefix}✅ Voucher encontrado: ${numAuthNumber} (tipo original: ${typeof numAuth})`);
        }
      });

      // 2. Consultar preregistroVouchers
      console.log(`${logPrefix}📋 Consultando preregistroVouchers`);
      try {
        const preregistroRef = collection(db, 'preregistroVouchers');
        const preregistroSnapshot = await getDocs(preregistroRef);

        preregistroSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const numAuth = data.numeroAutorizacion;
          // Convertir a número si es string o ya es número
          const numAuthNumber = typeof numAuth === 'string' ? parseInt(numAuth) : numAuth;
          if (typeof numAuthNumber === 'number' && !isNaN(numAuthNumber) && numAuthNumber >= 100) {
            numerosExistentes.add(numAuthNumber);
            console.log(`${logPrefix}✅ PreRegistro encontrado: ${numAuthNumber} (tipo original: ${typeof numAuth})`);
          }
        });
      } catch (preregistroError) {
        console.log(`${logPrefix}⚠️ No se pudo consultar preregistroVouchers`);
      }

      // 3. Consultar todosLosViajes (últimos 7 días)
      console.log(`${logPrefix}📋 Consultando todosLosViajes (últimos 7 días)`);
      const sieteDiasAtras = new Date();
      sieteDiasAtras.setDate(sieteDiasAtras.getDate() - 7);

      try {
        const viajesRef = collection(db, 'todosLosViajes');
        const q = query(viajesRef, where('fecha', '>=', sieteDiasAtras));
        const viajesSnapshot = await getDocs(q);

        viajesSnapshot.docs.forEach(doc => {
          const data = doc.data();

          // Verificar data.autorizacion
          if (data.autorizacion) {
            const authNumber = typeof data.autorizacion === 'string' ? parseInt(data.autorizacion) : data.autorizacion;
            if (typeof authNumber === 'number' && !isNaN(authNumber) && authNumber >= 100) {
              numerosExistentes.add(authNumber);
              console.log(`${logPrefix}✅ Autorización en viaje encontrada: ${authNumber}`);
            }
          }

          // Verificar data.numeroAutorizacion
          if (data.numeroAutorizacion) {
            const numAuthNumber = typeof data.numeroAutorizacion === 'string' ? parseInt(data.numeroAutorizacion) : data.numeroAutorizacion;
            if (typeof numAuthNumber === 'number' && !isNaN(numAuthNumber) && numAuthNumber >= 100) {
              numerosExistentes.add(numAuthNumber);
              console.log(`${logPrefix}✅ NumeroAutorizacion en viaje encontrado: ${numAuthNumber}`);
            }
          }

          // Verificar data.numeroAutorizacionVoucher
          if (data.numeroAutorizacionVoucher) {
            const voucherNumber = typeof data.numeroAutorizacionVoucher === 'string' ? parseInt(data.numeroAutorizacionVoucher) : data.numeroAutorizacionVoucher;
            if (typeof voucherNumber === 'number' && !isNaN(voucherNumber) && voucherNumber >= 100) {
              numerosExistentes.add(voucherNumber);
              console.log(`${logPrefix}✅ NumeroAutorizacionVoucher en viaje encontrado: ${voucherNumber}`);
            }
          }
        });
      } catch (viajesError) {
        console.log(`${logPrefix}⚠️ No se pudo consultar todosLosViajes`, viajesError);
      }

      // 4. Consultar pedidosDisponibles
      console.log(`${logPrefix}📋 Consultando pedidosDisponibles`);
      try {
        const pedidosRef = collection(db, 'pedidosDisponibles');
        const pedidosSnapshot = await getDocs(pedidosRef);

        pedidosSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.autorizacion) {
            const authNumber = typeof data.autorizacion === 'string' ? parseInt(data.autorizacion) : data.autorizacion;
            if (typeof authNumber === 'number' && !isNaN(authNumber) && authNumber >= 100) {
              numerosExistentes.add(authNumber);
              console.log(`${logPrefix}✅ Autorización en pedido encontrada: ${authNumber}`);
            }
          }
        });
      } catch (pedidosError) {
        console.log(`${logPrefix}⚠️ No se pudo consultar pedidosDisponibles`);
      }

      const numerosOrdenados = Array.from(numerosExistentes).sort((a, b) => a - b);
      console.log(`${logPrefix}🔍 Total números encontrados: ${numerosOrdenados.length}`);
      if (numerosOrdenados.length > 0) {
        console.log(`${logPrefix}🔍 Últimos 10 números: ${numerosOrdenados.slice(-10).join(', ')}`);
      }

      return numerosExistentes;
    } catch (error) {
      console.error(`${logPrefix}❌ Error al consultar colecciones:`, error);
      return numerosExistentes;
    }
  };

  // Abrir modal cuando se dispare authTrigger desde el Header
  useEffect(() => {
    if (authTrigger > lastAuthTrigger.current) {
      setMostrarModalOperador(true);
      setCodigoOperador('');
      setErrorAutenticacion('');
    }
    lastAuthTrigger.current = authTrigger;
  }, [authTrigger]);

  // Restaurar operadora autenticada desde localStorage al montar el formulario
  useEffect(() => {
    try {
      if (!operadorAutenticado) {
        const saved = localStorage.getItem('operadorAutenticado');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && parsed.nombre) {
            setOperadorAutenticado(parsed);
          }
        }
      }
    } catch (e) {
      console.error('Error restaurando operadorAutenticado en TaxiForm:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // Función para obtener el siguiente número de autorización disponible (consulta múltiples colecciones)
  const obtenerSiguienteNumeroAutorizacion = async () => {
    try {
      console.log('🔍 Consultando múltiples colecciones para obtener el siguiente número de autorización');
      const numerosExistentes = await obtenerNumerosAutorizacionExistentes('[Taxi] ');

      // Buscar el primer número consecutivo que NO esté en uso
      let siguienteNumero = 100;
      while (numerosExistentes.has(siguienteNumero)) {
        siguienteNumero++;
      }

      console.log(`✅ [Taxi] Siguiente número disponible: ${siguienteNumero} (verificado en múltiples colecciones)`);
      setSiguienteNumeroAutorizacion(siguienteNumero);
    } catch (error) {
      console.error('Error al obtener número de autorización:', error);
      setSiguienteNumeroAutorizacion(100);
    }
  };

  // Función para obtener un número de autorización único garantizado
  const obtenerNumeroAutorizacionUnico = async () => {
    // Usar la nueva lógica de secuencia de autorización
    return await obtenerSiguienteAutorizacion();
  };

  // Función para autenticar operador
  const autenticarOperador = async () => {
    if (!codigoOperador || codigoOperador.length !== 4 || !/^\d{4}$/.test(codigoOperador)) {
      setErrorAutenticacion('El código debe tener exactamente 4 dígitos numéricos');
      return;
    }

    setCargandoAutenticacion(true);
    setErrorAutenticacion('');

    try {
      const operadoresRef = collection(db, 'operadores');
      const q = query(operadoresRef,
        where('codigo', '==', codigoOperador)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const operador = snapshot.docs[0].data();
        const operadorState = {
          id: snapshot.docs[0].id,
          nombre: operador.nombre,
          usuario: operador.usuario,
          codigo: operador.codigo
        };
        setOperadorAutenticado(operadorState);
        try { localStorage.setItem('operadorAutenticado', JSON.stringify(operadorState)); } catch { }
        setMostrarModalOperador(false);
        console.log('✅ Operador autenticado:', operador.nombre);

        // Cargar reporte diario del operador
        await cargarReporteDiario(operador.nombre);
      } else {
        setErrorAutenticacion('Código incorrecto');
      }
    } catch (error) {
      console.error('❌ Error al autenticar operador:', error);
      setErrorAutenticacion('Error al autenticar operador');
    } finally {
      setCargandoAutenticacion(false);
    }
  };

  // Función para cargar reporte diario
  const cargarReporteDiario = async (nombreOperador) => {
    try {
      const hoy = new Date();
      const fechaHoy = hoy.toLocaleDateString('es-EC', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).replace(/\//g, '-');

      const reporteRef = doc(db, 'reportesDiarios', `${nombreOperador}_${fechaHoy}`);
      const reporteDoc = await getDoc(reporteRef);

      if (reporteDoc.exists()) {
        setReporteDiario(reporteDoc.data());
      } else {
        // Crear reporte inicial si no existe
        const reporteInicial = {
          operador: nombreOperador,
          fecha: fechaHoy,
          viajesRegistrados: 0,
          viajesAsignados: 0,
          viajesCancelados: 0,
          viajesCanceladosPorCliente: 0,
          viajesCanceladosPorConductor: 0,
          viajesSinUnidad: 0,
          viajesFinalizados: 0,
          vouchersGenerados: 0,
          viajesAutomaticos: 0,
          viajesManuales: 0,
          clientesNuevos: 0,
          ultimaActualizacion: new Date().toISOString()
        };
        await setDoc(reporteRef, reporteInicial);
        setReporteDiario(reporteInicial);
      }
    } catch (error) {
      console.error('❌ Error al cargar reporte diario:', error);
    }
  };

  // Función utilitaria: incrementar contador de clientes nuevos para el operador actual
  const incrementarClienteNuevo = async () => {
    try {
      if (!operadorAutenticado) return;
      const hoy = new Date();
      const fechaHoy = hoy.toLocaleDateString('es-EC', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      }).replace(/\//g, '-');

      const reporteRef = doc(db, 'reportesDiarios', `${operadorAutenticado.nombre}_${fechaHoy}`);
      await updateDoc(reporteRef, {
        clientesNuevos: increment(1),
        ultimaActualizacion: new Date().toISOString()
      });

      if (operadorAutenticado.id) {
        const operadorRef = doc(db, 'operadores', operadorAutenticado.id);
        await updateDoc(operadorRef, { clientesNuevosRegistrados: increment(1) });
      }
    } catch (e) {
      console.error('Error incrementando clientes nuevos:', e);
    }
  };

  // Función para actualizar contador en reporte diario (crea el documento si no existe)
  const actualizarContadorReporte = async (tipoAccion) => {
    if (!operadorAutenticado) return;

    try {
      const hoy = new Date();
      const fechaHoy = hoy.toLocaleDateString('es-EC', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).replace(/\//g, '-');

      const reporteRef = doc(db, 'reportesDiarios', `${operadorAutenticado.nombre}_${fechaHoy}`);
      const campoContador = tipoAccion;

      const reporteSnap = await getDoc(reporteRef);
      if (reporteSnap.exists()) {
        await updateDoc(reporteRef, {
          [campoContador]: increment(1),
          ultimaActualizacion: new Date().toISOString()
        });
      } else {
        const datosIniciales = {
          operador: operadorAutenticado.nombre,
          fecha: fechaHoy,
          viajesRegistrados: 0,
          viajesAsignados: 0,
          viajesCancelados: 0,
          viajesCanceladosPorCliente: 0,
          viajesCanceladosPorConductor: 0,
          viajesSinUnidad: 0,
          viajesManuales: 0,
          ultimaActualizacion: new Date().toISOString()
        };
        datosIniciales[campoContador] = 1;
        await setDoc(reporteRef, datosIniciales);
      }

      setReporteDiario(prev => ({
        ...prev,
        [campoContador]: (prev[campoContador] || 0) + 1
      }));

      console.log(`📊 Contador actualizado: ${tipoAccion}`);
    } catch (error) {
      console.error('❌ Error al actualizar contador:', error);
    }
  };

  // Función para cambiar de usuario
  const cambiarUsuario = () => {
    setMostrarModalOperador(true);
    setCodigoOperador('');
    setErrorAutenticacion('');
  };

  // Función para cerrar sesión de operador
  const cerrarSesionOperador = () => {
    setOperadorAutenticado(null);
    setMostrarModalOperador(false);
    setCodigoOperador('');
    setErrorAutenticacion('');
    setReporteDiario({
      viajesRegistrados: 0,
      viajesAsignados: 0,
      viajesCancelados: 0,
      viajesCanceladosPorCliente: 0,
      viajesCanceladosPorConductor: 0,
      viajesSinUnidad: 0,
      viajesAutomaticos: 0,
      viajesManuales: 0
    });
  };



  
  const [telefono, setTelefono] = useState('');
  const [nombre, setNombre] = useState('');
  const [notaCliente, setNotaCliente] = useState('');
  const [coleccionClienteEncontrada, setColeccionClienteEncontrada] = useState('');
  const [idClienteEncontrado, setIdClienteEncontrado] = useState('');
  const [coordenadas, setCoordenadas] = useState('');
  const [direccion, setDireccion] = useState('');
  const [base, setBase] = useState('');
  const [tiempo, setTiempo] = useState('');
  const [unidad, setUnidad] = useState('');
  const [sector, setSector] = useState('');
  // Inicializar modoSeleccion (fijo en manual, no cambia la interfaz)
  const [modoSeleccion, setModoSeleccion] = useState('manual');
  const [usuarioEncontrado, setUsuarioEncontrado] = useState(null);
  const [buscandoUsuario, setBuscandoUsuario] = useState(false);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState({
    nombre: '',
    direccion: '',
    coordenadas: '',
    email: ''
  });
  const [modal, setModal] = useState({ open: false, success: true, message: '' });
  const [mapaVisible, setMapaVisible] = useState(false); // Para controlar el mapa desde el formulario
  const [modalRegistroCliente, setModalRegistroCliente] = useState({
    open: false,
    tipoCliente: '',
    coleccion: '',
    modoAplicacion: false,
    datosCliente: { telefono: '', nombre: '', direccion: '', coordenadas: '', sector: '', prefijo: 'Ecuador' }
  });
  // Modal de Reserva (F7)
  const [modalReserva, setModalReserva] = useState({
    open: false,
    datosCliente: { telefono: '', nombre: '', direccion: '', coordenadas: '' },
    fechaHora: '',
    motivo: '',
    destino: '',
    pedidoOriginalId: '' // ID del pedido original para eliminarlo después
  });
  // Estado de guardado y error visible DENTRO del modal de reserva
  // (el modal de mensajes general queda por debajo del modal de reserva, por eso no se veía)
  const [guardandoReserva, setGuardandoReserva] = useState(false);
  const [errorReserva, setErrorReserva] = useState('');
  const [viajesAsignados, setViajesAsignados] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Efecto para actualizar la hora actual cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const [cargandoViajes, setCargandoViajes] = useState(false);
  const [editandoViaje, setEditandoViaje] = useState(null);
  const [tiempoEdit, setTiempoEdit] = useState('');
  const [unidadEdit, setUnidadEdit] = useState('');
  const [baseEdit, setBaseEdit] = useState('');
  const [observacionesDraft, setObservacionesDraft] = useState({}); // { [pedidoId]: string }
  const [guardandoObservaciones, setGuardandoObservaciones] = useState({}); // { [pedidoId]: boolean }
  const [modalObservaciones, setModalObservaciones] = useState({
    open: false,
    pedido: null,
    texto: '',
    colecciones: []
  });
  const [pedidosEnCurso, setPedidosEnCurso] = useState([]);
  const [todosLosViajesDia, setTodosLosViajesDia] = useState([]);
  const [todosLosViajesDiaPorCreated, setTodosLosViajesDiaPorCreated] = useState([]);
  const [viajesSubcoleccionDia, setViajesSubcoleccionDia] = useState([]);
  const [fechaFiltroViajes, setFechaFiltroViajes] = useState(ymdEcuador());
  const [filtroTelefonoUltimos3, setFiltroTelefonoUltimos3] = useState('');
  const [filtroUnidad, setFiltroUnidad] = useState('');
  const [cargandoPedidosCurso, setCargandoPedidosCurso] = useState(false);

  const todosLosViajesCombinadosMemo = React.useMemo(() => {
    // "Viajes del Día": solo viajes asignados/en curso e históricos (sin los "disponibles")
    return [
      ...(pedidosEnCurso || []),
      ...(todosLosViajesDia || []),
      ...(todosLosViajesDiaPorCreated || []),
      ...(viajesSubcoleccionDia || [])
    ].filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);
  }, [pedidosEnCurso, todosLosViajesDia, todosLosViajesDiaPorCreated, viajesSubcoleccionDia]);

  const pedidosDisponiblesTablaMemo = React.useMemo(() => {
    // Tabla superior: pedidosDisponibles + pedidosDisponibles1 (tiempo real)
    const parseDate = (val) => {
      if (!val) return null;
      if (val.toDate) return val.toDate();
      if (val.seconds) return new Date(val.seconds * 1000);
      if (val instanceof Date) return val;
      if (typeof val === 'string') {
        const d = new Date(val);
        if (!isNaN(d.getTime())) return d;
      }
      return null;
    };
    return (viajesAsignados || [])
      .filter(v => v?.origenColeccion === 'pedidosDisponibles' || v?.origenColeccion === 'pedidosDisponibles1' || v?.origenColeccion === 'pedidoSolicitado')
      .sort((a, b) => {
        const da = parseDate(a.createdAt) || parseDate(a.fecha) || new Date(0);
        const db2 = parseDate(b.createdAt) || parseDate(b.fecha) || new Date(0);
        return db2 - da;
      });
  }, [viajesAsignados]);

  const conteoPedidosDisponiblesMemo = React.useMemo(() => {
    const arr = pedidosDisponiblesTablaMemo || [];
    const pd = arr.filter(v => v?.origenColeccion === 'pedidosDisponibles').length;
    const pd1 = arr.filter(v => v?.origenColeccion === 'pedidosDisponibles1').length;
    const ps = arr.filter(v => v?.origenColeccion === 'pedidoSolicitado').length;
    return { pd, pd1, ps, total: pd + pd1 + ps };
  }, [pedidosDisponiblesTablaMemo]);

  const pedidosEnCursoIdSetMemo = React.useMemo(() => {
    return new Set((pedidosEnCurso || []).map(p => p.id));
  }, [pedidosEnCurso]);

  const todosLosViajesCombinadosOrdenadosMemo = React.useMemo(() => {
    // Ordenar solo cuando cambie la data (no al tipear)
    const parseDate = (val) => {
      if (!val) return null;
      if (val.toDate) return val.toDate();
      if (val.seconds) return new Date(val.seconds * 1000);
      if (val instanceof Date) return val;
      if (typeof val === 'string') {
        let d = new Date(val);
        if (!isNaN(d.getTime())) return d;
        if (val.includes('/')) {
          try {
            const parts = val.split(' ');
            if (parts.length === 2) {
              const p1 = parts[0]; const p2 = parts[1];
              const timeParts = p1.includes(':') ? p1.split(':') : p2.split(':');
              const dateParts = p1.includes('/') ? p1.split('/') : p2.split('/');
              if (timeParts.length >= 2 && dateParts.length === 3) {
                return new Date(dateParts[2], dateParts[1] - 1, dateParts[0], timeParts[0], timeParts[1], timeParts[2] || 0);
              }
            }
          } catch (e) { }
        }
      }
      return null;
    };

    return [...(todosLosViajesCombinadosMemo || [])].sort((a, b) => {
      const dateA = parseDate(a.createdAt) || parseDate(a.fecha) || new Date(0);
      const dateB = parseDate(b.createdAt) || parseDate(b.fecha) || new Date(0);
      return dateB - dateA;
    });
  }, [todosLosViajesCombinadosMemo]);

  // Nuevo estado para direcciones guardadas
  const [direccionesGuardadas, setDireccionesGuardadas] = useState([]);
  const [direccionSeleccionada, setDireccionSeleccionada] = useState(null);
  // Estados para edición de direcciones
  const [editandoDireccion, setEditandoDireccion] = useState(null);
  const [textoEditado, setTextoEditado] = useState('');
  const [agregandoNuevaDireccion, setAgregandoNuevaDireccion] = useState(false);
  const [nuevaDireccionTexto, setNuevaDireccionTexto] = useState('');
  const [direccionesPorViaje, setDireccionesPorViaje] = useState({});
  const [mostrandoDropdownDireccion, setMostrandoDropdownDireccion] = useState({});
  const [agregandoDireccionViaje, setAgregandoDireccionViaje] = useState({});
  const [nuevaDireccionViajeTexto, setNuevaDireccionViajeTexto] = useState({});

  // Referencias para los inputs del formulario
  const tiempoInputRef = useRef(null);
  const unidadInputRef = useRef(null);
  const tiempoEditInputRef = useRef(null);
  const unidadEditInputRef = useRef(null);

  // Estados para modal de acciones del pedido
  const [modalAccionesPedido, setModalAccionesPedido] = useState({
    open: false,
    pedido: null,
    coleccion: '' // 'pedidosDisponibles' o 'pedidoEnCurso'
  });

  const conductoresCercanosOrdenadosMemo = React.useMemo(() => {
    const arr = modalAccionesPedido?.pedido?.conductoresCercanos;
    if (!Array.isArray(arr)) return [];
    return [...arr].sort((a, b) => (a.distancia || 0) - (b.distancia || 0));
  }, [modalAccionesPedido?.pedido?.conductoresCercanos]);

  // Modal para seleccionar empresa al generar autorización
  const [modalEmpresaAutorizacion, setModalEmpresaAutorizacion] = useState({
    open: false,
    empresaSeleccionada: EMPRESAS_VOUCHER[0] || ''
  });

  // Estado para mensaje del conductor
  const [mensajeConductor, setMensajeConductor] = useState('');

  // Estado para controlar múltiples inserciones
  const [insertandoRegistro, setInsertandoRegistro] = useState(false);

  // Estado para mostrar el texto de la selección
  const [textoSeleccion, setTextoSeleccion] = useState('Selección Manual');

  // Estados para los nuevos campos de empresa y modo de selección
  const [tipoEmpresa, setTipoEmpresa] = useState('Efectivo');
  const [modoSeleccionUI, setModoSeleccionUI] = useState('Manual');

  // Estado para autorización pre-generada
  const [numeroAutorizacion, setAutorizacionPreGenerada] = useState('');

  // Estado para visibilidad del mapa en el dashboard
  const [mapaDashboardVisible, setMapaDashboardVisible] = useState(true);
  const [mapaDashboardAncho, setMapaDashboardAncho] = useState('40%'); // 25%, 40%, 60%
  const [focusPedidoMapaId, setFocusPedidoMapaId] = useState(null);
  const [modalNotificados, setModalNotificados] = useState({ open: false, pedido: null });

  const abrirModalNotificados = (pedido) => {
    setModalNotificados({ open: true, pedido });
    setMapaDashboardVisible(true);
    setFocusPedidoMapaId(pedido?.id || null);
  };
  const cerrarModalNotificados = () => setModalNotificados({ open: false, pedido: null });

  const [marcandoNoAtendido, setMarcandoNoAtendido] = useState(false);
  const marcarPedidoComoNoAtendido = async () => {
    const p = modalNotificados.pedido;
    if (!p?.id) return;
    const origen = (p.origenColeccion || '').toString();
    const coleccion = origen === 'pedidosDisponibles1' ? 'pedidosDisponibles1' : 'pedidosDisponibles';
    setMarcandoNoAtendido(true);
    try {
      const fechaActual = new Date();
      const viajeNoAtendidoData = {
        ...p,
        estado: 'No atendido',
        pedido: 'No atendido',
        fecha: fechaActual,
        fechaRegistroNoAtendido: fechaActual,
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador'
      };
      await setDoc(doc(db, 'todosLosViajes', p.id), viajeNoAtendidoData);
      await deleteDoc(doc(db, coleccion, p.id));
      try {
        await actualizarContadorReporte('viajesSinUnidad');
      } catch (_) {}
      cerrarModalNotificados();
      setModal({ open: true, success: true, message: 'Pedido marcado como no atendido y eliminado de disponibles.' });
    } catch (error) {
      console.error('Error al marcar pedido como no atendido:', error);
      setModal({ open: true, success: false, message: 'Error al marcar como no atendido.' });
    } finally {
      setMarcandoNoAtendido(false);
    }
  };

  // useEffect para monitorear cambios en el modal de reserva
  useEffect(() => {
    console.log('🔄 Estado del modal de reserva cambió:', modalReserva);
  }, [modalReserva]);

  // Ya no se pre-genera ni se muestra la autorización al seleccionar empresa (solo se asigna al registrar el pedido)
  useEffect(() => {
    setAutorizacionPreGenerada('');
  }, [tipoEmpresa]);

  // useEffect eliminado: la pre-generación de autorización solo ocurre al insertar el pedido

  // Función para actualizar la configuración en la colección
  const actualizarConfiguracion = async (nuevoEstado) => {
    try {
      // Obtener el documento de configuración
      const configRef = doc(db, 'configuracion', 'status');

      // Actualizar el documento
      await updateDoc(configRef, {
        estado: nuevoEstado,
        fechaActualizacion: new Date()
      });

      console.log(`✅ Estado de configuración actualizado a: ${nuevoEstado ? 'Automático' : 'Manual'}`);
    } catch (error) {
      console.error('❌ Error al actualizar configuración:', error);
    }
  };

  // Función para cambiar el estado en la colección configuracion (F1)
  const cambiarEstadoConfiguracion = async () => {
    try {
      // Obtener el documento de configuración
      const configRef = doc(db, 'configuracion', 'status');

      // Obtener el estado actual del documento
      const configDoc = await getDoc(configRef);

      if (configDoc.exists()) {
        const estadoActual = configDoc.data().estado;
        // Cambiar al estado opuesto (true = automático, false = manual)
        const nuevoEstado = !estadoActual;

        // Actualizar el documento
        await updateDoc(configRef, {
          estado: nuevoEstado,
          fechaActualizacion: new Date()
        });

        // Actualizar solo el texto mostrado y los nuevos campos
        setTextoSeleccion(nuevoEstado ? 'Selección Automática' : 'Selección Manual');
        setModoSeleccionUI(nuevoEstado ? 'Automática' : 'Manual');

        console.log(`✅ Estado de configuración cambiado de ${estadoActual ? 'Automático' : 'Manual'} a ${nuevoEstado ? 'Automático' : 'Manual'}`);
      } else {
        // Si el documento no existe, crearlo con estado manual (false)
        await setDoc(configRef, {
          estado: false,
          fechaActualizacion: new Date()
        });

        setTextoSeleccion('Selección Manual');
        setModoSeleccionUI('Manual');
        console.log('✅ Documento de configuración creado con estado Manual');
      }
    } catch (error) {
      console.error('❌ Error al actualizar configuración:', error);
    }
  };

  // Función para cargar el estado inicial desde la colección configuracion
  const cargarEstadoConfiguracion = async () => {
    try {
      const configRef = doc(db, 'configuracion', 'status');
      const configDoc = await getDoc(configRef);

      if (configDoc.exists()) {
        const estado = configDoc.data().estado;
        // Actualizar el texto según el estado en la BD
        setTextoSeleccion(estado ? 'Selección Automática' : 'Selección Manual');
        setModoSeleccionUI(estado ? 'Automática' : 'Manual');
        console.log(`📋 Estado cargado: ${estado ? 'Automático' : 'Manual'} - Texto: ${estado ? 'Selección Automática' : 'Selección Manual'}`);
      } else {
        // Si no existe el documento, crear con estado manual por defecto
        await setDoc(configRef, {
          estado: false,
          fechaActualizacion: new Date()
        });
        setTextoSeleccion('Selección Manual');
        setModoSeleccionUI('Manual');
        console.log('📋 Documento de configuración creado con estado Manual por defecto');
      }
    } catch (error) {
      console.error('❌ Error al cargar configuración:', error);
    }
  };

  // Cargar estado inicial al montar el componente (solo para crear documento si no existe)
  useEffect(() => {
    cargarEstadoConfiguracion();
  }, []);

  // Guardar modoSeleccion en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem('modoSeleccion', modoSeleccion);
    console.log(`🔄 Modo cambiado a: ${modoSeleccion}`);
  }, [modoSeleccion]);

  // Efecto para cerrar el mapa si la dirección se borra por completo manualmente
  useEffect(() => {
    // Solo cerrar si la dirección está vacía Y el mapa estaba visible
    // Pero evitamos cerrarlo si el mapa acaba de ser activado (prevención de cierre inmediato)
    if (direccion.trim() === '' && mapaVisible) {
      // Pequeño retraso para evitar cierres accidentales durante transiciones
      const timer = setTimeout(() => {
        if (direccion.trim() === '') {
          setMapaVisible(false);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [direccion, mapaVisible]);

  // Función para actualizar el nombre del cliente en la base de datos
  const actualizarNombreCliente = async () => {
    try {
      if (!telefono || !nombre.trim()) {
        console.log('❌ Teléfono o nombre no válidos para actualizar');
        return;
      }
      // notaCliente se guarda junto con el nombre (opcional)

      console.log('🔄 Actualizando nombre del cliente:', nombre.trim(), 'para teléfono:', telefono);

      // Usar la colección donde se encontró el cliente (si se guardó), o determinar por longitud
      let coleccionNombre = coleccionClienteEncontrada || 'clientestelefonos';
      let telefonoId = telefono;

      if (telefono.length < 7) {
        console.log('❌ Teléfono demasiado corto para actualizar');
        return;
      }

      if (!coleccionClienteEncontrada) {
        // Fallback: determinar colección por longitud si no se guardó
        if (telefono.length > 7) {
          coleccionNombre = 'clientestelefonos1';
          if (telefono.startsWith('593')) telefonoId = telefono;
          else if (telefono.length === 10 && telefono.startsWith('0')) telefonoId = '593' + telefono.substring(1);
          else if (telefono.length === 9) telefonoId = '593' + telefono;
        }
      } else if (telefono.length > 7 && coleccionNombre === 'clientestelefonos1') {
        if (telefono.startsWith('593')) telefonoId = telefono;
        else if (telefono.length === 10 && telefono.startsWith('0')) telefonoId = '593' + telefono.substring(1);
        else if (telefono.length === 9) telefonoId = '593' + telefono;
      }

      // Intentar primero en la colección determinada
      let clienteRef = doc(db, coleccionNombre, telefonoId);
      let clienteSnapshot = await getDoc(clienteRef);

      // Si no existe en la colección principal, intentar otras colecciones para 7 dígitos
      if (!clienteSnapshot.exists() && telefono.length === 7) {
        const coleccionesIntento = ['clientes', 'clientestelefonos', 'clientestelefonos1'].filter(c => c !== coleccionNombre);
        for (const col of coleccionesIntento) {
          const candidatos = [telefono, `02${telefono}`, `59302${telefono}`];
          for (const cand of candidatos) {
            const ref = doc(db, col, cand);
            // eslint-disable-next-line no-await-in-loop
            const snap = await getDoc(ref);
            if (snap.exists()) {
              clienteRef = ref;
              clienteSnapshot = snap;
              coleccionNombre = col;
              telefonoId = cand;
              break;
            }
          }
          if (clienteSnapshot.exists()) break;
        }
      }

      if (!clienteSnapshot.exists()) {
        console.log('❌ Cliente no encontrado para actualizar nombre');
        return;
      }

      // Actualizar nombre y nota en Firestore
      await updateDoc(clienteRef, {
        nombre: nombre.trim(),
        nota: (notaCliente || '').trim()
      });

      // Actualizar el estado local del usuario encontrado
      if (usuarioEncontrado) {
        setUsuarioEncontrado({
          ...usuarioEncontrado,
          nombre: nombre.trim(),
          nota: (notaCliente || '').trim()
        });
      }

      console.log('✅ Nombre y nota del cliente actualizados:', nombre.trim());

      // Mostrar mensaje de confirmación
      setModal({
        open: true,
        success: true,
        message: `Datos del cliente guardados correctamente.`
      });

    } catch (error) {
      console.error('💥 Error al actualizar nombre del cliente:', error);
      setModal({
        open: true,
        success: false,
        message: 'Error al actualizar el nombre del cliente. Intente nuevamente.'
      });
    }
  };

  const actualizarDireccionCliente = async () => {
    try {
      if (!telefono || !direccion.trim()) {
        alert('⚠️ Ingrese un teléfono y una dirección válidos');
        return;
      }
      const exito = await guardarEnHistorialCliente(telefono, direccion.trim(), coordenadas || '', 'panel');
      if (exito) {
        alert('✅ Dirección guardada exitosamente en el historial del cliente');
        await mostrarDireccionesGuardadas(telefono);
      } else {
        alert('❌ No se pudo guardar la dirección. Verifique si el cliente existe.');
      }
    } catch (error) {
      console.error('Error al actualizar dirección:', error);
      alert('Error técnico al intentar guardar la dirección');
    }
  };

  const handleSaveLocationFromMap = async (coords) => {
    try {
      if (!telefono || !direccion.trim()) {
        alert('⚠️ No se puede guardar sin teléfono o dirección');
        return;
      }

      console.log('📍 Guardando ubicación desde el mapa:', { telefono, direccion, coords });
      const exito = await guardarEnHistorialCliente(telefono, direccion.trim(), coords, 'mapa');

      if (exito) {
        alert('✅ Ubicación guardada exitosamente en el historial del cliente');
        await mostrarDireccionesGuardadas(telefono);
        setCoordenadas(coords);
        setMapaVisible(false);
      } else {
        alert('❌ Error al guardar la ubicación. Asegúrese de que el cliente esté registrado.');
      }
    } catch (error) {
      console.error('💥 Error al guardar ubicación desde mapa:', error);
      alert('Error técnico al guardar la ubicación');
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'F1') {
        event.preventDefault();
        // Solo cambiar el estado en la colección configuracion
        cambiarEstadoConfiguracion();
      }
      // Abrir modal de reserva con F7
      if (event.key === 'F7') {
        event.preventDefault();
        // Validar que exista un cliente buscado o datos mínimos cargados
        const telefonoValido = (telefono || '').trim().length >= 7;
        const nombreValido = (nombre || '').trim().length > 0;
        const direccionValida = (direccion || '').trim().length > 0;
        if (!telefonoValido || (!usuarioEncontrado && (!nombreValido || !direccionValida))) {
          setModal({ open: true, success: false, message: 'Primero busque un cliente por teléfono y cargue sus datos (nombre y dirección) para poder reservar.' });
          return;
        }

        // Prefijar fecha y hora actuales en formato datetime-local
        const ahora = new Date();
        const tzOffset = ahora.getTimezoneOffset();
        const localISOTime = new Date(ahora.getTime() - tzOffset * 60000).toISOString().slice(0, 16);

        setErrorReserva('');
        setModalReserva({
          open: true,
          datosCliente: {
            telefono: telefono || '',
            nombre: nombre || usuarioEncontrado?.nombre || '',
            direccion: direccion || '',
            coordenadas: coordenadas || ''
          },
          fechaHora: localISOTime,
          motivo: '',
          destino: ''
        });
      }
      if (event.key === 'Escape') {
        setMostrarModal(false);
        setModalReserva(prev => ({ ...prev, open: false }));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [modoSeleccion, telefono, nombre, direccion, usuarioEncontrado, coordenadas, operadorAutenticado]); // Dependencias para valores recientes en F7

  // Guardar reserva en Firestore
  // Helper: formatear fecha/hora local dd/MM/yyyy HH:mm
  const formatearFechaHora = (fecha) => {
    try {
      const d = new Date(fecha);
      const pad = (n) => String(n).padStart(2, '0');
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = d.getFullYear();
      const hours = pad(d.getHours());
      const mins = pad(d.getMinutes());
      return `${day}/${month}/${year} ${hours}:${mins}`;
    } catch {
      return '';
    }
  };

  // Helper: POST x-www-form-urlencoded
  const postFormUrlEncoded = async (url, params) => {
    const body = new URLSearchParams(params).toString();
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body
    });
    return res;
  };

  // Helper: POST JSON (envíos de WhatsApp). Lleva la sesión del panel: el servidor la exige.
  const postJson = async (url, params) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await cabeceraSesion()) },
      body: JSON.stringify(params)
    });
    return res;
  };

  // En producción (HTTPS) usar proxy para evitar Mixed Content; en dev usar URL directa
  const getWhatsAppSendUrl = () => {
    // El servidor del panel decide el proveedor (WhatsApp Web o la API anterior)
    if (process.env.REACT_APP_WHATSAPP_SEND_URL) return process.env.REACT_APP_WHATSAPP_SEND_URL;
    return '/api/whatsapp/send';
  };

  // API para enviar mensaje con imagen al cliente (info del conductor). Ruta relativa para evitar CORS (proxy en dev y rewrite en Vercel).
  const getSendImageMessageUrl = () => {
    if (process.env.REACT_APP_SEND_IMAGE_MESSAGE_URL) return process.env.REACT_APP_SEND_IMAGE_MESSAGE_URL;
    return '/api/send-image-message';
  };
  const IMAGEN_DEFAULT_CLIENTE = 'https://firebasestorage.googleapis.com/v0/b/ecudriver-30c0a.appspot.com/o/Generated%20Image%20February%2001%2C%202026%20-%208_10AM%20(1).jpeg?alt=media&token=c4a6d0f4-1b1a-488c-b9d0-b1e2507807b2';

  /** Formato del mensaje al cliente: CONDUCTOR EN CAMINO con datos del conductor y vehículo */
  const formatearMensajeConductorAlCliente = (conductorData, unidad, tiempoMinutos) => {
    const nombre = (conductorData?.nombre || 'N/D').toString().trim();
    let telRaw = (conductorData?.telefono || conductorData?.celular || '').toString().replace(/\D/g, '');
    if (telRaw.startsWith('593') && telRaw.length >= 12) telRaw = '0' + telRaw.slice(3);
    else if (telRaw.length === 9 && !telRaw.startsWith('0')) telRaw = '0' + telRaw;
    const telefonoDisplay = telRaw || 'N/D';
    const unidadDisplay = (unidad || 'N/D').toString().trim();
    const placa = (conductorData?.placa || 'N/D').toString().trim();
    const color = (conductorData?.color || 'N/D').toString().trim();
    const tiempo = (tiempoMinutos != null && tiempoMinutos !== '') ? `${tiempoMinutos}` : '10';
    return `🚕 *CONDUCTOR EN CAMINO* 🚕

⏳ Tiempo de llegada: ${tiempo} Minutos
👤 Conductor: ${nombre}
📱 Teléfono: ${telefonoDisplay}
🚕 Unidad: ${unidadDisplay}

Datos del vehículo:
🆔 Placa: ${placa}
🎨 Color: ${color}

Gracias por preferir Coop. Plaza Del Valle. ¡Buen viaje!`;
  };

  const enviarMensajeImagenAlCliente = async (telefonoCliente, mensajeTexto, imageUrl) => {
    const phone = normalizarTelefonoParaWhatsAppApi(telefonoCliente || '');
    if (!phone) return;
    try {
      const url = getSendImageMessageUrl();
      const body = { phone, message: mensajeTexto || 'Tu pedido está listo.', imageUrl: imageUrl || IMAGEN_DEFAULT_CLIENTE };
      const res = await postJson(url, body);
      if (!res.ok) console.error('API send-image-message cliente:', res.status, await res.text());
      else console.log('✅ Mensaje con imagen enviado al cliente.');
    } catch (e) {
      console.error('Error enviar imagen al cliente:', e);
    }
  };

  const normalizarTelefonoParaWhatsAppApi = (telefonoRaw) => {
    const digits = (telefonoRaw ?? '').toString().replace(/\D/g, '');
    if (!digits) return '';

    // API 3005 espera "593994633688" (sin +)
    if (digits.startsWith('593')) return digits;
    if (digits.length === 10 && digits.startsWith('0')) return `593${digits.substring(1)}`;
    if (digits.length === 9 && !digits.startsWith('0')) return `593${digits}`;
    if (digits.length === 7) return `02${digits}`;
    return digits;
  };

  const extraUbicacionGoogleMaps = (pedido, { skipTipoCheck = false } = {}) => {
    try {
      if (!skipTipoCheck) {
        const tipo = (pedido?.tipoPedido || pedido?.tipopedido || '').toString().trim().toLowerCase();
        if (tipo !== 'ubicacion') return null;
      }

      let coords = (pedido?.coordenadas || '').toString().trim();
      if (!coords || !coords.includes(',')) {
        const lat = (pedido?.latitud ?? '').toString().trim();
        const lng = (pedido?.longitud ?? '').toString().trim();
        if (lat && lng) coords = `${lat},${lng}`;
      }
      if (!coords || !coords.includes(',')) return null;

      const [latRaw, lngRaw] = coords.split(',').map(s => s.trim());
      const lat = Number(latRaw);
      const lng = Number(lngRaw);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

      const coordsFinal = `${lat},${lng}`;
      const link = `https://www.google.com/maps?q=${encodeURIComponent(coordsFinal)}`;
      return { coords: coordsFinal, link };
    } catch {
      return null;
    }
  };

  // Enviar mensajes a grupo y al cliente (según regla)
  const enviarMensajesReserva = async (reserva) => {
    try {
      const fechaTxt = formatearFechaHora(reserva.fechaHoraReserva);
      const msgGrupo = [
        '📅 Nueva Reserva',
        `👤 Cliente: ${reserva.nombreCliente || 'N/D'}`,
        `📍 Dirección: ${reserva.direccion || 'N/D'}`,
        `🎯 Destino: ${reserva.destino || 'N/D'}`,
        `🕒 Fecha/Hora: ${fechaTxt || 'N/D'}`,
        `📝 Motivo: ${reserva.motivo || 'N/D'}`,
        reserva.tipoEmpresa && reserva.tipoEmpresa !== 'Efectivo' ? `🏢 Empresa: ${reserva.tipoEmpresa}` : null,
        reserva.numeroAutorizacion ? `🔑 Autorización: ${reserva.numeroAutorizacion}` : null,
        reserva.operador?.nombre ? `👨‍💼 Operador: ${reserva.operador.nombre}` : null
      ].filter(Boolean).join('\n');

      // Enviar al grupo (sin teléfono)
      await postFormUrlEncoded('http://84.46.245.131:3001/send/group/message', {
        to: '120363343871245265',
        message: msgGrupo
      });

      // Enviar al cliente si el teléfono tiene más de 8 dígitos
      const telRaw = (reserva.telefono || '').trim();
      if (telRaw.length > 8) {
        const toNumber = reserva.telefonoCompleto || concatenarTelefonoWhatsApp(telRaw, 'Ecuador');
        const msgCliente = [
          '✅ Reserva registrada',
          `🕒 ${fechaTxt}`,
          `📍 Desde: ${reserva.direccion || 'N/D'}`,
          reserva.destino ? `🎯 Hacia: ${reserva.destino}` : null,
          reserva.motivo ? `📝 Motivo: ${reserva.motivo}` : null,
          reserva.tipoEmpresa && reserva.tipoEmpresa !== 'Efectivo' ? `🏢 Empresa: ${reserva.tipoEmpresa}` : null
        ].filter(Boolean).join('\n');

        await postJson(getWhatsAppSendUrl(), {
          to: normalizarTelefonoParaWhatsAppApi(toNumber),
          message: msgCliente
        });
      }
    } catch (e) {
      console.error('⚠️ Error al enviar notificaciones de reserva:', e);
    }
  };

  const guardarReserva = async () => {
    if (guardandoReserva) return; // evitar doble clic / reservas duplicadas
    setErrorReserva('');
    try {
      // Único dato obligatorio: la fecha/hora. El motivo es opcional.
      if (!modalReserva.fechaHora) {
        setErrorReserva('Seleccione la fecha y hora de la reserva.');
        return;
      }
      if (isNaN(new Date(modalReserva.fechaHora).getTime())) {
        setErrorReserva('La fecha y hora de la reserva no es válida.');
        return;
      }

      setGuardandoReserva(true);

      // Intentar obtener teléfono completo para WhatsApp si es celular
      let telefonoCompleto = modalReserva.datosCliente.telefono || '';
      const telRaw = (modalReserva.datosCliente.telefono || '').trim();
      if (telRaw.length >= 9 && telRaw.length <= 10) {
        try {
          const telefonoCompletoBusqueda = concatenarTelefonoWhatsApp(telRaw, 'Ecuador');
          let clienteRef = doc(db, 'clientestelefonos', telefonoCompletoBusqueda);
          let clienteSnap = await getDoc(clienteRef);
          if (clienteSnap.exists()) {
            const pref = clienteSnap.data().prefijo || 'Ecuador';
            telefonoCompleto = concatenarTelefonoWhatsApp(telRaw, pref);
          } else {
            const ult9 = telRaw.slice(-9);
            clienteRef = doc(db, 'clientestelefonos', ult9);
            clienteSnap = await getDoc(clienteRef);
            if (clienteSnap.exists()) {
              const pref = clienteSnap.data().prefijo || 'Ecuador';
              telefonoCompleto = concatenarTelefonoWhatsApp(telRaw, pref);
            }
          }
        } catch (e) {
          // fallback: dejar el número tal cual
        }
      }

      const fechaReserva = new Date(modalReserva.fechaHora);

      const reservaData = {
        telefono: modalReserva.datosCliente.telefono || '',
        telefonoCompleto: telefonoCompleto || modalReserva.datosCliente.telefono || '',
        nombreCliente: modalReserva.datosCliente.nombre || '',
        direccion: modalReserva.datosCliente.direccion || '',
        coordenadas: modalReserva.datosCliente.coordenadas || '',
        fechaHoraReserva: fechaReserva,
        motivo: modalReserva.motivo.trim(),
        destino: (modalReserva.destino || '').trim(),
        estado: 'pendiente',
        origen: 'central',
        createdAt: new Date(),
        // Información de empresa y autorización (si aplica)
        tipoEmpresa: tipoEmpresa || 'Efectivo',
        autorizacion: tipoEmpresa && tipoEmpresa !== 'Efectivo' ? await obtenerSiguienteAutorizacion() : '',
        operador: operadorAutenticado ? {
          id: operadorAutenticado.id || '',
          nombre: operadorAutenticado.nombre || '',
          email: operadorAutenticado.email || ''
        } : { id: '', nombre: 'Sin operador', email: '' }
      };

      const docRef = await addDoc(collection(db, 'reservas'), reservaData);
      await updateDoc(docRef, { id: docRef.id });
      console.log('✅ Reserva creada en Firestore:', docRef.id);

      // Disparar actualización de la UI si se generó autorización
      if (reservaData.autorizacion) {
        setUiUpdateTrigger(prev => prev + 1);
      }

      // Notificaciones de WhatsApp en segundo plano: si la API no responde
      // no debe dejar el modal congelado (la reserva ya está guardada)
      enviarMensajesReserva(reservaData).catch(e =>
        console.error('⚠️ Error al enviar notificaciones de reserva:', e)
      );

      // Si la reserva se creó desde un pedido disponible, eliminar el pedido original
      if (modalReserva.pedidoOriginalId) {
        try {
          const pedidoRef = doc(db, 'pedidosDisponibles', modalReserva.pedidoOriginalId);
          await deleteDoc(pedidoRef);
          console.log('✅ Pedido original eliminado de pedidosDisponibles:', modalReserva.pedidoOriginalId);
        } catch (error) {
          console.error('⚠️ Error al eliminar pedido original:', error);
        }
      }

      setModalReserva({
        open: false,
        datosCliente: { telefono: '', nombre: '', direccion: '', coordenadas: '' },
        fechaHora: '',
        motivo: '',
        destino: '',
        pedidoOriginalId: ''
      });

      // Resetear tipo de empresa a "Efectivo" después de guardar la reserva
      setTipoEmpresa('Efectivo');

      setModal({ open: true, success: true, message: 'Reserva guardada exitosamente.' });
    } catch (error) {
      console.error('❌ Error al guardar la reserva:', error);
      // Mostrar el motivo real dentro del propio modal de reserva
      setErrorReserva(`Error al guardar la reserva: ${error?.message || error}`);
    } finally {
      setGuardandoReserva(false);
    }
  };

  // Configurar listeners en tiempo real para las colecciones
  useEffect(() => {
    // Estados para almacenar pedidos de ambas colecciones
    let pedidosDisponibles = [];
    let pedidosDisponibles1 = [];
    let pedidosSolicitados = [];

    // Función para combinar y actualizar los pedidos
    const combinarPedidos = () => {
      const todosLosPedidos = [...pedidosDisponibles, ...pedidosDisponibles1, ...pedidosSolicitados];

      // Ordenar por fecha de creación más reciente primero
      todosLosPedidos.sort((a, b) => {
        if (a.fecha && b.fecha) {
          let fechaA, fechaB;

          if (a.fecha.toDate) {
            fechaA = a.fecha.toDate();
          } else if (a.fecha.seconds) {
            fechaA = new Date(a.fecha.seconds * 1000);
          } else {
            fechaA = new Date(a.fecha);
          }

          if (b.fecha.toDate) {
            fechaB = b.fecha.toDate();
          } else if (b.fecha.seconds) {
            fechaB = new Date(b.fecha.seconds * 1000);
          } else {
            fechaB = new Date(b.fecha);
          }

          return fechaB - fechaA;
        }
        return 0;
      });

      console.log('✅ Pedidos combinados para mostrar:', todosLosPedidos.length);
      console.log('   - pedidosDisponibles:', pedidosDisponibles.length);
      console.log('   - pedidosDisponibles1:', pedidosDisponibles1.length);
      console.log('   - pedidoSolicitado:', pedidosSolicitados.length);
      setViajesAsignados(todosLosPedidos);
      setCargandoViajes(false);
    };

    // Listener para pedidosDisponibles
    const qDisponibles = query(collection(db, 'pedidosDisponibles'));
    const unsubscribeDisponibles = onSnapshot(qDisponibles, (querySnapshot) => {
      console.log('🔄 Listener de pedidosDisponibles ejecutado');
      console.log('📊 Número de documentos:', querySnapshot.docs.length);

      pedidosDisponibles = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📄 Documento encontrado:', doc.id, data);
        return {
          id: doc.id,
          ...data,
          origenColeccion: 'pedidosDisponibles'
        };
      });

      combinarPedidos();
    }, (error) => {
      console.error('❌ Error en listener de pedidosDisponibles:', error);
      setCargandoViajes(false);
    });

    // Listener para pedidosDisponibles1
    const qDisponibles1 = query(collection(db, 'pedidosDisponibles1'));
    const unsubscribeDisponibles1 = onSnapshot(qDisponibles1, (querySnapshot) => {
      console.log('🔄 Listener de pedidosDisponibles1 ejecutado');
      console.log('📊 Número de documentos:', querySnapshot.docs.length);

      pedidosDisponibles1 = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📄 Documento encontrado (pedidosDisponibles1):', doc.id, data);
        return {
          id: doc.id,
          ...data,
          origenColeccion: 'pedidosDisponibles1'
        };
      });

      combinarPedidos();
    }, (error) => {
      console.error('❌ Error en listener de pedidosDisponibles1:', error);
      // No detener la carga si hay error en una colección
    });

    // Listener para pedidoSolicitado (App)
    const qSolicitados = query(collection(db, 'pedidoSolicitado'));
    const unsubscribeSolicitados = onSnapshot(qSolicitados, (querySnapshot) => {
      console.log('🔄 Listener de pedidoSolicitado ejecutado');
      console.log('📊 Número de documentos:', querySnapshot.docs.length);

      pedidosSolicitados = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📱 Documento encontrado (pedidoSolicitado):', doc.id, data);
        return {
          id: doc.id,
          ...data,
          origenPedido: 'app', // Flag para identificar origen
          origenColeccion: 'pedidoSolicitado',
          nombreCliente: data.nombreCliente || data.codigo,
          telefono: data.telefono,
          direccion: data.direccion,
          destino: data.destino,
          fecha: data.fecha || data.createdAt
        };
      });

      combinarPedidos();
    }, (error) => {
      console.error('❌ Error en listener de pedidoSolicitado:', error);
      // No detener la carga si hay error en una colección
    });

    // Listener para pedidoEnCurso
    const qEnCurso = query(
      collection(db, 'pedidoEnCurso'),
      orderBy('fecha', 'desc')
    );
    const unsubscribeEnCurso = onSnapshot(qEnCurso, async (querySnapshot) => {
      const pedidos = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setPedidosEnCurso(pedidos);
      setCargandoPedidosCurso(false);

      // Verificar si hay pedidos de aplicación con estado "Iniciado"
      const pedidosIniciados = pedidos.filter(pedido =>
        pedido.tipopedido === 'Automático' && pedido.pedido === 'Iniciado'
      );

      if (pedidosIniciados.length > 0) {
        console.log(`🚀 ${pedidosIniciados.length} pedidos de aplicación iniciados`);
      }
    }, (error) => {
      console.error('Error en listener de pedidoEnCurso:', error);
      setCargandoPedidosCurso(false);
    });
    return () => {
      if (typeof unsubscribeDisponibles === 'function') {
        unsubscribeDisponibles();
      }
      if (typeof unsubscribeDisponibles1 === 'function') {
        unsubscribeDisponibles1();
      }
      if (typeof unsubscribeSolicitados === 'function') {
        unsubscribeSolicitados();
      }
      if (typeof unsubscribeEnCurso === 'function') {
        unsubscribeEnCurso();
      }
    };
  }, []);

  // Listener para todosLosViajes (basado en la fecha seleccionada)
  useEffect(() => {
    if (!fechaFiltroViajes) return;

    const { inicio: fechaInicio, fin: fechaFin } = rangoDiaEcuadorDesdeYmd(fechaFiltroViajes);
    if (!fechaInicio || !fechaFin) return;

    console.log(`📡 Consultando todosLosViajes para: ${fechaFiltroViajes}`);

    const qTodosViajes = query(
      collection(db, 'todosLosViajes'),
      where('fecha', '>=', Timestamp.fromDate(fechaInicio)),
      where('fecha', '<=', Timestamp.fromDate(fechaFin)),
      orderBy('fecha', 'desc')
    );

    const unsubscribeTodosViajes = onSnapshot(qTodosViajes, (querySnapshot) => {
      const viajes = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        esHistorico: true
      }));
      setTodosLosViajesDia(viajes);
    }, (error) => {
      console.error('Error en listener de todosLosViajes:', error);
    });

    // Consulta secundaria por createdAt (para capturar registros donde fecha es string)
    let unsubscribeTodosViajesCreated = () => { };
    try {
      const qTodosViajesCreated = query(
        collection(db, 'todosLosViajes'),
        where('createdAt', '>=', Timestamp.fromDate(fechaInicio)),
        where('createdAt', '<=', Timestamp.fromDate(fechaFin)),
        orderBy('createdAt', 'desc')
      );

      unsubscribeTodosViajesCreated = onSnapshot(qTodosViajesCreated, (querySnapshot) => {
        const viajes = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          esHistorico: true
        }));
        setTodosLosViajesDiaPorCreated(viajes);
      }, (error) => {
        console.warn('⚠️ Nota: Es posible que falte el índice para createdAt en todosLosViajes', error);
      });
    } catch (e) {
      console.warn('Error configurando listener secundario:', e);
    }

    return () => {
      if (typeof unsubscribeTodosViajes === 'function') {
        unsubscribeTodosViajes();
      }
      if (typeof unsubscribeTodosViajesCreated === 'function') {
        unsubscribeTodosViajesCreated();
      }
    };
  }, [fechaFiltroViajes]);

  // Listener para la subcolección viajes/{DD-MM-YYYY}/viajes (estructura por fecha)
  useEffect(() => {
    if (!fechaFiltroViajes) return;

    const fechaDoc = ymdADdMmYyyy(fechaFiltroViajes);
    if (!fechaDoc) return;

    const rutaColeccion = `viajes/${fechaDoc}/viajes`;
    console.log(`📡 Consultando ${rutaColeccion}`);

    const unsubscribeViajesFecha = onSnapshot(
      collection(db, 'viajes', fechaDoc, 'viajes'),
      (querySnapshot) => {
        const viajes = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          esHistorico: true,
          origenColeccion: 'viajesPorFecha',
          rutaColeccion
        }));
        setViajesSubcoleccionDia(viajes);
      },
      (error) => {
        console.warn(`⚠️ Error en listener de ${rutaColeccion}:`, error);
        setViajesSubcoleccionDia([]);
      }
    );

    return () => {
      if (typeof unsubscribeViajesFecha === 'function') {
        unsubscribeViajesFecha();
      }
      setViajesSubcoleccionDia([]);
    };
  }, [fechaFiltroViajes]);

  const cargarViajesAsignados = async () => {
    console.log('🔄 Iniciando carga manual de pedidos disponibles...');
    setCargandoViajes(true);

    try {
      // Leer todos los pedidos disponibles de ambas colecciones
      const qDisponibles = query(collection(db, 'pedidosDisponibles'));
      const qDisponibles1 = query(collection(db, 'pedidosDisponibles1'));
      const qSolicitados = query(collection(db, 'pedidoSolicitado'));

      console.log('📡 Ejecutando consulta a pedidosDisponibles, pedidosDisponibles1 y pedidoSolicitado...');

      // Variables para almacenar los datos de cada colección
      let pedidosDisponiblesData = [];
      let pedidosDisponibles1Data = [];
      let pedidoSolicitadoData = [];

      // Función para combinar y actualizar
      const combinarYActualizar = () => {
        const todosLosPedidos = [...pedidosDisponiblesData, ...pedidosDisponibles1Data, ...pedidoSolicitadoData];

        // Ordenar por fecha de creación más reciente primero
        todosLosPedidos.sort((a, b) => {
          const getDate = (fecha) => {
            if (!fecha) return new Date(0);
            if (fecha.toDate) return fecha.toDate();
            if (fecha.seconds) return new Date(fecha.seconds * 1000);
            return new Date(fecha);
          };

          const fechaA = getDate(a.fecha || a.createdAt);
          const fechaB = getDate(b.fecha || b.createdAt);

          return fechaB - fechaA;
        });

        console.log('✅ Pedidos combinados actualizados:', todosLosPedidos.length);
        console.log('   - De pedidosDisponibles:', pedidosDisponiblesData.length);
        console.log('   - De pedidosDisponibles1:', pedidosDisponibles1Data.length);
        console.log('   - De pedidoSolicitado:', pedidoSolicitadoData.length);

        setViajesAsignados(todosLosPedidos);
        setCargandoViajes(false);
      };

      // Suscripción a pedidosDisponibles
      const unsubscribeDisponibles = onSnapshot(qDisponibles, (snapshot) => {
        pedidosDisponiblesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          origenColeccion: 'pedidosDisponibles'
        }));

        // Detectar nuevos pedidos automáticos del panel ('enpoidpanel')
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const data = change.doc.data();
            if (data.tipoPedido === 'enpoidpanel') {
              console.log('🤖 Pedido automático detectado (enpoidpanel). Iniciando búsqueda...');
              // Aquí podría invocarse una función de búsqueda específica si existe
            }
          }
        });

        combinarYActualizar();
      });

      // Suscripción a pedidosDisponibles1
      const unsubscribeDisponibles1 = onSnapshot(qDisponibles1, (snapshot) => {
        pedidosDisponibles1Data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          origenColeccion: 'pedidosDisponibles1'
        }));
        combinarYActualizar();
      });

      // Suscripción a pedidoSolicitado
      const unsubscribeSolicitados = onSnapshot(qSolicitados, (snapshot) => {
        pedidoSolicitadoData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            origenPedido: 'app', // Flag para identificar origen
            origenColeccion: 'pedidoSolicitado',
            // Asegurar que los campos necesarios existan
            nombreCliente: data.nombreCliente || data.codigo,
            telefono: data.telefono,
            direccion: data.direccion,
            destino: data.destino,
            fecha: data.fecha || data.createdAt
          };
        });
        combinarYActualizar();
      });

      // Retornar función de limpieza
      return () => {
        unsubscribeDisponibles();
        unsubscribeDisponibles1();
        unsubscribeSolicitados();
      };
    } catch (error) {
      console.error('❌ Error al cargar pedidos disponibles:', error);
      setCargandoViajes(false);
    }
  };


  // Cargar pedidos en curso
  const cargarPedidosEnCurso = async () => {
    setCargandoPedidosCurso(true);
    try {
      const q = query(
        collection(db, 'pedidoEnCurso'),
        orderBy('fecha', 'desc') // Ordenar por fecha más reciente
      );

      // Usar onSnapshot para escuchar cambios en tiempo real
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const pedidos = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setPedidosEnCurso(pedidos);
        console.log(`📊 Pedidos en curso cargados: ${pedidos.length}`);

        // Verificar si hay pedidos de aplicación con estado "Iniciado"
        const pedidosIniciados = pedidos.filter(pedido =>
          pedido.tipopedido === 'Automático' && pedido.pedido === 'Iniciado'
        );

        if (pedidosIniciados.length > 0) {
          console.log(`🚀 ${pedidosIniciados.length} pedidos de aplicación iniciados`);
        }
      }, (error) => {
        console.error('Error al escuchar pedidos en curso:', error);
      });

      // Guardar la función de limpieza para cuando el componente se desmonte
      return unsubscribe;
    } catch (error) {
      console.error('Error al cargar pedidos en curso:', error);
    } finally {
      setCargandoPedidosCurso(false);
    }
  };

  // Cargar pedidos disponibles





  // Nueva función para buscar en clientestelefonos cuando se presione Insertar
  const buscarClienteFijo = async (numeroTelefono) => {
    if (numeroTelefono.length !== 7) {
      return null; // Solo buscar si tiene exactamente 7 dígitos
    }

    try {
      // Buscar en la colección "clientestelefonos"
      const qClientesFijos = query(
        collection(db, 'clientestelefonos'),
        where("telefono", "==", numeroTelefono)
      );
      const clientesSnapshot = await getDocs(qClientesFijos);

      if (!clientesSnapshot.empty) {
        const clienteData = clientesSnapshot.docs[0].data();
        console.log('Cliente fijo encontrado:', clienteData);
        return clienteData;
      }

      // Si no se encuentra en "clientestelefonos", buscar en "teléfonos fijos"
      const qTelefonosFijos = query(
        collection(db, 'teléfonos fijos'),
        where("telefono", "==", numeroTelefono)
      );
      const telefonosSnapshot = await getDocs(qTelefonosFijos);

      if (!telefonosSnapshot.empty) {
        const telefonoData = telefonosSnapshot.docs[0].data();
        console.log('Teléfono fijo encontrado:', telefonoData);
        return telefonoData;
      }

      return null; // No se encontró en ninguna colección
    } catch (error) {
      console.error('Error al buscar cliente fijo:', error);
      return null;
    }
  };

  // Nueva función para buscar en ambas colecciones de clientes
  const buscarCliente = async (numeroTelefono) => {
    try {
      let coleccionNombre = '';
      let tipoCliente = '';
      let telefonoBusqueda = numeroTelefono;

      console.log('🔍 Iniciando búsqueda de cliente con teléfono:', numeroTelefono);

      const raw = (numeroTelefono || '').toString().trim();
      if (raw.length < 7) {
        console.log('❌ Teléfono no cumple criterios:', raw.length, 'dígitos');
        return { encontrado: false, tipoCliente: null };
      }

      // Caso especial: 7 dígitos (teléfono fijo) => buscar en colección "clientes"
      if (raw.length === 7) {
        try {
          tipoCliente = 'cliente fijo';
          coleccionNombre = 'clientes';

          const candidates = [];
          const add = (x) => {
            const v = (x || '').toString().trim();
            if (v && !candidates.includes(v)) candidates.push(v);
          };
          add(raw); // ej. 2862503
          add(`02${raw}`); // ej. 022862503 (campo "telefono")
          add(`59302${raw}`); // ej. 593022862503 (campo "telefonoCompleto")

          // 1) Intentar por ID de documento (algunos registros usan raw como ID)
          for (const cand of candidates) {
            console.log('🔎 (7 dígitos) Buscando en "clientes" por ID:', cand);
            // eslint-disable-next-line no-await-in-loop
            const snap = await getDoc(doc(db, 'clientes', cand));
            if (snap.exists()) {
              const clienteData = snap.data();
              clienteData.id = clienteData.id || snap.id;
              return { encontrado: true, datos: clienteData, tipoCliente, coleccion: 'clientes' };
            }
          }

          // 2) Buscar por campo idSinPrefijo == raw
          console.log('🔎 (7 dígitos) Buscando en "clientes" por campo idSinPrefijo:', raw);
          const q1 = query(collection(db, 'clientes'), where('idSinPrefijo', '==', raw));
          const s1 = await getDocs(q1);
          if (!s1.empty) {
            const d = s1.docs[0];
            const clienteData = d.data();
            clienteData.id = clienteData.id || d.id;
            return { encontrado: true, datos: clienteData, tipoCliente, coleccion: 'clientes' };
          }

          // 3) Buscar por campo telefono == 02xxxxxxx
          const tel02 = `02${raw}`;
          console.log('🔎 (7 dígitos) Buscando en "clientes" por campo telefono:', tel02);
          const q2 = query(collection(db, 'clientes'), where('telefono', '==', tel02));
          const s2 = await getDocs(q2);
          if (!s2.empty) {
            const d = s2.docs[0];
            const clienteData = d.data();
            clienteData.id = clienteData.id || d.id;
            return { encontrado: true, datos: clienteData, tipoCliente, coleccion: 'clientes' };
          }
        } catch (e) {
          console.error('💥 Error buscando cliente fijo en "clientes":', e);
        }
        // Si no se encontró en "clientes", continuar con lógica general (fallbacks)
      }

      // Helpers: priorizar búsqueda por telefonoCompleto (593...) como pidió el usuario
      const buildCandidates = (t) => {
        const candidates = [];
        const add = (x) => {
          const v = (x || '').toString().trim();
          if (v && !candidates.includes(v)) candidates.push(v);
        };
        add(t);

        // 09XXXXXXXX -> 5939XXXXXXXX
        if (t.length === 10 && t.startsWith('0')) add('593' + t.substring(1));
        // 9XXXXXXXX -> 5939XXXXXXXX y 09XXXXXXXX
        if (t.length === 9 && !t.startsWith('0') && !t.startsWith('593')) {
          add('0' + t);
          add('593' + t);
        }
        // 5939XXXXXXXX -> 09XXXXXXXX
        if (t.length > 10 && t.startsWith('593')) add('0' + t.substring(3));

        return candidates;
      };

      const telefonoCompleto = (() => {
        if (raw.startsWith('593')) return raw;
        if (raw.length === 10 && raw.startsWith('0')) return '593' + raw.substring(1);
        if (raw.length === 9 && !raw.startsWith('0')) return '593' + raw;
        return raw;
      })();

      const candidates = buildCandidates(raw);
      if (telefonoCompleto && !candidates.includes(telefonoCompleto)) candidates.unshift(telefonoCompleto);

      // Determinar tipo (solo informativo)
      if (raw.length !== 7) {
        tipoCliente = 'cliente celular';
        console.log('📱 Búsqueda (celular) por telefonoCompleto:', telefonoCompleto);
      }

      // Colecciones a intentar (prioriza clientestelefonos donde ya se actualiza nombre/dirección)
      const colecciones = raw.length === 7 ? ['clientestelefonos'] : ['clientestelefonos', 'clientestelefonos1'];

      let clienteSnapshot = null;
      let clienteDoc = null;
      let coleccionEncontrada = '';

      // 1) Intentar por ID directo con candidatos (incluye telefonoCompleto 593...)
      for (const col of colecciones) {
        for (const cand of candidates) {
          console.log('🔎 Buscando cliente por ID:', col, cand);
          clienteDoc = doc(db, col, cand);
          // eslint-disable-next-line no-await-in-loop
          const snap = await getDoc(clienteDoc);
          if (snap.exists()) {
            clienteSnapshot = snap;
            coleccionEncontrada = col;
            telefonoBusqueda = cand;
            break;
          }
        }
        if (clienteSnapshot) break;
      }

      // 2) Fallback: buscar por campo telefonoCompleto (si el ID no coincide)
      if (!clienteSnapshot && telefonoCompleto && telefonoCompleto.startsWith('593')) {
        for (const col of colecciones) {
          console.log('🔎 Fallback: Buscando por campo telefonoCompleto en', col, telefonoCompleto);
          const q = query(collection(db, col), where('telefonoCompleto', '==', telefonoCompleto));
          // eslint-disable-next-line no-await-in-loop
          const s = await getDocs(q);
          if (!s.empty) {
            clienteSnapshot = s.docs[0];
            coleccionEncontrada = col;
            telefonoBusqueda = clienteSnapshot.id;
            break;
          }
        }
      }

      if (clienteSnapshot && clienteSnapshot.exists()) {
        const clienteData = clienteSnapshot.data();
        console.log('📄 Documento del cliente encontrado:', clienteData);
        // Normalizar datos mínimos: guardar ID y telefonoCompleto si viene por ID
        clienteData.id = clienteData.id || clienteSnapshot.id;
        if (!clienteData.telefonoCompleto && clienteSnapshot.id && clienteSnapshot.id.startsWith('593')) {
          clienteData.telefonoCompleto = clienteSnapshot.id;
        }

        // Cargar la primera dirección del array (si existe)
        if (clienteData.direcciones && clienteData.direcciones.length > 0) {
          const direccionActiva = clienteData.direcciones.find(dir => dir.activa === true) || clienteData.direcciones[0];
          clienteData.direccion = direccionActiva.direccion;
          clienteData.coordenadas = direccionActiva.coordenadas;
          console.log('📍 Dirección seleccionada:', direccionActiva);
        } else {
          console.log('⚠️ No se encontraron direcciones para el cliente');
          clienteData.direccion = clienteData.direccion || '';
          clienteData.coordenadas = clienteData.coordenadas || '';
        }

        return {
          encontrado: true,
          datos: clienteData,
          tipoCliente: tipoCliente,
          coleccion: coleccionEncontrada || coleccionNombre || (raw.length === 7 ? 'clientestelefonos' : 'clientestelefonos')
        };
      } else {
        console.log(`❌ No se encontró ${tipoCliente} en ninguna colección probada`);
        return {
          encontrado: false,
          tipoCliente: tipoCliente,
          coleccion: coleccionNombre
        };
      }
    } catch (error) {
      console.error('💥 Error al buscar cliente:', error);
      return { encontrado: false, tipoCliente: null };
    }
  };



  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Datos del formulario:', { telefono, nombre, coordenadas, direccion, tiempo, unidad, modoSeleccion, usuarioEncontrado });
  };

  const handleTelefonoChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setTelefono(value);
      // Limpiar datos cuando el teléfono cambie
      if (value.length < 7) {
        setUsuarioEncontrado(null);
        setNombre('');
        setNotaCliente('');
        setDireccion('');
        setCoordenadas('');
        setMostrarModal(false);
      }
    }
  };
  // Nueva función para manejar Enter en el campo teléfono
  const handleTelefonoKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      // Solo buscar si el teléfono tiene 7 dígitos, 9-10 dígitos, o más de 10
      if (telefono && telefono.length >= 7) {
        console.log('🔍 Buscando cliente con teléfono:', telefono);

        // Debug: Verificar estructura de datos directamente
        if (telefono === '2511511') {
          console.log('🔍 DEBUG: Verificando estructura para HOTEL VIENA...');
          try {
            const q = query(collection(db, 'clientestelefonos'), where("telefono", "==", "2511511"));
            const snapshot = await getDocs(q);
            console.log('📊 Documentos encontrados:', snapshot.size);

            if (!snapshot.empty) {
              const doc = snapshot.docs[0];
              console.log('📄 Documento principal:', doc.data());
              console.log('🆔 ID del documento:', doc.id);

              // Verificar subcolección direcciones
              const direccionesRef = collection(db, 'clientes', doc.id, 'direcciones');
              const direccionesSnapshot = await getDocs(direccionesRef);
              console.log('📍 Direcciones en subcolección:', direccionesSnapshot.size);

              direccionesSnapshot.forEach((doc, index) => {
                console.log(`📍 Dirección ${index + 1}:`, doc.data());
              });
            }
          } catch (error) {
            console.error('💥 Error en debug:', error);
          }
        }

        const resultadoBusqueda = await buscarCliente(telefono);
        console.log('📋 Resultado de búsqueda:', resultadoBusqueda);

        if (resultadoBusqueda && resultadoBusqueda.encontrado) {
          // Cliente encontrado, cargar datos automáticamente
          const clienteData = resultadoBusqueda.datos;
          console.log('📋 Datos completos del cliente encontrado:', clienteData);

          // Si se buscó con 7 dígitos, mantener los 7 dígitos (no concatenar a 593...)
          if (telefono.length === 7) {
            console.log('✅ Teléfono de 7 dígitos, se mantiene tal cual:', telefono);
          } else {
            const telOut = (clienteData.telefonoCompleto || clienteData.id || clienteData.telefono || '').toString();
            if (telOut) {
              setTelefono(telOut);
              console.log('✅ Teléfono cargado:', telOut);
            }
          }

          if (clienteData.nombre) {
            setNombre(clienteData.nombre);
            console.log('✅ Nombre cargado:', clienteData.nombre);
          }
          setNotaCliente((clienteData.nota || clienteData.observacion || '').toString().trim());

          if (clienteData.direccion) {
            setDireccion(clienteData.direccion);
            console.log('✅ Dirección cargada:', clienteData.direccion);
          } else {
            console.log('⚠️ No se encontró dirección para el cliente');
          }

          if (clienteData.coordenadas) {
            setCoordenadas(clienteData.coordenadas);
            console.log('✅ Coordenadas cargadas:', clienteData.coordenadas);
          } else {
            console.log('⚠️ No se encontraron coordenadas para el cliente');
          }

          console.log(`✅ Datos del ${resultadoBusqueda.tipoCliente} cargados automáticamente:`, clienteData);

          // Guardar la colección y el ID real donde se encontró el cliente
          // (para editar notas/direcciones sobre el documento correcto)
          setColeccionClienteEncontrada(resultadoBusqueda.coleccion || '');
          setIdClienteEncontrado((clienteData.id || '').toString());

          // Cargar direcciones guardadas directamente
          if (clienteData.direcciones && clienteData.direcciones.length > 0) {
            setDireccionesGuardadas(clienteData.direcciones);
            // Seleccionar la primera dirección por defecto
            if (clienteData.direcciones.length > 0) {
              const primeraDireccion = clienteData.direcciones[0];
              setDireccionSeleccionada(primeraDireccion);
              setDireccion(primeraDireccion.direccion);
              setCoordenadas(primeraDireccion.coordenadas || '');
              console.log('📍 Primera dirección seleccionada automáticamente:', primeraDireccion);
            }
            console.log('📍 Direcciones guardadas cargadas:', clienteData.direcciones.length);
          } else {
            setDireccionesGuardadas([]);
            setDireccionSeleccionada(null);
            console.log('⚠️ No hay direcciones guardadas para este cliente');
          }

          // Enfocar Tiempo cuando se encuentran valores en la búsqueda por teléfono
          setTimeout(() => {
            if (tiempoInputRef.current) tiempoInputRef.current.focus();
          }, 100);
        } else {
          // Cliente no encontrado, mostrar modal de registro
          console.log('❌ Cliente no encontrado, mostrando modal de registro');
          setDireccionesGuardadas([]);
          setDireccionSeleccionada(null);
          setModalRegistroCliente({
            open: true,
            tipoCliente: resultadoBusqueda ? resultadoBusqueda.tipoCliente : 'cliente',
            coleccion: resultadoBusqueda ? resultadoBusqueda.coleccion : 'clientes',
            modoAplicacion: modoSeleccion === 'aplicacion',
            datosCliente: {
              // Prellenar con los dígitos tal cual se marcaron (sin agregar 593).
              // Fijos se guardan directos; celulares se normalizan al registrar.
              telefono: (telefono || '').toString().replace(/\D/g, ''),
              nombre: '',
              direccion: '',
              coordenadas: '',
              sector: ''
            }
          });
          console.log('📝 Modal de registro configurado:', {
            open: true,
            tipoCliente: resultadoBusqueda ? resultadoBusqueda.tipoCliente : 'cliente',
            coleccion: resultadoBusqueda ? resultadoBusqueda.coleccion : 'clientes',
            modoAplicacion: modoSeleccion === 'aplicacion'
          });
        }
      } else {
        console.log('📱 Teléfono no cumple criterios para búsqueda:', telefono);
      }
    }
  };
  // Función para seleccionar una dirección del listado
  const seleccionarDireccion = (direccion) => {
    setDireccionSeleccionada(direccion);
    setDireccion(direccion.direccion);
    setCoordenadas(direccion.coordenadas || '');
    console.log('📍 Dirección seleccionada:', direccion);
  };

  // Función para iniciar edición de dirección
  const iniciarEdicionDireccion = (direccion) => {
    setEditandoDireccion(direccion);
    setTextoEditado(direccion.direccion);
  };

  // Localiza el documento real del cliente probando colecciones e IDs conocidos.
  // El cliente pudo guardarse en distintas colecciones (clientes / clientestelefonos /
  // clientestelefonos1) y con distintos formatos de ID (fijo: 2959611, 022959611,
  // 59302...; celular: 593...). Devuelve { clienteRef, clienteSnapshot, coleccion, id } o null.
  const localizarClienteDoc = async (telefonoParam) => {
    const rawTel = (telefonoParam || '').toString().replace(/\D/g, '');
    if (!rawTel) return null;

    const candidatosId = [];
    const addCand = (x) => {
      const v = (x || '').toString().trim();
      if (v && !candidatosId.includes(v)) candidatosId.push(v);
    };
    // ID exacto donde se encontró el cliente en la búsqueda (máxima prioridad)
    addCand(idClienteEncontrado);
    if (rawTel.length === 7) {
      addCand(rawTel);            // 2959611
      addCand(`02${rawTel}`);     // 022959611
      addCand(`59302${rawTel}`);  // 593022959611
    } else {
      addCand(rawTel);
      if (rawTel.startsWith('593')) addCand('0' + rawTel.substring(3));
      if (rawTel.length === 10 && rawTel.startsWith('0')) addCand('593' + rawTel.substring(1));
      if (rawTel.length === 9 && !rawTel.startsWith('0')) addCand('593' + rawTel);
    }

    // Priorizar la colección donde se encontró el cliente
    const coleccionesIntento = [];
    const addCol = (c) => { if (c && !coleccionesIntento.includes(c)) coleccionesIntento.push(c); };
    addCol(coleccionClienteEncontrada);
    ['clientes', 'clientestelefonos', 'clientestelefonos1'].forEach(addCol);

    for (const col of coleccionesIntento) {
      for (const cand of candidatosId) {
        const ref = doc(db, col, cand);
        // eslint-disable-next-line no-await-in-loop
        const snap = await getDoc(ref);
        if (snap.exists()) {
          return { clienteRef: ref, clienteSnapshot: snap, coleccion: col, id: cand };
        }
      }
    }
    return null;
  };

  // Función para guardar edición de dirección
  const guardarEdicionDireccion = async () => {
    if (!editandoDireccion || !textoEditado.trim()) return;

    try {
      const encontrado = await localizarClienteDoc(telefono);
      if (!encontrado) {
        console.log('❌ Cliente no encontrado para editar historial');
        return;
      }
      const { clienteRef, clienteSnapshot, coleccion: coleccionNombre, id: telefonoId } = encontrado;
      console.log(`📝 Editando en colección "${coleccionNombre}" para cliente: ${telefonoId}`);

      const clienteData = clienteSnapshot.data();
      const direccionesActuales = clienteData.direcciones || [];

      // Encontrar y actualizar la dirección específica
      const direccionIndex = direccionesActuales.findIndex(dir =>
        dir.direccion === editandoDireccion.direccion &&
        dir.coordenadas === editandoDireccion.coordenadas
      );

      if (direccionIndex !== -1) {
        direccionesActuales[direccionIndex].direccion = textoEditado.trim();
        direccionesActuales[direccionIndex].fechaActualizacion = new Date();

        // Actualizar en Firestore
        await updateDoc(clienteRef, {
          direcciones: direccionesActuales
        });

        // Actualizar el estado local
        setDireccionesGuardadas(direccionesActuales);

        // Si la dirección editada es la seleccionada, actualizar también
        if (direccionSeleccionada === editandoDireccion) {
          const direccionActualizada = direccionesActuales[direccionIndex];
          setDireccionSeleccionada(direccionActualizada);
          setDireccion(direccionActualizada.direccion);
        }

        console.log('✅ Dirección editada exitosamente');
      }

      // Limpiar estado de edición
      setEditandoDireccion(null);
      setTextoEditado('');
    } catch (error) {
      console.error('💥 Error al editar dirección:', error);
    }
  };


  // Función para cancelar edición
  const cancelarEdicionDireccion = () => {
    setEditandoDireccion(null);
    setTextoEditado('');
  };

  // Función para eliminar dirección del historial
  const eliminarDireccion = async (direccionAEliminar) => {
    try {
      const encontrado = await localizarClienteDoc(telefono);
      if (!encontrado) {
        console.log('❌ Cliente no encontrado para eliminar del historial');
        return;
      }
      const { clienteRef, clienteSnapshot, coleccion: coleccionNombre, id: telefonoId } = encontrado;
      console.log(`🗑️ Eliminando de colección "${coleccionNombre}" para cliente: ${telefonoId}`);

      const clienteData = clienteSnapshot.data();
      const direccionesActuales = clienteData.direcciones || [];

      // Filtrar la dirección a eliminar
      const direccionesFiltradas = direccionesActuales.filter(dir =>
        !(dir.direccion === direccionAEliminar.direccion &&
          dir.coordenadas === direccionAEliminar.coordenadas)
      );

      // Actualizar en Firestore
      await updateDoc(clienteRef, {
        direcciones: direccionesFiltradas
      });

      // Actualizar el estado local
      setDireccionesGuardadas(direccionesFiltradas);

      // Si la dirección eliminada era la seleccionada, limpiar selección
      if (direccionSeleccionada === direccionAEliminar) {
        setDireccionSeleccionada(null);
        setDireccion('');
        setCoordenadas('');
      }

      console.log('✅ Dirección eliminada del historial');
    } catch (error) {
      console.error('💥 Error al eliminar dirección:', error);
    }
  };

  const registrarCliente = async () => {
    try {
      const coleccionNombre = telefono.length === 7 ? 'usuarios' : 'usuariosfijos';
      const nuevoUsuario = {
        telefono: telefono,
        nombre: nuevoCliente.nombre,
        direccion: nuevoCliente.direccion,
        coordenadas: nuevoCliente.coordenadas,
        email: nuevoCliente.email,
        fechaRegistro: new Date().toISOString()
      };
      // Usar teléfono como ID para evitar duplicados y detectar existencia
      const userDocRef = doc(db, coleccionNombre, String(telefono));
      const userSnap = await getDoc(userDocRef);
      const yaExistia = userSnap.exists();
      await setDoc(userDocRef, nuevoUsuario, { merge: true });
      // Incrementar solo si no existía
      if (!yaExistia) {
        await incrementarClienteNuevo();
      }
      setNombre(nuevoCliente.nombre);
      setNotaCliente((nuevoCliente.nota || '').toString().trim());
      setDireccion(nuevoCliente.direccion);
      setCoordenadas(nuevoCliente.coordenadas);
      setUsuarioEncontrado(nuevoUsuario);
      setMostrarModal(false);
      alert('Cliente registrado exitosamente');
    } catch (error) {
      console.error('Error al registrar cliente:', error);
      alert('Error al registrar cliente. Intente nuevamente.');
    }
  };

  // Nueva función para registrar clientes con direcciones mapeadas
  const registrarNuevoCliente = async (datosCliente, tipoCliente, modoAplicacion) => {
    try {
      const telefonoInput = (datosCliente.telefono || telefono || '').toString().replace(/\D/g, '');
      // Teléfono fijo (7 dígitos): se guarda directo, sin prefijo 593.
      const esFijo = telefonoInput.length === 7;
      const telefonoCompleto593 = esFijo ? telefonoInput : concatenarTelefonoWhatsApp(telefonoInput, 'Ecuador');

      let coleccionNombre = '';
      let telefonoId = telefonoInput;

      if (esFijo) {
        coleccionNombre = 'clientestelefonos';
        telefonoId = telefonoInput;
      } else if (telefonoInput.length > 7) {
        coleccionNombre = 'clientestelefonos1';
        telefonoId = telefonoCompleto593;
      } else {
        throw new Error('Longitud de teléfono no válida para registro');
      }

      console.log(`📝 Registrando en colección "${coleccionNombre}" con ID: ${telefonoId}, telefonoCompleto: ${telefonoCompleto593}`);

      // Crear array de direcciones
      const direcciones = [];

      // Si hay dirección, agregarla al array
      if (datosCliente.direccion) {
        const nuevaDireccion = {
          direccion: datosCliente.direccion,
          coordenadas: datosCliente.coordenadas || '',
          sector: datosCliente.sector || '',
          fechaRegistro: new Date(),
          activa: true,
          modoRegistro: modoAplicacion ? 'aplicacion' : 'manual'
        };

        direcciones.push(nuevaDireccion);
        console.log('📍 Dirección agregada al array:', nuevaDireccion);
      }

      // Crear el documento principal del cliente:
      // - telefono: formato local/ID (para compatibilidad con búsquedas existentes)
      // - telefonoCompleto: formato 593... (estándar)
      const nuevoCliente = {
        telefono: telefonoId,
        telefonoOriginal: telefonoInput,
        telefonoCompleto: telefonoCompleto593,
        nombre: datosCliente.nombre,
        fechaRegistro: new Date(),
        activo: true,
        sector: datosCliente.sector || '',
        direcciones: direcciones
      };

      const clienteRef = doc(db, coleccionNombre, telefonoId);
      const clienteExistenteSnap = await getDoc(clienteRef);
      const yaExistia = clienteExistenteSnap.exists();

      await setDoc(clienteRef, nuevoCliente, { merge: true });

      // Incrementar contador de clientes nuevos solo si no existía antes
      if (!yaExistia) {
        await incrementarClienteNuevo();
      }

      console.log('✅ Cliente registrado exitosamente:', nuevoCliente);

      // Actualizar los campos del formulario
      setNombre(datosCliente.nombre);
      setNotaCliente((datosCliente.nota || '').toString().trim());
      setDireccion(datosCliente.direccion);
      if (datosCliente.coordenadas) {
        setCoordenadas(datosCliente.coordenadas);
      }

      // Cerrar el modal de registro
      setModalRegistroCliente({
        open: false,
        tipoCliente: '',
        coleccion: '',
        modoAplicacion: false,
        datosCliente: { telefono: '', nombre: '', direccion: '', coordenadas: '', sector: '', prefijo: 'Ecuador' }
      });

      setModal({
        open: true,
        success: true,
        message: `Cliente registrado exitosamente en la colección ${coleccionNombre}`
      });
    } catch (error) {
      console.error('Error al registrar cliente:', error);
      setModal({
        open: true,
        success: false,
        message: `Error al registrar cliente. Intente nuevamente.`
      });
    }
  };


  // Función para agregar nueva dirección a cliente existente
  const agregarNuevaDireccion = async (telefonoParam, nuevaDireccion, tipoCliente) => {
    try {
      // clientestelefonos1 usa ID = telefono completo (593...)
      let coleccionNombre = 'clientestelefonos';
      let telefonoId = telefonoParam;

      if (telefonoParam.length > 7) {
        coleccionNombre = 'clientestelefonos1';
        if (telefonoParam.startsWith('593')) telefonoId = telefonoParam;
        else if (telefonoParam.length === 10 && telefonoParam.startsWith('0')) telefonoId = '593' + telefonoParam.substring(1);
        else if (telefonoParam.length === 9) telefonoId = '593' + telefonoParam;
      }

      console.log(`📍 Agregando dirección en "${coleccionNombre}" para cliente: ${telefonoId}`);
      let clienteRef = doc(db, coleccionNombre, telefonoId);
      let clienteSnapshot = await getDoc(clienteRef);

      if (!clienteSnapshot.exists()) {
        throw new Error('Cliente no encontrado');
      }

      const clienteData = clienteSnapshot.data();
      const direccionesActuales = clienteData.direcciones || [];

      // Agregar nueva dirección al array
      const nuevaDireccionData = {
        direccion: nuevaDireccion.direccion,
        coordenadas: nuevaDireccion.coordenadas || '',
        fechaRegistro: new Date(),
        activa: true,
        modoRegistro: nuevaDireccion.modoRegistro || 'manual'
      };

      direccionesActuales.push(nuevaDireccionData);

      // Actualizar el documento con el nuevo array de direcciones
      await updateDoc(clienteRef, {
        direcciones: direccionesActuales
      });

      console.log('📍 Nueva dirección agregada al cliente:', nuevaDireccionData);
      console.log('📍 Total de direcciones del cliente:', direccionesActuales.length);

      return true;
    } catch (error) {
      console.error('Error al agregar nueva dirección:', error);
      return false;
    }
  };

  // Guardar cliente nuevo en clientestelefonos/clientestelefonos1 (desde pedido sin dirección)
  const guardarClienteNuevoDesdePedido = async (telefonoParam, nombreParam, direccionParam) => {
    try {
      const telRaw = (telefonoParam || '').toString().replace(/\D/g, '');
      if (!telRaw || telRaw.length < 7) {
        console.warn('⚠️ Teléfono inválido para registrar cliente');
        return false;
      }
      const telefonoCompleto593 = concatenarTelefonoWhatsApp(telRaw, 'Ecuador');
      let coleccionNombre = 'clientestelefonos';
      let telefonoId = telRaw;
      if (telRaw.length > 7) {
        coleccionNombre = 'clientestelefonos1';
        telefonoId = telefonoCompleto593;
      }
      const direcciones = [{
        direccion: (direccionParam || '').trim(),
        coordenadas: '',
        fechaRegistro: new Date(),
        activa: true,
        modoRegistro: 'manual'
      }];
      const nuevoCliente = {
        telefono: telefonoId,
        telefonoOriginal: telRaw,
        telefonoCompleto: telefonoCompleto593,
        nombre: (nombreParam || '').trim() || telefonoId,
        fechaRegistro: new Date(),
        activo: true,
        direcciones
      };
      const clienteRef = doc(db, coleccionNombre, telefonoId);
      await setDoc(clienteRef, nuevoCliente, { merge: true });
      console.log(`✅ Cliente nuevo guardado en ${coleccionNombre}:`, telefonoId);
      return true;
    } catch (error) {
      console.error('Error al guardar cliente nuevo desde pedido:', error);
      return false;
    }
  };

  // Función para validar si el token del conductor está configurado
  const validarTokenConductor = (token) => {
    if (!token || token.trim() === '') {
      return false;
    }
    // Validar que el token tenga el formato básico de FCM (al menos 100 caracteres)
    return token.length >= 100;
  };

  // Función para concatenar prefijo con teléfono para WhatsApp (si ya viene completo 593..., se devuelve tal cual)
  const concatenarTelefonoWhatsApp = (telefono, prefijo) => {
    const prefijosWhatsApp = {
      'Ecuador': '593',
      'Nicaragua': '505',
      'Colombia': '57',
      'Peru': '51',
      'Chile': '56',
      'Argentina': '54',
      'Mexico': '52',
      'Espana': '34',
      'Estados Unidos': '1'
    };

    const codigoPais = prefijosWhatsApp[prefijo] || '593'; // Por defecto Ecuador
    let telefonoLimpio = (telefono || '').toString().replace(/\D/g, ''); // Solo dígitos

    // Si ya tiene el prefijo completo (ej. 593...), devolverlo tal cual para no duplicar
    if (telefonoLimpio.startsWith(codigoPais) && telefonoLimpio.length >= 11) {
      return telefonoLimpio;
    }
    // Remover el 0 inicial si existe
    if (telefonoLimpio.startsWith('0')) {
      telefonoLimpio = telefonoLimpio.substring(1);
    }

    return `${codigoPais}${telefonoLimpio}`;
  };

  // Función para actualizar coordenadas de cliente existente (solo para teléfonos de 7 dígitos y celulares)
  const actualizarCoordenadasCliente = async (telefono, nuevasCoordenadas, nuevaDireccion) => {
    try {
      // Solo actualizar si el teléfono tiene 7 dígitos o es mayor a 7
      if (telefono.length < 7) {
        console.log('⚠️ Solo se actualizan coordenadas para teléfonos de 7 dígitos o más');
        return false;
      }

      console.log('📍 Actualizando coordenadas para cliente:', telefono);

      // Determinar la colección y el ID según la longitud del teléfono
      let coleccionNombre = 'clientestelefonos';
      let telefonoId = telefono;

      if (telefono.length > 7) {
        coleccionNombre = 'clientestelefonos1';
        if (telefono.startsWith('593')) telefonoId = telefono;
        else if (telefono.length === 10 && telefono.startsWith('0')) telefonoId = '593' + telefono.substring(1);
        else if (telefono.length === 9) telefonoId = '593' + telefono;
      }

      console.log(`📍 Buscando en coleccion ${coleccionNombre} con ID ${telefonoId}`);
      let clienteRef = doc(db, coleccionNombre, telefonoId);
      let clienteSnapshot = await getDoc(clienteRef);

      if (!clienteSnapshot.exists()) {
        throw new Error('Cliente no encontrado');
      }

      const clienteData = clienteSnapshot.data();
      const direccionesActuales = clienteData.direcciones || [];

      // Buscar si ya existe una dirección con coordenadas
      const direccionConCoordenadas = direccionesActuales.find(dir => dir.coordenadas && dir.coordenadas.trim() !== '');

      if (direccionConCoordenadas) {
        // Actualizar las coordenadas existentes
        direccionConCoordenadas.coordenadas = nuevasCoordenadas;
        direccionConCoordenadas.direccion = nuevaDireccion;
        direccionConCoordenadas.fechaActualizacion = new Date();
        console.log('📍 Coordenadas actualizadas en dirección existente:', direccionConCoordenadas);
      } else {
        // Agregar nueva dirección con coordenadas
        const nuevaDireccionData = {
          direccion: nuevaDireccion,
          coordenadas: nuevasCoordenadas,
          fechaRegistro: new Date(),
          activa: true,
          modoRegistro: 'aplicacion'
        };

        direccionesActuales.push(nuevaDireccionData);
        console.log('📍 Nueva dirección con coordenadas agregada:', nuevaDireccionData);
      }

      // Actualizar el documento del cliente
      await updateDoc(clienteRef, {
        direcciones: direccionesActuales
      });

      console.log('✅ Coordenadas actualizadas exitosamente para el cliente:', telefono);
      return true;

    } catch (error) {
      console.error('💥 Error al actualizar coordenadas del cliente:', error);
      return false;
    }
  };

  const limpiarFormulario = () => {
    setTelefono('');
    setNombre('');
    setNotaCliente('');
    setColeccionClienteEncontrada('');
    setIdClienteEncontrado('');
    setCoordenadas('');
    setDireccion('');
    setTiempo('');
    setUnidad('');
    setSector('');
    setUsuarioEncontrado(null);
    setBuscandoUsuario(false);
    setMostrarModal(false);
    setNuevoCliente({ nombre: '', direccion: '', coordenadas: '', email: '' });
    setMapaVisible(false); // Oculta el mapa
    // Limpiar direcciones guardadas
    setDireccionesGuardadas([]);
    setDireccionSeleccionada(null);
    // Limpiar estados de edición
    setEditandoDireccion(null);
    setTextoEditado('');
  };

  // Normaliza base a solo número (string). Rango permitido: 01-15
  // - Vacío / "0" / "aire" => "0"
  // - "base 1", "base01", "1" => "01"
  // - "10" => "10", "15" => "15"
  const convertirNumeroABase = (valorBase) => {
    const raw = (valorBase ?? '').toString().trim().toLowerCase();
    if (!raw || raw === '0' || raw === 'aire') return '0';

    // baseXX / base XX
    const m = raw.match(/^base\s*(\d{1,2})$/) || raw.match(/^base(\d{1,2})$/);
    if (m?.[1]) {
      const n = parseInt(m[1], 10);
      if (!Number.isFinite(n) || n <= 0) return '0';
      return String(Math.min(15, n)).padStart(2, '0');
    }

    // Número directo (01-15)
    if (/^\d{1,2}$/.test(raw)) {
      const n = parseInt(raw, 10);
      if (!Number.isFinite(n) || n <= 0) return '0';
      return String(Math.min(15, n)).padStart(2, '0');
    }

    // Si llega un valor inesperado, no rompemos: lo tratamos como vacío ("0")
    return '0';
  };

  // Valida que el valor de Base esté en rango 01-15 (permite vacío, 0, 1 durante escritura)
  const esBaseValida = (valor) => /^(0[1-9]|1[0-5]|0|1)?$/.test((valor ?? '').toString());

  // Nueva función para limpiar solo tiempo y unidad, manteniendo datos del cliente
  const limpiarTiempoYUnidad = () => {
    setBase('');
    setTiempo('');
    setUnidad('');
    setMapaVisible(false); // Oculta el mapa
  };

  // Nueva función para limpiar formulario completo y enfocar teléfono
  const limpiarFormularioCompleto = () => {
    setTelefono('');
    setNombre('');
    setNotaCliente('');
    setCoordenadas('');
    setDireccion('');
    setBase('');
    setTiempo('');
    setUnidad('');
    setSector('');
    setUsuarioEncontrado(null);
    setBuscandoUsuario(false);
    setMostrarModal(false);
    setNuevoCliente({ nombre: '', direccion: '', coordenadas: '', email: '' });
    setMapaVisible(false);
    setDireccionesGuardadas([]);
    setDireccionSeleccionada(null);
    setEditandoDireccion(null);
    setTextoEditado('');

    // Enfocar el campo de teléfono después de limpiar
    setTimeout(() => {
      const telefonoInput = document.querySelector('input[placeholder="Ingrese Teléfono"]');
      if (telefonoInput) {
        telefonoInput.focus();
      }
    }, 100);
  };

  // Función para insertar pedido disponible
  const handleInsertarViajePendiente = async () => {
    // Evitar múltiples inserciones simultáneas
    if (insertandoRegistro) {
      console.log('⚠️ Ya se está insertando un registro, esperando...');
      return;
    }

    setInsertandoRegistro(true);

    try {

      const fecha = new Date(); // Timestamp
      const clave = Math.random().toString(36).substring(2, 8).toUpperCase();

      // Desde el formulario: NO enviar coordenadas, solo nombre, dirección y teléfono
      const latitud = '';
      const longitud = '';

      // Determinar el teléfono completo para WhatsApp
      let telefonoCompleto = telefono || '';
      if (telefono && telefono.length > 7) {
        // Para celulares o números largos, buscar el cliente y obtener su prefijo
        try {
          // Determinar colección
          const coleccionWhatsApp = telefono.length === 7 ? 'clientestelefonos' : 'clientestelefonos1';

          let clienteRef = doc(db, coleccionWhatsApp, telefono);
          let clienteSnapshot = await getDoc(clienteRef);

          if (clienteSnapshot.exists()) {
            const clienteData = clienteSnapshot.data();
            telefonoCompleto = concatenarTelefonoWhatsApp(telefono, clienteData.prefijo || 'Ecuador');
            console.log('📱 Teléfono completo para WhatsApp:', telefonoCompleto);
          } else {
            // Si no se encuentra, intentar con los últimos 9 dígitos (método anterior)
            const telefonoBusqueda = telefono.slice(-9);
            clienteRef = doc(db, 'clientestelefonos', telefonoBusqueda);
            clienteSnapshot = await getDoc(clienteRef);

            if (clienteSnapshot.exists()) {
              const clienteData = clienteSnapshot.data();
              telefonoCompleto = concatenarTelefonoWhatsApp(telefono, clienteData.prefijo || 'Ecuador');
              console.log('📱 Teléfono completo para WhatsApp (fallback):', telefonoCompleto);
            }
          }
        } catch (error) {
          console.log('⚠️ No se pudo obtener el prefijo del cliente, usando teléfono original');
        }
      }

      const pedidoData = {
        // Estructura basada en tu colección pedidosDisponibles
        clave: clave,
        codigo: nombre || '',
        nombreCliente: nombre || '',
        telefono: (telefono && String(telefono).replace(/\D/g, '').startsWith('593') ? String(telefono).replace(/\D/g, '') : (telefonoCompleto || telefono || '')), // Siempre guardar completo (593...)
        telefonoCompleto: telefonoCompleto || telefono || '',
        direccion: direccion || '',
        base: convertirNumeroABase(base || '0'), // Nuevo campo base
        destino: '', // Se puede editar después
        fecha: fecha,
        estado: 'Disponible',
        idConductor: 'Sin asignar',
        latitud: latitud,
        longitud: longitud,
        latitudDestino: '',
        longitudDestino: '',
        sector: sector || '',
        tipoPedido: 'enpoidpanel', // Identificador para pedidos del panel
        valor: 'Central',
        central: true,
        coorporativo: false,
        llegue: false,
        pedido: 'Disponible',
        puerto: '3020',
        randon: clave,
        rango: '0', // Rango siempre 0 para pedidos manuales
        viajes: '',
        foto: '0',
        tarifaSeleccionada: true,
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador',
        // Campo de autorización (pre-generado opcionalmente desde el botón de voucher o automáticamente para empresas)
        autorizacion: preRegistroVoucher?.activo ? preRegistroVoucher.numeroAutorizacion :
          (tipoEmpresa !== 'Efectivo' && numeroAutorizacion ? numeroAutorizacion : null),
        modoSeleccion: modoSeleccionUI, // Nuevo campo para el modo de selección UI
        tipoEmpresa: tipoEmpresa, // Nuevo campo para empresa/efectivo
        observaciones: (notaCliente || '').trim()
      };

      // Guardar en la colección "pedidosDisponibles1"
      console.log('💾 Guardando pedido en pedidosDisponibles1:', pedidoData);
      const docRef = doc(collection(db, 'pedidosDisponibles1'));
      await setDoc(docRef, { ...pedidoData, id: docRef.id });
      console.log('✅ Pedido guardado con ID:', docRef.id);

      // Si se usó un número de autorización pre-generado, desactivarlo para evitar reutilización accidental
      try {
        if (preRegistroVoucher?.activo) {
          setPreRegistroVoucher({ numeroAutorizacion: null, activo: false });
        }

        // Si se usó autorización automática para empresa, CREAR el voucher en la base de datos
        if (tipoEmpresa !== 'Efectivo' && numeroAutorizacion) {
          console.log(`✅ Creando voucher con autorización: ${numeroAutorizacion} para empresa: ${tipoEmpresa}`);

          // Crear el documento del voucher en voucherCorporativos con los datos del pedido
          const autorizacionData = {
            numeroAutorizacion: parseInt(numeroAutorizacion),
            cliente: nombre || '',
            telefono: telefono || '',
            empresa: tipoEmpresa,
            fecha: new Date(),
            tipo: 'autorización_automática',
            origen: direccion || 'Sin dirección',
            destino: 'Sin destino definido',
            fechaCreacion: new Date(),
            asignado: true, // Marcar como asignado
            status: 'en_uso', // Estado en uso
            operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador',
            estado: 'utilizada'
          };
          const autorizacionDocRef = doc(db, 'voucherCorporativos', numeroAutorizacion.toString());
          await setDoc(autorizacionDocRef, autorizacionData);

          console.log(`✅ Voucher ${numeroAutorizacion} creado exitosamente en la base de datos`);

          // Limpiar la autorización pre-generada para que se genere una nueva
          setAutorizacionPreGenerada('');
        }
      } catch (error) {
        console.error('❌ Error al manejar autorización:', error);
      }

      // Guardar en historial del cliente si hay dirección
      try {
        if (telefono && direccion) {
          await guardarEnHistorialCliente(telefono, direccion, coordenadas, 'manual');
        }
      } catch (e) {
        console.warn('⚠️ No se pudo guardar historial del cliente (no crítico):', e);
      }

      // Los listeners en tiempo real actualizarán automáticamente las tablas

      // Ocultar el mapa después del registro exitoso
      setMapaVisible(false);

      // Limpiar formulario completo y enfocar teléfono
      limpiarFormularioCompleto();

      // Registro silencioso - sin mostrar alert de éxito
      console.log('✅ Pedido registrado silenciosamente en pedidosDisponibles1');
      // Contadores (no bloquear registro si fallan)
      try {
        await actualizarContadorReporte('viajesRegistrados');
        await actualizarContadorReporte('viajesManuales');
      } catch (e) {
        console.warn('⚠️ No se pudieron actualizar contadores (no crítico):', e);
      }

      // Resetear empresa a "Efectivo" después de enviar exitosamente
      if (tipoEmpresa !== 'Efectivo') {
        setTipoEmpresa('Efectivo');
      }
    } catch (error) {
      console.error('Error al registrar el pedido:', error);
      setModal({ open: true, success: false, message: 'Error al registrar el pedido.' });
    } finally {
      setInsertandoRegistro(false);
    }
  };

  // Función para insertar viaje en modo manual
  // Incluye el token del conductor para notificaciones push cuando se asigna manualmente
  const handleInsertarViaje = async () => {
    // Evitar múltiples inserciones simultáneas
    if (insertandoRegistro) {
      console.log('⚠️ Ya se está insertando un registro, esperando...');
      return;
    }

    // Validaciones
    if (!tiempo.trim()) {
      setModal({ open: true, success: false, message: 'Por favor, ingrese el tiempo del viaje.' });
      return;
    }
    if (!unidad.trim()) {
      setModal({ open: true, success: false, message: 'Por favor, ingrese el número de unidad.' });
      return;
    }

    setInsertandoRegistro(true);

    try {
      // Buscar datos del conductor por número de unidad
      const conductoresQuery = query(
        collection(db, 'conductores'),
        where("unidad", "==", unidad.trim())
      );

      const conductoresSnapshot = await getDocs(conductoresQuery);

      if (conductoresSnapshot.empty) {
        setModal({ open: true, success: false, message: `No se encontró un conductor con la unidad ${unidad}. Por favor, ingrese una unidad válida.` });
        // NO hacer return, permitir que el usuario siga editando
        return;
      }

      // Obtener datos del conductor
      const conductorDoc = conductoresSnapshot.docs[0];
      const conductorData = conductorDoc.data();
      const emailConductor = conductorDoc.id || conductorData.correo || conductorData.email || '';
      // Validar estatus del conductor (inactivo => bloquear asignación)
      try {
        const estatusValor = conductorData && 'estatus' in conductorData ? conductorData.estatus : true;
        const estatusBool = typeof estatusValor === 'string' ? estatusValor.toLowerCase() !== 'false' : Boolean(estatusValor);
        if (!estatusBool) {
          setModal({ open: true, success: false, message: `La unidad ${unidad} está suspendida/inactiva. No se puede asignar.` });
          setInsertandoRegistro(false);
          return;
        }
      } catch (e) {
        console.warn('No se pudo validar estatus del conductor, continuando por defecto.', e);
      }

      // Determinar número de autorización antes de enviar al conductor (para incluirlo en el mensaje)
      let numeroAutorizacionParaMensaje = null;
      if (preRegistroVoucher?.activo && preRegistroVoucher?.numeroAutorizacion) {
        numeroAutorizacionParaMensaje = preRegistroVoucher.numeroAutorizacion;
      } else if (tipoEmpresa !== 'Efectivo') {
        try {
          numeroAutorizacionParaMensaje = await obtenerSiguienteAutorizacion();
        } catch (e) { console.warn('No se pudo pre-generar autorización para mensaje:', e); }
      }

      // Determinar teléfono completo del cliente ANTES de enviar WhatsApp (evitar ReferenceError)
      let telefonoCompleto = telefono || '';
      if (telefono && telefono.length > 7) {
        try {
          const coleccionWhatsApp = telefono.length === 7 ? 'clientestelefonos' : 'clientestelefonos1';
          let clienteRef = doc(db, coleccionWhatsApp, telefono);
          let clienteSnapshot = await getDoc(clienteRef);
          if (clienteSnapshot.exists()) {
            const clienteData = clienteSnapshot.data();
            telefonoCompleto = concatenarTelefonoWhatsApp(telefono, clienteData.prefijo || 'Ecuador');
            console.log('📱 Teléfono completo para WhatsApp:', telefonoCompleto);
          } else {
            const telefonoBusqueda = telefono.slice(-9);
            clienteRef = doc(db, 'clientestelefonos', telefonoBusqueda);
            clienteSnapshot = await getDoc(clienteRef);
            if (clienteSnapshot.exists()) {
              const clienteData = clienteSnapshot.data();
              telefonoCompleto = concatenarTelefonoWhatsApp(telefono, clienteData.prefijo || 'Ecuador');
              console.log('📱 Teléfono completo para WhatsApp (fallback):', telefonoCompleto);
            }
          }
        } catch (e) { /* fallback: telefonoCompleto = telefono */ }
      }

      // Enviar mensaje de WhatsApp al conductor (cliente, dirección y autorización)
      try {
        const telefonoConductorRaw = conductorData.telefono || conductorData.celular || '';
        if (telefonoConductorRaw) {
          // Limpiar teléfono y asegurar prefijo 593 correcto
          let telefonoLimpio = telefonoConductorRaw.replace(/\D/g, '');

          // Si ya empieza con 593, quitarlo para normalizar
          if (telefonoLimpio.startsWith('593')) {
            telefonoLimpio = telefonoLimpio.substring(3);
          }

          // Quitar el 0 inicial si existe
          if (telefonoLimpio.startsWith('0')) {
            telefonoLimpio = telefonoLimpio.substring(1);
          }

          const telefonoConductorFormateado = normalizarTelefonoParaWhatsAppApi(`593${telefonoLimpio}`);

          const telClienteMsg = normalizarTelefonoParaWhatsAppApi(telefonoCompleto || telefono || '');
          const extraUbicacion = extraUbicacionGoogleMaps({
            tipoPedido: modoSeleccionUI,
            coordenadas: coordenadas
          });
          let mensajeConductor = `🚖 *Nuevo Pedido Asignado* 🚖\n\n👤 *Cliente:* ${nombre || 'Sin nombre'}\n📍 *Dirección:* ${direccion || 'Sin dirección'}\n📞 *Teléfono:* ${telClienteMsg || 'N/D'}`;
          if (numeroAutorizacionParaMensaje != null) {
            mensajeConductor += `\n🔑 *Autorización:* ${numeroAutorizacionParaMensaje}`;
          }
          if (extraUbicacion) {
            mensajeConductor += `\n📍 *Coordenadas:* ${extraUbicacion.coords}\n🗺️ *Google Maps:* ${extraUbicacion.link}`;
          }
          mensajeConductor += `\n\nPor favor, dirígete a la ubicación.`;

          const apiUrl = getWhatsAppSendUrl();
          const res = await postJson(apiUrl, {
            to: telefonoConductorFormateado,
            message: mensajeConductor
          });
          if (!res.ok) console.error('API WhatsApp conductor:', res.status, await res.text());
          else console.log('✅ Mensaje de WhatsApp enviado al conductor.');
        } else {
          console.warn('⚠️ El conductor no tiene número de teléfono registrado.');
        }
      } catch (error) {
        console.error('❌ Error al enviar WhatsApp al conductor:', error);
        // No detenemos el flujo, solo logueamos el error
      }
      // Enviar al cliente mensaje con imagen (formato CONDUCTOR EN CAMINO)
      try {
        const msgCliente = formatearMensajeConductorAlCliente(conductorData, unidad, tiempo);
        await enviarMensajeImagenAlCliente(telefonoCompleto || telefono, msgCliente, IMAGEN_DEFAULT_CLIENTE);
      } catch (e) { console.error('Error enviar imagen al cliente:', e); }

      // ID conductor aleatorio (no email)
      const idConductorRandom = `cid_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

      // Obtener el token del conductor (si existe)
      const tokenConductor = conductorData.token || conductorData.fcmToken || conductorData.deviceToken || '';
      const tokenValido = validarTokenConductor(tokenConductor);

      // Desde el formulario: NO enviar coordenadas, solo nombre, dirección y teléfono
      const latitud = '';
      const longitud = '';

      const fecha = new Date(); // Timestamp
      const clave = Math.random().toString(36).substring(2, 8).toUpperCase();

      // Usar el mismo número de autorización ya resuelto para el mensaje (evitar duplicado)
      let numeroAutorizacion = numeroAutorizacionParaMensaje;
      if (numeroAutorizacion == null && tipoEmpresa !== 'Efectivo') {
        numeroAutorizacion = await obtenerSiguienteAutorizacion();
        console.log(`✅ Nueva autorización generada para asignación manual: ${numeroAutorizacion}`);
      }

      const pedidoEnCursoData = {
        // ...estructura igual...
        clave: clave,
        codigo: nombre || '',
        nombreCliente: nombre || '',
        telefono: (telefono && String(telefono).replace(/\D/g, '').startsWith('593') ? String(telefono).replace(/\D/g, '') : (telefonoCompleto || telefono || '')),
        telefonoCompleto: telefonoCompleto || telefono || '',
        direccion: direccion || '',
        base: convertirNumeroABase(base || '0'),
        destino: '',
        fecha: fecha,
        estado: 'Aceptado',
        pedido: 'Aceptado',
        idConductor: idConductorRandom,
        correo: emailConductor,
        emailConductor: emailConductor,
        nombre: conductorData.nombre || '',
        nombreConductor: conductorData.nombre || '',
        placa: conductorData.placa || '',
        color: conductorData.color || '',
        telefonoConductor: conductorData.telefono || '',
        foto: conductorData.foto || '',
        tokenConductor: conductorData.token || '',
        tiempo: tiempo,
        numeroUnidad: unidad,
        unidad: unidad,
        minutos: parseInt(tiempo) || 0,
        distancia: '0.00 Mts',
        latitudConductor: '',
        longitudConductor: '',
        latitud: latitud,
        longitud: longitud,
        latitudDestino: '',
        longitudDestino: '',
        sector: sector || '',
        tipopedido: 'Manual',
        valor: '',
        central: false,
        coorporativo: false,
        observaciones: (notaCliente || '').trim(),
        llegue: false,
        puerto: '3020',
        randon: clave,
        rango: coordenadas ? '1' : '0',
        viajes: unidad || '',
        tarifaSeleccionada: true,
        modoSeleccion: 'manual',
        modoAsignacion: 'manual',
        tipoEmpresa: tipoEmpresa,
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador',
        autorizacion: numeroAutorizacion
      };

      // Si hay número de autorización, actualizar el documento existente en voucherCorporativos
      if (numeroAutorizacion) {
        const voucherDocRef = doc(db, 'voucherCorporativos', numeroAutorizacion.toString());
        await setDoc(voucherDocRef, {
          ...pedidoEnCursoData,
          numeroAutorizacion: parseInt(numeroAutorizacion),
          estado: 'utilizada',
          status: 'en_uso',
          asignado: true,
          tipo: 'autorización_automática',
          fecha: new Date(),
          fechaCreacion: new Date(),
        }, { merge: true });
      }

      // Guardar en todosLosViajes (en lugar de pedidoEnCurso)
      const docRef = await addDoc(collection(db, 'todosLosViajes'), {
        ...pedidoEnCursoData,
        fechaAsignacion: new Date(),
        origenColeccion: 'manual-directo'
      });

      // Actualizar el documento con su propio ID
      await updateDoc(docRef, { id: docRef.id });

      // Si se usó un número de autorización pre-generado, desactivarlo para evitar reutilización accidental
      try {
        if (preRegistroVoucher?.activo) {
          setPreRegistroVoucher({ numeroAutorizacion: null, activo: false });
        }

        // Si se usó autorización automática para empresa, CREAR el voucher en la base de datos
        if (tipoEmpresa !== 'Efectivo' && numeroAutorizacion) {
          console.log(`✅ Creando voucher con autorización: ${numeroAutorizacion} para empresa: ${tipoEmpresa}`);

          // Crear el documento del voucher en voucherCorporativos con los datos del pedido
          const autorizacionData = {
            numeroAutorizacion: parseInt(numeroAutorizacion),
            cliente: nombre || '',
            telefono: telefono || '',
            empresa: tipoEmpresa,
            fecha: new Date(),
            tipo: 'autorización_automática',
            origen: direccion || 'Sin dirección',
            destino: 'Sin destino definido',
            fechaCreacion: new Date(),
            asignado: true, // Marcar como asignado
            status: 'en_uso', // Estado en uso
            operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador',
            estado: 'utilizada'
          };
          const autorizacionDocRef = doc(db, 'voucherCorporativos', numeroAutorizacion.toString());
          await setDoc(autorizacionDocRef, autorizacionData);

          console.log(`✅ Voucher ${numeroAutorizacion} creado exitosamente en la base de datos`);

          // Limpiar la autorización pre-generada para que se genere una nueva
          setAutorizacionPreGenerada('');
        }
      } catch (error) {
        console.error('❌ Error al manejar autorización:', error);
      }

      // Crear duplicado en la colección "NotificaciOnenCurso" para sistema de notificaciones
      const notificacionEnCursoData = {
        ...pedidoEnCursoData,
        id: docRef.id, // ID del documento en todosLosViajes
        fechaNotificacion: new Date(), // Fecha específica para la notificación
        estadoNotificacion: 'pendiente' // Estado de la notificación (pendiente, enviada, fallida)
      };

      await addDoc(collection(db, 'NotificaciOnenCurso'), notificacionEnCursoData);

      // Guardar en historial del cliente si hay dirección
      if (telefono && direccion) {
        await guardarEnHistorialCliente(telefono, direccion, coordenadas, 'manual');
      }

      // Registrar automáticamente en la colección de pedidos manuales
      try {
        const pedidoManualData = {
          ...pedidoEnCursoData,
          idOriginal: docRef.id, // Referencia al documento en todosLosViajes
          fechaRegistro: new Date(),
          tipo: 'manual',
          estadoRegistro: 'Registrado',
          modoRegistro: 'manual'
        };

        await addDoc(collection(db, 'pedidosManuales'), pedidoManualData);
        console.log('✅ Pedido manual registrado en colección separada');
      } catch (error) {
        console.error('❌ Error al registrar pedido manual:', error);
        // No fallar si no se puede registrar en la colección separada
      }

      // Los listeners en tiempo real actualizarán automáticamente las tablas

      // Ocultar el mapa después del registro exitoso
      setMapaVisible(false);

      // Limpiar formulario completo y enfocar teléfono
      limpiarFormularioCompleto();

      // Registro silencioso - sin mostrar alert de éxito
      console.log(`✅ Pedido registrado silenciosamente en "TodosLosViajes" - Conductor: ${conductorData.nombre}, Unidad: ${unidad}`);

      // Actualizar contador de viajes registrados
      await actualizarContadorReporte('viajesRegistrados');
      // Actualizar contador de viajes manuales (porque se registró manualmente)
      await actualizarContadorReporte('viajesManuales');

      // Resetear empresa a "Efectivo" después de enviar exitosamente
      if (tipoEmpresa !== 'Efectivo') {
        setTipoEmpresa('Efectivo');
        // Disparar actualización de la UI para refrescar la autorización
        setUiUpdateTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error al registrar el viaje:', error);
      setModal({ open: true, success: false, message: 'Error al registrar el pedido en todos los viajes.' });
    } finally {
      setInsertandoRegistro(false);
    }
  };

  // Función para abrir modal de acciones del pedido
  const abrirModalAccionesPedido = (pedido, coleccion) => {
    setModalAccionesPedido({
      open: true,
      pedido: pedido,
      coleccion: coleccion
    });
  };

  // Función para cerrar modal de acciones del pedido
  const cerrarModalAccionesPedido = () => {
    setModalAccionesPedido({
      open: false,
      pedido: null,
      coleccion: ''
    });
    // Limpiar el mensaje cuando se cierra el modal
    setMensajeConductor('');
  };

  const eliminarPedidoDisponible = async (viaje) => {
    const id = viaje?.id;
    if (!id) return;
    const origen = (viaje?.origenColeccion || '').toString();
    try {
      const coleccion = origen === 'pedidosDisponibles1' ? 'pedidosDisponibles1' : (origen === 'pedidoSolicitado' ? 'pedidoSolicitado' : 'pedidosDisponibles');
      await deleteDoc(doc(db, coleccion, id));
      // Sin modal de éxito: eliminación silenciosa
    } catch (error) {
      console.error('❌ Error eliminando pedido (Disponibles):', error);
      setModal({ open: true, success: false, message: 'Error al eliminar el pedido.' });
    }
  };

  // Enviar al conductor el nombre y teléfono del cliente del pedido asignado (misma API 3006)
  const enviarTelefonoYClienteAlConductor = async () => {
    if (!modalAccionesPedido.pedido) return;
    const pedido = modalAccionesPedido.pedido;
    const unidadPedido = (pedido.unidad || pedido.numeroUnidad || '').toString().trim();
    if (!unidadPedido) {
      setModal({ open: true, success: false, message: 'Este pedido no tiene unidad asignada.' });
      return;
    }
    try {
      const conductoresQuery = query(
        collection(db, 'conductores'),
        where('unidad', '==', unidadPedido)
      );
      const conductoresSnapshot = await getDocs(conductoresQuery);
      if (conductoresSnapshot.empty) {
        setModal({ open: true, success: false, message: 'No se encontró conductor para la unidad ' + unidadPedido + '.' });
        return;
      }
      const conductorData = conductoresSnapshot.docs[0].data();
      const telefonoConductorRaw = conductorData.telefono || conductorData.celular || '';
      if (!telefonoConductorRaw) {
        setModal({ open: true, success: false, message: 'El conductor no tiene teléfono registrado.' });
        return;
      }
      let telefonoLimpio = telefonoConductorRaw.replace(/\D/g, '');
      if (telefonoLimpio.startsWith('593')) telefonoLimpio = telefonoLimpio.substring(3);
      if (telefonoLimpio.startsWith('0')) telefonoLimpio = telefonoLimpio.substring(1);
      const telefonoConductorFormateado = normalizarTelefonoParaWhatsAppApi(`593${telefonoLimpio}`);
      const nombreCliente = pedido.nombreCliente || pedido.codigo || 'Sin nombre';
      const direccionCliente = pedido.direccion || 'Sin dirección';
      const telefonoCliente = normalizarTelefonoParaWhatsAppApi(pedido.telefonoCompleto || pedido.telefono || '');
      const extraUbicacion = extraUbicacionGoogleMaps(pedido);
      const mensaje = [
        '📋 *Datos del cliente de su pedido asignado*',
        `👤 *Cliente:* ${nombreCliente}`,
        `📍 *Dirección:* ${direccionCliente}`,
        `📞 *Teléfono:* ${telefonoCliente || 'N/D'}`,
        extraUbicacion ? `📍 *Coordenadas:* ${extraUbicacion.coords}` : null,
        extraUbicacion ? `🗺️ *Google Maps:* ${extraUbicacion.link}` : null,
        '',
        'Puede contactar al cliente si lo necesita.'
      ].join('\n');
      const apiUrl = getWhatsAppSendUrl();
      const res = await postJson(apiUrl, { to: telefonoConductorFormateado, message: mensaje });
      if (!res.ok) {
        const text = await res.text();
        console.error('API WhatsApp:', res.status, text);
        setModal({ open: true, success: false, message: 'No se pudo enviar el mensaje. Revisar consola.' });
      } else {
        setModal({ open: true, success: true, message: 'Teléfono y nombre del cliente enviados al conductor.' });
      }
    } catch (err) {
      console.error('Error al enviar datos al conductor:', err);
      setModal({ open: true, success: false, message: 'Error al enviar: ' + (err.message || err) });
    }
  };

  // Función para enviar mensaje al conductor
  const enviarMensajeConductor = async () => {
    if (!modalAccionesPedido.pedido || !mensajeConductor.trim()) {
      setModal({ open: true, success: false, message: 'Por favor escriba un mensaje antes de enviarlo.' });
      return;
    }

    try {
      const pedido = modalAccionesPedido.pedido;

      // Crear el mensaje en la colección mensajesConductor
      await addDoc(collection(db, 'mensajesConductor'), {
        unidad: pedido.numeroUnidad || pedido.unidad || 'Sin unidad',
        conductor: pedido.nombre || 'Sin conductor',
        telefonoConductor: pedido.telefono || pedido.telefonoCompleto || 'Sin teléfono',
        cliente: pedido.nombreCliente || pedido.codigo || 'Sin cliente',
        mensaje: mensajeConductor.trim(),
        fecha: serverTimestamp(),
        leido: false,
        pedidoId: pedido.id || 'Sin ID',
        origen: 'modal-acciones'
      });

      setModal({ open: true, success: true, message: 'Mensaje enviado exitosamente al conductor.' });
      setMensajeConductor(''); // Limpiar el campo de mensaje

    } catch (error) {
      console.error('Error al enviar mensaje al conductor:', error);
      setModal({ open: true, success: false, message: 'Error al enviar el mensaje: ' + error.message });
    }
  };

  // Función para cancelar pedido por cliente
  const cancelarPedidoPorCliente = async () => {
    if (!modalAccionesPedido.pedido) return;

    try {
      const pedidoRef = doc(db, modalAccionesPedido.coleccion, modalAccionesPedido.pedido.id);

      // Actualizar el pedido original
      await updateDoc(pedidoRef, {
        estado: 'Cancelado por Cliente',
        fechaCancelacion: new Date(),
        motivoCancelacion: 'Cancelado por el cliente'
      });

      // Guardar en todosLosViajes con la estructura de fecha
      const fechaActual = new Date();


      // Guardar en todosLosViajes directamente
      const viajeCanceladoData = {
        ...modalAccionesPedido.pedido,
        estado: 'Cancelado por Cliente',
        fechaCancelacion: fechaActual,
        motivoCancelacion: 'Cancelado por el cliente',
        fechaRegistroCancelacion: fechaActual,
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador'
      };

      await setDoc(doc(db, 'todosLosViajes', modalAccionesPedido.pedido.id), viajeCanceladoData);

      // Eliminar el documento original de la colección
      await deleteDoc(pedidoRef);

      console.log('✅ Pedido cancelado por cliente, guardado en todosLosViajes y eliminado de la colección original');

      // Actualizar contadores específicos
      await actualizarContadorReporte('viajesCancelados');
      await actualizarContadorReporte('viajesCanceladosPorCliente');

      cerrarModalAccionesPedido();
    } catch (error) {
      console.error('❌ Error al cancelar pedido:', error);
      setModal({ open: true, success: false, message: 'Error al cancelar el pedido.' });
    }
  };

  // Función para cancelar pedido por unidad
  const cancelarPedidoPorUnidad = async () => {
    if (!modalAccionesPedido.pedido) return;

    try {
      const pedidoRef = doc(db, modalAccionesPedido.coleccion, modalAccionesPedido.pedido.id);

      // Actualizar el pedido original
      await updateDoc(pedidoRef, {
        estado: 'Cancelado por Unidad',
        fechaCancelacion: new Date(),
        motivoCancelacion: 'Cancelado por la unidad'
      });

      // Guardar en todosLosViajes con la estructura de fecha
      const fechaActual = new Date();


      // Guardar en todosLosViajes directamente
      const viajeCanceladoData = {
        ...modalAccionesPedido.pedido,
        estado: 'Cancelado por Unidad',
        fechaCancelacion: fechaActual,
        motivoCancelacion: 'Cancelado por la unidad',
        fechaRegistroCancelacion: fechaActual,
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador'
      };

      await setDoc(doc(db, 'todosLosViajes', modalAccionesPedido.pedido.id), viajeCanceladoData);

      // Eliminar el documento original de la colección
      await deleteDoc(pedidoRef);

      console.log('✅ Pedido cancelado por unidad, guardado en todosLosViajes y eliminado de la colección original');

      // Actualizar contadores específicos
      await actualizarContadorReporte('viajesCancelados');
      await actualizarContadorReporte('viajesCanceladosPorConductor');

      cerrarModalAccionesPedido();
    } catch (error) {
      console.error('❌ Error al cancelar pedido por unidad:', error);
      setModal({ open: true, success: false, message: 'Error al cancelar el pedido por unidad.' });
    }
  };

  // Estados para modal de voucher
  const [modalVoucher, setModalVoucher] = useState({
    open: false,
    voucher: {
      fechaHoraInicio: '',
      fechaHoraFinal: '',
      nombreCliente: '',
      telefono: '',
      direccion: '',
      destino: '',
      valor: '',
      motivo: '',
      informacionViaje: '',
      numeroUnidad: '',
      empresa: '',
      tipoVoucher: 'electronico', // 'electronico' o 'fisico'
      numeroVoucherFisico: '' // Solo para vouchers físicos
    }
  });

  // Estado para pre-registro de voucher
  const [preRegistroVoucher, setPreRegistroVoucher] = useState({
    numeroAutorizacion: null,
    activo: false
  });

  // Abrir el sidebar cuando se cambia de sección (excepto cuando el modal está abierto)
  useEffect(() => {
    if (setIsCollapsed && !modalVoucher.open) {
      setIsCollapsed(false);
    }
  }, [setIsCollapsed, modalVoucher.open]);

  // Lista de empresas para el voucher
  const empresasVoucher = EMPRESAS_VOUCHER;



  // Función para cerrar modal de voucher
  const cerrarModalVoucher = () => {
    setModalVoucher({
      open: false,
      voucher: {
        fechaHoraInicio: '',
        fechaHoraFinal: '',
        nombreCliente: '',
        telefono: '',
        direccion: '',
        destino: '',
        valor: '',
        motivo: '',
        numeroUnidad: '',
        empresa: '',
        tipoVoucher: 'electronico',
        numeroVoucherFisico: ''
      }
    });

    // Limpiar pre-registro cuando se cierra el modal
    setPreRegistroVoucher({
      numeroAutorizacion: null,
      activo: false
    });

    // Mantener el sidebar cerrado cuando se cierra el modal
    if (setIsCollapsed) {
      setIsCollapsed(true);
    }
  };



  // Función para guardar voucher corporativo desde el modal
  const guardarVoucherCorporativo = async () => {
    try {
      // Validar campos requeridos
      if (!modalVoucher.voucher.nombreCliente || !modalVoucher.voucher.destino || !modalVoucher.voucher.empresa || !modalVoucher.voucher.valor || !modalVoucher.voucher.direccion) {
        setModal({ open: true, success: false, message: 'Por favor complete todos los campos requeridos (Nombre del Cliente, Valor, Dirección, Destino y Empresa).' });
        return;
      }

      // Validar que empresa no sea 'efectivo'
      if (modalVoucher.voucher.empresa.toLowerCase() === 'efectivo') {
        setModal({ open: true, success: false, message: 'La empresa no puede ser "efectivo". Por favor seleccione una empresa válida.' });
        return;
      }

      // Validar número de voucher físico si es necesario
      if (modalVoucher.voucher.tipoVoucher === 'fisico' && !modalVoucher.voucher.numeroVoucherFisico.trim()) {
        setModal({ open: true, success: false, message: 'Por favor ingrese el número de voucher físico.' });
        return;
      }


      // Refuerzo: Si el número de autorización está visible en el modal, úsalo siempre
      let numeroAutorizacion = null;
      if (modalVoucher?.voucher?.numeroAutorizacion) {
        numeroAutorizacion = modalVoucher.voucher.numeroAutorizacion;
      } else if (modalAccionesPedido.pedido?.autorizacion) {
        numeroAutorizacion = modalAccionesPedido.pedido.autorizacion;
      } else if (preRegistroVoucher?.numeroAutorizacion) {
        numeroAutorizacion = preRegistroVoucher.numeroAutorizacion;
      }
      if (!numeroAutorizacion) {
        setModal({ open: true, success: false, message: 'No existe un número de autorización pre-registrado para este pedido. Debe pre-registrar la autorización antes de generar el voucher.' });
        return;
      }
      console.log('Número de autorización para voucher corporativo:', numeroAutorizacion);

      // Crear el voucher corporativo
      const voucherData = {
        ...modalVoucher.voucher,
        numeroAutorizacion: numeroAutorizacion,
        fechaCreacion: new Date(),
        estado: 'Activo',
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador',
        pedidoId: modalAccionesPedido.pedido?.id || 'N/A',
        telefono: modalVoucher.voucher.telefono || modalAccionesPedido.pedido?.telefono || '', // Teléfono del cliente
        telefonoConductor: modalVoucher.voucher.telefonoConductor || modalAccionesPedido.pedido?.telefonoConductor || '' // Teléfono del conductor
      };

      // Guardar en voucherCorporativos usando el número de autorización como ID del documento
      const voucherDocRef = doc(db, 'voucherCorporativos', numeroAutorizacion.toString());
      await setDoc(voucherDocRef, voucherData, { merge: true });

      // Si hay un pedido asociado, moverlo a todosLosViajes y eliminarlo de pedidoEnCurso
      if (modalAccionesPedido.pedido && modalAccionesPedido.coleccion === 'pedidoEnCurso') {
        try {
          const pedidoRef = doc(db, modalAccionesPedido.coleccion, modalAccionesPedido.pedido.id);

          // Crear datos del viaje finalizado tipo voucher
          const fechaActual = new Date();


          const viajeVoucherFinalizadoData = {
            ...modalAccionesPedido.pedido,
            estado: 'Finalizado',
            pedido: 'Voucher',
            fechaFinalizacion: fechaActual,
            fechaRegistroFinalizacion: fechaActual,
            motivoFinalizacion: 'Voucher corporativo generado',
            numeroAutorizacionVoucher: numeroAutorizacion,
            voucherData: voucherData,
            esVoucher: true,
            colorFondo: '#fef3c7',
            operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador'
          };

          // Guardar en todosLosViajes directamente
          await setDoc(doc(db, 'todosLosViajes', modalAccionesPedido.pedido.id), viajeVoucherFinalizadoData);

          // Eliminar de pedidoEnCurso
          await deleteDoc(pedidoRef);

          console.log('✅ Pedido movido a todosLosViajes y eliminado de pedidoEnCurso');
        } catch (error) {
          console.error('❌ Error al procesar el pedido:', error);
        }
      }

      setModal({
        open: true,
        success: true,
        message: `Voucher corporativo generado exitosamente. Número de autorización: ${numeroAutorizacion}`
      });

      cerrarModalVoucher();
    } catch (error) {
      console.error('❌ Error al generar voucher corporativo:', error);
      setModal({ open: true, success: false, message: 'Error al generar el voucher corporativo.' });
    }
  };

  // Función para ver ubicación
  const verUbicacion = () => {
    if (!modalAccionesPedido.pedido) return;

    try {
      const pedido = modalAccionesPedido.pedido;
      const latitud = pedido.latitud || pedido.latitudConductor;
      const longitud = pedido.longitud || pedido.longitudConductor;

      if (latitud && longitud) {
        // Abrir Google Maps con las coordenadas
        const url = `https://www.google.com/maps?q=${latitud},${longitud}`;
        window.open(url, '_blank');
        console.log('📍 Abriendo ubicación en Google Maps:', url);
      } else {
        console.log('⚠️ No hay coordenadas disponibles para este pedido');
        setModal({ open: true, success: false, message: 'No hay coordenadas disponibles para este pedido.' });
      }
    } catch (error) {
      console.error('❌ Error al abrir ubicación:', error);
      setModal({ open: true, success: false, message: 'Error al abrir la ubicación.' });
    }
  };

  // Funciones para pedidos disponibles
  const cancelarPedidoSinAsignar = async () => {
    if (!modalAccionesPedido.pedido) return;

    try {
      const pedidoRef = doc(db, modalAccionesPedido.coleccion, modalAccionesPedido.pedido.id);

      // Actualizar el pedido original
      await updateDoc(pedidoRef, {
        estado: 'Cancelado por Cliente Sin Asignar',
        fechaCancelacion: new Date(),
        motivoCancelacion: 'Cancelado por el cliente sin asignar unidad'
      });

      // Guardar en todosLosViajes con la estructura de fecha
      const fechaActual = new Date();


      // Guardar en todosLosViajes directamente
      const viajeCanceladoData = {
        ...modalAccionesPedido.pedido,
        estado: 'Cancelado por Cliente Sin Asignar',
        fechaCancelacion: fechaActual,
        motivoCancelacion: 'Cancelado por el cliente sin asignar unidad',
        fechaRegistroCancelacion: fechaActual,
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador'
      };

      await setDoc(doc(db, 'todosLosViajes', modalAccionesPedido.pedido.id), viajeCanceladoData);

      // Eliminar el documento original de la colección
      await deleteDoc(pedidoRef);

      console.log('✅ Pedido cancelado sin asignar, guardado en todosLosViajes y eliminado de la colección original');

      // Actualizar contadores específicos
      await actualizarContadorReporte('viajesCancelados');
      await actualizarContadorReporte('viajesCanceladosPorCliente');
      await actualizarContadorReporte('viajesSinUnidad');

      cerrarModalAccionesPedido();
    } catch (error) {
      console.error('❌ Error al cancelar pedido sin asignar:', error);
      setModal({ open: true, success: false, message: 'Error al cancelar el pedido sin asignar.' });
    }
  };

  const noHuboUnidadDisponible = async () => {
    if (!modalAccionesPedido.pedido) return;

    try {
      const pedidoRef = doc(db, modalAccionesPedido.coleccion, modalAccionesPedido.pedido.id);

      // Actualizar el pedido original
      await updateDoc(pedidoRef, {
        estado: 'No Hubo Unidad Disponible',
        fechaCancelacion: new Date(),
        motivoCancelacion: 'No hubo unidad disponible para asignar'
      });

      // Guardar en todosLosViajes con la estructura de fecha
      const fechaActual = new Date();


      // Guardar en todosLosViajes directamente
      const viajeCanceladoData = {
        ...modalAccionesPedido.pedido,
        estado: 'No Hubo Unidad Disponible',
        fechaCancelacion: fechaActual,
        motivoCancelacion: 'No hubo unidad disponible para asignar',
        fechaRegistroCancelacion: fechaActual,
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador'
      };

      await setDoc(doc(db, 'todosLosViajes', modalAccionesPedido.pedido.id), viajeCanceladoData);

      // Eliminar el documento original de la colección
      await deleteDoc(pedidoRef);

      console.log('✅ Pedido marcado como no hubo unidad disponible, guardado en todosLosViajes y eliminado de la colección original');

      // Actualizar contadores específicos
      await actualizarContadorReporte('viajesCancelados');
      await actualizarContadorReporte('viajesSinUnidad');

      cerrarModalAccionesPedido();
    } catch (error) {
      console.error('❌ Error al marcar como no hubo unidad disponible:', error);
      setModal({ open: true, success: false, message: 'Error al marcar como no hubo unidad disponible.' });
    }
  };

  const generarReserva = async () => {
    if (!modalAccionesPedido.pedido) return;

    try {
      const pedido = modalAccionesPedido.pedido;
      console.log('📅 Generando reserva para pedido:', pedido.id);
      console.log('📋 Datos del pedido:', pedido);

      // Pre-llenar el modal de reserva con los datos del pedido
      // Usar hora LOCAL (no UTC) para que datetime-local no adelante 5 horas
      const fechaHoraActual = new Date();
      const tzOffsetPedido = fechaHoraActual.getTimezoneOffset();
      const fechaHoraFormateada = new Date(fechaHoraActual.getTime() - tzOffsetPedido * 60000).toISOString().slice(0, 16);

      const datosReserva = {
        open: true,
        datosCliente: {
          telefono: pedido.telefono || '',
          nombre: pedido.nombreCliente || pedido.codigo || '',
          direccion: pedido.direccion || '',
          coordenadas: pedido.coordenadas || null
        },
        fechaHora: fechaHoraFormateada,
        motivo: '',
        destino: '',
        pedidoOriginalId: pedido.id // Guardar el ID del pedido original
      };

      console.log('🔧 Configurando modal de reserva con:', datosReserva);

      // Primero abrir el modal de reserva
      setErrorReserva('');
      setModalReserva(datosReserva);

      console.log('✅ Modal de reserva configurado, estado actual:', modalReserva);

      // Luego cerrar el modal de acciones del pedido
      cerrarModalAccionesPedido();

      console.log('✅ Modal de reserva abierto y modal de acciones cerrado');

    } catch (error) {
      console.error('❌ Error al generar reserva:', error);
      setModal({ open: true, success: false, message: 'Error al generar la reserva.' });
    }
  };

  // Generar autorización para pedido en 'pedidoEnCurso' (empresa: para qué empresa se genera)
  const generarAutorizacionParaPedidoEnCurso = async (empresa) => {
    try {
      if (!modalAccionesPedido.pedido || modalAccionesPedido.coleccion !== 'pedidoEnCurso') return;
      const pedido = modalAccionesPedido.pedido;
      if (pedido.autorizacion) {
        setModal({ open: true, success: false, message: `Este pedido ya tiene autorización: ${pedido.autorizacion}` });
        return;
      }
      const empresaFinal = (empresa || modalEmpresaAutorizacion.empresaSeleccionada || '').trim();
      if (!empresaFinal) {
        setModal({ open: true, success: false, message: 'Seleccione la empresa para la cual se genera la autorización.' });
        return;
      }

      // Usar la nueva lógica de secuencia de autorización
      const numeroAutorizacion = await obtenerSiguienteAutorizacion();

      // Registrar pre-registro
      let operadorNombre = null;
      try {
        const almacenado = localStorage.getItem('operadorAutenticado');
        if (almacenado) {
          const op = JSON.parse(almacenado);
          operadorNombre = op?.nombre || null;
        }
      } catch { }

      const preRegistro = {
        nombreCliente: pedido.nombreCliente || pedido.codigo || '',
        telefono: pedido.telefono || '',
        empresa: empresaFinal,
        createdAt: new Date(),
        numeroAutorizacion,
        origen: 'pedidoEnCurso',
        pedidoId: pedido.id,
        estado: 'pre-registrado',
        operador: operadorNombre || null
      };

      // Usar setDoc con numeroAutorizacion como ID para mantener la secuencia
      const voucherDocRef = doc(db, 'voucherCorporativos', numeroAutorizacion.toString());
      await setDoc(voucherDocRef, preRegistro);

      // Actualizar el pedido: primero en pedidoEnCurso; si ya no está (finalizado/movido), actualizar en todosLosViajes
      const pedidoRef = doc(db, 'pedidoEnCurso', pedido.id);
      const pedidoSnap = await getDoc(pedidoRef);
      if (pedidoSnap.exists()) {
        await updateDoc(pedidoRef, { autorizacion: numeroAutorizacion });
      } else {
        const viajeRef = doc(db, 'todosLosViajes', pedido.id);
        const viajeSnap = await getDoc(viajeRef);
        if (viajeSnap.exists()) {
          await updateDoc(viajeRef, { autorizacion: numeroAutorizacion });
        }
      }

      // Enviar al conductor el mensaje con autorización usando la misma API de WhatsApp
      try {
        const unidadPedido = (pedido.unidad || pedido.numeroUnidad || '').toString().trim();
        if (unidadPedido) {
          const conductoresQuery = query(
            collection(db, 'conductores'),
            where('unidad', '==', unidadPedido)
          );
          const conductoresSnapshot = await getDocs(conductoresQuery);
          if (!conductoresSnapshot.empty) {
            const conductorData = conductoresSnapshot.docs[0].data();
            const telefonoConductorRaw = conductorData.telefono || conductorData.celular || '';
            if (telefonoConductorRaw) {
              let telefonoLimpio = telefonoConductorRaw.replace(/\D/g, '');
              if (telefonoLimpio.startsWith('593')) telefonoLimpio = telefonoLimpio.substring(3);
              if (telefonoLimpio.startsWith('0')) telefonoLimpio = telefonoLimpio.substring(1);
              const telefonoConductorFormateado = normalizarTelefonoParaWhatsAppApi(`593${telefonoLimpio}`);
              const telefonoClienteMsg = normalizarTelefonoParaWhatsAppApi(pedido.telefonoCompleto || pedido.telefono || '');
              const extraUbicacion = extraUbicacionGoogleMaps(pedido);
              const mensajeConductor = [
                '🔑 *Autorización generada* 🔑',
                `🏢 *Empresa:* ${empresaFinal}`,
                `👤 *Cliente:* ${pedido.nombreCliente || pedido.codigo || 'Sin nombre'}`,
                `📍 *Dirección:* ${pedido.direccion || 'Sin dirección'}`,
                `📞 *Teléfono:* ${telefonoClienteMsg || 'N/D'}`,
                extraUbicacion ? `📍 *Coordenadas:* ${extraUbicacion.coords}` : null,
                extraUbicacion ? `🗺️ *Google Maps:* ${extraUbicacion.link}` : null,
                `🔑 *Autorización:* ${numeroAutorizacion}`,
                '',
                'Guarde este número de autorización.'
              ].filter(Boolean).join('\n');
              const apiUrl = getWhatsAppSendUrl();
              const res = await postJson(apiUrl, {
                to: telefonoConductorFormateado,
                message: mensajeConductor
              });
              if (!res.ok) console.error('API WhatsApp:', res.status, await res.text());
              else console.log('✅ Mensaje de autorización enviado al conductor.');
            }
          }
        }
      } catch (err) {
        console.error('Error al enviar autorización al conductor:', err);
      }

      setModalEmpresaAutorizacion({ open: false, empresaSeleccionada: EMPRESAS_VOUCHER[0] || '' });

      setModal({ open: true, success: true, message: `Autorización generada: ${numeroAutorizacion}` });
      setModalAccionesPedido(prev => ({
        ...prev,
        pedido: { ...prev.pedido, autorizacion: numeroAutorizacion }
      }));
      setUiUpdateTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error al generar autorización (en curso):', error);
      setModal({ open: true, success: false, message: 'No se pudo generar la autorización.' });
    }
  };

  // Generar autorización para pedido en 'pedidosDisponibles'
  const generarAutorizacionParaPedidoDisponible = async () => {
    try {
      if (!modalAccionesPedido.pedido || modalAccionesPedido.coleccion !== 'pedidosDisponibles') return;
      const pedido = modalAccionesPedido.pedido;
      // Evitar duplicados si ya existe
      if (pedido.autorizacion) {
        setModal({ open: true, success: false, message: `Este pedido ya tiene autorización: ${pedido.autorizacion}` });
        return;
      }

      // Usar la nueva lógica de secuencia de autorización
      const numeroAutorizacion = await obtenerSiguienteAutorizacion();

      // Registrar pre-registro en voucherCorporativos para reservar el número
      let operadorNombre = null;
      try {
        const almacenado = localStorage.getItem('operadorAutenticado');
        if (almacenado) {
          const op = JSON.parse(almacenado);
          operadorNombre = op?.nombre || null;
        }
      } catch { }
      const preRegistro = {
        nombreCliente: pedido.nombreCliente || pedido.codigo || '',
        telefono: pedido.telefono || '',
        empresa: null,
        createdAt: new Date(),
        numeroAutorizacion,
        origen: 'pedidoDisponible',
        pedidoId: pedido.id,
        estado: 'pre-registrado',
        operador: operadorNombre || null
      };

      // Usar setDoc con numeroAutorizacion como ID para mantener la secuencia
      const voucherDocRef = doc(db, 'voucherCorporativos', numeroAutorizacion.toString());
      await setDoc(voucherDocRef, preRegistro);

      // Guardar en el pedido disponible
      const pedidoRef = doc(db, 'pedidosDisponibles', pedido.id);
      await updateDoc(pedidoRef, { autorizacion: numeroAutorizacion });

      // Feedback y refresco local del modal
      setModal({ open: true, success: true, message: `Autorización generada: ${numeroAutorizacion}` });
      setModalAccionesPedido(prev => ({
        ...prev,
        pedido: { ...prev.pedido, autorizacion: numeroAutorizacion }
      }));

      // Disparar actualización de la UI para refrescar la autorización
      setUiUpdateTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error al generar autorización:', error);
      setModal({ open: true, success: false, message: 'No se pudo generar la autorización.' });
    }
  };

  // Función específica para finalizar pedido
  const finalizarPedido = async () => {
    if (!modalAccionesPedido.pedido) return;

    try {
      const fechaActual = new Date();

      const pedidoRef = doc(db, modalAccionesPedido.coleccion, modalAccionesPedido.pedido.id);

      // Preparar datos del viaje finalizado
      const viajeFinalizadoData = {
        ...modalAccionesPedido.pedido,
        estado: 'Finalizado',
        pedido: 'Finalizado',
        fechaFinalizacion: fechaActual,
        fechaRegistroFinalizacion: fechaActual,
        motivoFinalizacion: 'Pedido completado exitosamente',
        esViajeFinalizado: true,
        colorFondo: '#dbeafe', // Color de fondo azul claro para viajes finalizados
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador'
      };

      // Guardar en todosLosViajes directamente
      await setDoc(doc(db, 'todosLosViajes', modalAccionesPedido.pedido.id), viajeFinalizadoData);

      // Eliminar el documento original de la colección
      await deleteDoc(pedidoRef);

      console.log('✅ Pedido finalizado y guardado en todosLosViajes');

      setModal({ open: true, success: true, message: 'Pedido finalizado exitosamente.' });

      cerrarModalAccionesPedido();
    } catch (error) {
      console.error('❌ Error al finalizar el pedido:', error);
      setModal({ open: true, success: false, message: 'Error al finalizar el pedido.' });
    }
  };

  // Función para iniciar edición de un viaje
  const iniciarEdicionViaje = (viaje) => {
    setEditandoViaje(viaje.id);
    setTiempoEdit(viaje.tiempo || viaje.minutos || '');
    setUnidadEdit(viaje.numeroUnidad || viaje.unidad || '');
    setBaseEdit(convertirNumeroABase(viaje.base || '0'));
  };

  // Guardar edición desde "Viajes del Día" (actualiza en pedidoEnCurso o todosLosViajes)
  const guardarEdicionViajeDesdeTabla = async (viaje) => {
    const tiempoFinal = (tiempoEdit || '').toString().trim();
    const unidadFinal = (unidadEdit || '').toString().trim();
    const baseFinal = (baseEdit || '').toString().trim() || '0';

    if (!tiempoFinal || !unidadFinal) {
      setModal({ open: true, success: false, message: 'Por favor, complete Tiempo y Unidad.' });
      return;
    }

    const origen = (viaje.origenColeccion || '').toString();
    const unidadOriginal = (viaje.numeroUnidad || viaje.unidad || '').toString().trim();
    const esReasignacion = unidadFinal !== unidadOriginal;

    const updatePayload = {
      tiempo: tiempoFinal,
      minutos: parseInt(tiempoFinal, 10) || 0,
      unidad: unidadFinal,
      numeroUnidad: unidadFinal,
      base: convertirNumeroABase(baseFinal),
      updatedAt: new Date()
    };

    if (esReasignacion) {
      updatePayload.estado = 'Reasignado';
      updatePayload.pedido = 'Reasignado';
      // Al reasignar, eliminar el idConductor (correo) del documento
      updatePayload.idConductor = deleteField();
    }

    // Siempre enviar WhatsApp al conductor al guardar (asignación o reasignación)
    let whatsAppEnviado = false;
    try {
      const conductoresQuery = query(
        collection(db, 'conductores'),
        where('unidad', '==', unidadFinal)
      );
      const conductoresSnapshot = await getDocs(conductoresQuery);

      if (!conductoresSnapshot.empty) {
        const conductorData = conductoresSnapshot.docs[0].data();
        const estatusValor = conductorData?.estatus ?? true;
        const estatusBool = typeof estatusValor === 'string' ? estatusValor.toLowerCase() !== 'false' : Boolean(estatusValor);
        if (estatusBool) {
          updatePayload.nombreConductor = conductorData.nombre || '';
          updatePayload.telefonoConductor = conductorData.telefono || conductorData.celular || '';
          updatePayload.placa = conductorData.placa || '';
          updatePayload.color = conductorData.color || '';

          const telefonoConductorRaw = conductorData.telefono || conductorData.celular || '';
          if (telefonoConductorRaw) {
            let telefonoLimpio = telefonoConductorRaw.replace(/\D/g, '');
            if (telefonoLimpio.startsWith('593')) telefonoLimpio = telefonoLimpio.substring(3);
            if (telefonoLimpio.startsWith('0')) telefonoLimpio = telefonoLimpio.substring(1);
            const telefonoConductorFormateado = normalizarTelefonoParaWhatsAppApi(`593${telefonoLimpio}`);
            const telefonoClienteMsg = normalizarTelefonoParaWhatsAppApi(viaje.telefonoCompleto || viaje.telefono || '');

            const extraUbicacion = extraUbicacionGoogleMaps(viaje, { skipTipoCheck: (viaje.origenColeccion || '') === 'pedidoSolicitado' });
            const tituloMsg = esReasignacion ? 'Pedido Reasignado' : 'Nuevo Pedido Asignado';
            let mensajeConductor = `🚖 *${tituloMsg}* 🚖\n\n👤 *Cliente:* ${viaje.nombreCliente || viaje.codigo || 'Sin nombre'}\n📍 *Dirección:* ${viaje.direccion || 'Sin dirección'}\n📞 *Teléfono:* ${telefonoClienteMsg || 'N/D'}\n⏱️ *Tiempo:* ${tiempoFinal} min`;
            if (viaje.autorizacion != null && viaje.autorizacion !== '') {
              mensajeConductor += `\n🔑 *Autorización:* ${viaje.autorizacion}`;
            }
            if (extraUbicacion) {
              mensajeConductor += `\n📍 *Coordenadas:* ${extraUbicacion.coords}\n🗺️ *Google Maps:* ${extraUbicacion.link}`;
            }
            mensajeConductor += `\n\nPor favor, dirígete a la ubicación.`;

            const apiUrl = getWhatsAppSendUrl();
            whatsAppEnviado = true;
            postJson(apiUrl, {
              to: telefonoConductorFormateado,
              message: mensajeConductor
            }).then(res => {
              if (!res.ok) res.text().then(t => console.error('API WhatsApp conductor:', res.status, t));
              else console.log('✅ Mensaje enviado al conductor.');
            }).catch(err => console.error('Error envío conductor:', err));
            // Enviar al cliente mensaje con imagen (formato CONDUCTOR EN CAMINO)
            const msgCliente = formatearMensajeConductorAlCliente(conductorData, unidadFinal, tiempoFinal);
            enviarMensajeImagenAlCliente(viaje.telefonoCompleto || viaje.telefono, msgCliente, conductorData.foto || undefined);
          }
        } else if (esReasignacion) {
          setModal({ open: true, success: false, message: `La unidad ${unidadFinal} está suspendida/inactiva. No se puede reasignar.` });
          return;
        }
      } else if (esReasignacion) {
        setModal({ open: true, success: false, message: `No se encontró un conductor con la unidad ${unidadFinal}. Por favor, ingrese una unidad válida.` });
        return;
      }
    } catch (err) {
      console.error('❌ Error al buscar conductor o enviar WhatsApp:', err);
      if (esReasignacion) {
        setModal({ open: true, success: false, message: 'Error al procesar la reasignación.' });
        return;
      }
    }

    try {
      // Reasignación: si el documento existe en pedidoEnCurso, transferir siempre a todosLosViajes
      // (No depender del arreglo/Set en memoria, que puede no incluir todos los docs.)
      if (esReasignacion) {
        const enCursoRef = doc(db, 'pedidoEnCurso', viaje.id);
        const enCursoSnap = await getDoc(enCursoRef);
        if (enCursoSnap.exists()) {
          const baseData = enCursoSnap.data() || {};
          const destinoData = {
            ...baseData,
            ...updatePayload,
            fechaReasignacion: new Date(),
          };
          // setDoc (sin merge) no debe recibir deleteField(); omitir el campo para que no exista en el destino
          try { delete destinoData.idConductor; } catch (_) { }

          try {
            await setDoc(doc(db, 'todosLosViajes', viaje.id), destinoData);
            await deleteDoc(enCursoRef);
          } catch (e) {
            console.error('❌ Error transfiriendo a todosLosViajes:', e);
            setModal({ open: true, success: false, message: 'No se pudo transferir a Todos los Viajes.' });
            return;
          }
          cancelarEdicionViaje();
          return;
        }
      }

      // Si es pedido en curso
      if (pedidosEnCursoIdSetMemo.has(viaje.id)) {
        await updateDoc(doc(db, 'pedidoEnCurso', viaje.id), updatePayload);
        cancelarEdicionViaje();
        return;
      }

      // Si viene de la subcolección viajes/{DD-MM-YYYY}/viajes
      if (viaje.rutaColeccion) {
        await updateDoc(doc(db, viaje.rutaColeccion, viaje.id), updatePayload);
        cancelarEdicionViaje();
        return;
      }

      // Si es histórico (todosLosViajes)
      if (viaje.esHistorico) {
        await updateDoc(doc(db, 'todosLosViajes', viaje.id), updatePayload);
        cancelarEdicionViaje();
        return;
      }

      // Intentar actualizar en todosLosViajes (el documento puede estar ahí)
      try {
        await updateDoc(doc(db, 'todosLosViajes', viaje.id), updatePayload);
        cancelarEdicionViaje();
        return;
      } catch (errTodos) {
        // Si el documento no está en todosLosViajes y viene de "disponibles" (PD/PD1) o App,
        // usar guardarEdicionViaje (mueve a pedidoEnCurso, envía WhatsApp, elimina del origen)
        if (origen === 'pedidosDisponibles' || origen === 'pedidosDisponibles1' || origen === 'pedidoSolicitado') {
          cancelarEdicionViaje();
          await guardarEdicionViaje(viaje.id, tiempoFinal, unidadFinal, baseFinal);
          return;
        }
        console.warn('Error al actualizar todosLosViajes:', errTodos);
      }

      setModal({
        open: true,
        success: false,
        message: 'Este registro no está en Pedido en Curso ni en Todos los Viajes.'
      });
    } catch (error) {
      console.error('❌ Error al guardar edición del viaje:', error);
      setModal({ open: true, success: false, message: 'Error al guardar cambios.' });
    }
  };



  const getObservacionesValue = (pedido) => {
    const v = observacionesDraft[pedido?.id];
    if (v !== undefined) return v;
    return (pedido?.observaciones ?? pedido?.observacion ?? '').toString();
  };

  const guardarObservacionesPedido = async ({ pedido, texto, colecciones }) => {
    const id = pedido?.id;
    if (!id) return false;
    const payload = { observaciones: (texto ?? '').toString(), updatedAt: new Date() };

    setGuardandoObservaciones(prev => ({ ...prev, [id]: true }));
    try {
      const cols = Array.isArray(colecciones) ? colecciones.filter(Boolean) : [];
      const tried = new Set();
      for (const col of cols) {
        if (tried.has(col)) continue;
        tried.add(col);
        try {
          // eslint-disable-next-line no-await-in-loop
          await updateDoc(doc(db, col, id), payload);
          return true;
        } catch (e) {
          const code = (e && e.code) ? String(e.code) : '';
          if (code.includes('not-found') || code.includes('not_found')) continue;
          throw e;
        }
      }

      // Último fallback (por si cambió de colección justo)
      try {
        await updateDoc(doc(db, 'pedidoEnCurso', id), payload);
        return true;
      } catch { }
      try {
        await updateDoc(doc(db, 'todosLosViajes', id), payload);
        return true;
      } catch { }
    } catch (error) {
      console.error('❌ Error guardando observaciones:', error);
      setModal({ open: true, success: false, message: 'No se pudo guardar la observación.' });
      return false;
    } finally {
      setGuardandoObservaciones(prev => ({ ...prev, [id]: false }));
    }
    return false;
  };

  const abrirModalObservaciones = (pedido, colecciones) => {
    const txt = getObservacionesValue(pedido);
    setModalObservaciones({
      open: true,
      pedido: pedido || null,
      texto: (txt ?? '').toString(),
      colecciones: Array.isArray(colecciones) ? colecciones.filter(Boolean) : []
    });
  };

  const cerrarModalObservaciones = () => {
    setModalObservaciones({ open: false, pedido: null, texto: '', colecciones: [] });
  };

  const confirmarObservaciones = async () => {
    const pedido = modalObservaciones.pedido;
    if (!pedido?.id) return;
    const texto = (modalObservaciones.texto ?? '').toString();
    const ok = await guardarObservacionesPedido({
      pedido,
      texto,
      colecciones: modalObservaciones.colecciones
    });
    if (ok) {
      setObservacionesDraft(prev => ({ ...prev, [pedido.id]: texto }));
      cerrarModalObservaciones();
    }
  };

  // Función para cancelar edición
  const cancelarEdicionViaje = () => {
    setEditandoViaje(null);
    setTiempoEdit('');
    setUnidadEdit('');
    setBaseEdit('0');
  };



  // Función para mover pedido de disponibles a en curso
  // Incluye el token del conductor para notificaciones push cuando se asigna manualmente
  const guardarEdicionViaje = async (viajeId, tiempoParam = null, unidadParam = null, baseParam = null) => {
    // Usar parámetros si se proporcionan, de lo contrario usar el estado
    const tiempoFinal = tiempoParam !== null ? tiempoParam : tiempoEdit;
    const unidadFinal = unidadParam !== null ? unidadParam : unidadEdit;
    const baseFinal = baseParam !== null
      ? ((baseParam ?? '').toString().trim() || '0')
      : ((baseEdit ?? '').toString().trim() || '0');

    if (!tiempoFinal || !tiempoFinal.trim() || !unidadFinal || !unidadFinal.trim()) {
      setModal({ open: true, success: false, message: 'Por favor, ingrese tiempo y número de unidad.' });
      return;
    }

    try {
      // Buscar datos del conductor por número de unidad
      const conductoresQuery = query(
        collection(db, 'conductores'),
        where("unidad", "==", unidadFinal.trim())
      );

      const conductoresSnapshot = await getDocs(conductoresQuery);

      if (conductoresSnapshot.empty) {
        setModal({ open: true, success: false, message: `No se encontró un conductor con la unidad ${unidadFinal}. Por favor, ingrese una unidad válida.` });
        // NO hacer return, permitir que el usuario siga editando
        // NO limpiar el formulario, mantener los datos
        // Mantener el estado de edición activo
        return;
      }

      // Obtener datos del conductor
      const conductorDoc = conductoresSnapshot.docs[0];
      const conductorData = conductorDoc.data();
      const emailConductor = conductorDoc.id || conductorData.correo || conductorData.email || '';
      // Validar estatus del conductor (inactivo => bloquear asignación)
      try {
        const estatusValor = conductorData && 'estatus' in conductorData ? conductorData.estatus : true;
        const estatusBool = typeof estatusValor === 'string' ? estatusValor.toLowerCase() !== 'false' : Boolean(estatusValor);
        if (!estatusBool) {
          setModal({ open: true, success: false, message: `La unidad ${unidadFinal} está suspendida/inactiva. No se puede asignar.` });
          return;
        }
      } catch (e) {
        console.warn('No se pudo validar estatus del conductor en edición, continuando por defecto.', e);
      }

      // ID conductor aleatorio (no email)
      const idConductorRandom = `cid_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

      // Obtener el token del conductor (si existe)
      const tokenConductor = conductorData.token || conductorData.fcmToken || conductorData.deviceToken || '';

      // 1. Obtener el pedido actual de pedidosDisponibles, pedidosDisponibles1 o pedidoSolicitado
      let pedidoOriginalRef = doc(db, 'pedidosDisponibles', viajeId);
      let pedidoOriginalSnap = await getDoc(pedidoOriginalRef);
      let coleccionOrigen = 'pedidosDisponibles';

      // Si no se encuentra en pedidosDisponibles, intentar en pedidosDisponibles1
      if (!pedidoOriginalSnap.exists()) {
        pedidoOriginalRef = doc(db, 'pedidosDisponibles1', viajeId);
        pedidoOriginalSnap = await getDoc(pedidoOriginalRef);
        coleccionOrigen = 'pedidosDisponibles1';
      }

      // Si no se encuentra en pedidosDisponibles1, intentar en pedidoSolicitado (App)
      if (!pedidoOriginalSnap.exists()) {
        pedidoOriginalRef = doc(db, 'pedidoSolicitado', viajeId);
        pedidoOriginalSnap = await getDoc(pedidoOriginalRef);
        coleccionOrigen = 'pedidoSolicitado';
      }

      if (!pedidoOriginalSnap.exists()) {
        setModal({ open: true, success: false, message: 'Pedido no encontrado en ninguna colección.' });
        return;
      }


      const pedidoOriginal = pedidoOriginalSnap.data();

      // ==========================================
      // NOTIFICACIONES POR WHATSAPP
      // ==========================================

      // 1. Enviar WhatsApp al CONDUCTOR con datos del cliente
      try {
        const telefonoConductorRaw = conductorData.telefono || conductorData.celular || '';
        if (telefonoConductorRaw) {
          let telefonoLimpio = telefonoConductorRaw.replace(/\D/g, '');
          if (telefonoLimpio.startsWith('593')) telefonoLimpio = telefonoLimpio.substring(3);
          if (telefonoLimpio.startsWith('0')) telefonoLimpio = telefonoLimpio.substring(1);
          const telefonoConductorFormateado = normalizarTelefonoParaWhatsAppApi(`593${telefonoLimpio}`);

          const telefonoClienteMsg = normalizarTelefonoParaWhatsAppApi(pedidoOriginal.telefonoCompleto || pedidoOriginal.telefono || '');
          // Enviar coordenadas + Google Maps al conductor cuando es tipo "ubicación" o cuando es pedidoSolicitado.
          const extraUbicacion = extraUbicacionGoogleMaps(pedidoOriginal, { skipTipoCheck: coleccionOrigen === 'pedidoSolicitado' });
          let mensajeConductor = `🚖 *Nuevo Pedido Asignado* 🚖\n\n👤 *Cliente:* ${pedidoOriginal.nombreCliente || pedidoOriginal.codigo || 'Sin nombre'}\n📍 *Dirección:* ${pedidoOriginal.direccion || 'Sin dirección'}\n📞 *Teléfono:* ${telefonoClienteMsg || 'N/D'}\n⏱️ *Tiempo:* ${tiempoFinal} min`;
          if (pedidoOriginal.autorizacion != null && pedidoOriginal.autorizacion !== '') {
            mensajeConductor += `\n🔑 *Autorización:* ${pedidoOriginal.autorizacion}`;
          }
          if (extraUbicacion) {
            mensajeConductor += `\n📍 *Coordenadas:* ${extraUbicacion.coords}\n🗺️ *Google Maps:* ${extraUbicacion.link}`;
          }
          mensajeConductor += `\n\nPor favor, dirígete a la ubicación.`;

          console.log(`📤 Enviando mensaje a conductor (desde disponibles): ${conductorData.nombre}`);

          const apiUrl = getWhatsAppSendUrl();
          postJson(apiUrl, {
            to: telefonoConductorFormateado,
            message: mensajeConductor
          }).then(res => {
            if (!res.ok) res.text().then(t => console.error('API WhatsApp conductor:', res.status, t));
            else console.log('✅ Mensaje enviado al conductor.');
          }).catch(err => console.error('Error envío conductor:', err));
        }
        // Enviar al cliente mensaje con imagen (formato CONDUCTOR EN CAMINO) - siempre que haya conductor asignado
        const msgCliente = formatearMensajeConductorAlCliente(conductorData, unidadFinal, tiempoFinal);
        enviarMensajeImagenAlCliente(pedidoOriginal.telefonoCompleto || pedidoOriginal.telefono, msgCliente, conductorData.foto || undefined);
      } catch (e) { console.error('Error preparando mensaje conductor:', e); }
      // ==========================================

      // Verificar si el tipoPedido es "Ubicacion" para cambiarlo a "Manual"
      const tipoPedidoOriginal = pedidoOriginal.tipoPedido || pedidoOriginal.tipopedido || '';
      const esUbicacion = tipoPedidoOriginal.toString().toLowerCase() === 'ubicacion';

      // 2. Crear el pedido en curso con todos los datos del conductor
      const pedidoEnCursoData = {
        ...pedidoOriginal,
        // Datos de asignación
        base: convertirNumeroABase(baseFinal),
        tiempo: tiempoFinal,
        numeroUnidad: unidadFinal,
        unidad: unidadFinal,
        estado: 'Aceptado',
        pedido: 'Aceptado',
        // Fecha como timestamp
        fecha: new Date(),
        // Datos del conductor - ID único para asignación manual
        idConductor: idConductorRandom, // ID aleatorio
        correo: emailConductor, // Email real del conductor
        emailConductor: emailConductor,
        nombre: conductorData.nombre || '',
        nombreConductor: conductorData.nombre || '',
        placa: conductorData.placa || '',
        color: conductorData.color || '', // Solo una vez
        telefonoConductor: conductorData.telefono || '',
        foto: conductorData.foto || '',
        tokenConductor: tokenConductor || conductorData.fcmToken || conductorData.token || conductorData.notificationToken || '', // Token del conductor para notificaciones
        minutos: parseInt(tiempoFinal) || 0,
        distancia: '0.00 Mts', // Valor inicial
        latitudConductor: '',
        longitudConductor: '',
        tarifaSeleccionada: true,
        modoAsignacion: 'manual', // Campo adicional para indicar asignación manual
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador'
      };

      // Si el tipoPedido es "Ubicacion", cambiarlo a "Manual"
      if (esUbicacion) {
        pedidoEnCursoData.tipoPedido = 'Manual';
        pedidoEnCursoData.tipopedido = 'Manual';
        console.log('🔄 Tipo de pedido cambiado de "Ubicacion" a "Manual" al asignar unidad');
      }

      // 3. Guardar en "todosLosViajes" con el MISMO ID (ya no en pedidoEnCurso)
      const updateData = {
        ...pedidoEnCursoData,
        id: viajeId,
        fechaAsignacion: new Date(),
        origenColeccion: coleccionOrigen,
      };
      if (esUbicacion) {
        updateData.tipoPedido = 'Manual';
        updateData.tipopedido = 'Manual';
      }
      await setDoc(doc(db, 'todosLosViajes', viajeId), updateData, { merge: true });

      // 4. Crear duplicado en la colección "NotificaciOnenCurso" para sistema de notificaciones
      const notificacionEnCursoData = {
        ...pedidoEnCursoData,
        id: viajeId, // ID del documento en todosLosViajes
        fechaNotificacion: new Date(), // Fecha específica para la notificación
        estadoNotificacion: 'pendiente' // Estado de la notificación (pendiente, enviada, fallida)
      };

      // Asegurar que el tipoPedido también esté actualizado en la notificación
      if (esUbicacion) {
        notificacionEnCursoData.tipoPedido = 'Manual';
        notificacionEnCursoData.tipopedido = 'Manual';
      }

      try {
        await addDoc(collection(db, 'NotificaciOnenCurso'), notificacionEnCursoData);
        console.log('✅ Notificación push encolada en NotificaciOnenCurso');
      } catch (error) {
        console.error('⚠️ Error al encolar notificación push (no crítico):', error);
      }

      // 5. Eliminar de pedidosDisponibles o pedidosDisponibles1
      await deleteDoc(pedidoOriginalRef);

      // Guardar en historial del cliente si hay dirección
      if (pedidoOriginal.telefono && pedidoOriginal.direccion) {
        await guardarEnHistorialCliente(
          pedidoOriginal.telefono,
          pedidoOriginal.direccion,
          `${pedidoOriginal.latitud || '', pedidoOriginal.longitud || ''}`,
          'manual'
        );
      }

      // Registrar automáticamente en la colección de pedidos manuales
      try {
        const pedidoManualData = {
          ...pedidoEnCursoData,
          idOriginal: viajeId, // Referencia al documento en todosLosViajes
          fechaRegistro: new Date(),
          tipo: 'manual',
          estadoRegistro: 'Registrado',
          modoRegistro: 'manual',
          origen: coleccionOrigen // Indicar de dónde viene (pedidosDisponibles o pedidosDisponibles1)
        };

        await addDoc(collection(db, 'pedidosManuales'), pedidoManualData);
        console.log('✅ Pedido manual registrado en colección separada (desde disponibles)');
      } catch (error) {
        console.error('❌ Error al registrar pedido manual:', error);
        // No fallar si no se puede registrar en la colección separada
      }

      // Cancelar edición - los listeners en tiempo real actualizarán automáticamente las tablas
      cancelarEdicionViaje();

      // Registro silencioso - sin mostrar alert de éxito
      console.log(`✅ Pedido guardado en todosLosViajes - Conductor: ${conductorData.nombre}, Unidad: ${unidadFinal}`);

      // Actualizar contador de viajes asignados
      await actualizarContadorReporte('viajesAsignados');
    } catch (error) {
      console.error('Error al mover el pedido:', error);
      setModal({ open: true, success: false, message: 'Error al mover el pedido a "En Curso".' });
    }
  };



  const handleSolicitarAplicacion = async (direccionCustom, coordenadasCustom, sectorCustom) => {
    // Evitar múltiples inserciones simultáneas
    if (insertandoRegistro) {
      console.log('⚠️ Ya se está insertando un registro, esperando...');
      return;
    }

    setInsertandoRegistro(true);

    try {
      // Usar parámetros si se proporcionan (desde el historial), de lo contrario usar estado
      const direccionFinal = (typeof direccionCustom === 'string') ? direccionCustom : direccion;
      const coordenadasFinalesArg = (typeof coordenadasCustom === 'string') ? coordenadasCustom : coordenadas;
      const sectorFinal = (typeof sectorCustom === 'string') ? sectorCustom : (sector || '');

      // Coordenadas por defecto si no hay coordenadas
      const coordenadasPorDefecto = '-0.2298500,-78.5249500'; // Quito centro
      const coordenadasFinales = coordenadasFinalesArg || coordenadasPorDefecto;
      const [latitud, longitud] = coordenadasFinales.split(',').map(s => s.trim());
      const fecha = new Date(); // Timestamp
      const clave = Math.random().toString(36).substring(2, 8).toUpperCase();

      // Determinar el teléfono completo para WhatsApp
      let telefonoCompleto = telefono || '';
      if (telefono && telefono.length > 7) {
        // Para celulares o números largos, buscar el cliente y obtener su prefijo
        try {
          // Determinar colección
          const coleccionWhatsApp = telefono.length === 7 ? 'clientestelefonos' : 'clientestelefonos1';

          // Intentar primero con telefonoCompleto (Ecuador por defecto)
          const telefonoCompletoBusqueda = concatenarTelefonoWhatsApp(telefono, 'Ecuador');
          let clienteRef = doc(db, coleccionWhatsApp, telefono);
          let clienteSnapshot = await getDoc(clienteRef);

          if (clienteSnapshot.exists()) {
            const clienteData = clienteSnapshot.data();
            telefonoCompleto = concatenarTelefonoWhatsApp(telefono, clienteData.prefijo || 'Ecuador');
            console.log('📱 Teléfono completo para WhatsApp:', telefonoCompleto);
          } else {
            // Si no se encuentra, intentar con los últimos 9 dígitos (método anterior)
            const telefonoBusqueda = telefono.slice(-9);
            clienteRef = doc(db, 'clientestelefonos', telefonoBusqueda);
            clienteSnapshot = await getDoc(clienteRef);

            if (clienteSnapshot.exists()) {
              const clienteData = clienteSnapshot.data();
              telefonoCompleto = concatenarTelefonoWhatsApp(telefono, clienteData.prefijo || 'Ecuador');
              console.log('📱 Teléfono completo para WhatsApp (fallback):', telefonoCompleto);
            }
          }
        } catch (error) {
          console.log('⚠️ No se pudo obtener el prefijo del cliente, usando teléfono original');
        }
      }

      // Datos para inserción directa en pedidosDisponibles
      const pedidoData = {
        // Datos básicos del pedido
        clave: clave,
        codigo: nombre || '',
        nombreCliente: nombre || '',
        telefono: (telefono && String(telefono).replace(/\D/g, '').startsWith('593') ? String(telefono).replace(/\D/g, '') : (telefonoCompleto || telefono || '')),
        telefonoCompleto: telefonoCompleto || telefono || '',
        direccion: direccionFinal || '',
        base: convertirNumeroABase(base || '0'),
        destino: sectorFinal || '',
        fecha: fecha,
        estado: 'Disponible',
        pedido: 'Disponible',
        idConductor: 'Sin asignar',

        // Coordenadas
        latitud: latitud || '',
        longitud: longitud || '',
        latitudDestino: '',
        longitudDestino: '',

        // Datos adicionales
        sector: sectorFinal || '',
        tipoPedido: 'enpoidpanel', // Actualizado según requerimiento
        valor: '',
        central: false,
        coorporativo: false,
        llegue: false,
        puerto: '3020',
        randon: clave,
        rango: modoSeleccionUI === 'Manual' ? '0' : (coordenadasFinalesArg ? '1' : '0'),
        viajes: unidad || '',
        foto: '0',
        tarifaSeleccionada: true,

        // Identificación del modo
        modoSeleccion: modoSeleccionUI.toLowerCase(),
        tipoEmpresa: tipoEmpresa,
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador',
        observaciones: (notaCliente || '').trim()
      };

      // Generar autorización si es empresa corporativa
      if (tipoEmpresa && tipoEmpresa !== 'Efectivo') {
        try {
          const numeroAutorizacionGen = await obtenerSiguienteAutorizacion();
          pedidoData.autorizacion = numeroAutorizacionGen;
          console.log(`✅ Autorización generada para pedido: ${numeroAutorizacionGen}`);
          setUiUpdateTrigger(prev => prev + 1);
        } catch (error) {
          console.error('❌ Error al generar autorización:', error);
        }
      }

      // Inserción directa en la colección "pedidosDisponibles"
      const docRef = await addDoc(collection(db, 'pedidosDisponibles'), pedidoData);
      await updateDoc(docRef, { id: docRef.id });

      // Guardar en historial del cliente si hay dirección
      if (telefono && direccionFinal) {
        await guardarEnHistorialCliente(telefono, direccionFinal, coordenadasFinalesArg, 'aplicacion');
      }

      // Cerrar el mapa y limpiar formulario
      setMapaVisible(false);
      limpiarFormularioCompleto();

      // Actualizar contadores
      await actualizarContadorReporte('viajesRegistrados');
      await actualizarContadorReporte('viajesManuales');

      if (tipoEmpresa !== 'Efectivo') {
        setTipoEmpresa('Efectivo');
      }

    } catch (error) {
      console.error('Error al registrar el pedido:', error);
      setModal({ open: true, success: false, message: 'Error al registrar el pedido en la base de datos.' });
    } finally {
      setInsertandoRegistro(false);
    }
  };

  // Función para solicitar APP desde el historial de direcciones
  const solicitarAppDesdeHistorial = async (dir) => {
    if (!dir.coordenadas) {
      setModal({ open: true, success: false, message: 'Esta dirección no tiene coordenadas para solicitud de APP.' });
      return;
    }

    // Actualizar visualmente el formulario
    setDireccion(dir.direccion);
    setCoordenadas(dir.coordenadas);

    // Ejecutar la solicitud directamente con los datos del historial
    await handleSolicitarAplicacion(dir.direccion, dir.coordenadas, '');
  };

  // Callbacks memoizados para evitar re-renders innecesarios
  const handleCoordinatesSelect = useCallback(async (nuevasCoordenadas) => {
    setCoordenadas(nuevasCoordenadas);
    // Ya no se cierra automáticamente para permitir al usuario ajustar la ubicación
  }, []);

  const handleAddressSelect = useCallback((nuevaDireccion) => {
    setDireccion(nuevaDireccion);
  }, []);

  // Función para guardar coordenadas y direcciones en el historial del cliente
  const guardarEnHistorialCliente = async (telefono, direccion, coordenadas, modoRegistro = 'manual', sectorParam = null) => {
    try {
      if (!telefono || !direccion) {
        console.log('⚠️ No se pueden guardar coordenadas sin teléfono o dirección');
        return false;
      }

      // Usar el parámetro si se proporciona, de lo contrario usar el estado global 'sector'
      const sectorFinal = sectorParam !== null ? sectorParam : (sector || '');

      console.log('📍 Guardando en historial del cliente:', { telefono, direccion, coordenadas, modoRegistro, sector: sectorFinal });

      // Localizar el documento real del cliente (colecciones/IDs variados).
      const encontrado = await localizarClienteDoc(telefono);
      if (!encontrado) {
        console.log('❌ Cliente no encontrado para guardar historial');
        return false;
      }
      const { clienteRef, clienteSnapshot, coleccion: coleccionNombre, id: telefonoId } = encontrado;
      console.log(`📍 Cliente localizado para historial (colección: ${coleccionNombre}, ID: ${telefonoId})`);

      const clienteData = clienteSnapshot.data();
      const direccionesActuales = clienteData.direcciones || [];

      // Normalizar la dirección y coordenadas para comparación
      const direccionNormalizada = direccion.toLowerCase().trim();
      const coordenadasNormalizadas = coordenadas ? coordenadas.trim() : '';

      // Verificar si ya existe esta dirección exacta O estas coordenadas exactas
      const direccionExistente = direccionesActuales.find(dir => {
        const dirNormalizada = dir.direccion.toLowerCase().trim();
        const coordNormalizadas = dir.coordenadas ? dir.coordenadas.trim() : '';

        // Si la dirección es exactamente igual
        if (dirNormalizada === direccionNormalizada) {
          return true;
        }

        // Si las coordenadas son exactamente iguales (y no están vacías)
        if (coordenadasNormalizadas && coordNormalizadas && coordenadasNormalizadas === coordNormalizadas) {
          return true;
        }

        return false;
      });

      if (direccionExistente) {
        // Si encontramos una dirección existente, actualizar información si es necesario
        let actualizado = false;

        // Si la dirección es igual pero las coordenadas son diferentes, actualizar coordenadas
        if (direccionExistente.direccion.toLowerCase().trim() === direccionNormalizada &&
          direccionExistente.coordenadas !== coordenadasNormalizadas) {
          direccionExistente.coordenadas = coordenadasNormalizadas;
          direccionExistente.fechaActualizacion = new Date();
          actualizado = true;
          console.log('📍 Coordenadas actualizadas en dirección existente:', direccionExistente);
        }

        // Si las coordenadas son iguales pero la dirección es diferente, actualizar dirección
        else if (direccionExistente.coordenadas === coordenadasNormalizadas &&
          direccionExistente.direccion.toLowerCase().trim() !== direccionNormalizada) {
          direccionExistente.direccion = direccion;
          direccionExistente.fechaActualizacion = new Date();
          actualizado = true;
          console.log('📍 Dirección actualizada en coordenadas existentes:', direccionExistente);
        }

        // Si el sector es diferente, actualizar sector
        if (direccionExistente.sector !== sectorFinal) {
          direccionExistente.sector = sectorFinal;
          direccionExistente.fechaActualizacion = new Date();
          actualizado = true;
          console.log('📍 Sector actualizado en dirección existente:', direccionExistente);
        }

        // Si tanto dirección como coordenadas son iguales, no hacer nada
        else {
          console.log('📍 Dirección y coordenadas ya existen exactamente iguales');
        }

        // Solo actualizar en Firestore si hubo cambios
        if (actualizado) {
          await updateDoc(clienteRef, {
            direcciones: direccionesActuales
          });
          console.log('✅ Historial actualizado exitosamente para el cliente:', telefono);
        }

        return true;
      } else {
        // Agregar nueva dirección al historial solo si es realmente diferente
        const nuevaDireccionData = {
          direccion: direccion,
          coordenadas: coordenadasNormalizadas,
          sector: sectorFinal,
          fechaRegistro: new Date(),
          activa: true,
          modoRegistro: modoRegistro
        };

        direccionesActuales.push(nuevaDireccionData);
        console.log('📍 Nueva dirección agregada al historial:', nuevaDireccionData);

        // Actualizar el documento del cliente
        await updateDoc(clienteRef, {
          direcciones: direccionesActuales
        });

        console.log('✅ Historial actualizado exitosamente para el cliente:', telefono);
        return true;
      }

    } catch (error) {
      console.error('💥 Error al guardar en historial del cliente:', error);
      return false;
    }
  };

  // Función para mostrar direcciones guardadas del cliente
  const mostrarDireccionesGuardadas = async (telefonoParam) => {
    try {
      if (!telefonoParam) return;

      console.log('🔍 Buscando direcciones guardadas para:', telefonoParam);

      // Determinar la colección y el ID según la longitud del teléfono
      let coleccionNombre = 'clientestelefonos';
      let telefonoId = telefonoParam;

      if (telefonoParam.length > 7) {
        coleccionNombre = 'clientestelefonos1';
        if (telefonoParam.startsWith('593')) telefonoId = telefonoParam;
        else if (telefonoParam.length === 10 && telefonoParam.startsWith('0')) telefonoId = '593' + telefonoParam.substring(1);
        else if (telefonoParam.length === 9) telefonoId = '593' + telefonoParam;
      }

      let clienteRef = doc(db, coleccionNombre, telefonoId);
      let clienteSnapshot = await getDoc(clienteRef);

      if (clienteSnapshot && clienteSnapshot.exists()) {
        const data = clienteSnapshot.data();
        if (data.direcciones) {
          setDireccionesGuardadas(data.direcciones);
        }
      } else {
        setDireccionesGuardadas([]);
        console.log('⚠️ No se encontraron direcciones para el cliente:', telefonoId);
      }
    } catch (error) {
      console.error('Error al mostrar direcciones guardadas:', error);
    }
  };


  // Modal de autenticación de operador
  if (mostrarModalOperador) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center'
        }}>
          <h2 style={{
            color: '#1f2937',
            marginBottom: '20px',
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            🔄 Cambiar Operador
          </h2>

          <p style={{
            color: '#6b7280',
            marginBottom: '25px',
            fontSize: '14px'
          }}>
            Ingrese el código del operador para cambiar de usuario
          </p>

          <input
            type="password"
            value={codigoOperador}
            onChange={(e) => setCodigoOperador(e.target.value)}
            placeholder="Código de 4 dígitos"
            maxLength="4"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '16px',
              marginBottom: '15px',
              textAlign: 'center',
              letterSpacing: '8px',
              fontFamily: 'monospace'
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                autenticarOperador();
              }
            }}
            autoFocus
          />

          {errorAutenticacion && (
            <div style={{
              color: '#dc2626',
              fontSize: '14px',
              marginBottom: '15px',
              padding: '10px',
              backgroundColor: '#fef2f2',
              borderRadius: '6px',
              border: '1px solid #fecaca'
            }}>
              {errorAutenticacion}
            </div>
          )}

          <button
            onClick={autenticarOperador}
            disabled={cargandoAutenticacion}
            style={{
              width: '100%',
              padding: '12px 20px',
              backgroundColor: cargandoAutenticacion ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: cargandoAutenticacion ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!cargandoAutenticacion) {
                e.target.style.backgroundColor = '#2563eb';
              }
            }}
            onMouseLeave={(e) => {
              if (!cargandoAutenticacion) {
                e.target.style.backgroundColor = '#3b82f6';
              }
            }}
          >
            {cargandoAutenticacion ? '🔐 Autenticando...' : '🔐 Ingresar'}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div style={{
      background: '#f3f4f6',
      display: 'flex',
      flexDirection: 'row',
      height: '100%',
      width: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      {/* PANEL IZQUIERDO: Formulario y Tablas */}
      <div style={{
        flex: mapaDashboardVisible ? `0 0 ${100 - parseInt(mapaDashboardAncho)}%` : '1',
        minWidth: mapaDashboardVisible ? '400px' : '100%',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        borderRight: mapaDashboardVisible ? '1px solid #e5e7eb' : 'none',
        background: '#fff',
        transition: 'all 0.3s ease',
        position: 'relative',
        zIndex: 1
      }}>
        {/* SECCIÓN SUPERIOR: Formulario y Nuevo Apartado */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          flexShrink: 0,
          background: '#fff',
          borderBottom: '1px solid #d1d5db'
        }}>
          {/* Lado Izquierdo: Formulario */}
          <div style={{ flex: 2, padding: '25px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <form onSubmit={handleSubmit}>
              {/* Fila 1: Teléfono, Empresa y Modo */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '15px'
              }}>
                <div style={{ flex: 1 }}>
                  <input
                    type="text"
                    placeholder="Teléfono"
                    value={telefono}
                    onChange={handleTelefonoChange}
                    onKeyDown={handleTelefonoKeyDown}
                    style={{
                      padding: '12px 16px',
                      border: `2px solid ${buscandoUsuario ? '#f59e0b' :
                        usuarioEncontrado ? '#10b981' :
                          telefono.length >= 7 && !usuarioEncontrado ? '#ef4444' : '#d1d5db'
                        }`,
                      borderRadius: 8,
                      fontSize: '18px',
                      fontWeight: 'bold',
                      width: '100%',
                      backgroundColor: buscandoUsuario ? '#fef3c7' :
                        usuarioEncontrado ? '#d1fae5' :
                          telefono.length >= 7 && !usuarioEncontrado ? '#fee2e2' : 'white',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Contador de dígitos - Badge Lateral */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fbbf24',
                  color: '#000',
                  fontWeight: '900',
                  borderRadius: '8px',
                  minWidth: '55px',
                  fontSize: '20px',
                  border: '2px solid #d97706',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {telefono.length}
                </div>

                <div style={{ flex: 1 }}>
                  <select
                    value={tipoEmpresa}
                    onChange={(e) => setTipoEmpresa(e.target.value)}
                    style={{
                      padding: '12px 16px',
                      border: '2px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: '18px',
                      fontWeight: 'bold',
                      width: '100%',
                      backgroundColor: 'white',
                      height: '100%',
                      cursor: 'pointer'
                    }}
                  >
                    {EMPRESAS_MENU_PRINCIPAL.map(emp => (
                      <option key={emp} value={emp}>
                        {emp === 'Efectivo' ? '💵' : '🏢'} {emp}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ flex: 1 }}>
                  <select
                    value={modoSeleccionUI}
                    onChange={(e) => {
                      const nuevoModo = e.target.value;
                      setModoSeleccionUI(nuevoModo);
                      const textoCompleto = nuevoModo === 'Automática' ? 'Selección Automática' : 'Selección Manual';
                      setTextoSeleccion(textoCompleto);
                      const nuevoEstado = nuevoModo === 'Automática';
                      actualizarConfiguracion(nuevoEstado);
                    }}
                    style={{
                      padding: '12px 16px',
                      border: '2px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: '18px',
                      fontWeight: 'bold',
                      width: '100%',
                      backgroundColor: 'white',
                      height: '100%',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="Manual">🔧 Manual</option>
                    <option value="Automática">⚡ Automática</option>
                  </select>
                </div>
              </div>

              {/* Fila 2: Nombre y Nota (observación) */}
              <div style={{ marginBottom: '15px', display: 'flex', gap: '12px', alignItems: 'center', minWidth: 0 }}>
                <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Nombre"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    autoComplete="off"
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (telefono && nombre.trim()) await actualizarNombreCliente();
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      border: '2px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: '18px',
                      fontWeight: 'bold',
                      width: '100%',
                      minWidth: 0,
                      outline: 'none',
                      paddingRight: (nombre && !mapaVisible) ? '45px' : '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {nombre && !mapaVisible && (
                    <button
                      type="button"
                      onClick={actualizarNombreCliente}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        background: 'none',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                        color: '#3b82f6'
                      }}
                      title="Guardar nombre y nota del cliente"
                    >
                      ✏️
                    </button>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Nota / observación"
                    value={notaCliente}
                    onChange={(e) => setNotaCliente(e.target.value)}
                    autoComplete="off"
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (telefono && nombre.trim()) await actualizarNombreCliente();
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      border: '2px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: '18px',
                      fontWeight: 'bold',
                      width: '100%',
                      minWidth: 0,
                      outline: 'none',
                      paddingRight: (notaCliente && !mapaVisible) ? '95px' : '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {!mapaVisible && (
                    <div style={{ position: 'absolute', right: '10px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (telefono) {
                            setNotaCliente(prev => prev ? `${prev} Tel:${telefono}` : `Tel:${telefono}`);
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '16px',
                          cursor: telefono ? 'pointer' : 'default',
                          color: telefono ? '#3b82f6' : '#ccc',
                          padding: '2px'
                        }}
                        title="Agregar número a observación"
                        disabled={!telefono}
                      >
                        📞
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (tiempo) {
                            setNotaCliente(prev => prev ? `${prev} T:${tiempo}min` : `T:${tiempo}min`);
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '16px',
                          cursor: tiempo ? 'pointer' : 'default',
                          color: tiempo ? '#3b82f6' : '#ccc',
                          padding: '2px'
                        }}
                        title="Agregar tiempo a observación"
                        disabled={!tiempo}
                      >
                        ⏱️
                      </button>
                      {notaCliente && (
                        <button
                          type="button"
                          onClick={actualizarNombreCliente}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '20px',
                            cursor: 'pointer',
                            color: '#3b82f6',
                            padding: '2px'
                          }}
                          title="Guardar nombre y nota del cliente"
                        >
                          ✏️
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Fila 3: Dirección, Tiempo y Unidad */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '15px',
                alignItems: 'center',
                minWidth: 0
              }}>
                <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Dirección"
                    value={direccion}
                    onChange={(e) => setDireccion(e.target.value)}
                    autoComplete="off"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleInsertarViajePendiente();
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      border: '2px solid #d1d5db',
                      borderRadius: 8,
                      fontSize: '18px',
                      fontWeight: 'bold',
                      width: '100%',
                      minWidth: 0,
                      outline: 'none',
                      paddingRight: (direccion && !mapaVisible) ? '45px' : '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {direccion && !mapaVisible && (
                    <button
                      type="button"
                      onClick={actualizarDireccionCliente}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        background: 'none',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                        color: '#3b82f6'
                      }}
                      title="Actualizar dirección del cliente"
                    >
                      ✏️
                    </button>
                  )}
                </div>

                <input
                  ref={tiempoInputRef}
                  type="text"
                  placeholder="Tiempo"
                  value={tiempo}
                  onChange={(e) => {
                    const valor = e.target.value;
                    if (/^\d{0,2}$/.test(valor)) {
                      setTiempo(valor);
                      if (valor.length === 2) {
                        setTimeout(() => {
                          if (unidadInputRef.current) unidadInputRef.current.focus();
                        }, 50);
                      }
                    }
                  }}
                  maxLength="2"
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #d1d5db',
                    borderRadius: 8,
                    fontSize: '18px',
                    fontWeight: 'bold',
                    width: '120px',
                    textAlign: 'center',
                    outline: 'none'
                  }}
                />
                <input
                  ref={unidadInputRef}
                  type="text"
                  placeholder="Unidad"
                  value={unidad}
                  onChange={(e) => setUnidad(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      if (tiempo.trim() && unidad.trim()) {
                        handleInsertarViaje();
                      } else {
                        handleInsertarViajePendiente();
                      }
                    }
                  }}
                  maxLength="4"
                  style={{
                    padding: '12px 16px',
                    border: '2px solid #d1d5db',
                    borderRadius: 8,
                    fontSize: '18px',
                    fontWeight: 'bold',
                    width: '120px',
                    textAlign: 'center',
                    outline: 'none'
                  }}
                />
                {modoSeleccion === 'aplicacion' && (
                    <button
                      type="button"
                      onClick={handleSolicitarAplicacion}
                      disabled={!coordenadas.trim()}
                      style={{
                        padding: '12px 20px',
                        background: !coordenadas.trim() ? '#9ca3af' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: '18px',
                        fontWeight: 'bold',
                        cursor: !coordenadas.trim() ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      🚀 App
                    </button>
                )}
              </div>

              {/* El historial se movió debajo del mapa */}
            </form>

            {/* 1. MAPA (ARRIBA DEL HISTORIAL) */}
            {mapaVisible && (
              <div style={{
                marginTop: '20px',
                border: '2px solid #e5e7eb',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                height: '800px',
                position: 'relative'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#8b5cf6',
                  padding: '8px 15px',
                  color: 'white'
                }}>
                  <span style={{ fontWeight: 'bold', fontSize: '14px' }}>📍 Mapa de Ubicación</span>
                  <button
                    type="button"
                    onClick={() => setMapaVisible(false)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ height: 'calc(100% - 35px)', position: 'relative' }}>
                  <MapaSelector
                    mapaVisible={true}
                    setMapaVisible={setMapaVisible}
                    onCoordinatesSelect={handleCoordinatesSelect}
                    onAddressSelect={handleAddressSelect}
                    onSolicitarApp={handleSolicitarAplicacion}
                    onSaveLocation={handleSaveLocationFromMap}
                    coordenadas={coordenadas}
                    direccionFormulario={direccion}
                  />
                </div>
              </div>
            )}

            {/* 2. HISTORIAL DE DIRECCIONES (ABAJO DEL MAPA) */}
            {direccionesGuardadas.length > 0 && (
              <div style={{
                marginTop: 20,
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <div style={{
                  padding: '12px 15px',
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ color: '#ef4444', fontSize: '16px' }}>📍</span>
                  <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#1e293b' }}>
                    Direcciones guardadas ({direccionesGuardadas.length}):
                  </span>
                </div>
                <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                  {direccionesGuardadas.map((dir, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        setDireccion(dir.direccion);
                        setCoordenadas(dir.coordenadas || '');
                      }}
                      style={{
                        padding: '12px 15px',
                        borderBottom: index === direccionesGuardadas.length - 1 ? 'none' : '1px solid #f1f5f9',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '15px'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1e293b' }}>
                          {dir.direccion}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 3 }}>
                          {dir.sector && (
                            <div style={{ fontSize: 11, fontWeight: 'bold', color: '#6366f1', background: '#e0e7ff', padding: '1px 6px', borderRadius: '4px' }}>
                              🏘️ {dir.sector}
                            </div>
                          )}
                          {dir.coordenadas && (
                            <div style={{ fontSize: 11, color: '#ef4444' }}>
                              📍 {dir.coordenadas}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Modal moderno */}
          {
            modal.open && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100001 // por encima del modal de reserva (9999) para que los mensajes siempre se vean
              }}>
                <div style={{
                  background: '#fff',
                  borderRadius: 18,
                  boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
                  padding: '40px 32px 32px 32px',
                  minWidth: 340,
                  maxWidth: '90vw',
                  textAlign: 'center',
                  position: 'relative',
                  fontFamily: 'inherit'
                }}>
                  <div style={{ fontSize: 54, marginBottom: 12 }}>
                    {modal.success ? '✅' : '❌'}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>
                    {modal.success ? '¡Éxito!' : 'Error'}
                  </div>
                  <div style={{ fontSize: 18, color: '#444', marginBottom: 28 }}>
                    {modal.message}
                  </div>
                  <button
                    onClick={() => {
                      setModal({ ...modal, open: false });
                      // Solo limpiar el formulario si no es un mensaje de registro de cliente
                      // Y NO limpiar si es un mensaje de unidad no encontrada
                      if (!modal.message.includes('registrado') &&
                        !modal.message.includes('cliente') &&
                        !modal.message.includes('unidad') &&
                        !modal.message.includes('conductor')) {
                        limpiarFormulario();
                      }
                    }}
                    style={{
                      background: modal.success ? '#10b981' : '#ef4444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 20,
                      fontWeight: 700,
                      padding: '14px 48px',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      transition: 'background 0.2s',
                      outline: 'none',
                    }}
                    autoFocus
                  >
                    Aceptar
                  </button>
                </div>
              </div>
            )
          }

          {modalObservaciones.open && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                background: '#fff',
                borderRadius: 14,
                boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
                padding: 20,
                width: '100%',
                maxWidth: 520
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>📝 Observaciones</div>
                  <button
                    type="button"
                    onClick={cerrarModalObservaciones}
                    style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}
                    title="Cerrar"
                  >
                    ✕
                  </button>
                </div>

                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                  <div><b>Cliente:</b> {modalObservaciones.pedido?.nombreCliente || modalObservaciones.pedido?.codigo || '-'}</div>
                  <div><b>Teléfono:</b> {modalObservaciones.pedido?.telefono || modalObservaciones.pedido?.telefonoCompleto || '-'}</div>
                </div>

                <input
                  type="text"
                  value={modalObservaciones.texto}
                  onChange={(e) => setModalObservaciones(prev => ({ ...prev, texto: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      confirmarObservaciones();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cerrarModalObservaciones();
                    }
                  }}
                  placeholder="Escribe observaciones y presiona Enter para guardar"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: '2px solid #e5e7eb',
                    outline: 'none',
                    fontSize: 14
                  }}
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
                  <button
                    type="button"
                    onClick={cerrarModalObservaciones}
                    style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', fontWeight: 700 }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={confirmarObservaciones}
                    disabled={Boolean(guardandoObservaciones[modalObservaciones.pedido?.id])}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: 'none',
                      background: Boolean(guardandoObservaciones[modalObservaciones.pedido?.id]) ? '#9ca3af' : '#3b82f6',
                      color: 'white',
                      cursor: Boolean(guardandoObservaciones[modalObservaciones.pedido?.id]) ? 'not-allowed' : 'pointer',
                      fontWeight: 800
                    }}
                  >
                    {Boolean(guardandoObservaciones[modalObservaciones.pedido?.id]) ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* SECCIÓN INFERIOR: Tablas */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          background: '#f3f4f6'
        }}>
          <div style={{ padding: '0 20px 20px 20px' }}>
            {/* Contenedor flex para las dos tablas */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              flex: 1,
              minHeight: 0
            }}>
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingBottom: 20, display: 'flex', flexDirection: 'column' }}>

                {/* Tabla de Pedidos Disponibles (Tiempo Real): pedidosDisponibles + pedidosDisponibles1 */}
                {conteoPedidosDisponiblesMemo.total > 0 && (
                  <div style={{
                    marginTop: 30,
                    background: '#fff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    overflow: 'visible'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      padding: '15px 20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h3 style={{
                        margin: 0,
                        fontSize: 20,
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10
                      }}>
                          🚗 Pedidos Disponibles
                        <span style={{
                          background: 'rgba(255,255,255,0.2)',
                          padding: '4px 12px',
                          borderRadius: 20,
                          fontSize: 14,
                          fontWeight: 'normal'
                        }}>
                          {conteoPedidosDisponiblesMemo.total} (PD: {conteoPedidosDisponiblesMemo.pd} · PD1: {conteoPedidosDisponiblesMemo.pd1} · PS: {conteoPedidosDisponiblesMemo.ps})
                        </span>
                      </h3>

                      <button
                        onClick={() => {
                          console.log('🔄 Botón actualizar presionado');
                          cargarViajesAsignados();
                        }}
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          color: 'white',
                          padding: '8px 16px',
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8
                        }}
                      >
                        🔄 Actualizar
                      </button>
                    </div>

                    {cargandoViajes ? (
                      <div style={{
                        padding: 40,
                        textAlign: 'center',
                        color: '#666'
                      }}>
                        <div style={{ fontSize: 24, marginBottom: 10 }}>⏳</div>
                        <div>Cargando pedidos disponibles...</div>
                      </div>
                    ) : pedidosDisponiblesTablaMemo.length === 0 ? (
                      <div style={{
                        padding: 40,
                        textAlign: 'center',
                        color: '#666'
                      }}>
                        <div style={{ fontSize: 48, marginBottom: 10 }}>📋</div>
                        <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 5 }}>
                          No hay pedidos disponibles
                        </div>
                        <div style={{ fontSize: 14, marginBottom: 10 }}>
                          Los pedidos aparecerán aquí cuando se registren desde el formulario
                        </div>
                        <div style={{ fontSize: 12, color: '#999', marginBottom: 10 }}>
                          Colecciones: pedidosDisponibles, pedidosDisponibles1
                        </div>
                        <button
                          onClick={() => {
                            console.log('🔍 Verificando conexión a Firestore...');
                            cargarViajesAsignados();
                          }}
                          style={{
                            background: '#667eea',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 12
                          }}
                        >
                          🔍 Verificar Conexión
                        </button>
                      </div>
                    ) : (
                      <div style={{
                        maxWidth: '100%'
                      }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '14px',
                          tableLayout: 'fixed'
                        }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '80px'
                              }}>
                                🕐 Hora
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'left',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '120px'
                              }}>
                                📞 Teléfono
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'left',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '150px'
                              }}>
                                👤 Cliente
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'left',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '150px'
                              }}>
                                🏘️ Sector
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'left',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '350px',
                                minWidth: '300px'
                              }}>
                                📍 Dirección
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '80px'
                              }}>
                                ⏱️ Tiempo
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '80px'
                              }}>
                                🚕 Unidad
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '100px'
                              }}>
                                ⚙️ Acción
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pedidosDisponiblesTablaMemo.map((viaje, index) => {
                              // Calcular bloqueo (solo PD: 59 segundos)
                              const BLOQUEO_SEGUNDOS = 59;
                              let fechaCreacion;
                              if (viaje.fecha && viaje.fecha.toDate) fechaCreacion = viaje.fecha.toDate();
                              else if (viaje.fecha && viaje.fecha.seconds) fechaCreacion = new Date(viaje.fecha.seconds * 1000);
                              else if (viaje.fecha) fechaCreacion = new Date(viaje.fecha);
                              else fechaCreacion = new Date();

                              const diffSegundos = Math.floor((currentTime - fechaCreacion) / 1000);
                              const origenColeccion = (viaje.origenColeccion || '').toString();
                              const esPD = origenColeccion === 'pedidosDisponibles';
                              const esPS = origenColeccion === 'pedidoSolicitado';
                              const transcurridos = Math.max(0, Number.isFinite(diffSegundos) ? diffSegundos : 0);
                              const bloqueado = (esPD || esPS) && transcurridos < BLOQUEO_SEGUNDOS;
                              const segundosRestantes = Math.max(0, BLOQUEO_SEGUNDOS - transcurridos);
                              const esLlamada = ((viaje.tipoPedido || '').toString().toLowerCase() === 'llamada');
                              const bgLlamada = '#dcfce7';
                              const bgLlamadaHover = '#bbf7d0';
                              const bgPD = '#fef9c3';
                              const bgPDHover = '#fef08a';
                              const bgNormal = viaje.origenPedido === 'app' ? '#e0e7ff' : (index % 2 === 0 ? '#fff' : '#fafbff');
                              const bgNormalHover = viaje.origenPedido === 'app' ? '#c7d2fe' : '#fef2f2';
                              return (
                                <tr
                                  key={viaje.id}
                                  style={{
                                    borderBottom: '1px solid #f1f5f9',
                                    background: esPD ? bgPD : (esLlamada ? bgLlamada : bgNormal),
                                    transition: 'background 0.2s ease',
                                    position: 'relative',
                                    zIndex: mostrandoDropdownDireccion[viaje.id] ? 100000 : 'auto'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = esPD ? bgPDHover : (esLlamada ? bgLlamadaHover : bgNormalHover);
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = esPD ? bgPD : (esLlamada ? bgLlamada : bgNormal);
                                  }}
                                >
                                  <td style={{
                                    padding: '4px 8px',
                                    textAlign: 'center',
                                    color: '#6b7280',
                                    fontSize: 12,
                                    fontWeight: 'bold',
                                    width: '80px'
                                  }}>
                                    {(() => {
                                      const fechaVal = viaje.fecha;
                                      if (!fechaVal) return '-';
                                      let fechaObj = null;
                                      if (fechaVal?.toDate) fechaObj = fechaVal.toDate();
                                      else if (fechaVal?.seconds) fechaObj = new Date(fechaVal.seconds * 1000);
                                      else if (fechaVal instanceof Date) fechaObj = fechaVal;
                                      else if (typeof fechaVal === 'string') {
                                        const s = fechaVal.trim();
                                        // "24/2/2026, 11:20:34 p. m."
                                        const parts = s.split(',');
                                        if (parts.length >= 2 && parts[0].includes('/')) {
                                          const datePart = parts[0].trim();
                                          const timePart = parts.slice(1).join(',').trim();
                                          const [dd, mm, yy] = datePart.split('/').map(n => parseInt(n, 10));
                                          const m = timePart.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(a\.?\s*m\.?|p\.?\s*m\.?)/i);
                                          if (m && dd && mm && yy) {
                                            let h = parseInt(m[1], 10);
                                            const mi = parseInt(m[2], 10);
                                            const se = parseInt(m[3] || '0', 10);
                                            const mer = (m[4] || '').toLowerCase();
                                            if (mer.includes('p') && h < 12) h += 12;
                                            if (mer.includes('a') && h === 12) h = 0;
                                            fechaObj = new Date(yy, mm - 1, dd, h, mi, se);
                                          }
                                        }
                                        if (!fechaObj) {
                                          const d2 = new Date(s);
                                          if (!isNaN(d2.getTime())) fechaObj = d2;
                                        }
                                      } else {
                                        const d2 = new Date(fechaVal);
                                        if (!isNaN(d2.getTime())) fechaObj = d2;
                                      }
                                      if (!fechaObj || isNaN(fechaObj.getTime())) return '-';
                                      return fechaObj.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
                                    })()}
                                  </td>
                                  <td style={{
                                    padding: '4px 8px',
                                    fontWeight: 'bold',
                                    color: '#1f2937',
                                    width: '120px'
                                  }}>
                                    {viaje.telefono || viaje.telefonoCompleto || '-'}
                                  </td>
                                  <td style={{
                                    padding: '4px 8px',
                                    color: '#374151',
                                    width: '150px'
                                  }}>
                                    {viaje.nombreCliente || viaje.codigo || '-'}
                                  </td>
                                  <td style={{
                                    padding: '4px 8px',
                                    color: '#374151',
                                    width: '150px'
                                  }}>
                                    {viaje.destino || '-'}
                                  </td>
                                  <td style={{
                                    padding: '12px 8px',
                                    color: '#374151',
                                    width: '350px',
                                    minWidth: '300px',
                                    wordBreak: 'break-word'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      gap: '4px',
                                      alignItems: 'center'
                                    }}>
                                      {agregandoDireccionViaje[viaje.id] ? (
                                        <>
                                          <input
                                            type="text"
                                            placeholder="Ingrese nueva dirección"
                                            value={nuevaDireccionViajeTexto[viaje.id] || ''}
                                            disabled={bloqueado}
                                            onChange={(e) => {
                                              setNuevaDireccionViajeTexto(prev => ({
                                                ...prev,
                                                [viaje.id]: e.target.value
                                              }));
                                            }}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                                const nuevaDirTexto = nuevaDireccionViajeTexto[viaje.id] || '';
                                                if (nuevaDirTexto.trim()) {
                                                  const nuevaDir = {
                                                    direccion: nuevaDirTexto.trim(),
                                                    coordenadas: viaje.coordenadas || '',
                                                    fechaRegistro: new Date(),
                                                    activa: true,
                                                    modoRegistro: 'manual'
                                                  };
                                                  const direccionesActuales = direccionesPorViaje[viaje.id] || [];
                                                  const nuevasDirecciones = [...direccionesActuales, nuevaDir];
                                                  setDireccionesPorViaje(prev => ({
                                                    ...prev,
                                                    [viaje.id]: nuevasDirecciones
                                                  }));

                                                  // Actualizar el viaje con la nueva dirección
                                                  const telefonoViaje = viaje.telefono || viaje.telefonoCompleto;
                                                  const nombreCliente = viaje.nombreCliente || viaje.codigo || '';
                                                  if (telefonoViaje) {
                                                    buscarCliente(telefonoViaje).then(async (resultadoBusqueda) => {
                                                      if (resultadoBusqueda && resultadoBusqueda.encontrado) {
                                                        await agregarNuevaDireccion(telefonoViaje, nuevaDir, resultadoBusqueda.tipoCliente);
                                                      } else if (telefonoViaje.length > 7) {
                                                        await guardarClienteNuevoDesdePedido(telefonoViaje, nombreCliente, nuevaDirTexto.trim());
                                                      }
                                                    });
                                                  }

                                                  // Actualizar el documento del viaje
                                                  const viajeRef = doc(db, 'pedidosDisponibles', viaje.id);
                                                  const viajeRef1 = doc(db, 'pedidosDisponibles1', viaje.id);
                                                  const viajeRefPS = doc(db, 'pedidoSolicitado', viaje.id);
                                                  Promise.all([
                                                    getDoc(viajeRef).then(snap => snap.exists() ? updateDoc(viajeRef, { direccion: nuevaDirTexto.trim() }) : null),
                                                    getDoc(viajeRef1).then(snap => snap.exists() ? updateDoc(viajeRef1, { direccion: nuevaDirTexto.trim() }) : null),
                                                    getDoc(viajeRefPS).then(snap => snap.exists() ? updateDoc(viajeRefPS, { direccion: nuevaDirTexto.trim() }) : null)
                                                  ]).catch(err => console.error('Error al actualizar dirección:', err));

                                                  setNuevaDireccionViajeTexto(prev => {
                                                    const nuevo = { ...prev };
                                                    delete nuevo[viaje.id];
                                                    return nuevo;
                                                  });
                                                  setAgregandoDireccionViaje(prev => {
                                                    const nuevo = { ...prev };
                                                    delete nuevo[viaje.id];
                                                    return nuevo;
                                                  });
                                                }
                                              } else if (e.key === 'Escape') {
                                                setAgregandoDireccionViaje(prev => {
                                                  const nuevo = { ...prev };
                                                  delete nuevo[viaje.id];
                                                  return nuevo;
                                                });
                                                setNuevaDireccionViajeTexto(prev => {
                                                  const nuevo = { ...prev };
                                                  delete nuevo[viaje.id];
                                                  return nuevo;
                                                });
                                              }
                                            }}
                                            style={{
                                              padding: '4px 8px',
                                              border: '1px solid #10b981',
                                              borderRadius: '4px',
                                              fontSize: '12px',
                                              flex: 1,
                                              minWidth: '150px'
                                            }}
                                            autoFocus
                                          />
                                          <button
                                            type="button"
                                            disabled={bloqueado}
                                            onClick={() => {
                                              const nuevaDirTexto = nuevaDireccionViajeTexto[viaje.id] || '';
                                              if (nuevaDirTexto.trim()) {
                                                const nuevaDir = {
                                                  direccion: nuevaDirTexto.trim(),
                                                  coordenadas: viaje.coordenadas || '',
                                                  fechaRegistro: new Date(),
                                                  activa: true,
                                                  modoRegistro: 'manual'
                                                };
                                                const direccionesActuales = direccionesPorViaje[viaje.id] || [];
                                                const nuevasDirecciones = [...direccionesActuales, nuevaDir];
                                                setDireccionesPorViaje(prev => ({
                                                  ...prev,
                                                  [viaje.id]: nuevasDirecciones
                                                }));

                                                const telefonoViaje = viaje.telefono || viaje.telefonoCompleto;
                                                const nombreCliente = viaje.nombreCliente || viaje.codigo || '';
                                                if (telefonoViaje) {
                                                  buscarCliente(telefonoViaje).then(async (resultadoBusqueda) => {
                                                    if (resultadoBusqueda && resultadoBusqueda.encontrado) {
                                                      await agregarNuevaDireccion(telefonoViaje, nuevaDir, resultadoBusqueda.tipoCliente);
                                                    } else {
                                                      await guardarClienteNuevoDesdePedido(telefonoViaje, nombreCliente, nuevaDirTexto.trim());
                                                    }
                                                  });
                                                }

                                                const viajeRef = doc(db, 'pedidosDisponibles', viaje.id);
                                                const viajeRef1 = doc(db, 'pedidosDisponibles1', viaje.id);
                                                const viajeRefPS = doc(db, 'pedidoSolicitado', viaje.id);
                                                Promise.all([
                                                  getDoc(viajeRef).then(snap => snap.exists() ? updateDoc(viajeRef, { direccion: nuevaDirTexto.trim() }) : null),
                                                  getDoc(viajeRef1).then(snap => snap.exists() ? updateDoc(viajeRef1, { direccion: nuevaDirTexto.trim() }) : null),
                                                  getDoc(viajeRefPS).then(snap => snap.exists() ? updateDoc(viajeRefPS, { direccion: nuevaDirTexto.trim() }) : null)
                                                ]).catch(err => console.error('Error al actualizar dirección:', err));

                                                setNuevaDireccionViajeTexto(prev => {
                                                  const nuevo = { ...prev };
                                                  delete nuevo[viaje.id];
                                                  return nuevo;
                                                });
                                                setAgregandoDireccionViaje(prev => {
                                                  const nuevo = { ...prev };
                                                  delete nuevo[viaje.id];
                                                  return nuevo;
                                                });
                                              }
                                            }}
                                            style={{
                                              padding: '4px 8px',
                                              background: bloqueado ? '#9ca3af' : '#10b981',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              fontSize: '12px',
                                              cursor: bloqueado ? 'not-allowed' : 'pointer',
                                              opacity: bloqueado ? 0.7 : 1
                                            }}
                                          >
                                            ✓
                                          </button>
                                          <button
                                            type="button"
                                            disabled={bloqueado}
                                            onClick={() => {
                                              setAgregandoDireccionViaje(prev => {
                                                const nuevo = { ...prev };
                                                delete nuevo[viaje.id];
                                                return nuevo;
                                              });
                                              setNuevaDireccionViajeTexto(prev => {
                                                const nuevo = { ...prev };
                                                delete nuevo[viaje.id];
                                                return nuevo;
                                              });
                                            }}
                                            style={{
                                              padding: '4px 8px',
                                              background: bloqueado ? '#9ca3af' : '#ef4444',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              fontSize: '12px',
                                              cursor: bloqueado ? 'not-allowed' : 'pointer',
                                              opacity: bloqueado ? 0.7 : 1
                                            }}
                                          >
                                            ✕
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <div style={{ position: 'relative', flex: 1, minWidth: '150px', zIndex: mostrandoDropdownDireccion[viaje.id] ? 100000 : 'auto' }}>
                                            <input
                                              type="text"
                                              value={viaje.direccion || ''}
                                              disabled={bloqueado}
                                              onChange={(e) => {
                                                const nuevaDireccion = e.target.value;
                                                const viajeRef = doc(db, 'pedidosDisponibles', viaje.id);
                                                const viajeRef1 = doc(db, 'pedidosDisponibles1', viaje.id);
                                                const viajeRefPS = doc(db, 'pedidoSolicitado', viaje.id);
                                                Promise.all([
                                                  getDoc(viajeRef).then(snap => snap.exists() ? updateDoc(viajeRef, { direccion: nuevaDireccion }) : null),
                                                  getDoc(viajeRef1).then(snap => snap.exists() ? updateDoc(viajeRef1, { direccion: nuevaDireccion }) : null),
                                                  getDoc(viajeRefPS).then(snap => snap.exists() ? updateDoc(viajeRefPS, { direccion: nuevaDireccion }) : null)
                                                ]).catch(err => console.error('Error al actualizar dirección:', err));
                                              }}
                                              onClick={async () => {
                                                // Cargar direcciones del cliente si no están cargadas
                                                if (!direccionesPorViaje[viaje.id]) {
                                                  let direccionesCombinadas = [];

                                                  // 0. Si el documento ya trae "direcciones", usar ese historial primero (PD1)
                                                  if (viaje.direcciones && Array.isArray(viaje.direcciones) && viaje.direcciones.length > 0) {
                                                    direccionesCombinadas = viaje.direcciones
                                                      .map(d => ({
                                                        direccion: d?.direccion || '',
                                                        coordenadas: d?.coordenadas || '',
                                                        activa: d?.activa !== false
                                                      }))
                                                      .filter(d => (d.direccion || '').toString().trim());
                                                  }

                                                  // 1. Obtener direcciones del campo direccionesCliente del documento del viaje
                                                  if (direccionesCombinadas.length === 0 && viaje.direccionesCliente && Array.isArray(viaje.direccionesCliente)) {
                                                    direccionesCombinadas = [...viaje.direccionesCliente];
                                                  }

                                                  // 2. Obtener direcciones del cliente desde la base de datos
                                                  const telefonoViaje = viaje.telefono || viaje.telefonoCompleto;
                                                  if (direccionesCombinadas.length === 0 && telefonoViaje) {
                                                    const resultadoBusqueda = await buscarCliente(telefonoViaje);
                                                    if (resultadoBusqueda && resultadoBusqueda.encontrado && resultadoBusqueda.datos.direcciones) {
                                                      // Combinar direcciones del cliente con las del documento
                                                      const direccionesCliente = resultadoBusqueda.datos.direcciones;

                                                      // Agregar direcciones del cliente que no estén ya en direccionesCombinadas
                                                      direccionesCliente.forEach(dirCliente => {
                                                        const existe = direccionesCombinadas.some(dir =>
                                                          dir.direccion === dirCliente.direccion
                                                        );
                                                        if (!existe) {
                                                          direccionesCombinadas.push(dirCliente);
                                                        }
                                                      });
                                                    }
                                                  }

                                                  // 3. Si no hay direcciones, usar la dirección actual del viaje
                                                  if (direccionesCombinadas.length === 0 && viaje.direccion) {
                                                    direccionesCombinadas = [{
                                                      direccion: viaje.direccion,
                                                      coordenadas: viaje.coordenadas || '',
                                                      activa: true
                                                    }];
                                                  }

                                                  // Guardar las direcciones combinadas
                                                  setDireccionesPorViaje(prev => ({
                                                    ...prev,
                                                    [viaje.id]: direccionesCombinadas
                                                  }));
                                                }
                                                // Mostrar el dropdown
                                                setMostrandoDropdownDireccion(prev => ({
                                                  ...prev,
                                                  [viaje.id]: true
                                                }));
                                              }}
                                              onFocus={async (e) => {
                                                e.target.style.borderColor = '#2563eb';
                                                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';

                                                // Cargar direcciones del cliente si no están cargadas
                                                if (!direccionesPorViaje[viaje.id]) {
                                                  let direccionesCombinadas = [];

                                                  // 0. Si el documento ya trae "direcciones", usar ese historial primero (PD1)
                                                  if (viaje.direcciones && Array.isArray(viaje.direcciones) && viaje.direcciones.length > 0) {
                                                    direccionesCombinadas = viaje.direcciones
                                                      .map(d => ({
                                                        direccion: d?.direccion || '',
                                                        coordenadas: d?.coordenadas || '',
                                                        activa: d?.activa !== false
                                                      }))
                                                      .filter(d => (d.direccion || '').toString().trim());
                                                  }

                                                  // 1. Obtener direcciones del campo direccionesCliente del documento del viaje
                                                  if (direccionesCombinadas.length === 0 && viaje.direccionesCliente && Array.isArray(viaje.direccionesCliente)) {
                                                    direccionesCombinadas = [...viaje.direccionesCliente];
                                                  }

                                                  // 2. Obtener direcciones del cliente desde la base de datos
                                                  const telefonoViaje = viaje.telefono || viaje.telefonoCompleto;
                                                  if (direccionesCombinadas.length === 0 && telefonoViaje) {
                                                    const resultadoBusqueda = await buscarCliente(telefonoViaje);
                                                    if (resultadoBusqueda && resultadoBusqueda.encontrado && resultadoBusqueda.datos.direcciones) {
                                                      // Combinar direcciones del cliente con las del documento
                                                      const direccionesCliente = resultadoBusqueda.datos.direcciones;

                                                      // Agregar direcciones del cliente que no estén ya en direccionesCombinadas
                                                      direccionesCliente.forEach(dirCliente => {
                                                        const existe = direccionesCombinadas.some(dir =>
                                                          dir.direccion === dirCliente.direccion
                                                        );
                                                        if (!existe) {
                                                          direccionesCombinadas.push(dirCliente);
                                                        }
                                                      });
                                                    }
                                                  }

                                                  // 3. Si no hay direcciones, usar la dirección actual del viaje
                                                  if (direccionesCombinadas.length === 0 && viaje.direccion) {
                                                    direccionesCombinadas = [{
                                                      direccion: viaje.direccion,
                                                      coordenadas: viaje.coordenadas || '',
                                                      activa: true
                                                    }];
                                                  }

                                                  // Guardar las direcciones combinadas
                                                  setDireccionesPorViaje(prev => ({
                                                    ...prev,
                                                    [viaje.id]: direccionesCombinadas
                                                  }));
                                                }
                                                // Mostrar el dropdown
                                                setMostrandoDropdownDireccion(prev => ({
                                                  ...prev,
                                                  [viaje.id]: true
                                                }));
                                              }}
                                              onBlur={(e) => {
                                                e.target.style.borderColor = '#3b82f6';
                                                e.target.style.boxShadow = 'none';

                                                // Cerrar el dropdown después de un pequeño delay para permitir clics
                                                setTimeout(() => {
                                                  setMostrandoDropdownDireccion(prev => {
                                                    const nuevo = { ...prev };
                                                    delete nuevo[viaje.id];
                                                    return nuevo;
                                                  });
                                                }, 200);
                                              }}
                                              placeholder="Seleccione o ingrese dirección"
                                              style={{
                                                padding: '6px 32px 6px 12px',
                                                border: '2px solid #3b82f6',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                width: '100%',
                                                backgroundColor: 'white',
                                                cursor: 'text',
                                                transition: 'border-color 0.2s ease'
                                              }}
                                            />
                                            <span style={{
                                              position: 'absolute',
                                              right: '10px',
                                              top: '50%',
                                              transform: 'translateY(-50%)',
                                              pointerEvents: 'none',
                                              fontSize: '14px',
                                              color: '#3b82f6',
                                              fontWeight: 'bold'
                                            }}>▼</span>
                                            {mostrandoDropdownDireccion[viaje.id] && (
                                              <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                backgroundColor: 'white',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                marginTop: '2px',
                                                maxHeight: '400px',
                                                overflowY: 'auto',
                                                overflowX: 'hidden',
                                                zIndex: 99999,
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                minWidth: '100%',
                                                width: '100%'
                                              }}
                                                onWheel={(e) => {
                                                  e.stopPropagation();
                                                }}
                                              >
                                                {direccionesPorViaje[viaje.id] && direccionesPorViaje[viaje.id].length > 0 ? (
                                                  direccionesPorViaje[viaje.id].map((dir, idx) => (
                                                    <div
                                                      key={idx}
                                                      onClick={() => {
                                                        const viajeRef = doc(db, 'pedidosDisponibles', viaje.id);
                                                        const viajeRef1 = doc(db, 'pedidosDisponibles1', viaje.id);
                                                        const viajeRefPS = doc(db, 'pedidoSolicitado', viaje.id);
                                                        Promise.all([
                                                          getDoc(viajeRef).then(snap => snap.exists() ? updateDoc(viajeRef, { direccion: dir.direccion }) : null),
                                                          getDoc(viajeRef1).then(snap => snap.exists() ? updateDoc(viajeRef1, { direccion: dir.direccion }) : null),
                                                          getDoc(viajeRefPS).then(snap => snap.exists() ? updateDoc(viajeRefPS, { direccion: dir.direccion }) : null)
                                                        ]).catch(err => console.error('Error al actualizar dirección:', err));
                                                        setMostrandoDropdownDireccion(prev => {
                                                          const nuevo = { ...prev };
                                                          delete nuevo[viaje.id];
                                                          return nuevo;
                                                        });
                                                      }}
                                                      style={{
                                                        padding: '10px 12px',
                                                        cursor: 'pointer',
                                                        backgroundColor: dir.direccion === viaje.direccion ? '#3b82f6' : 'white',
                                                        color: dir.direccion === viaje.direccion ? 'white' : '#374151',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        fontSize: '13px',
                                                        borderBottom: idx < direccionesPorViaje[viaje.id].length - 1 ? '1px solid #e5e7eb' : 'none',
                                                        transition: 'background-color 0.15s ease',
                                                        paddingBottom: idx === direccionesPorViaje[viaje.id].length - 1 ? '12px' : '10px'
                                                      }}
                                                      onMouseEnter={(e) => {
                                                        if (dir.direccion !== viaje.direccion) {
                                                          e.currentTarget.style.backgroundColor = '#3b82f6';
                                                          e.currentTarget.style.color = 'white';
                                                        }
                                                      }}
                                                      onMouseLeave={(e) => {
                                                        if (dir.direccion !== viaje.direccion) {
                                                          e.currentTarget.style.backgroundColor = 'white';
                                                          e.currentTarget.style.color = '#374151';
                                                        }
                                                      }}
                                                    >
                                                      <span style={{
                                                        fontSize: '14px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: '20px'
                                                      }}>{dir.coordenadas ? '📍' : '🏠'}</span>
                                                      <span style={{
                                                        flex: 1,
                                                        lineHeight: '1.5',
                                                        wordBreak: 'break-word'
                                                      }}>{dir.direccion}</span>
                                                      <button
                                                        type="button"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const telefonoViaje = viaje.telefono || viaje.telefonoCompleto;
                                                          if (telefonoViaje && window.confirm('¿Está seguro de que desea eliminar esta dirección?')) {
                                                            // Eliminar dirección del cliente
                                                            buscarCliente(telefonoViaje).then(resultadoBusqueda => {
                                                              if (resultadoBusqueda && resultadoBusqueda.encontrado) {
                                                                const clienteRef = doc(db, resultadoBusqueda.coleccion, resultadoBusqueda.datos.id || telefonoViaje);
                                                                getDoc(clienteRef).then(snap => {
                                                                  if (snap.exists()) {
                                                                    const clienteData = snap.data();
                                                                    const direccionesActuales = clienteData.direcciones || [];
                                                                    const direccionesFiltradas = direccionesActuales.filter(d => d.direccion !== dir.direccion);
                                                                    updateDoc(clienteRef, { direcciones: direccionesFiltradas });

                                                                    // Actualizar el estado local
                                                                    setDireccionesPorViaje(prev => ({
                                                                      ...prev,
                                                                      [viaje.id]: direccionesFiltradas
                                                                    }));
                                                                  }
                                                                });
                                                              }
                                                            });
                                                          }
                                                        }}
                                                        style={{
                                                          background: 'transparent',
                                                          border: 'none',
                                                          color: dir.direccion === viaje.direccion ? 'white' : '#ef4444',
                                                          cursor: 'pointer',
                                                          padding: '4px 8px',
                                                          fontSize: '16px',
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                          borderRadius: '4px',
                                                          transition: 'background-color 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                          e.target.style.backgroundColor = dir.direccion === viaje.direccion ? 'rgba(255,255,255,0.2)' : 'rgba(239,68,68,0.1)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                          e.target.style.backgroundColor = 'transparent';
                                                        }}
                                                        title="Eliminar dirección"
                                                      >
                                                        🗑️
                                                      </button>
                                                    </div>
                                                  ))
                                                ) : (
                                                  <div style={{
                                                    padding: '12px',
                                                    textAlign: 'center',
                                                    color: '#6b7280',
                                                    fontSize: '13px'
                                                  }}>
                                                    No hay direcciones disponibles
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                          <button
                                            type="button"
                                            disabled={bloqueado}
                                            onClick={() => {
                                              if (bloqueado) return;
                                              // Cerrar el dropdown si está abierto
                                              setMostrandoDropdownDireccion(prev => {
                                                const nuevo = { ...prev };
                                                delete nuevo[viaje.id];
                                                return nuevo;
                                              });
                                              // Activar el modo de agregar nueva dirección
                                              setAgregandoDireccionViaje(prev => ({
                                                ...prev,
                                                [viaje.id]: true
                                              }));
                                              setNuevaDireccionViajeTexto(prev => ({
                                                ...prev,
                                                [viaje.id]: ''
                                              }));
                                            }}
                                            style={{
                                              padding: '4px 8px',
                                              background: bloqueado ? '#9ca3af' : '#10b981',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              fontSize: '12px',
                                              cursor: bloqueado ? 'not-allowed' : 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              minWidth: '24px',
                                              opacity: bloqueado ? 0.7 : 1
                                            }}
                                            title="Agregar nueva dirección"
                                          >
                                            +
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                  <td style={{
                                    padding: '4px 8px',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: 'rgba(5, 150, 105, 0.7)',
                                    width: '80px'
                                  }}>
                                    <input
                                      type="text"
                                      defaultValue={viaje.tiempo || viaje.minutos || ''}
                                      placeholder={bloqueado ? `${segundosRestantes}s` : "Tiempo"}
                                      disabled={bloqueado}
                                      data-viaje-id={viaje.id}
                                      data-field="tiempo"
                                      onChange={(e) => {
                                        const raw = (e.target.value ?? '').toString();
                                        const v = raw.replace(/\D/g, '').slice(0, 2);
                                        if (raw !== v) e.target.value = v;
                                        if (v.length === 2) {
                                          setTimeout(() => {
                                            const tr = e.target.closest('tr');
                                            const unidadEl = tr?.querySelector('input[data-field="unidad"]');
                                            if (unidadEl) {
                                              unidadEl.focus();
                                              unidadEl.select?.();
                                            }
                                          }, 0);
                                        }
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '4px 8px',
                                        border: `1px solid ${bloqueado ? '#d1d5db' : '#059669'}`,
                                        borderRadius: '4px',
                                        textAlign: 'center',
                                        fontSize: '18px',
                                        backgroundColor: bloqueado ? '#f3f4f6' : 'white',
                                        cursor: bloqueado ? 'not-allowed' : 'text',
                                        opacity: bloqueado ? 0.7 : 1
                                      }}
                                    />
                                  </td>
                                  <td style={{
                                    padding: '4px 8px',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: '#dc2626',
                                    width: '80px'
                                  }}>
                                    <input
                                      type="text"
                                      defaultValue={viaje.unidad || viaje.numeroUnidad || ''}
                                      data-field="unidad"
                                      disabled={bloqueado}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          // Encontrar el tr padre
                                          const tr = e.target.closest('tr');
                                          if (!tr) return;

                                          // Obtener valores de los inputs y select de la misma fila
                                          const tiempoInput = tr.querySelector('input[data-field="tiempo"]');
                                          const unidadInput = e.target;
                                          const tiempoVal = tiempoInput?.value || '';
                                          const unidadVal = unidadInput?.value || '';
                                          const baseVal = '0';

                                          // Validar que hay valores
                                          if (!tiempoVal.trim() || !unidadVal.trim()) {
                                            setModal({ open: true, success: false, message: 'Por favor, complete Tiempo y Unidad antes de enviar.' });
                                            return;
                                          }

                                          // Establecer valores en el estado (para compatibilidad con otros usos)
                                          setTiempoEdit(tiempoVal);
                                          setUnidadEdit(unidadVal);
                                          setBaseEdit(baseVal);

                                          // Enviar a pedidos en curso pasando los valores directamente
                                          guardarEdicionViaje(viaje.id, tiempoVal, unidadVal, baseVal);
                                        }
                                      }}
                                      placeholder={bloqueado ? "Bloqueado" : "Unidad (Enter)"}
                                      style={{
                                        width: '100%',
                                        padding: '4px 8px',
                                        border: `1px solid ${bloqueado ? '#d1d5db' : '#dc2626'}`,
                                        borderRadius: '4px',
                                        textAlign: 'center',
                                        fontSize: '18px',
                                        backgroundColor: bloqueado ? '#f3f4f6' : 'white',
                                        cursor: bloqueado ? 'not-allowed' : 'text',
                                        opacity: bloqueado ? 0.7 : 1
                                      }}
                                    />
                                  </td>
                                  <td style={{
                                    padding: '4px 8px',
                                    textAlign: 'center',
                                    width: '100px'
                                  }}>
                                    {esPD ? (
                                      <button
                                        type="button"
                                        onClick={() => abrirModalNotificados(viaje)}
                                        style={{
                                          padding: '4px 10px',
                                          borderRadius: 20,
                                          border: '1px solid #f59e0b',
                                          fontSize: 12,
                                          fontWeight: 'bold',
                                          cursor: 'pointer',
                                          background: '#fff7ed',
                                          color: '#9a3412',
                                          transition: 'all 0.2s ease',
                                          whiteSpace: 'nowrap'
                                        }}
                                        title="Ver notificados y ubicación en el mapa"
                                      >
                                        🔔 Notificados
                                      </button>
                                    ) : esPS && bloqueado ? (
                                      <button
                                        type="button"
                                        onClick={() => abrirModalNotificados(viaje)}
                                        style={{
                                          padding: '4px 10px',
                                          borderRadius: 20,
                                          border: '1px solid #8b5cf6',
                                          fontSize: 12,
                                          fontWeight: 'bold',
                                          cursor: 'pointer',
                                          background: '#ede9fe',
                                          color: '#5b21b6',
                                          transition: 'all 0.2s ease',
                                          whiteSpace: 'nowrap'
                                        }}
                                        title="Ver notificados y ubicación en el mapa"
                                      >
                                        🔔 Notificados
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => !bloqueado && eliminarPedidoDisponible(viaje)}
                                        disabled={bloqueado}
                                        style={{
                                          padding: '4px 12px',
                                          borderRadius: 20,
                                          border: 'none',
                                          fontSize: 12,
                                          fontWeight: 'bold',
                                          cursor: bloqueado ? 'not-allowed' : 'pointer',
                                          background: bloqueado ? '#9ca3af' : (esLlamada ? '#10b981' : '#ef4444'),
                                          color: 'white',
                                          transition: 'all 0.2s ease',
                                          opacity: bloqueado ? 0.6 : 1
                                        }}
                                        onMouseEnter={(e) => {
                                          if (!bloqueado) {
                                            e.target.style.transform = 'scale(1.05)';
                                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (!bloqueado) {
                                            e.target.style.transform = 'scale(1)';
                                            e.target.style.boxShadow = 'none';
                                          }
                                        }}
                                      >
                                        {bloqueado ? `Espera ${segundosRestantes}s` : 'Eliminar'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}



                <div style={{ height: '30px' }} />

                {/* Tabla de Pedidos en Curso */}
                <div style={{
                  marginTop: viajesAsignados.length > 0 ? 30 : 0,
                  background: colors.surface,
                  borderTop: `4px solid ${colors.danger}`,
                  borderRadius: 8,
                  padding: '15px 15px 0 15px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  flex: viajesAsignados.length === 0 ? 1 : 'auto',
                  minHeight: viajesAsignados.length === 0 ? 'auto' : 'auto'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 20,
                    color: colors.text
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: 20,
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10
                    }}>
                      📋 Viajes del Día
                      <span style={{
                        background: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                        color: colors.primary,
                        padding: '4px 12px',
                        borderRadius: 20,
                        fontSize: 14,
                        border: `1px solid ${colors.primary}`,
                        fontWeight: 'bold'
                      }}>
                        {todosLosViajesCombinadosOrdenadosMemo.length} total
                      </span>
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 15, flexWrap: 'wrap' }}>
                      <input
                        type="date"
                        value={fechaFiltroViajes}
                        onChange={(e) => setFechaFiltroViajes(e.target.value)}
                        style={{
                          padding: '8px 12px',
                          borderRadius: 8,
                          border: '1px solid #d1d5db',
                          background: isDarkMode ? '#374151' : 'white',
                          color: colors.text,
                          fontSize: 14,
                          fontWeight: 'bold',
                          outline: 'none',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Últimos 3 dígitos teléfono"
                        value={filtroTelefonoUltimos3}
                        onChange={(e) => setFiltroTelefonoUltimos3((e.target.value || '').replace(/\D/g, '').slice(0, 3))}
                        maxLength={3}
                        style={{
                          padding: '8px 12px',
                          borderRadius: 8,
                          border: '1px solid #d1d5db',
                          background: isDarkMode ? '#374151' : 'white',
                          color: colors.text,
                          fontSize: 14,
                          width: 140,
                          outline: 'none',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Número de unidad"
                        value={filtroUnidad}
                        onChange={(e) => setFiltroUnidad((e.target.value || '').replace(/\D/g, '').slice(0, 3))}
                        maxLength={3}
                        style={{
                          padding: '8px 12px',
                          borderRadius: 8,
                          border: '1px solid #d1d5db',
                          background: isDarkMode ? '#374151' : 'white',
                          color: colors.text,
                          fontSize: 14,
                          width: 140,
                          outline: 'none',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                      />
                      <button
                        onClick={cargarPedidosEnCurso}
                        disabled={cargandoPedidosCurso}
                        style={{
                          background: isDarkMode ? '#374151' : '#f3f4f6',
                          border: '1px solid #d1d5db',
                          color: colors.text,
                          padding: '8px 16px',
                          borderRadius: 8,
                          cursor: cargandoPedidosCurso ? 'not-allowed' : 'pointer',
                          fontSize: 14,
                          fontWeight: 'bold',
                          opacity: cargandoPedidosCurso ? 0.7 : 1
                        }}
                      >
                        {cargandoPedidosCurso ? '🔄 Cargando...' : '🔄 Actualizar'}
                      </button>
                    </div>
                  </div>

                  {(() => {
                    let todosLosViajesCombinados = todosLosViajesCombinadosOrdenadosMemo;
                    const tel3 = (filtroTelefonoUltimos3 || '').trim();
                    const uni = (filtroUnidad || '').trim();
                    if (tel3 || uni) {
                      todosLosViajesCombinados = todosLosViajesCombinados.filter((v) => {
                        if (tel3) {
                          const t = (v.telefono || v.telefonoCompleto || '').toString();
                          const ultimos3 = t.slice(-3);
                          if (ultimos3 !== tel3) return false;
                        }
                        if (uni) {
                          const u = (v.unidad || v.numeroUnidad || '').toString().trim();
                          if (u !== uni) return false;
                        }
                        return true;
                      });
                    }
                    if (cargandoPedidosCurso) {
                      return (
                        <div style={{
                          padding: 40,
                          textAlign: 'center',
                          color: '#666'
                        }}>
                          <div style={{ fontSize: 24, marginBottom: 10 }}>⏳</div>
                          <div>Cargando viajes...</div>
                        </div>
                      );
                    }

                    if (todosLosViajesCombinados.length === 0) {
                      return (
                        <div style={{
                          padding: 40,
                          textAlign: 'center',
                          color: '#666'
                        }}>
                          <div style={{ fontSize: 48, marginBottom: 10 }}>🚗</div>
                          <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 5 }}>
                            No hay viajes registrados para este día
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div style={{
                        maxWidth: '100%'
                      }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '14px',
                          tableLayout: 'fixed'
                        }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '80px'
                              }}>
                                🕐 Hora
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'left',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '120px'
                              }}>
                                📞 Teléfono
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'left',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '150px'
                              }}>
                                👤 Cliente
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'left',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '350px',
                                minWidth: '300px'
                              }}>
                                📍 Dirección
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '80px'
                              }}>
                                ⏱️ Tiempo
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '80px'
                              }}>
                                🚕 Unidad
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '100px'
                              }}>
                                Estado
                              </th>
                              <th style={{
                                padding: '4px 8px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: '#374151',
                                borderBottom: '2px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                                width: '220px'
                              }}>
                                📝 Observaciones
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {todosLosViajesCombinados.map((pedido, index) => {
                                // Determinar si el pedido está iniciado (solo para pedidos de aplicación)
                                const esPedidoIniciado = pedido.tipopedido === 'Automático' && pedido.pedido === 'Iniciado';
                                // Si la fila viene de la colección pedidoEnCurso (pedido en curso)
                                const esDePedidoEnCurso = pedidosEnCursoIdSetMemo.has(pedido.id);
                                const esLlamada = ((pedido.tipoPedido || pedido.tipopedido || '').toString().toLowerCase() === 'llamada');
                                const estadoRaw = (pedido.pedido || pedido.estado || '').trim();
                                const esBuscandoUnidad = /disponible/i.test(estadoRaw);
                                // Colores por estado
                                const estadoConfig = (() => {
                                  if (esBuscandoUnidad) return { texto: 'Buscando', bg: '#fef9c3', color: '#92400e', borde: '#f59e0b' };
                                  if (/reasignado/i.test(estadoRaw)) return { texto: 'Reasignado', bg: '#ede9fe', color: '#5b21b6', borde: '#7c3aed' };
                                  if (/aceptado/i.test(estadoRaw)) return { texto: 'Aceptado', bg: '#dcfce7', color: '#166534', borde: '#22c55e' };
                                  if (/iniciado/i.test(estadoRaw)) return { texto: 'Iniciado', bg: '#ffedd5', color: '#c2410c', borde: '#f97316' };
                                  if (/en curso/i.test(estadoRaw)) return { texto: 'En Curso', bg: '#dbeafe', color: '#1d4ed8', borde: '#3b82f6' };
                                  if (/cancelado por cliente/i.test(estadoRaw)) return { texto: 'Cancelado por Cliente', bg: '#fee2e2', color: '#b91c1c', borde: '#ef4444' };
                                  if (/cancelado por unidad|cancelado por conductor/i.test(estadoRaw)) return { texto: 'Cancelado por Unidad', bg: '#fecaca', color: '#991b1b', borde: '#dc2626' };
                                  if (/finalizado/i.test(estadoRaw)) return { texto: 'Finalizado', bg: '#ccfbf1', color: '#0f766e', borde: '#14b8a6' };
                                  return { texto: estadoRaw || '—', bg: '#f1f5f9', color: '#475569', borde: '#94a3b8' };
                                })();

                                const bgLlamada = '#dcfce7';
                                const bgLlamadaHover = '#bbf7d0';
                                const bgDefault = esDePedidoEnCurso
                                  ? '#fef9c3'
                                  : esBuscandoUnidad
                                    ? '#fef9c3'
                                    : esPedidoIniciado
                                      ? '#fef3c7'
                                      : (index % 2 === 0 ? '#fff' : '#fafbff');
                                const bgDefaultHover = esDePedidoEnCurso
                                  ? '#fef08a'
                                  : esBuscandoUnidad
                                    ? '#fef08a'
                                    : esPedidoIniciado
                                      ? '#fde68a'
                                      : '#fef2f2';
                                return (
                                  <tr
                                    key={pedido.id}
                                    style={{
                                      borderBottom: '1px solid #f1f5f9',
                                      background: esLlamada ? bgLlamada : bgDefault,
                                      transition: 'background 0.2s ease',
                                      borderLeft: esLlamada
                                        ? '4px solid #22c55e'
                                        : esDePedidoEnCurso
                                          ? '4px solid #f59e0b'
                                          : esBuscandoUnidad
                                            ? '4px solid #f59e0b'
                                            : esPedidoIniciado
                                              ? '4px solid #f59e0b'
                                              : 'none'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = esLlamada ? bgLlamadaHover : bgDefaultHover;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = esLlamada ? bgLlamada : bgDefault;
                                    }}
                                  >
                                    <td style={{
                                      padding: '12px 16px',
                                      textAlign: 'center',
                                      color: '#6b7280',
                                      fontSize: 12,
                                      fontWeight: 'bold'
                                    }}>
                                      {(() => {
                                        const parseDate = (val) => {
                                          if (!val) return null;
                                          if (val.toDate) return val.toDate();
                                          if (val.seconds) return new Date(val.seconds * 1000);
                                          if (val instanceof Date) return val;
                                          if (typeof val === 'string') {
                                            let d = new Date(val);
                                            if (!isNaN(d.getTime())) return d;
                                            if (val.includes('/')) {
                                              try {
                                                const parts = val.split(' ');
                                                if (parts.length === 2) {
                                                  const p1 = parts[0]; const p2 = parts[1];
                                                  const timeParts = p1.includes(':') ? p1.split(':') : p2.split(':');
                                                  const dateParts = p1.includes('/') ? p1.split('/') : p2.split('/');
                                                  if (timeParts.length >= 2 && dateParts.length === 3) {
                                                    return new Date(dateParts[2], dateParts[1] - 1, dateParts[0], timeParts[0], timeParts[1], timeParts[2] || 0);
                                                  }
                                                }
                                              } catch (e) { }
                                            }
                                          }
                                          return null;
                                        };

                                        const dateObj = parseDate(pedido.createdAt) || parseDate(pedido.fecha);

                                        return (dateObj && !isNaN(dateObj.getTime()))
                                          ? dateObj.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })
                                          : '-';
                                      })()}
                                    </td>
                                    <td style={{
                                      padding: '12px 16px',
                                      fontWeight: 'bold',
                                      color: '#1f2937'
                                    }}>
                                      {pedido.telefono || '-'}
                                    </td>
                                    <td style={{
                                      padding: '12px 16px',
                                      color: '#374151'
                                    }}>
                                      {pedido.nombreCliente || pedido.codigo || '-'}
                                    </td>
                                    <td style={{
                                      padding: '12px 16px',
                                      color: '#374151',
                                      maxWidth: 200,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {(() => {
                                        const dir = pedido.direccion || '-';
                                        const destino = (pedido.destino || '').toString().trim();
                                        // Solo para registros que vienen de pedidoEnCurso
                                        if (esDePedidoEnCurso && destino) {
                                          return `${dir} (${destino})`;
                                        }
                                        return dir;
                                      })()}
                                    </td>
                                    <td style={{
                                      padding: '12px 16px',
                                      textAlign: 'center',
                                      fontWeight: 'bold',
                                      color: 'rgba(5, 150, 105, 0.7)'
                                    }}>
                                      {editandoViaje === pedido.id ? (
                                        <input
                                          ref={tiempoEditInputRef}
                                          type="text"
                                          value={tiempoEdit}
                                          onChange={(e) => {
                                            const v = (e.target.value ?? '').toString().replace(/\D/g, '').slice(0, 2);
                                            setTiempoEdit(v);
                                            if (v.length === 2) {
                                              setTimeout(() => {
                                                if (unidadEditInputRef.current) {
                                                  unidadEditInputRef.current.focus();
                                                  unidadEditInputRef.current.select?.();
                                                }
                                              }, 0);
                                            }
                                          }}
                                          placeholder="Tiempo"
                                          maxLength={2}
                                          style={{
                                            width: '80px',
                                            padding: '4px 8px',
                                            border: '1px solid #059669',
                                            borderRadius: '4px',
                                            textAlign: 'center',
                                            fontSize: '18px'
                                          }}
                                        />
                                      ) : (
                                        <span style={{ cursor: 'pointer' }} onClick={() => iniciarEdicionViaje(pedido)}>
                                          {pedido.tiempo || pedido.minutos ? `${pedido.tiempo || pedido.minutos} min` : '-'}
                                        </span>
                                      )}
                                    </td>
                                    <td style={{
                                      padding: '12px 16px',
                                      textAlign: 'center',
                                      fontWeight: 'bold',
                                      color: '#dc2626'
                                    }}>
                                      {editandoViaje === pedido.id ? (
                                        <input
                                          ref={unidadEditInputRef}
                                          type="text"
                                          value={unidadEdit}
                                          onChange={(e) => setUnidadEdit(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              guardarEdicionViajeDesdeTabla(pedido);
                                            } else if (e.key === 'Escape') {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              cancelarEdicionViaje();
                                            }
                                          }}
                                          placeholder="Unidad"
                                          style={{
                                            width: '80px',
                                            padding: '4px 8px',
                                            border: '1px solid #dc2626',
                                            borderRadius: '4px',
                                            textAlign: 'center',
                                            fontSize: '18px'
                                          }}
                                        />
                                      ) : (
                                        <span style={{ cursor: 'pointer' }} onClick={() => iniciarEdicionViaje(pedido)}>
                                          {pedido.unidad || pedido.numeroUnidad || '-'}
                                        </span>
                                      )}
                                    </td>
                                    <td style={{
                                      padding: '12px 16px',
                                      textAlign: 'center'
                                    }}>
                                      <button
                                        onClick={() => abrirModalAccionesPedido(pedido, 'pedidoEnCurso')}
                                        style={{
                                          padding: '6px 12px',
                                          borderRadius: 20,
                                          border: `2px solid ${estadoConfig.borde}`,
                                          fontSize: 12,
                                          fontWeight: 'bold',
                                          cursor: 'pointer',
                                          background: estadoConfig.bg,
                                          color: estadoConfig.color,
                                          transition: 'all 0.2s ease',
                                          whiteSpace: 'nowrap'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.transform = 'scale(1.05)';
                                          e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.transform = 'scale(1)';
                                          e.target.style.boxShadow = 'none';
                                        }}
                                        title="Ver detalles del viaje"
                                      >
                                        {estadoConfig.texto === 'Buscando' ? <BuscandoAnimado /> : estadoConfig.texto}
                                      </button>
                                    </td>
                                    <td style={{
                                      padding: '12px 16px',
                                      textAlign: 'left',
                                      fontWeight: 'bold',
                                      color: '#111827'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <button
                                          type="button"
                                          disabled={Boolean(guardandoObservaciones[pedido.id])}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            abrirModalObservaciones(
                                              pedido,
                                              pedido.rutaColeccion
                                                ? [pedido.rutaColeccion, 'todosLosViajes', 'pedidoEnCurso']
                                                : (esDePedidoEnCurso ? ['pedidoEnCurso', 'todosLosViajes'] : ['todosLosViajes', 'pedidoEnCurso'])
                                            );
                                          }}
                                          style={{
                                            padding: '6px 10px',
                                            borderRadius: 8,
                                            border: '1px solid #3b82f6',
                                            background: 'white',
                                            color: '#1d4ed8',
                                            cursor: Boolean(guardandoObservaciones[pedido.id]) ? 'not-allowed' : 'pointer',
                                            fontWeight: 800,
                                            fontSize: 12,
                                            whiteSpace: 'nowrap',
                                          }}
                                          title="Agregar/editar observaciones"
                                        >
                                          {getObservacionesValue(pedido) ? 'Editar' : 'Agregar'} observaciones
                                        </button>
                                        <div
                                          style={{
                                            fontSize: 12,
                                            color: '#6b7280',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: 260
                                          }}
                                          title={getObservacionesValue(pedido) || ''}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {getObservacionesValue(pedido) || ''}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    )
                  })()}
                </div>
                {/* Modal de registro de clientes */}
                {modalRegistroCliente.open && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                  }}>
                    <div style={{
                      background: 'white',
                      padding: '30px',
                      borderRadius: '12px',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      width: '90%', maxWidth: '1000px',
                      maxHeight: '90vh',
                      overflow: 'auto'
                    }}>
                      <h2 style={{
                        margin: '0 0 20px 0',
                        color: '#1f2937',
                        fontSize: '24px',
                        fontWeight: 'bold',
                        textAlign: 'center'
                      }}>
                        📝 Registrar {modalRegistroCliente.tipoCliente}
                        {/* Texto de modo eliminado */}
                      </h2>

                      <p style={{
                        margin: '0 0 20px 0',
                        color: '#6b7280',
                        fontSize: '16px',
                        textAlign: 'center'
                      }}>
                        El teléfono <strong>{telefono}</strong> no está registrado en la colección <strong>{modalRegistroCliente.coleccion}</strong>.
                        <br />
                        ¿Deseas registrarlo ahora?
                      </p>

                      {/* Formulario simplificado: teléfono, nombre y dirección */}
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                          <div>
                            <label style={{
                              display: 'block',
                              marginBottom: '8px',
                              fontWeight: 'bold',
                              color: '#374151'
                            }}>
                              Teléfono: *
                            </label>
                            <input
                              type="text"
                              placeholder="Ingrese el teléfono"
                              value={modalRegistroCliente.datosCliente.telefono}
                              onChange={(e) => setModalRegistroCliente(prev => ({
                                ...prev,
                                datosCliente: { ...prev.datosCliente, telefono: e.target.value.replace(/\D/g, '') }
                              }))}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: '2px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '16px',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{
                              display: 'block',
                              marginBottom: '8px',
                              fontWeight: 'bold',
                              color: '#374151'
                            }}>
                              Nombre del cliente: *
                            </label>
                            <input
                              type="text"
                              placeholder="Ingrese el nombre completo"
                              value={modalRegistroCliente.datosCliente.nombre}
                              onChange={(e) => setModalRegistroCliente(prev => ({
                                ...prev,
                                datosCliente: { ...prev.datosCliente, nombre: e.target.value }
                              }))}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: `2px solid ${modalRegistroCliente.datosCliente.nombre.trim() ? '#10b981' : '#d1d5db'}`,
                                borderRadius: '8px',
                                fontSize: '16px',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>

                          <div>
                            <label style={{
                              display: 'block',
                              marginBottom: '8px',
                              fontWeight: 'bold',
                              color: '#374151'
                            }}>
                              Dirección: *
                            </label>
                            <input
                              type="text"
                              placeholder="Ingrese la dirección"
                              value={modalRegistroCliente.datosCliente.direccion}
                              onChange={(e) => setModalRegistroCliente(prev => ({
                                ...prev,
                                datosCliente: { ...prev.datosCliente, direccion: e.target.value }
                              }))}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: `2px solid ${modalRegistroCliente.datosCliente.direccion.trim() ? '#10b981' : '#d1d5db'}`,
                                borderRadius: '8px',
                                fontSize: '16px',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div style={{
                        display: 'flex',
                        gap: '15px',
                        justifyContent: 'center',
                        marginTop: '20px'
                      }}>
                        <button
                          onClick={() => setModalRegistroCliente({
                            open: false,
                            tipoCliente: '',
                            coleccion: '',
                            modoAplicacion: false,
                            datosCliente: { telefono: '', nombre: '', direccion: '', coordenadas: '', sector: '', prefijo: 'Ecuador' }
                          })}
                          style={{
                            padding: '12px 24px',
                            border: '2px solid #d1d5db',
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            color: '#374151',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          ❌ Cancelar
                        </button>

                        <button
                          onClick={async () => {
                            const tel = (modalRegistroCliente.datosCliente.telefono || telefono || '').toString().replace(/\D/g, '');
                            if (!tel || tel.length < 7) {
                              setModal({
                                open: true,
                                success: false,
                                message: 'Por favor, ingrese un teléfono válido (mínimo 7 dígitos).'
                              });
                              return;
                            }
                            if (!modalRegistroCliente.datosCliente.nombre.trim()) {
                              setModal({
                                open: true,
                                success: false,
                                message: 'Por favor, ingrese el nombre del cliente.'
                              });
                              return;
                            }

                            if (!modalRegistroCliente.datosCliente.direccion.trim()) {
                              setModal({
                                open: true,
                                success: false,
                                message: 'Por favor, ingrese la dirección del cliente.'
                              });
                              return;
                            }

                            await registrarNuevoCliente(
                              modalRegistroCliente.datosCliente,
                              modalRegistroCliente.tipoCliente,
                              modalRegistroCliente.modoAplicacion
                            );
                            setModalRegistroCliente({
                              open: false,
                              tipoCliente: '',
                              coleccion: '',
                              modoAplicacion: false,
                              datosCliente: { telefono: '', nombre: '', direccion: '', coordenadas: '', sector: '', prefijo: 'Ecuador' }
                            });
                          }}
                          style={{
                            padding: '12px 24px',
                            border: 'none',
                            borderRadius: '8px',
                            backgroundColor: '#059669',
                            color: 'white',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          ✅ Registrar {modalRegistroCliente.tipoCliente}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Modal de confirmación */}
                {modal.open && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                  }}>
                    <div style={{
                      background: 'white',
                      padding: '30px',
                      borderRadius: '12px',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      width: '400px',
                      maxWidth: '90vw',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        fontSize: '48px',
                        marginBottom: '20px'
                      }}>
                        {modal.success ? '✅' : '❌'}
                      </div>
                      <h3 style={{
                        margin: '0 0 15px 0',
                        color: modal.success ? '#059669' : '#dc2626',
                        fontSize: '20px',
                        fontWeight: 'bold'
                      }}>
                        {modal.success ? 'Éxito' : 'Error'}
                      </h3>
                      <div style={{
                        fontSize: '16px',
                        marginBottom: '20px'
                      }}>
                        {modal.message}
                      </div>
                      <button
                        onClick={() => setModal({ open: false, success: true, message: '' })}
                        style={{
                          padding: '12px 24px',
                          border: 'none',
                          borderRadius: '8px',
                          backgroundColor: modal.success ? '#059669' : '#dc2626',
                          color: 'white',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        Aceptar
                      </button>
                    </div>
                  </div>
                )}

                {/* Modal: ver notificados (PD) + enfocar en mapa */}
                {modalNotificados.open && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1200,
                    padding: 16
                  }}>
                    <div style={{
                      background: 'white',
                      borderRadius: 12,
                      width: '100%',
                      maxWidth: 720,
                      maxHeight: '85vh',
                      overflow: 'auto',
                      boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        padding: '14px 16px',
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12
                      }}>
                        <div style={{ fontWeight: 'bold', fontSize: 18, color: '#111827' }}>
                          🔔 Notificaciones del pedido
                        </div>
                        <button
                          type="button"
                          onClick={cerrarModalNotificados}
                          style={{
                            border: '1px solid #d1d5db',
                            background: 'white',
                            borderRadius: 8,
                            padding: '6px 10px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          ✕
                        </button>
                      </div>

                      {(() => {
                        const p = modalNotificados.pedido || {};
                        const pickArr = (...keys) => {
                          for (const k of keys) {
                            const v = p?.[k];
                            if (Array.isArray(v)) return v;
                          }
                          return [];
                        };

                        const notificados = pickArr('notificados', 'conductoresNotificados', 'unidadesNotificadas');
                        const cercanos = pickArr('conductoresCercanos');

                        const coords = (() => {
                          const lat = (p.latitud ?? '').toString().trim();
                          const lng = (p.longitud ?? '').toString().trim();
                          if (lat && lng) return `${lat},${lng}`;
                          const c = (p.coordenadas ?? '').toString().trim();
                          return c || '';
                        })();

                        return (
                          <div style={{ padding: 16 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, fontSize: 14 }}>
                              <div style={{ color: '#6b7280', fontWeight: 'bold' }}>Cliente</div>
                              <div style={{ color: '#111827' }}>{p.nombreCliente || p.codigo || '—'}</div>
                              <div style={{ color: '#6b7280', fontWeight: 'bold' }}>Teléfono</div>
                              <div style={{ color: '#111827' }}>{p.telefono || p.telefonoCompleto || '—'}</div>
                              <div style={{ color: '#6b7280', fontWeight: 'bold' }}>Dirección</div>
                              <div style={{ color: '#111827' }}>{p.direccion || '—'}</div>
                              <div style={{ color: '#6b7280', fontWeight: 'bold' }}>Coordenadas</div>
                              <div style={{ color: '#111827' }}>{coords || '—'}</div>
                            </div>

                            <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setMapaDashboardVisible(true);
                                  setFocusPedidoMapaId(p.id || null);
                                }}
                                style={{
                                  background: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 8,
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  fontWeight: 'bold'
                                }}
                              >
                                📍 Ver en mapa
                              </button>
                              <button
                                type="button"
                                onClick={marcarPedidoComoNoAtendido}
                                disabled={marcandoNoAtendido}
                                style={{
                                  background: marcandoNoAtendido ? '#9ca3af' : '#dc2626',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 8,
                                  padding: '8px 12px',
                                  cursor: marcandoNoAtendido ? 'not-allowed' : 'pointer',
                                  fontWeight: 'bold'
                                }}
                              >
                                {marcandoNoAtendido ? '⏳ Procesando...' : '❌ No atendido (eliminar de disponibles)'}
                              </button>
                              <button
                                type="button"
                                onClick={cerrarModalNotificados}
                                style={{
                                  background: 'white',
                                  color: '#374151',
                                  border: '1px solid #d1d5db',
                                  borderRadius: 8,
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  fontWeight: 'bold'
                                }}
                              >
                                Cerrar
                              </button>
                            </div>

                            {(() => {
                              const normUnidad = (x) => {
                                if (x == null) return '';
                                if (typeof x === 'string' || typeof x === 'number') return String(x);
                                if (typeof x === 'object') return String(x.unidad || x.numeroUnidad || x.unit || x.id || x.email || '');
                                return String(x);
                              };
                              const normDist = (x) => {
                                if (!x || typeof x !== 'object') return null;
                                const d = x.distancia ?? x.km ?? x.distance ?? null;
                                const n = typeof d === 'number' ? d : parseFloat(String(d ?? '').replace(',', '.'));
                                return Number.isFinite(n) ? n : null;
                              };

                              const listaCercanos = Array.isArray(cercanos) ? [...cercanos].sort((a, b) => (a?.distancia || 0) - (b?.distancia || 0)) : [];
                              const listaNotificados = Array.isArray(notificados) ? notificados.map((x) => ({
                                unidad: normUnidad(x),
                                distancia: normDist(x),
                                aceptado: !!(x && typeof x === 'object' && x.aceptado),
                                rechazado: !!(x && typeof x === 'object' && (x.rechazado || x.rechazo)),
                                nombre: (x && typeof x === 'object') ? (x.nombre || '') : '',
                                placa: (x && typeof x === 'object') ? (x.placa || '') : '',
                              })).filter(i => (i.unidad || '').trim()) : [];

                              const items = (listaCercanos.length ? listaCercanos : listaNotificados);

                              return (
                                <div style={{ marginTop: 16 }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 10,
                                    borderBottom: '1px solid #e5e7eb',
                                    paddingBottom: 8,
                                    marginBottom: 10
                                  }}>
                                    <div style={{ fontWeight: 800, color: '#111827' }}>
                                      📡 Unidades Notificadas ({items.length})
                                    </div>
                                  </div>

                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    maxHeight: '45vh',
                                    overflowY: 'auto',
                                    paddingRight: 4
                                  }}>
                                    {items.length ? (
                                      items.map((c, i) => {
                                        const unidadOut = (c?.unidad || c?.numeroUnidad || c?.unit || '').toString().trim() || normUnidad(c);
                                        const dist = normDist(c);
                                        const aceptado = !!(c && typeof c === 'object' && c.aceptado);
                                        const rechazado = !!(c && typeof c === 'object' && c.rechazado);
                                        const nombreOut = (c && typeof c === 'object') ? (c.nombre || '') : '';
                                        const placaOut = (c && typeof c === 'object') ? (c.placa || '') : '';
                                        return (
                                          <div key={i} style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            fontSize: '12px',
                                            padding: '6px 10px',
                                            background: rechazado ? '#fef2f2' : '#f0fdf4',
                                            borderRadius: '6px',
                                            border: `1px solid ${rechazado ? '#fecaca' : '#bbf7d0'}`
                                          }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                              <span style={{ fontWeight: '800', color: '#1e293b' }}>🚖 {unidadOut || 'S/N'}</span>
                                              <span style={{ color: '#64748b' }}>📍 {dist != null ? `${dist.toFixed(2)} km` : '--'}</span>
                                            </div>
                                            <div>
                                              {aceptado ? (
                                                <div style={{ textAlign: 'right' }}>
                                                  <span style={{ color: '#2563eb', fontWeight: 'bold', fontSize: '10px', display: 'block' }}>✅ Aceptado</span>
                                                  <span style={{ color: '#475569', fontSize: '9px', display: 'block' }}>👤 {nombreOut || 'N/A'}</span>
                                                  <span style={{ color: '#475569', fontSize: '9px', display: 'block' }}>🆔 {placaOut || 'N/A'}</span>
                                                </div>
                                              ) : rechazado ? (
                                                <span style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '10px' }}>✖️ Rechazado</span>
                                              ) : (
                                                <span style={{ color: '#166534', fontWeight: 'bold', fontSize: '10px' }}>⏳ Notificado</span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })
                                    ) : (
                                      <div style={{ color: '#6b7280', fontSize: 13 }}>No hay unidades notificadas.</div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Modal de Reserva (F7) */}
                {console.log('🔍 Renderizando modal de reserva, estado:', modalReserva)}
                {modalReserva.open && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                  }}>
                    <div style={{
                      background: 'white',
                      padding: '28px',
                      borderRadius: '12px',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      width: '540px',
                      maxWidth: '90vw',
                      maxHeight: '90vh',
                      overflowY: 'auto'
                    }}>
                      <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: 20, fontWeight: 700 }}>
                        📅 Crear Reserva
                      </h3>

                      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                        <div style={{ fontSize: 14, color: '#374151', marginBottom: 6 }}>👤 Cliente</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>Nombre</div>
                            <input type="text" readOnly value={modalReserva.datosCliente.nombre}
                              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#f3f4f6' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>Teléfono</div>
                            <input type="text" readOnly value={modalReserva.datosCliente.telefono}
                              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#f3f4f6' }} />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>Dirección</div>
                            <input type="text" readOnly value={modalReserva.datosCliente.direccion}
                              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#f3f4f6' }} />
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 16 }}>
                        <div>
                          <div style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Fecha y Hora de la Reserva</div>
                          <input
                            type="datetime-local"
                            value={modalReserva.fechaHora}
                            onChange={(e) => setModalReserva(prev => ({ ...prev, fechaHora: e.target.value }))}
                            style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: 6 }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Destino</div>
                          <input
                            type="text"
                            placeholder="Ej. Aeropuerto, Centro Comercial, Dirección específica..."
                            value={modalReserva.destino}
                            onChange={(e) => setModalReserva(prev => ({ ...prev, destino: e.target.value }))}
                            style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: 6 }}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Motivo</div>
                          <textarea
                            rows="3"
                            value={modalReserva.motivo}
                            onChange={(e) => setModalReserva(prev => ({ ...prev, motivo: e.target.value }))}
                            placeholder="Ej. Traslado al aeropuerto, cliente solicita unidad a primera hora..."
                            style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: 6, resize: 'vertical' }}
                          />
                        </div>

                        {/* Mostrar información de empresa y autorización si no es Efectivo */}
                        {tipoEmpresa && tipoEmpresa !== 'Efectivo' && (
                          <div style={{
                            background: '#eff6ff',
                            border: '1px solid #3b82f6',
                            borderRadius: 8,
                            padding: 12,
                            marginTop: 8
                          }}>
                            <div style={{ fontSize: 14, color: '#1e40af', fontWeight: 600, marginBottom: 8 }}>
                              🏢 Información de Empresa
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                              <div>
                                <div style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Empresa</div>
                                <input
                                  type="text"
                                  value={tipoEmpresa}
                                  readOnly
                                  style={{
                                    width: '100%',
                                    padding: '8px 10px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: 6,
                                    background: '#f8fafc',
                                    color: '#374151',
                                    fontWeight: 500
                                  }}
                                />
                              </div>
                              <div>
                                <div style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Autorización</div>
                                <input
                                  type="text"
                                  value={numeroAutorizacion}
                                  readOnly
                                  style={{
                                    width: '100%',
                                    padding: '8px 10px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: 6,
                                    background: '#f8fafc',
                                    color: '#374151',
                                    fontWeight: 500
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {errorReserva && (
                        <div style={{
                          background: '#fef2f2',
                          border: '1px solid #fca5a5',
                          color: '#b91c1c',
                          borderRadius: 8,
                          padding: '10px 12px',
                          marginBottom: 12,
                          fontSize: 13,
                          fontWeight: 600
                        }}>
                          ⚠️ {errorReserva}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                        <button
                          onClick={guardarReserva}
                          disabled={guardandoReserva}
                          style={{ padding: '10px 18px', border: 'none', borderRadius: 8, background: guardandoReserva ? '#9ca3af' : '#10b981', color: 'white', fontWeight: 700, cursor: guardandoReserva ? 'not-allowed' : 'pointer' }}
                        >
                          {guardandoReserva ? '⏳ Guardando...' : '💾 Guardar Reserva'}
                        </button>
                        <button
                          onClick={() => { setErrorReserva(''); setModalReserva(prev => ({ ...prev, open: false })); }}
                          style={{ padding: '10px 18px', border: '1px solid #6b7280', borderRadius: 8, background: 'transparent', color: '#374151', fontWeight: 700, cursor: 'pointer' }}
                        >
                          ❌ Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Modal de acciones del pedido */}
                {modalAccionesPedido.open && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                  }}>
                    <div style={{
                      background: 'white',
                      padding: '18px 20px',
                      borderRadius: '12px',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      width: '500px',
                      maxWidth: '90vw',
                      maxHeight: '90vh',
                      overflow: 'auto'
                    }}>
                      <div style={{
                        position: 'sticky',
                        top: 0,
                        background: 'white',
                        zIndex: 5,
                        paddingBottom: 12,
                        marginBottom: 14,
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10
                      }}>
                        <h3 style={{
                          margin: 0,
                          color: '#1f2937',
                          fontSize: '20px',
                          fontWeight: 'bold',
                          textAlign: 'left'
                        }}>
                          🎛️ Acciones del Pedido
                        </h3>
                        <button
                          onClick={cerrarModalAccionesPedido}
                          style={{
                            border: '1px solid #e5e7eb',
                            background: 'white',
                            color: '#111827',
                            width: 34,
                            height: 34,
                            borderRadius: 10,
                            cursor: 'pointer',
                            fontSize: 18,
                            lineHeight: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Cerrar"
                        >
                          ✕
                        </button>
                      </div>

                      <div style={{
                        marginBottom: '20px',
                        padding: '15px',
                        background: '#f8fafc',
                        borderRadius: '8px',
                        textAlign: 'left'
                      }}>
                        <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#374151' }}>
                          📞 Cliente: {modalAccionesPedido.pedido?.nombreCliente || modalAccionesPedido.pedido?.codigo || 'N/A'}
                        </p>
                        <p style={{ margin: '0 0 10px 0', color: '#6b7280' }}>
                          📍 Dirección: {modalAccionesPedido.pedido?.direccion || 'N/A'}
                        </p>
                      </div>

                      {/* Listado de Conductores Cercanos Notificados */}
                      {modalAccionesPedido.pedido?.conductoresCercanos && modalAccionesPedido.pedido.conductoresCercanos.length > 0 && (
                        <div style={{
                          marginBottom: '20px',
                          padding: '12px',
                          background: '#fff',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <h4 style={{
                            margin: '0 0 10px 0',
                            fontSize: '13px',
                            fontWeight: 'bold',
                            color: '#1e3a8a',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            borderBottom: '1px solid #f1f5f9',
                            paddingBottom: '5px'
                          }}>
                            📡 Unidades Notificadas ({modalAccionesPedido.pedido.conductoresCercanos.length})
                          </h4>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            maxHeight: '45vh',
                            overflowY: 'auto',
                            paddingRight: 4
                          }}>
                            {conductoresCercanosOrdenadosMemo.map((c, i) => (
                                <div key={i} style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  fontSize: '12px',
                                  padding: '4px 8px',
                                  background: c.rechazado ? '#fef2f2' : '#f0fdf4',
                                  borderRadius: '4px',
                                  border: `1px solid ${c.rechazado ? '#fecaca' : '#bbf7d0'}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontWeight: '800', color: '#1e293b' }}>🚖 {c.unidad || 'S/N'}</span>
                                    <span style={{ color: '#64748b' }}>📍 {c.distancia ? `${c.distancia.toFixed(2)} km` : '--'}</span>
                                  </div>
                                  <div>
                                    {c.aceptado ? (
                                      <div style={{ textAlign: 'right' }}>
                                        <span style={{ color: '#2563eb', fontWeight: 'bold', fontSize: '10px', display: 'block' }}>✅ Aceptado</span>
                                        <span style={{ color: '#475569', fontSize: '9px', display: 'block' }}>👤 {c.nombre || 'N/A'}</span>
                                        <span style={{ color: '#475569', fontSize: '9px', display: 'block' }}>🆔 {c.placa || 'N/A'}</span>
                                      </div>
                                    ) : c.rechazado ? (
                                      <span style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '10px' }}>✖️ Rechazado</span>
                                    ) : (
                                      <span style={{ color: '#166534', fontWeight: 'bold', fontSize: '10px' }}>⏳ Notificado</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                      }}>
                        {/* Botones para pedidos en curso */}
                        {modalAccionesPedido.coleccion === 'pedidoEnCurso' && (
                          <>
                            <button
                              onClick={cancelarPedidoPorCliente}
                              style={{
                                padding: '12px 20px',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#b91c1c';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#dc2626';
                              }}
                            >
                              ❌ Cancelado por Cliente
                            </button>

                            <button
                              onClick={cancelarPedidoPorUnidad}
                              style={{
                                padding: '12px 20px',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: '#ef4444',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#dc2626';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#ef4444';
                              }}
                            >
                              🚫 Cancelado por Unidad
                            </button>

                            <button
                              onClick={finalizarPedido}
                              style={{
                                padding: '12px 20px',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: '#10b981',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#059669';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#10b981';
                              }}
                            >
                              🏁 Finalizar Pedido
                            </button>
                          </>
                        )}

                        {/* Botones para pedidos disponibles */}
                        {modalAccionesPedido.coleccion === 'pedidosDisponibles' && (
                          <>
                            {/* Mostrar autorización actual si existe */}
                            {modalAccionesPedido.pedido?.autorizacion && (
                              <div style={{
                                padding: '10px 12px',
                                borderRadius: 8,
                                background: '#eef2ff',
                                color: '#3730a3',
                                fontWeight: 700,
                                textAlign: 'center'
                              }}>
                                🔐 Autorización: {modalAccionesPedido.pedido.autorizacion}
                              </div>
                            )}

                            <button
                              onClick={generarAutorizacionParaPedidoDisponible}
                              disabled={Boolean(modalAccionesPedido.pedido?.autorizacion)}
                              style={{
                                padding: '12px 20px',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: Boolean(modalAccionesPedido.pedido?.autorizacion) ? '#9ca3af' : '#2563eb',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: Boolean(modalAccionesPedido.pedido?.autorizacion) ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (!modalAccionesPedido.pedido?.autorizacion) e.target.style.backgroundColor = '#1d4ed8';
                              }}
                              onMouseLeave={(e) => {
                                if (!modalAccionesPedido.pedido?.autorizacion) e.target.style.backgroundColor = '#2563eb';
                              }}
                            >
                              🔐 Generar Autorización
                            </button>

                            <button
                              onClick={cancelarPedidoSinAsignar}
                              style={{
                                padding: '12px 20px',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#b91c1c';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#dc2626';
                              }}
                            >
                              ❌ Cancelado por Cliente Sin Asignar
                            </button>

                            <button
                              onClick={noHuboUnidadDisponible}
                              style={{
                                padding: '12px 20px',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: '#ef4444',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#dc2626';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#ef4444';
                              }}
                            >
                              🚫 No Hubo Unidad Disponible
                            </button>

                            <button
                              onClick={generarReserva}
                              style={{
                                padding: '12px 20px',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: '#7c3aed',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#6d28d9';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#7c3aed';
                              }}
                            >
                              📅 Generar Reserva
                            </button>
                          </>
                        )}

                        <button
                          onClick={cerrarModalAccionesPedido}
                          style={{
                            padding: '12px 20px',
                            border: '2px solid #6b7280',
                            borderRadius: '8px',
                            backgroundColor: 'transparent',
                            color: '#6b7280',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          ✖️ Cerrar
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Modal: seleccionar empresa para generar autorización */}
                {modalEmpresaAutorizacion.open && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1001
                  }}>
                    <div style={{
                      background: 'white',
                      padding: '24px',
                      borderRadius: '12px',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                      width: '380px',
                      maxWidth: '90vw'
                    }}>
                      <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                        🔐 Empresa para la autorización
                      </h3>
                      <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280' }}>
                        Seleccione la empresa para la cual se generará la autorización:
                      </p>
                      <select
                        value={modalEmpresaAutorizacion.empresaSeleccionada}
                        onChange={(e) => setModalEmpresaAutorizacion(prev => ({ ...prev, empresaSeleccionada: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          marginBottom: '16px',
                          border: '2px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        {EMPRESAS_VOUCHER.map(emp => (
                          <option key={emp} value={emp}>🏢 {emp}</option>
                        ))}
                      </select>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setModalEmpresaAutorizacion({ open: false, empresaSeleccionada: EMPRESAS_VOUCHER[0] || '' })}
                          style={{
                            padding: '10px 18px',
                            border: '2px solid #6b7280',
                            borderRadius: '8px',
                            background: 'transparent',
                            color: '#6b7280',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={() => generarAutorizacionParaPedidoEnCurso(modalEmpresaAutorizacion.empresaSeleccionada)}
                          style={{
                            padding: '10px 18px',
                            border: 'none',
                            borderRadius: '8px',
                            background: '#2563eb',
                            color: 'white',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                        >
                          🔐 Generar
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Modal de voucher */}
                {modalVoucher.open && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                  }}>
                    <div style={{
                      background: 'white',
                      padding: '30px',
                      borderRadius: '12px',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      width: '600px',
                      maxWidth: '90vw',
                      maxHeight: '90vh',
                      overflowY: 'auto'
                    }}>
                      <h3 style={{
                        margin: '0 0 20px 0',
                        color: '#1f2937',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        textAlign: 'center'
                      }}>
                        🎫 Generar Voucher Corporativo
                      </h3>

                      {/* Información del operador y número de autorización */}
                      <div style={{
                        background: '#f3f4f6',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          <div>
                            <label style={{
                              display: 'block',
                              marginBottom: '5px',
                              fontWeight: 'bold',
                              color: '#374151',
                              fontSize: '12px',
                              textAlign: 'center'
                            }}>
                              🔢 N° Autorización
                            </label>
                            <div style={{
                              padding: '8px 12px',
                              background: '#3b82f6',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              textAlign: 'center'
                            }}>
                              {modalVoucher.voucher.numeroAutorizacion || siguienteNumeroAutorizacion}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Selector de tipo de voucher */}
                      <div style={{
                        background: '#f8fafc',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '10px',
                          fontWeight: 'bold',
                          color: '#374151',
                          fontSize: '14px'
                        }}>
                          📋 Tipo de Voucher
                        </label>
                        <div style={{
                          display: 'flex',
                          gap: '15px'
                        }}>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            backgroundColor: modalVoucher.voucher.tipoVoucher === 'electronico' ? '#dbeafe' : 'transparent',
                            border: modalVoucher.voucher.tipoVoucher === 'electronico' ? '2px solid #3b82f6' : '2px solid #d1d5db'
                          }}>
                            <input
                              type="radio"
                              name="tipoVoucher"
                              value="electronico"
                              checked={modalVoucher.voucher.tipoVoucher === 'electronico'}
                              onChange={(e) => setModalVoucher(prev => ({
                                ...prev,
                                voucher: { ...prev.voucher, tipoVoucher: e.target.value }
                              }))}
                              style={{ margin: 0 }}
                            />
                            <span style={{ fontWeight: '500' }}>💻 Voucher Electrónico</span>
                          </label>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            backgroundColor: modalVoucher.voucher.tipoVoucher === 'fisico' ? '#fef3c7' : 'transparent',
                            border: modalVoucher.voucher.tipoVoucher === 'fisico' ? '2px solid #f59e0b' : '2px solid #d1d5db'
                          }}>
                            <input
                              type="radio"
                              name="tipoVoucher"
                              value="fisico"
                              checked={modalVoucher.voucher.tipoVoucher === 'fisico'}
                              onChange={(e) => setModalVoucher(prev => ({
                                ...prev,
                                voucher: { ...prev.voucher, tipoVoucher: e.target.value }
                              }))}
                              style={{ margin: 0 }}
                            />
                            <span style={{ fontWeight: '500' }}>📄 Voucher Físico</span>
                          </label>
                        </div>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '20px',
                        marginBottom: '20px'
                      }}>
                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            📅 Fecha y Hora de Inicio
                          </label>
                          <input
                            type="datetime-local"
                            value={modalVoucher.voucher.fechaHoraInicio}
                            onChange={(e) => setModalVoucher(prev => ({
                              ...prev,
                              voucher: { ...prev.voucher, fechaHoraInicio: e.target.value }
                            }))}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            📅 Fecha y Hora Final
                          </label>
                          <input
                            type="datetime-local"
                            value={modalVoucher.voucher.fechaHoraFinal}
                            onChange={(e) => setModalVoucher(prev => ({
                              ...prev,
                              voucher: { ...prev.voucher, fechaHoraFinal: e.target.value }
                            }))}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={{
                            display: 'block',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            👤 Nombre del Cliente <span style={{ color: '#ef4444' }}>*</span>
                          </label>
                          <input
                            type="text"
                            value={modalVoucher.voucher.nombreCliente}
                            onChange={(e) => setModalVoucher(prev => ({
                              ...prev,
                              voucher: { ...prev.voucher, nombreCliente: e.target.value }
                            }))}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                            placeholder="Ingrese el nombre del cliente"
                          />
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            💰 Valor <span style={{ color: '#ef4444' }}>*</span>
                          </label>
                          <input
                            type="text"
                            value={modalVoucher.voucher.valor}
                            onChange={(e) => setModalVoucher(prev => ({
                              ...prev,
                              voucher: { ...prev.voucher, valor: e.target.value }
                            }))}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                            placeholder="Ingrese el valor"
                          />
                        </div>

                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            🚗 Número de Unidad
                          </label>
                          <input
                            type="text"
                            value={modalVoucher.voucher.numeroUnidad}
                            readOnly
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px',
                              backgroundColor: '#f9fafb',
                              color: '#6b7280',
                              cursor: 'not-allowed'
                            }}
                          />
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={{
                            display: 'block',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            🏢 Empresa <span style={{ color: '#ef4444' }}>*</span>
                          </label>
                          <select
                            value={modalVoucher.voucher.empresa}
                            onChange={(e) => setModalVoucher(prev => ({
                              ...prev,
                              voucher: { ...prev.voucher, empresa: e.target.value }
                            }))}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px',
                              backgroundColor: 'white',
                              cursor: 'pointer'
                            }}
                            required
                            autoComplete="off"
                          >
                            <option value="">Seleccione una empresa</option>
                            {empresasVoucher.map((empresa, index) => (
                              <option key={index} value={empresa}>
                                {empresa}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Campo condicional para número de voucher físico */}
                        {modalVoucher.voucher.tipoVoucher === 'fisico' && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{
                              display: 'block',
                              marginBottom: '5px',
                              fontWeight: 'bold',
                              color: '#374151'
                            }}>
                              🔢 Número de Voucher Físico <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <input
                              type="text"
                              value={modalVoucher.voucher.numeroVoucherFisico}
                              onChange={(e) => setModalVoucher(prev => ({
                                ...prev,
                                voucher: { ...prev.voucher, numeroVoucherFisico: e.target.value }
                              }))}
                              style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                              placeholder="Ingrese el número del voucher físico"
                              autoComplete="off"
                            />
                          </div>
                        )}

                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={{
                            display: 'block',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            📍 Dirección <span style={{ color: '#ef4444' }}>*</span>
                          </label>
                          <input
                            type="text"
                            value={modalVoucher.voucher.direccion}
                            onChange={(e) => setModalVoucher(prev => ({
                              ...prev,
                              voucher: { ...prev.voucher, direccion: e.target.value }
                            }))}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                            autoComplete="off"
                          />
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={{
                            display: 'block',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            🎯 Destino <span style={{ color: '#ef4444' }}>*</span>
                          </label>
                          <input
                            type="text"
                            value={modalVoucher.voucher.destino}
                            onChange={(e) => setModalVoucher(prev => ({
                              ...prev,
                              voucher: { ...prev.voucher, destino: e.target.value }
                            }))}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                            placeholder="Ingrese el destino (campo obligatorio)"
                            required
                            autoComplete="off"
                          />
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={{
                            display: 'block',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            color: '#374151'
                          }}>
                            📝 Motivo
                          </label>
                          <textarea
                            value={modalVoucher.voucher.motivo}
                            onChange={(e) => setModalVoucher(prev => ({
                              ...prev,
                              voucher: { ...prev.voucher, motivo: e.target.value }
                            }))}
                            rows="3"
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px',
                              resize: 'vertical'
                            }}
                            autoComplete="off"
                          />
                        </div>
                      </div>

                      <div style={{
                        display: 'flex',
                        gap: '10px',
                        justifyContent: 'center'
                      }}>
                        <button
                          onClick={guardarVoucherCorporativo}
                          style={{
                            padding: '12px 24px',
                            border: 'none',
                            borderRadius: '8px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#059669';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#10b981';
                          }}
                        >
                          💾 Guardar Voucher
                        </button>

                        <button
                          onClick={cerrarModalVoucher}
                          style={{
                            padding: '12px 24px',
                            border: '2px solid #6b7280',
                            borderRadius: '8px',
                            backgroundColor: 'transparent',
                            color: '#6b7280',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          ❌ Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PANEL DERECHO: Mapa */}
      {mapaDashboardVisible && (
        <div style={{
          flex: `0 0 ${mapaDashboardAncho}`,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.3s ease',
          borderLeft: '1px solid #e5e7eb'
        }}>
          {/* Controles de Mapa */}
          <div style={{
            position: 'absolute',
            top: 15,
            right: 15,
            zIndex: 1000,
            display: 'flex',
            gap: '8px'
          }}>


            {/* Botón para ocultar mapa */}
            <button
              onClick={() => setMapaDashboardVisible(false)}
              style={{
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '8px 12px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                fontWeight: 'bold',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ❌ Ocultar
            </button>
          </div>

          <RealTimeLocationMap
            viajesAsignados={(viajesAsignados || []).filter(v => v?.origenColeccion === 'pedidosDisponibles')}
            alertaActiva={alertaActiva}
            focusPedidoId={focusPedidoMapaId}
          />
        </div>
      )}

      {/* Botón flotante para mostrar mapa si está oculto */}
      {!mapaDashboardVisible && (
        <button
          onClick={() => setMapaDashboardVisible(true)}
          style={{
            position: 'fixed',
            bottom: 30,
            right: 30,
            zIndex: 1000,
            background: '#1e3a8a',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}
          title="Mostrar Mapa"
        >
          📍
        </button>
      )}
    </div>
  );
}

// Otros componentes de contenido
function ReservasContent({ operadorAutenticado }) {
  const [reservas, setReservas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [unidadAsignar, setUnidadAsignar] = useState({}); // {reservaId: valor}
  const [mostrarAsignadas, setMostrarAsignadas] = useState(false); // filtro: false = no asignadas (por defecto), true = asignadas
  const [eliminandoId, setEliminandoId] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'reservas'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setReservas(arr);
      setCargando(false);
    }, (e) => {
      console.error('Error cargando reservas:', e);
      setCargando(false);
    });
    return () => unsub();
  }, []);

  // Helpers para enviar WhatsApp al conductor y mensaje al cliente (mismas APIs que en TaxiForm)
  const postJsonReservas = async (url, params) => {
    const res = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json', ...(await cabeceraSesion()) }, body: JSON.stringify(params) });
    return res;
  };
  const getWhatsAppSendUrlReservas = () => {
    // El servidor del panel decide el proveedor (WhatsApp Web o la API anterior)
    if (process.env.REACT_APP_WHATSAPP_SEND_URL) return process.env.REACT_APP_WHATSAPP_SEND_URL;
    return '/api/whatsapp/send';
  };
  const getSendImageMessageUrlReservas = () => {
    if (process.env.REACT_APP_SEND_IMAGE_MESSAGE_URL) return process.env.REACT_APP_SEND_IMAGE_MESSAGE_URL;
    return '/api/send-image-message';
  };
  const normalizarTelefonoReservas = (telefonoRaw) => {
    const digits = (telefonoRaw ?? '').toString().replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('593')) return digits;
    if (digits.length === 10 && digits.startsWith('0')) return `593${digits.substring(1)}`;
    if (digits.length === 9 && !digits.startsWith('0')) return `593${digits}`;
    return digits;
  };
  const formatearFechaReserva = (date) => {
    try {
      const d = new Date(date?.seconds ? date.seconds * 1000 : date);
      return d.toLocaleString('es-EC', { timeZone: ECUADOR_TIMEZONE, dateStyle: 'short', timeStyle: 'short' });
    } catch { return ''; }
  };

  // Las coordenadas guardadas pueden venir como "lat,lng", como GeoPoint,
  // como {lat,lng} o vacías. Siempre devolver strings (nunca undefined,
  // Firestore rechaza undefined y hacía fallar la asignación).
  const parsearCoordenadasReserva = (valor) => {
    try {
      if (!valor) return { latitud: '', longitud: '' };
      if (typeof valor === 'string') {
        const partes = valor.split(',').map(s => s.trim()).filter(Boolean);
        if (partes.length < 2) return { latitud: '', longitud: '' };
        return { latitud: partes[0], longitud: partes[1] };
      }
      if (Array.isArray(valor) && valor.length >= 2) {
        return { latitud: String(valor[0] ?? ''), longitud: String(valor[1] ?? '') };
      }
      if (typeof valor === 'object') {
        const lat = valor.latitude ?? valor.lat ?? valor._lat ?? '';
        const lng = valor.longitude ?? valor.lng ?? valor.lon ?? valor._long ?? '';
        return { latitud: lat === '' ? '' : String(lat), longitud: lng === '' ? '' : String(lng) };
      }
      return { latitud: '', longitud: '' };
    } catch {
      return { latitud: '', longitud: '' };
    }
  };

  // Eliminar claves con undefined: Firestore lanza error y abortaba toda la asignación
  const limpiarUndefined = (obj) => {
    const out = {};
    Object.keys(obj).forEach(k => { if (obj[k] !== undefined) out[k] = obj[k]; });
    return out;
  };

  const asignarUnidad = async (reserva) => {
    try {
      const unidad = (unidadAsignar[reserva.id] || '').trim();
      if (!unidad) return;

      // Buscar conductor por unidad
      const conductoresQuery = query(collection(db, 'conductores'), where('unidad', '==', unidad));
      const conductoresSnapshot = await getDocs(conductoresQuery);
      if (conductoresSnapshot.empty) {
        alert(`No se encontró un conductor con la unidad ${unidad}.`);
        return;
      }
      const conductorData = conductoresSnapshot.docs[0].data();

      // Validar estatus
      const estatusValor = conductorData && 'estatus' in conductorData ? conductorData.estatus : true;
      const estatusBool = typeof estatusValor === 'string' ? estatusValor.toLowerCase() !== 'false' : Boolean(estatusValor);
      if (!estatusBool) {
        alert(`La unidad ${unidad} está suspendida/inactiva. No se puede asignar.`);
        return;
      }

      // Preparar documento para pedidoEnCurso
      const { latitud, longitud } = parsearCoordenadasReserva(reserva.coordenadas);
      const tieneCoordenadas = Boolean(latitud && longitud);
      const fecha = new Date();
      const clave = Math.random().toString(36).substring(2, 8).toUpperCase();

      // Generar autorización si no existe y es empresa corporativa
      let autorizacionFinal = reserva.autorizacion || null;
      if (!autorizacionFinal && reserva.tipoEmpresa && reserva.tipoEmpresa !== 'Efectivo') {
        // Crear una función local para obtener siguiente autorización
        const obtenerSiguienteAutorizacionLocal = async () => {
          try {
            const secuenciaRef = doc(db, 'autorizacionSecuencia', 'contador');
            const secuenciaDoc = await getDoc(secuenciaRef);

            let siguienteNumero = 200;

            if (secuenciaDoc.exists()) {
              const data = secuenciaDoc.data();
              siguienteNumero = (data.numero || 199) + 1;
            }

            await setDoc(secuenciaRef, {
              numero: siguienteNumero,
              ultimaActualizacion: new Date()
            }, { merge: true });

            return siguienteNumero;
          } catch (error) {
            console.error('❌ Error al obtener siguiente autorización:', error);
            return 200;
          }
        };

        autorizacionFinal = await obtenerSiguienteAutorizacionLocal();
        console.log(`✅ Nueva autorización generada para reserva: ${autorizacionFinal}`);
      }

      const pedidoEnCursoData = {
        clave,
        codigo: reserva.nombreCliente || '',
        nombreCliente: reserva.nombreCliente || '',
        telefono: reserva.telefonoCompleto || reserva.telefono || '',
        telefonoCompleto: reserva.telefonoCompleto || '',
        direccion: reserva.direccion || '',
        base: '0',
        destino: reserva.destino || '',
        fecha,
        estado: 'Aceptado',
        pedido: 'Aceptado',
        idConductor: `conductor_${Date.now()}_${Math.random().toString(36).substring(2, 8)}@manual.com`,
        correo: conductorData.correo || conductorData.id || '',
        nombre: conductorData.nombre || '',
        nombreConductor: conductorData.nombre || '',
        placa: conductorData.placa || '',
        color: conductorData.color || '', // Solo una vez, elimina duplicados si existen
        telefonoConductor: conductorData.telefono || '',
        foto: conductorData.foto || '',
        tokenConductor: conductorData.token || '',
        tiempo: '0',
        numeroUnidad: unidad,
        unidad: unidad,
        minutos: 0,
        distancia: '0.00 Mts',
        latitudConductor: '',
        longitudConductor: '',
        latitud,
        longitud,
        latitudDestino: '',
        longitudDestino: '',
        sector: reserva.direccion || '',
        tipoPedido: 'Manual',
        valor: '',
        central: false,
        coorporativo: false,
        llegue: false,
        puerto: '3020',
        randon: clave,
        rango: tieneCoordenadas ? '1' : '0',
        viajes: unidad,
        tarifaSeleccionada: true,
        modoSeleccion: 'manual',
        modoAsignacion: 'manual',
        operadora: operadorAutenticado ? operadorAutenticado.nombre : 'Sin operador',
        origenReserva: true,
        reservaId: reserva.id,
        motivoReserva: reserva.motivo || '', // Preservar el motivo de la reserva
        // Información específica de la reserva
        fechaHoraReserva: reserva.fechaHoraReserva || null, // Fecha y hora programada de la reserva
        fechaCreacionReserva: reserva.createdAt || null, // Fecha cuando se creó la reserva
        // Información de empresa y autorización preservada de la reserva
        tipoEmpresa: reserva.tipoEmpresa || 'Efectivo',
        autorizacion: autorizacionFinal,
      };

      // Notificaciones de WhatsApp: se envían DESPUÉS de guardar el viaje.
      // Antes se enviaban primero y, si la escritura fallaba, el conductor y el
      // cliente ya habían recibido mensajes de una reserva que nunca se asignó.
      const enviarNotificacionesAsignacion = async () => {
      // 1. Enviar al conductor: datos de la reserva y del cliente (misma API WhatsApp)
      try {
        const telefonoConductorRaw = conductorData.telefono || conductorData.celular || '';
        if (telefonoConductorRaw) {
          let telLimpio = telefonoConductorRaw.replace(/\D/g, '');
          if (telLimpio.startsWith('593')) telLimpio = telLimpio.substring(3);
          if (telLimpio.startsWith('0')) telLimpio = telLimpio.substring(1);
          const toConductor = normalizarTelefonoReservas(`593${telLimpio}`);
          const fechaHoraTxt = formatearFechaReserva(reserva.fechaHoraReserva);
          let msgConductor = `📅 *Reserva asignada*\n\n👤 *Cliente:* ${reserva.nombreCliente || 'N/D'}\n📞 *Teléfono:* ${normalizarTelefonoReservas(reserva.telefonoCompleto || reserva.telefono) || 'N/D'}\n📍 *Dirección:* ${reserva.direccion || 'N/D'}\n🎯 *Destino:* ${reserva.destino || 'N/D'}\n🕒 *Fecha/Hora:* ${fechaHoraTxt || 'N/D'}\n📝 *Motivo:* ${(reserva.motivo || '').trim() || 'N/D'}\n🚕 *Unidad asignada:* ${unidad}\n\nPor favor, atiende esta reserva.`;
          if (autorizacionFinal != null && autorizacionFinal !== '') msgConductor += `\n🔑 *Autorización:* ${autorizacionFinal}`;
          await postJsonReservas(getWhatsAppSendUrlReservas(), { to: toConductor, message: msgConductor });
          console.log('✅ Mensaje de reserva enviado al conductor.');
        }
      } catch (e) { console.warn('Error enviar reserva al conductor:', e); }

      // 2. Enviar al cliente: datos de la reserva + conductor (API send-image-message)
      try {
        const telCliente = normalizarTelefonoReservas(reserva.telefonoCompleto || reserva.telefono);
        if (telCliente) {
          const fechaHoraTxt = formatearFechaReserva(reserva.fechaHoraReserva);
          let telCond = (conductorData.telefono || conductorData.celular || '').toString().replace(/\D/g, '');
          if (telCond.startsWith('593')) telCond = '0' + telCond.slice(3);
          else if (telCond.length === 9 && !telCond.startsWith('0')) telCond = '0' + telCond;
          const msgCliente = `📅 *Tu reserva está confirmada*\n\n🕒 *Fecha/Hora:* ${fechaHoraTxt || 'N/D'}\n🎯 *Destino:* ${reserva.destino || 'N/D'}\n📝 *Motivo:* ${(reserva.motivo || '').trim() || 'N/D'}\n\n🚕 *Conductor asignado:*\n👤 ${conductorData.nombre || 'N/D'}\n📱 Teléfono: ${telCond || 'N/D'}\n🚕 Unidad: ${unidad}\n🆔 Placa: ${conductorData.placa || 'N/D'}\n🎨 Color: ${conductorData.color || 'N/D'}\n\nGracias por preferir Coop. Plaza Del Valle. ¡Buen viaje!`;
          const imageUrlReserva = (conductorData.foto && String(conductorData.foto).trim()) ? String(conductorData.foto).trim() : 'https://firebasestorage.googleapis.com/v0/b/appecdriver.appspot.com/o/images%2Fbot.jpg?alt=media&token=2b7dd095-f73b-4247-87c3-f76fd04fb4a6';
          await postJsonReservas(getSendImageMessageUrlReservas(), { phone: telCliente, message: msgCliente, imageUrl: imageUrlReserva });
          console.log('✅ Mensaje de reserva enviado al cliente.');
        }
      } catch (e) { console.warn('Error enviar reserva al cliente:', e); }
      };

      const docRef = await addDoc(collection(db, 'todosLosViajes'), limpiarUndefined({
        ...pedidoEnCursoData,
        fechaAsignacion: new Date(),
        origenColeccion: 'reserva'
      }));
      await updateDoc(docRef, { id: docRef.id });
      console.log('✅ Reserva asignada y guardada en todosLosViajes:', docRef.id);

      // Ya está guardado: ahora sí notificar por WhatsApp
      await enviarNotificacionesAsignacion();

      // Duplicado en NotificaciOnenCurso
      try {
        await addDoc(collection(db, 'NotificaciOnenCurso'), limpiarUndefined({
          ...pedidoEnCursoData,
          id: docRef.id,
          fechaNotificacion: new Date(),
          estadoNotificacion: 'pendiente'
        }));
      } catch { }

      // Eliminar de reservas (documento ya está en todosLosViajes)
      try {
        await deleteDoc(doc(db, 'reservas', reserva.id));
      } catch (e) {
        console.warn('Error al eliminar reserva:', e);
      }
      setUnidadAsignar(prev => {
        const next = { ...prev };
        delete next[reserva.id];
        return next;
      });

      alert(`Reserva registrada en Viajes con la unidad ${unidad}. Se envió notificación al cliente y al conductor.`);
    } catch (error) {
      console.error('Error al asignar unidad a reserva:', error);
      alert(`Error al asignar unidad a la reserva.\n\nDetalle: ${error?.message || error}`);
    }
  };

  const formatearFecha = (date) => {
    try {
      const d = new Date(date?.seconds ? date.seconds * 1000 : date);
      return d.toLocaleString('es-EC', { timeZone: ECUADOR_TIMEZONE });
    } catch {
      return '';
    }
  };

  const eliminarReserva = async (reserva) => {
    try {
      if (!reserva?.id) return;
      const estado = String(reserva?.estado || '').toLowerCase();
      const msg =
        `¿Eliminar esta reserva?\n\n` +
        `Cliente: ${reserva?.nombreCliente || '-'}\n` +
        `Teléfono: ${reserva?.telefonoCompleto || reserva?.telefono || '-'}\n` +
        `Fecha/Hora: ${formatearFecha(reserva?.fechaHoraReserva) || '-'}\n` +
        (estado === 'asignada' ? `\nEsta reserva está ASIGNADA (Unidad ${reserva?.unidadAsignada || '-'}) y se eliminará igual.` : '');

      const ok = window.confirm(msg);
      if (!ok) return;

      setEliminandoId(reserva.id);
      await deleteDoc(doc(db, 'reservas', reserva.id));
      setUnidadAsignar(prev => {
        const next = { ...prev };
        delete next[reserva.id];
        return next;
      });
    } catch (error) {
      console.error('Error al eliminar reserva:', error);
      alert('Error al eliminar la reserva.');
    } finally {
      setEliminandoId(null);
    }
  };

  // Derivados para filtro y conteos
  const toDate = (v) => {
    try {
      return new Date(v?.seconds ? v.seconds * 1000 : v);
    } catch {
      return null;
    }
  };
  const ahora = new Date();
  const reservasAsignadas = reservas.filter(r => String(r?.estado || '').toLowerCase() === 'asignada');
  const reservasAsignadasFuturas = reservasAsignadas.filter(r => {
    const d = toDate(r.fechaHoraReserva);
    return d && d >= ahora;
  });
  const reservasNoAsignadas = reservas.filter(r => String(r?.estado || '').toLowerCase() !== 'asignada');
  const listaMostrada = mostrarAsignadas ? reservasAsignadasFuturas : reservasNoAsignadas;

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ margin: '0 0 16px 0' }}>📅 Reservas</h2>

      {/* Controles de filtro */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          onClick={() => setMostrarAsignadas(false)}
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid #d1d5db',
            cursor: 'pointer',
            fontWeight: 700,
            background: !mostrarAsignadas ? '#3b82f6' : 'white',
            color: !mostrarAsignadas ? 'white' : '#111827'
          }}
        >
          No asignadas ({reservasNoAsignadas.length})
        </button>
        <button
          onClick={() => setMostrarAsignadas(true)}
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid #d1d5db',
            cursor: 'pointer',
            fontWeight: 700,
            background: mostrarAsignadas ? '#3b82f6' : 'white',
            color: mostrarAsignadas ? 'white' : '#111827'
          }}
        >
          Asignadas ({reservasAsignadasFuturas.length})
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        {cargando ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Cargando reservas...</div>
        ) : listaMostrada.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
            {mostrarAsignadas ? 'No hay reservas asignadas futuras.' : 'No hay reservas no asignadas.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: 12, textAlign: 'left' }}>Cliente</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Dirección</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Destino</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Fecha/Hora</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Motivo</th>
                  <th style={{ padding: 12, textAlign: 'center' }}>Unidad</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Estado</th>
                  <th style={{ padding: 12, textAlign: 'center' }}>Asignar Unidad</th>
                  <th style={{ padding: 12, textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {listaMostrada.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: 12 }}>
                      <div style={{ fontWeight: 600 }}>{r.nombreCliente}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{r.telefono}</div>
                    </td>
                    <td style={{ padding: 12 }}>{r.direccion}</td>
                    <td style={{ padding: 12 }}>{r.destino || '-'}</td>
                    <td style={{ padding: 12 }}>{formatearFecha(r.fechaHoraReserva)}</td>
                    <td style={{ padding: 12 }}>{r.motivo}</td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      {String(r?.estado || '').toLowerCase() === 'asignada' && r.unidadAsignada ? (
                        <span style={{ padding: '4px 8px', borderRadius: 4, background: '#eef2ff', color: '#3730a3', fontSize: 12, fontWeight: 700 }}>{r.unidadAsignada}</span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 12 }}>
                      <span style={{ padding: '4px 8px', borderRadius: 4, background: '#e5f9f1', color: '#059669', fontSize: 12, fontWeight: 600 }}>{r.estado}</span>
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      {String(r?.estado || '').toLowerCase() === 'asignada' ? (
                        <div style={{
                          display: 'inline-block',
                          padding: '6px 10px',
                          borderRadius: 6,
                          background: '#f3f4f6',
                          color: '#111827',
                          fontWeight: 700
                        }}>
                          Unidad {r.unidadAsignada || '-'}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                          <input
                            type="text"
                            placeholder="Unidad"
                            value={unidadAsignar[r.id] || ''}
                            onChange={(e) => setUnidadAsignar(prev => ({ ...prev, [r.id]: e.target.value }))}
                            disabled={mostrarAsignadas}
                            style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 90, background: mostrarAsignadas ? '#f3f4f6' : 'white' }}
                          />
                          <button
                            onClick={() => asignarUnidad(r)}
                            disabled={mostrarAsignadas}
                            style={{ padding: '8px 12px', background: mostrarAsignadas ? '#9ca3af' : '#10b981', color: 'white', border: 'none', borderRadius: 6, cursor: mostrarAsignadas ? 'not-allowed' : 'pointer', fontWeight: 700 }}
                          >
                            Asignar
                          </button>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      <button
                        onClick={() => eliminarReserva(r)}
                        disabled={eliminandoId === r.id}
                        style={{
                          padding: '8px 12px',
                          background: eliminandoId === r.id ? '#9ca3af' : '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: eliminandoId === r.id ? 'not-allowed' : 'pointer',
                          fontWeight: 800
                        }}
                        title="Eliminar reserva"
                      >
                        {eliminandoId === r.id ? 'Eliminando…' : 'Eliminar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
function ConductoresContent() {
  const [conductores, setConductores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editIndex, setEditIndex] = useState(null);
  const [editData, setEditData] = useState({});
  const [viewMode, setViewMode] = useState('cards'); // 'cards' o 'table'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState('nombre'); // 'nombre' o 'unidad'
  const [statusFilter, setStatusFilter] = useState('todos'); // 'todos', 'nuevos', 'activos', 'inactivos'
  const [categoryFilter] = useState('todas');
  const fileInputRef = useRef(null);

  // eslint-disable-next-line no-unused-vars
  const CATEGORIAS_CONDUCTOR = [
    "GRÚAS",
    "DESPACHADORES DE GAS",
    "TAXIS",
    "CAMIONETAS DE CARGA",
    "CAMIONES DE CARGA",
    "MOTORIZADOS"
  ];

  // Helpers para enviar WhatsApp al conductor (misma API que en TaxiForm)
  const postJsonConductores = async (url, params) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await cabeceraSesion()) },
      body: JSON.stringify(params)
    });
    return res;
  };
  const getWhatsAppSendUrlConductores = () => {
    // El servidor del panel decide el proveedor (WhatsApp Web o la API anterior)
    if (process.env.REACT_APP_WHATSAPP_SEND_URL) return process.env.REACT_APP_WHATSAPP_SEND_URL;
    return '/api/whatsapp/send';
  };
  const normalizarTelefonoParaWhatsAppConductores = (telefonoRaw) => {
    const digits = (telefonoRaw ?? '').toString().replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('593')) return digits;
    if (digits.length === 10 && digits.startsWith('0')) return `593${digits.substring(1)}`;
    if (digits.length === 9 && !digits.startsWith('0')) return `593${digits}`;
    return digits;
  };

  // Modal para cambio de estatus con motivo (Conductores)
  const [modalCambioEstatus, setModalCambioEstatus] = useState({
    open: false,
    conductor: null,
    motivo: '',
    nuevoEstatus: false,
    suspensionHasta: ''
  });

  const abrirModalCambioEstatus = (conductor) => {
    setModalCambioEstatus({
      open: true,
      conductor,
      nuevoEstatus: !conductor.estatus,
      motivo: '',
      suspensionHasta: ''
    });
  };

  const cerrarModalCambioEstatus = () => {
    setModalCambioEstatus({ open: false, conductor: null, nuevoEstatus: null, motivo: '', suspensionHasta: '' });
  };

  const confirmarCambioEstatus = async () => {
    if (!modalCambioEstatus.conductor) return;
    const { conductor, nuevoEstatus, motivo, suspensionHasta } = modalCambioEstatus;

    let motivoFinal = motivo;

    // Si es suspensión: motivo obligatorio; fecha "Suspender hasta" opcional
    let suspensionHastaDate = null;
    if (nuevoEstatus === false) {
      if (!motivoFinal || motivoFinal.trim().length < 3) {
        alert('Por favor, ingresa un motivo (mínimo 3 caracteres).');
        return;
      }
      if (suspensionHasta && suspensionHasta.trim()) {
        const parsed = new Date(suspensionHasta);
        if (!isNaN(parsed.getTime())) suspensionHastaDate = parsed;
      }
    } else {
      // ACTIVACION: Motivo opcional
      if (!motivoFinal || motivoFinal.trim() === '') {
        motivoFinal = 'Reactivación por administrador';
      }
    }

    try {
      // Actualizar en Firestore
      const conductorRef = doc(db, 'conductores', conductor.id);
      await updateDoc(conductorRef, {
        estatus: nuevoEstatus,
        motivoCambioEstatus: motivoFinal.trim(),
        fechaCambioEstatus: new Date(),
        suspensionHasta: nuevoEstatus ? null : suspensionHastaDate
      });

      // Registrar evento en colección de auditoría: StatusUnidades (sin espacios)
      try {
        let changedBy = null;
        let operadorNombre = null;
        try {
          const { auth } = await import('../firebaseConfig');
          changedBy = auth?.currentUser?.email || null;
        } catch { }
        try {
          const almacenado = localStorage.getItem('operadorAutenticado');
          if (almacenado) {
            const op = JSON.parse(almacenado);
            operadorNombre = op?.nombre || null;
          }
        } catch { }

        const accion = nuevoEstatus ? 'Activación' : 'Suspensión';
        const registro = {
          conductorId: conductor.id,
          unidad: conductor.unidad || conductor.numeroUnidad || null,
          nombre: conductor.nombre || '',
          placa: conductor.placa || '',
          color: conductor.color || '',
          estado: nuevoEstatus ? 'Activo' : 'Inactivo',
          estatus: Boolean(nuevoEstatus),
          motivo: motivoFinal.trim(),
          accion,
          fecha: new Date(),
          suspensionHasta: suspensionHastaDate || null,
          changedBy,
          operadorNombre
        };
        await addDoc(collection(db, 'StatusUnidades'), registro);
      } catch (eLog) {
        console.warn('No se pudo registrar cambio en StatusUnidades:', eLog);
      }

      // Actualizar estado local
      setConductores(prev => prev.map(c => c.id === conductor.id ? { ...c, estatus: nuevoEstatus } : c));

      // Enviar notificación al conductor por WhatsApp (misma API que envío de datos al conductor)
      try {
        const telefonoConductorRaw = conductor.telefono || conductor.celular || '';
        if (telefonoConductorRaw) {
          let telefonoLimpio = telefonoConductorRaw.replace(/\D/g, '');
          if (telefonoLimpio.startsWith('593')) telefonoLimpio = telefonoLimpio.substring(3);
          if (telefonoLimpio.startsWith('0')) telefonoLimpio = telefonoLimpio.substring(1);
          const toConductor = normalizarTelefonoParaWhatsAppConductores(`593${telefonoLimpio}`);
          const unidadTxt = conductor.unidad || conductor.numeroUnidad || '';
          let mensajeConductor = '';
          if (nuevoEstatus) {
            mensajeConductor = `✅ *Tu unidad ha sido reactivada.*\n\n🚕 Unidad: ${unidadTxt}\n👤 ${conductor.nombre || ''}\n\nMotivo: ${motivoFinal.trim()}`;
          } else {
            let hastaTexto = '';
            if (suspensionHastaDate instanceof Date) {
              const dd = String(suspensionHastaDate.getDate()).padStart(2, '0');
              const mm = String(suspensionHastaDate.getMonth() + 1).padStart(2, '0');
              const yyyy = suspensionHastaDate.getFullYear();
              const hh = String(suspensionHastaDate.getHours()).padStart(2, '0');
              const min = String(suspensionHastaDate.getMinutes()).padStart(2, '0');
              hastaTexto = `\n⏰ Válido hasta: ${dd}/${mm}/${yyyy} ${hh}:${min}`;
            }
            mensajeConductor = `⛔ *Has sido suspendido.*\n\n🚕 Unidad: ${unidadTxt}\n👤 ${conductor.nombre || ''}\n\n📝 Motivo: ${motivoFinal.trim()}${hastaTexto}`;
          }
          const apiUrl = getWhatsAppSendUrlConductores();
          await postJsonConductores(apiUrl, { to: toConductor, message: mensajeConductor });
          console.log('✅ Notificación de estatus enviada al conductor por WhatsApp.');
        }
      } catch (errWhatsApp) {
        console.warn('No se pudo enviar WhatsApp al conductor:', errWhatsApp);
      }

      // Enviar notificación a API externa (grupo, no bloqueante)
      try {
        const apiUrl = process.env.REACT_APP_ESTATUS_API_URL || 'http://84.46.245.131:3001/send/group/message';
        const groupTo = process.env.REACT_APP_GROUP_TO_ID || '120363343871245265';
        const accion = nuevoEstatus ? 'Activación' : 'Suspensión';
        // Obtener operador autenticado (nombre desde localStorage o email desde auth)
        let operadorStr = '';
        try {
          const almacenado = localStorage.getItem('operadorAutenticado');
          if (almacenado) {
            const op = JSON.parse(almacenado);
            if (op?.nombre) operadorStr = op.nombre;
          }
        } catch { }
        if (!operadorStr) {
          try {
            const { auth } = await import('../firebaseConfig');
            operadorStr = auth?.currentUser?.email || '';
          } catch { }
        }
        const operadorTexto = operadorStr ? ` por ${operadorStr}` : '';

        let hastaTexto = '';
        if (!nuevoEstatus && suspensionHastaDate instanceof Date) {
          const dd = String(suspensionHastaDate.getDate()).padStart(2, '0');
          const mm = String(suspensionHastaDate.getMonth() + 1).padStart(2, '0');
          const yyyy = suspensionHastaDate.getFullYear();
          const hh = String(suspensionHastaDate.getHours()).padStart(2, '0');
          const min = String(suspensionHastaDate.getMinutes()).padStart(2, '0');
          // Fecha y hora por separado en el mensaje
          hastaTexto = ` Hasta: Fecha ${dd}-${mm}-${yyyy}. Hora ${hh}:${min}`;
        }

        const mensaje = `Unidad ${conductor.unidad || conductor.numeroUnidad || ''} - ${conductor.nombre || ''}: ${accion}${operadorTexto}. Motivo: ${motivoFinal.trim()}.${hastaTexto}`;

        await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ to: groupTo, message: mensaje }).toString()
        });
      } catch (errApi) {
        console.warn('No se pudo enviar el mensaje a la API externa:', errApi);
      }

      cerrarModalCambioEstatus();
      alert(`✅ Estatus ${nuevoEstatus ? 'activado' : 'suspendido'} correctamente`);
    } catch (error) {
      console.error('❌ Error al cambiar estatus:', error);
      alert('❌ Error al cambiar el estatus del conductor');
    }
  };

  const fetchConductores = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'conductores'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setConductores(data);

      console.log('✅ Conductores cargados y URLs validadas');
    } catch (error) {
      console.error('Error al cargar conductores:', error);
      alert('Error al cargar conductores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConductores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (index) => {
    const conductor = conductoresFiltrados[index];
    setEditIndex(conductor.id); // Usar el ID en lugar del índice
    setEditData({ ...conductor });
  };

  const toggleTaximetro = async (conductor) => {
    const nuevoValor = !(conductor.btn_taximetro ?? false);
    try {
      const conductorRef = doc(db, 'conductores', conductor.id);
      await updateDoc(conductorRef, { btn_taximetro: nuevoValor });
      setConductores(prev => prev.map(c => c.id === conductor.id ? { ...c, btn_taximetro: nuevoValor } : c));
    } catch (error) {
      console.error('Error al actualizar taxímetro:', error);
      alert('Error al actualizar el taxímetro');
    }
  };

  const [aplicandoTaximetroTodos, setAplicandoTaximetroTodos] = useState(false);
  const aplicarTaximetroATodos = async (valor) => {
    if (conductores.length === 0) {
      alert('No hay conductores para actualizar.');
      return;
    }
    if (!window.confirm(`¿${valor ? 'Activar' : 'Desactivar'} el taxímetro en los ${conductores.length} conductores?`)) return;
    setAplicandoTaximetroTodos(true);
    try {
      const batch = conductores.map(c => updateDoc(doc(db, 'conductores', c.id), { btn_taximetro: valor }));
      await Promise.all(batch);
      setConductores(prev => prev.map(c => ({ ...c, btn_taximetro: valor })));
      alert(`✅ Taxímetro ${valor ? 'activado' : 'desactivado'} en ${conductores.length} conductores.`);
    } catch (error) {
      console.error('Error al actualizar taxímetro en todos:', error);
      alert('Error al actualizar el taxímetro. Revisa la consola.');
    } finally {
      setAplicandoTaximetroTodos(false);
    }
  };

  const handleCancel = () => {
    // Limpiar URL temporal si existe
    if (editData.foto && editData.foto.startsWith('blob:')) {
      URL.revokeObjectURL(editData.foto);
      console.log('🧹 URL temporal limpiada');
    }

    setEditIndex(null);
    setEditData({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const conductorRef = doc(db, 'conductores', editData.id);

      // Si hay una nueva foto, eliminar la foto anterior si existe
      if (editData.foto && editData.foto !== conductores.find(c => c.id === editData.id)?.foto) {
        const conductorOriginal = conductores.find(c => c.id === editData.id);
        if (conductorOriginal?.foto && conductorOriginal.foto.startsWith('https://firebasestorage.googleapis.com')) {
          try {
            // Extraer la ruta del archivo de la URL
            const urlParts = conductorOriginal.foto.split('/');
            const filePath = urlParts.slice(urlParts.indexOf('o') + 1, urlParts.indexOf('?')).join('/');
            const decodedPath = decodeURIComponent(filePath);
            const oldPhotoRef = ref(storage, decodedPath);
            await deleteObject(oldPhotoRef);
            console.log('✅ Foto anterior eliminada');
          } catch (deleteError) {
            console.warn('⚠️ No se pudo eliminar la foto anterior:', deleteError);
            // No bloquear la operación si falla la eliminación de la foto
          }
        }
      }

      // Preparar datos para guardar en Firestore
      const datosParaGuardar = { ...editData };

      // Validar y limpiar la foto antes de guardar
      if (datosParaGuardar.foto) {
        if (datosParaGuardar.foto.startsWith('blob:')) {
          console.log('⚠️ No guardando URL temporal (blob) en Firestore');
          delete datosParaGuardar.foto;
          alert('⚠️ La foto no se pudo subir a Firebase Storage. Se guardará sin foto.');
        } else if (datosParaGuardar.foto.startsWith('https://firebasestorage.googleapis.com')) {
          console.log('✅ Guardando URL de Firebase Storage en Firestore');
        } else {
          console.log('⚠️ URL de foto no válida, eliminando del documento');
          delete datosParaGuardar.foto;
        }
      }

      await updateDoc(conductorRef, datosParaGuardar);

      // Actualizar tanto el array original como el filtrado
      setConductores(prev => prev.map((c, i) => c.id === editData.id ? { ...editData } : c));

      setEditIndex(null);
      setEditData({});
      alert('Conductor actualizado exitosamente');
    } catch (error) {
      console.error('❌ Error al actualizar conductor:', error);
      alert('Error al actualizar conductor');
    }
  };

  const handleFotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('📁 Archivo seleccionado:', file.name, 'Tamaño:', file.size, 'Tipo:', file.type);

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona solo archivos de imagen');
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen debe ser menor a 5MB');
      return;
    }

    // Crear URL temporal inmediatamente para preview
    const tempURL = URL.createObjectURL(file);
    console.log('🖼️ Setting editData.foto to:', tempURL);
    setEditData(prev => {
      const newState = { ...prev, foto: tempURL };
      console.log('🔄 New editData state:', newState);
      return newState;
    });
    console.log('🖼️ Preview creado con URL temporal');

    try {
      // Verificar configuración de Firebase
      console.log('🔧 Verificando configuración de Firebase...');
      console.log('• Storage Bucket:', storage.app.options.storageBucket);
      console.log('• Project ID:', storage.app.options.projectId);

      // Verificar autenticación antes de subir
      const { auth } = await import('../firebaseConfig');
      const user = auth.currentUser;

      if (!user) {
        console.warn('⚠️ Usuario no autenticado, usando fallback local');
        alert('⚠️ Debes estar autenticado para subir fotos. La foto se guardará localmente.');
        return;
      }

      console.log('👤 Usuario autenticado:', user.email);

      // Crear referencia única para la imagen usando el ID del conductor
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || 'jpg';
      const fileName = `conductores/${editData.id || 'temp'}_${timestamp}.${fileExtension}`;
      const storageRef = ref(storage, fileName);

      console.log('📤 Iniciando subida a Firebase Storage:', fileName);
      console.log('📍 Ruta completa:', `gs://${storage.app.options.storageBucket}/${fileName}`);

      // Subir archivo a Firebase Storage
      console.log('⏳ Subiendo archivo...');
      const snapshot = await uploadBytes(storageRef, file);

      console.log('✅ Archivo subido exitosamente');
      console.log('📊 Bytes transferidos:', snapshot.bytesTransferred);

      // Obtener URL de descarga
      console.log('🔗 Obteniendo URL de descarga...');
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log('✅ URL de descarga obtenida:', downloadURL);

      // Actualizar estado con la URL de Firebase Storage
      setEditData(prev => ({ ...prev, foto: downloadURL }));

      // Limpiar URL temporal
      URL.revokeObjectURL(tempURL);

      console.log('✅ Foto subida exitosamente a Firebase Storage');

      // Mostrar mensaje de éxito
      alert('✅ Foto subida exitosamente a Firebase Storage');

    } catch (error) {
      console.error('❌ Error al subir foto:', error);
      console.error('❌ Código de error:', error.code);
      console.error('❌ Mensaje de error:', error.message);

      // Mantener la URL temporal como fallback
      console.log('⚠️ Manteniendo URL temporal como fallback debido a error de subida');

      // Mostrar mensaje específico según el tipo de error
      let errorMessage = 'Error al subir la foto. Por favor intenta de nuevo.';

      if (error.code === 'storage/unauthorized') {
        errorMessage = 'Error de permisos. Verifica las reglas de Firebase Storage.';
      } else if (error.code === 'storage/cors') {
        errorMessage = 'Error de CORS. Verifica la configuración de dominios autorizados.';
      } else if (error.code === 'storage/network-request-failed') {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      } else if (error.code === 'storage/bucket-not-found') {
        errorMessage = 'Bucket de Storage no encontrado. Verifica la configuración.';
      } else if (error.code === 'storage/object-not-found') {
        errorMessage = 'Objeto no encontrado en Storage.';
      } else if (error.code === 'storage/quota-exceeded') {
        errorMessage = 'Cuota de Storage excedida.';
      }

      console.warn('⚠️', errorMessage);
      alert(`⚠️ ${errorMessage}\n\nCódigo: ${error.code}\n\nLa foto se guardará localmente por ahora.`);
    }
  };



  // --- MODAL DETALLES CONDUCTOR (NUEVO) ---
  const [modalDetalles, setModalDetalles] = useState({ open: false, conductor: null, editando: false, datos: {} });

  const abrirDetalles = (conductor) => {
    setModalDetalles({ open: true, conductor, editando: false, datos: { ...conductor } });
  };

  const guardarDetalles = async () => {
    try {
      const docRef = doc(db, 'conductores', modalDetalles.conductor.id);
      const datosActualizados = { ...modalDetalles.datos };
      await updateDoc(docRef, datosActualizados);

      setConductores(prev => prev.map(c => c.id === modalDetalles.conductor.id ? { ...c, ...datosActualizados } : c));
      alert('✅ Información actualizada correctamente');
      setModalDetalles(prev => ({ ...prev, editando: false, conductor: { ...prev.conductor, ...datosActualizados } }));
    } catch (e) {
      console.error(e);
      alert('Error al guardar: ' + e.message);
    }
  };

  const handleSubirFotoDetalle = async (e, campo) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      // Usar import dinámico o variables globales si están disponibles. Asumimos 'storage' y 'uploadBytes' en scope.
      const fileName = `conductores/${modalDetalles.conductor.id}/${campo}_${Date.now()}.jpg`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setModalDetalles(prev => ({ ...prev, datos: { ...prev.datos, [campo]: url } }));
    } catch (e) {
      console.error(e);
      alert('Error al subir foto');
    }
  };

  const handleEliminarConductor = async (conductor) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar al conductor ${conductor.nombre}?\n\nEsta acción eliminará:\n• El conductor de la base de datos\n• Su foto de Firebase Storage (si existe)`)) {
      return false;
    }

    try {
      console.log('🗑️ Iniciando eliminación del conductor:', conductor.nombre);

      // Eliminar foto de Firebase Storage si existe
      if (conductor.foto && conductor.foto.startsWith('https://firebasestorage.googleapis.com')) {
        try {
          // Extraer la ruta del archivo de la URL
          const urlParts = conductor.foto.split('/');
          const filePath = urlParts.slice(urlParts.indexOf('o') + 1, urlParts.indexOf('?')).join('/');
          const decodedPath = decodeURIComponent(filePath);
          const photoRef = ref(storage, decodedPath);
          await deleteObject(photoRef);
          console.log('✅ Foto eliminada exitosamente');
        } catch (deleteError) {
          console.warn('⚠️ No se pudo eliminar la foto:', deleteError);
        }
      }

      // Eliminar documento de Firestore
      const conductorRef = doc(db, 'conductores', conductor.id);
      await deleteDoc(conductorRef);

      // Actualizar estado local
      setConductores(prev => prev.filter(c => c.id !== conductor.id));

      alert('✅ Conductor eliminado exitosamente');
      return true;
    } catch (error) {
      console.error('❌ Error al eliminar conductor:', error);
      alert('Error al eliminar conductor');
      return false;
    }
  };



  // Filtrar conductores basado en búsqueda y estatus
  const conductoresFiltrados = conductores.filter(conductor => {
    // Primero filtrar por el selector de estado (Nuevos, Activos, Inactivos)
    const tieneUnidad = conductor.unidad && conductor.unidad.toString().trim() !== '';

    if (statusFilter === 'nuevos') {
      if (tieneUnidad) return false;
    } else if (statusFilter === 'activos') {
      if (!tieneUnidad || conductor.estatus !== true) return false;
    } else if (statusFilter === 'inactivos') {
      if (conductor.estatus !== false) return false;
    }

    // Filtrar por categoría
    if (categoryFilter !== 'todas') {
      if (conductor.categoria !== categoryFilter) return false;
    }

    if (!searchTerm) return true;

    const termino = searchTerm.toLowerCase();

    if (searchBy === 'nombre') {
      return conductor.nombre && conductor.nombre.toLowerCase().includes(termino);
    } else if (searchBy === 'unidad') {
      return conductor.unidad && conductor.unidad.toString().includes(termino);
    } else if (searchBy === 'categoria') {
      return conductor.categoria && conductor.categoria.toLowerCase().includes(termino);
    }

    return true;
  });



  // Función para verificar si una URL de imagen es válida
  const isImageUrlValid = (url) => {
    if (!url) return false;
    if (url.startsWith('blob:')) return true; // URLs temporales son válidas para preview
    if (url.startsWith('https://firebasestorage.googleapis.com')) return true; // URLs de Firebase Storage
    if (url.startsWith('https://') && (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || url.includes('.gif'))) return true; // URLs de imágenes válidas
    return false;
  };



  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ marginBottom: 20 }}>Gestión de Conductores</h2>

      {/* Controles de vista y búsqueda */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        flexWrap: 'wrap',
        gap: 15
      }}>
        {/* Controles de vista */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 'bold', color: '#374151' }}>Vista:</span>
          <button
            onClick={() => setViewMode('cards')}
            style={{
              background: viewMode === 'cards' ? '#3b82f6' : '#e5e7eb',
              color: viewMode === 'cards' ? 'white' : '#374151',
              border: 'none',
              borderRadius: 6,
              padding: '8px 16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            🃏 Cuadros
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              background: viewMode === 'table' ? '#3b82f6' : '#e5e7eb',
              color: viewMode === 'table' ? 'white' : '#374151',
              border: 'none',
              borderRadius: 6,
              padding: '8px 16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            📊 Tabla
          </button>

          <div style={{ display: 'flex', gap: 8, marginLeft: 20, borderLeft: '1px solid #e5e7eb', paddingLeft: 20 }}>
            <span style={{ fontWeight: 'bold', color: '#374151' }}>Filtro:</span>
            <button
              onClick={() => setStatusFilter('todos')}
              style={{
                background: statusFilter === 'todos' ? '#6b7280' : 'white',
                color: statusFilter === 'todos' ? 'white' : '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 20,
                padding: '4px 12px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: '600'
              }}
            >
              Todos
            </button>
            <button
              onClick={() => setStatusFilter('nuevos')}
              style={{
                background: statusFilter === 'nuevos' ? '#3b82f6' : 'white',
                color: statusFilter === 'nuevos' ? 'white' : '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 20,
                padding: '4px 12px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: '600'
              }}
            >
              🆕 Nuevos
            </button>
            <button
              onClick={() => setStatusFilter('activos')}
              style={{
                background: statusFilter === 'activos' ? '#10b981' : 'white',
                color: statusFilter === 'activos' ? 'white' : '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 20,
                padding: '4px 12px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: '600'
              }}
            >
              ✅ Activos
            </button>
            <button
              onClick={() => setStatusFilter('inactivos')}
              style={{
                background: statusFilter === 'inactivos' ? '#ef4444' : 'white',
                color: statusFilter === 'inactivos' ? 'white' : '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 20,
                padding: '4px 12px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: '600'
              }}
            >
              ❌ Inactivos
            </button>
          </div>


        </div>

        {/* Controles de búsqueda */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontWeight: 'bold', color: '#374151' }}>Buscar por:</span>
          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              background: 'white'
            }}
          >
            <option value="nombre">Nombre</option>
            <option value="unidad">Número de Unidad</option>

          </select>
          <input
            type="text"
            placeholder={`Buscar por ${searchBy === 'nombre' ? 'nombre' : 'unidad'}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              minWidth: 200
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 14
              }}
            >
              ✕ Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Información de resultados y acciones masivas taxímetro */}
      <div style={{
        marginBottom: 20,
        padding: '10px 15px',
        background: '#f3f4f6',
        borderRadius: 6,
        fontSize: 14,
        color: '#6b7280',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12
      }}>
        <span>
          Mostrando {conductoresFiltrados.length} de {conductores.length} conductores
          {searchTerm && ` (filtrado por "${searchTerm}")`}
        </span>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontWeight: 'bold', color: '#374151' }}>Taxímetro:</span>
          <button
            onClick={() => aplicarTaximetroATodos(true)}
            disabled={aplicandoTaximetroTodos || conductores.length === 0}
            style={{
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontWeight: 'bold',
              cursor: aplicandoTaximetroTodos || conductores.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: 13,
              opacity: aplicandoTaximetroTodos || conductores.length === 0 ? 0.7 : 1
            }}
          >
            {aplicandoTaximetroTodos ? '⏳ Aplicando...' : '✅ Activar a todos'}
          </button>
          <button
            onClick={() => aplicarTaximetroATodos(false)}
            disabled={aplicandoTaximetroTodos || conductores.length === 0}
            style={{
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontWeight: 'bold',
              cursor: aplicandoTaximetroTodos || conductores.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: 13,
              opacity: aplicandoTaximetroTodos || conductores.length === 0 ? 0.7 : 1
            }}
          >
            {aplicandoTaximetroTodos ? '⏳ Aplicando...' : '❌ Desactivar a todos'}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
          Cargando conductores...
        </div>
      ) : conductoresFiltrados.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#6b7280',
          background: '#f9fafb',
          borderRadius: 12,
          border: '2px dashed #d1d5db'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>🔍</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
            No se encontraron conductores
          </div>
          <div style={{ fontSize: '14px' }}>
            {searchTerm ? 'Intenta con otros términos de búsqueda' : 'No hay conductores registrados'}
          </div>
        </div>
      ) : viewMode === 'cards' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, justifyContent: 'flex-start' }}>
          {conductoresFiltrados.map((conductor, idx) => (
            <div key={conductor.id} style={{
              background: '#fff',
              borderRadius: 16,
              boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
              padding: 32,
              minWidth: 340,
              maxWidth: 370,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              marginBottom: 24
            }}>
              <div style={{ marginBottom: 24, width: '100%' }}>
                <div style={{ width: '100%', height: 180, border: '2.5px solid #3b82f6', borderRadius: '12px 12px 0 0', boxShadow: '0 2px 8px #3b82f633', overflow: 'hidden', background: 'transparent', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  {(() => {
                    const fotoUrl = editIndex === conductor.id ? (editData.foto || conductor.foto) : conductor.foto;
                    return isImageUrlValid(fotoUrl) ? (
                      <img
                        key={fotoUrl} // Force re-render on URL change
                        src={fotoUrl}
                        alt={conductor.nombre}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => {
                          console.warn('⚠️ Error al cargar imagen:', e.target.src);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={(e) => {
                          console.log('✅ Imagen cargada exitosamente:', e.target.src);
                        }}
                      />
                    ) : null;
                  })()}
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: (editIndex === conductor.id ? (editData.foto || conductor.foto) : conductor.foto) ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f3f4f6',
                    color: '#6b7280',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    👤 {conductor.nombre ? conductor.nombre.charAt(0) : 'C'}
                  </div>
                </div>
                {editIndex === conductor.id && (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFotoChange}
                    />
                    <button
                      onClick={() => fileInputRef.current.click()}
                      style={{ marginTop: 10, background: '#f59e0b', color: 'white', border: 'none', borderRadius: 4, padding: '5px 12px', fontWeight: 'bold', cursor: 'pointer', fontSize: 14 }}
                    >
                      Cambiar Foto
                    </button>
                  </>
                )}
              </div>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ minWidth: 90, textAlign: 'right' }}>Nombre:</strong>
                  {editIndex === conductor.id ? (
                    <input name="nombre" value={editData.nombre} onChange={handleChange} style={{ flex: 1, padding: 7, borderRadius: 4, border: '1px solid #ccc' }} />
                  ) : (
                    <span style={{ flex: 1 }}>{conductor.nombre}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ minWidth: 90, textAlign: 'right' }}>Correo:</strong>
                  <input
                    value={conductor.correo}
                    disabled
                    style={{ color: '#374151', background: '#f3f4f6', borderRadius: 4, padding: '7px 8px', width: '100%', border: 'none', fontWeight: 'bold' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ minWidth: 90, textAlign: 'right' }}>Teléfono:</strong>
                  {editIndex === conductor.id ? (
                    <input name="telefono" value={editData.telefono} onChange={handleChange} style={{ flex: 1, padding: 7, borderRadius: 4, border: '1px solid #ccc' }} />
                  ) : (
                    <span style={{ flex: 1 }}>{conductor.telefono}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ minWidth: 90, textAlign: 'right' }}>Unidad:</strong>
                  {editIndex === conductor.id ? (
                    <input name="unidad" value={editData.unidad} onChange={handleChange} style={{ flex: 1, padding: 7, borderRadius: 4, border: '1px solid #ccc' }} />
                  ) : (
                    <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {conductor.unidad || (
                        <span style={{
                          background: '#3b82f6',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          REGISTRO NUEVO
                        </span>
                      )}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ minWidth: 90, textAlign: 'right' }}>Placa:</strong>
                  {editIndex === conductor.id ? (
                    <input name="placa" value={editData.placa || ''} onChange={handleChange} style={{ flex: 1, padding: 7, borderRadius: 4, border: '1px solid #ccc' }} />
                  ) : (
                    <span style={{ flex: 1 }}>{conductor.placa || '-'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ minWidth: 90, textAlign: 'right' }}>Color:</strong>
                  {editIndex === conductor.id ? (
                    <input name="color" value={editData.color || ''} onChange={handleChange} style={{ flex: 1, padding: 7, borderRadius: 4, border: '1px solid #ccc' }} />
                  ) : (
                    <span style={{ flex: 1 }}>{conductor.color || '-'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ minWidth: 90, textAlign: 'right' }}>Taxímetro:</strong>
                  {editIndex === conductor.id ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={!!(editData.btn_taximetro ?? false)}
                        onChange={() => setEditData(prev => ({ ...prev, btn_taximetro: !(prev.btn_taximetro ?? false) }))}
                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                      />
                      <span>{editData.btn_taximetro ? 'Activado' : 'Desactivado'}</span>
                    </label>
                  ) : (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={!!(conductor.btn_taximetro ?? false)}
                        onChange={() => toggleTaximetro(conductor)}
                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                      />
                      <span>{conductor.btn_taximetro ? 'Activado' : 'Desactivado'}</span>
                    </label>
                  )}
                </div>
              </div>
              {/* Botones de acción y estatus en la misma línea, centrados y del mismo tamaño */}
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: 16, marginTop: 32 }}>
                {editIndex === conductor.id ? (
                  <>
                    <button onClick={handleSave} style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 6, padding: '14px 0', width: 120, fontWeight: 'bold', cursor: 'pointer', fontSize: 17 }}>Guardar</button>
                    <button onClick={handleCancel} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: 6, padding: '14px 0', width: 120, fontWeight: 'bold', cursor: 'pointer', fontSize: 17 }}>Cancelar</button>
                    <button
                      type="button"
                      onClick={() => abrirModalCambioEstatus(conductor)}
                      style={{
                        background: conductor.estatus ? '#10b981' : '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: 10,
                        padding: '14px 0',
                        width: 120,
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: 17,
                        transition: 'background 0.2s',
                        boxShadow: conductor.estatus ? '0 2px 8px #10b98133' : '0 2px 8px #ef444433'
                      }}
                    >
                      {conductor.estatus ? 'Activo' : 'Inactivo'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => abrirDetalles(conductor)}
                      style={{
                        background: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        padding: '14px 0',
                        width: 120,
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: 17
                      }}
                    >
                      📋 Ficha
                    </button>
                    <button
                      onClick={() => handleEdit(idx)}
                      style={{
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        padding: '14px 0',
                        width: 120,
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: 17
                      }}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => abrirModalCambioEstatus(conductor)}
                      style={{
                        background: conductor.estatus ? '#10b981' : '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: 10,
                        padding: '14px 0',
                        width: 120,
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: 17,
                        transition: 'background 0.2s',
                        boxShadow: conductor.estatus ? '0 2px 8px #10b98133' : '0 2px 8px #ef444433'
                      }}
                    >
                      {conductor.estatus ? 'Activo' : 'Inactivo'}
                    </button>

                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Vista de tabla
        <div style={{
          background: 'white',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            overflowX: 'auto',
            maxWidth: '100%'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
              minWidth: '800px'
            }}>
              <thead>
                <tr style={{
                  background: '#f8fafc',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  <th style={{
                    padding: '15px 12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    👤 Conductor
                  </th>
                  <th style={{
                    padding: '15px 12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    📧 Correo
                  </th>
                  <th style={{
                    padding: '15px 12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    📱 Teléfono
                  </th>
                  <th style={{
                    padding: '15px 12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    🚗 Unidad
                  </th>
                  <th style={{
                    padding: '15px 12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    🏷️ Placa
                  </th>
                  <th style={{
                    padding: '15px 12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    🎨 Color
                  </th>
                  <th style={{
                    padding: '15px 12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    🚕 Taxímetro
                  </th>
                  <th style={{
                    padding: '15px 12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    📊 Estado
                  </th>
                  <th style={{
                    padding: '15px 12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    ⚙️ Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {conductoresFiltrados.map((conductor, idx) => (
                  <tr key={conductor.id} style={{
                    borderBottom: '1px solid #f3f4f6',
                    transition: 'background 0.2s'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                    }}>
                    <td style={{
                      padding: '12px',
                      color: '#1f2937',
                      fontWeight: '500'
                    }}>
                      {editIndex === conductor.id ? (
                        <input
                          name="nombre"
                          value={editData.nombre}
                          onChange={handleChange}
                          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', width: '100%' }}
                        />
                      ) : (
                        conductor.nombre
                      )}
                    </td>
                    <td style={{
                      padding: '12px',
                      color: '#6b7280'
                    }}>
                      {conductor.correo}
                    </td>
                    <td style={{
                      padding: '12px',
                      color: '#374151'
                    }}>
                      {editIndex === conductor.id ? (
                        <input
                          name="telefono"
                          value={editData.telefono}
                          onChange={handleChange}
                          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', width: '100%' }}
                        />
                      ) : (
                        conductor.telefono
                      )}
                    </td>
                    <td style={{
                      padding: '12px',
                      color: '#374151'
                    }}>
                      {editIndex === conductor.id ? (
                        <input
                          name="unidad"
                          value={editData.unidad}
                          onChange={handleChange}
                          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', width: '100%' }}
                        />
                      ) : (
                        conductor.unidad || (
                          <span style={{
                            background: '#3b82f6',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold'
                          }}>
                            REGISTRO NUEVO
                          </span>
                        )
                      )}
                    </td>
                    <td style={{
                      padding: '12px',
                      color: '#374151'
                    }}>
                      {editIndex === conductor.id ? (
                        <input
                          name="placa"
                          value={editData.placa || ''}
                          onChange={handleChange}
                          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', width: '100%' }}
                        />
                      ) : (
                        conductor.placa || '-'
                      )}
                    </td>
                    <td style={{
                      padding: '12px',
                      color: '#374151'
                    }}>
                      {editIndex === conductor.id ? (
                        <input
                          name="color"
                          value={editData.color || ''}
                          onChange={handleChange}
                          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', width: '100%' }}
                        />
                      ) : (
                        conductor.color || '-'
                      )}
                    </td>
                    <td style={{
                      padding: '12px',
                      color: '#374151'
                    }}>
                      {editIndex === conductor.id ? (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!(editData.btn_taximetro ?? false)}
                            onChange={() => setEditData(prev => ({ ...prev, btn_taximetro: !(prev.btn_taximetro ?? false) }))}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                          />
                          <span>{editData.btn_taximetro ? 'Sí' : 'No'}</span>
                        </label>
                      ) : (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!(conductor.btn_taximetro ?? false)}
                            onChange={() => toggleTaximetro(conductor)}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                          />
                          <span>{conductor.btn_taximetro ? 'Sí' : 'No'}</span>
                        </label>
                      )}
                    </td>
                    <td style={{
                      padding: '12px'
                    }}>
                      <span style={{
                        background: conductor.estatus ? '#10b981' : '#ef4444',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                      }}>
                        {conductor.estatus ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={{
                      padding: '12px'
                    }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {editIndex === conductor.id ? (
                          <>
                            <button
                              onClick={handleSave}
                              style={{
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                            >
                              ✅ Guardar
                            </button>
                            <button
                              onClick={handleCancel}
                              style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                            >
                              ❌ Cancelar
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => abrirDetalles(conductor)}
                              style={{
                                background: '#8b5cf6',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                marginRight: 5
                              }}
                            >
                              📋 Ficha
                            </button>
                            <button
                              onClick={() => handleEdit(idx)}
                              style={{
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                            >
                              ✏️ Editar
                            </button>
                            <button
                              onClick={() => abrirModalCambioEstatus(conductor)}
                              style={{
                                background: conductor.estatus ? '#ef4444' : '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                            >
                              {conductor.estatus ? '❌ Desactivar' : '✅ Activar'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal para confirmar cambio de estatus con motivo */}
      {modalCambioEstatus.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: 24,
            borderRadius: 12,
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            width: '520px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 20, color: '#111827' }}>
              Cambiar estado de la unidad
            </h3>
            <p style={{ marginTop: 0, marginBottom: 16, color: '#374151' }}>
              {`Vas a ${modalCambioEstatus.nuevoEstatus ? 'activar' : 'suspender'} `}
              <strong>
                {modalCambioEstatus.conductor?.unidad ? `Unidad ${modalCambioEstatus.conductor.unidad}` : 'la unidad'}
              </strong>
              {modalCambioEstatus.conductor?.nombre ? ` (${modalCambioEstatus.conductor.nombre})` : ''}.
            </p>
            <label style={{ display: 'block', fontWeight: 'bold', color: '#374151', marginBottom: 6 }}>
              📝 Motivo
            </label>
            <textarea
              value={modalCambioEstatus.motivo}
              onChange={(e) => setModalCambioEstatus(prev => ({ ...prev, motivo: e.target.value }))}
              rows={3}
              placeholder={`Describe el motivo de la ${modalCambioEstatus.nuevoEstatus ? 'activación' : 'suspensión'} (mínimo 3 caracteres)`}
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontSize: 14,
                resize: 'vertical',
                marginBottom: 16
              }}
            />
            {!modalCambioEstatus.nuevoEstatus && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 'bold', color: '#374151', marginBottom: 6 }}>
                  ⏰ Suspender hasta <span style={{ fontWeight: 'normal', color: '#6b7280' }}>(opcional)</span>
                </label>
                <input
                  type="datetime-local"
                  value={modalCambioEstatus.suspensionHasta}
                  onChange={(e) => setModalCambioEstatus(prev => ({ ...prev, suspensionHasta: e.target.value }))}
                  style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
                />
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                  Si indicas fecha y hora, la unidad permanecerá inactiva hasta ese momento. Opcional.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={confirmarCambioEstatus}
                style={{
                  padding: '10px 18px',
                  border: 'none',
                  borderRadius: 8,
                  backgroundColor: modalCambioEstatus.nuevoEstatus ? '#10b981' : '#ef4444',
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {modalCambioEstatus.nuevoEstatus ? '✅ Activar' : '⛔ Suspender'}
              </button>
              <button
                onClick={cerrarModalCambioEstatus}
                style={{
                  padding: '10px 18px',
                  border: '2px solid #6b7280',
                  borderRadius: 8,
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  fontSize: 14,
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                ❌ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DETALLES (FICHA) - INSERTED FIX */}
      {modalDetalles.open && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{
            background: 'white', padding: 30, borderRadius: 16,
            width: '900px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 20
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: 10 }}>
              <h2 style={{ margin: 0 }}>📋 Ficha del Conductor</h2>
              <button
                onClick={() => setModalDetalles(prev => ({ ...prev, open: false }))}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* COLUMNA 1: Datos Personales */}
              <div style={{ background: '#f9fafb', padding: 20, borderRadius: 12 }}>
                <h3 style={{ marginTop: 0 }}>👤 Información Personal</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <label><strong>Nombre:</strong>
                    <input style={{ width: '100%', padding: 8, marginTop: 4 }} value={modalDetalles.datos.nombre || ''} onChange={e => setModalDetalles({ ...modalDetalles, datos: { ...modalDetalles.datos, nombre: e.target.value } })} />
                  </label>
                  <label><strong>Teléfono:</strong>
                    <input style={{ width: '100%', padding: 8, marginTop: 4 }} value={modalDetalles.datos.telefono || ''} onChange={e => setModalDetalles({ ...modalDetalles, datos: { ...modalDetalles.datos, telefono: e.target.value } })} />
                  </label>
                  <label><strong>Correo (ID):</strong>
                    <input readOnly style={{ width: '100%', padding: 8, marginTop: 4, background: '#eee' }} value={modalDetalles.datos.correo || modalDetalles.datos.id || ''} />
                  </label>
                  <label><strong>Cédula:</strong>
                    <input style={{ width: '100%', padding: 8, marginTop: 4 }} value={modalDetalles.datos.cedula || ''} onChange={e => setModalDetalles({ ...modalDetalles, datos: { ...modalDetalles.datos, cedula: e.target.value } })} />
                  </label>
                </div>
              </div>

              {/* COLUMNA 2: Datos de Unidad */}
              <div style={{ background: '#f9fafb', padding: 20, borderRadius: 12 }}>
                <h3 style={{ marginTop: 0 }}>🚖 Información de Unidad</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <label><strong>Unidad:</strong>
                    <input style={{ width: '100%', padding: 8 }} value={modalDetalles.datos.unidad || ''} onChange={e => setModalDetalles({ ...modalDetalles, datos: { ...modalDetalles.datos, unidad: e.target.value } })} />
                  </label>
                  <label><strong>Placa:</strong>
                    <input style={{ width: '100%', padding: 8 }} value={modalDetalles.datos.placa || ''} onChange={e => setModalDetalles({ ...modalDetalles, datos: { ...modalDetalles.datos, placa: e.target.value } })} />
                  </label>
                  <label><strong>Color:</strong>
                    <input style={{ width: '100%', padding: 8 }} value={modalDetalles.datos.color || ''} onChange={e => setModalDetalles({ ...modalDetalles, datos: { ...modalDetalles.datos, color: e.target.value } })} />
                  </label>

                  <label><strong>Disco:</strong>
                    <input style={{ width: '100%', padding: 8 }} value={modalDetalles.datos.disco || ''} onChange={e => setModalDetalles({ ...modalDetalles, datos: { ...modalDetalles.datos, disco: e.target.value } })} />
                  </label>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label><strong>Cooperativa/Compañía:</strong>
                      <input style={{ width: '100%', padding: 8 }} value={modalDetalles.datos.cooperativa || ''} onChange={e => setModalDetalles({ ...modalDetalles, datos: { ...modalDetalles.datos, cooperativa: e.target.value } })} />
                    </label>
                  </div>
                  <label><strong>Reg. Municipal:</strong>
                    <input style={{ width: '100%', padding: 8 }} value={modalDetalles.datos.registroMunicipal || ''} onChange={e => setModalDetalles({ ...modalDetalles, datos: { ...modalDetalles.datos, registroMunicipal: e.target.value } })} />
                  </label>
                  <label><strong>Institución:</strong>
                    <input style={{ width: '100%', padding: 8 }} value={modalDetalles.datos.institucion || ''} onChange={e => setModalDetalles({ ...modalDetalles, datos: { ...modalDetalles.datos, institucion: e.target.value } })} />
                  </label>

                </div>
              </div>
            </div>

            {/* SECCIÓN FOTOS */}
            <div style={{ background: '#f0f9ff', padding: 20, borderRadius: 12 }}>
              <h3 style={{ marginTop: 0 }}>📸 Documentos y Fotos</h3>
              <div style={{ display: 'flex', gap: 20, overflowX: 'auto', paddingBottom: 10 }}>
                {[
                  { label: 'Perfil', key: 'foto' },
                  { label: 'Vehículo', key: 'foto_carro' },
                  { label: 'Cédula', key: 'foto_cedula' },
                  { label: 'Licencia', key: 'foto_perfil_licencia' }
                ].map((item) => (
                  <div key={item.key} style={{ minWidth: 150, textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 5 }}>{item.label}</div>
                    <div style={{
                      width: 150, height: 150, background: '#fff', border: '2px dashed #ccc',
                      display: 'flex', justifyContent: 'center', alignItems: 'center',
                      borderRadius: 8, overflow: 'hidden', position: 'relative'
                    }}>
                      {modalDetalles.datos[item.key] && modalDetalles.datos[item.key] !== 'vacio' ? (
                        <a href={modalDetalles.datos[item.key]} target="_blank" rel="noopener noreferrer">
                          <img src={modalDetalles.datos[item.key]} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </a>
                      ) : (
                        <span style={{ color: '#ccc' }}>Sin foto</span>
                      )}
                    </div>
                    <label style={{
                      display: 'block', marginTop: 8, background: '#3b82f6', color: 'white',
                      padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 'bold'
                    }}>
                      Subir / Cambiar
                      <input type="file" hidden accept="image/*" onChange={(e) => handleSubirFotoDetalle(e, item.key)} />
                    </label>
                  </div>
                ))}
              </div>
            </div>
            {/* SECCIÓN NOTAS */}
            <div style={{ background: '#fefce8', padding: 20, borderRadius: 12, border: '1px solid #fef08a' }}>
              <h3 style={{ marginTop: 0 }}>📝 Notas y Comentarios</h3>
              <textarea
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #fde047',
                  minHeight: 100,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                placeholder="Agregar una nota, reporte o comentario sobre el conductor..."
                value={modalDetalles.datos.notas || ''}
                onChange={e => setModalDetalles({ ...modalDetalles, datos: { ...modalDetalles.datos, notas: e.target.value } })}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
              <button
                onClick={async () => {
                  const success = await handleEliminarConductor(modalDetalles.conductor);
                  if (success) setModalDetalles(prev => ({ ...prev, open: false }));
                }}
                style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}
              >
                🗑️ Eliminar Conductor
              </button>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setModalDetalles(prev => ({ ...prev, open: false }))}
                  style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #ccc', background: 'white', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={guardarDetalles}
                  style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#10b981', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  💾 Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
function ReportesContent() {
  const [viajes, setViajes] = useState([]);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [horaInicio, setHoraInicio] = useState('00:00');
  const [horaFin, setHoraFin] = useState('23:59');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [resumenOperadores, setResumenOperadores] = useState({});
  const [operadorSeleccionado, setOperadorSeleccionado] = useState('Todos');
  // const [estadisticasGlobales, setEstadisticasGlobales] = useState(null); // Eliminado para usar datos filtrados

  // Inicializar fechas por defecto al cargar el componente
  useEffect(() => {
    const hoy = new Date();
    const dia = String(hoy.getDate()).padStart(2, '0');
    const mes = String(hoy.getMonth() + 1).padStart(2, '0');
    const año = hoy.getFullYear();
    const fechaHoy = `${dia}-${mes}-${año}`;

    if (!fechaInicio && !fechaFin) {
      setFechaInicio(fechaHoy);
      setFechaFin(fechaHoy);
      // Cargar datos automáticamente después de un breve delay
      setTimeout(() => {
        cargarViajesPorRango(fechaHoy, fechaHoy);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaInicio, fechaFin]);

  // Función para obtener la fecha actual en formato DD-MM-YYYY
  const obtenerFechaActual = () => {
    const hoy = new Date();
    const dia = String(hoy.getDate()).padStart(2, '0');
    const mes = String(hoy.getMonth() + 1).padStart(2, '0');
    const año = hoy.getFullYear();
    return `${dia}-${mes}-${año}`;
  };

  const calcularResumenEstados = (viajes) => {
    let completados = 0;
    let cancelados = 0;
    let noAtendidos = 0;
    let porApp = 0;
    let ubicacionesRecibidas = 0;
    let convencionales = 0;
    let totalRechazos = 0;
    const unidadesStats = {};

    viajes.forEach(viaje => {
      const estadoRaw = (viaje.estado || viaje.pedido || '').toString().toLowerCase();
      const operadora = viaje.operadora || viaje.operador || '';

      const esAceptado = estadoRaw.includes('aceptado') || estadoRaw.includes('finalizado');
      const esCancelado = estadoRaw.includes('cancelado');
      const esNoAtendido = estadoRaw.includes('no atendido') || estadoRaw.includes('sin asignar') || estadoRaw.includes('no hubo unidad');
      const esDisponible = estadoRaw.includes('disponible');

      if (esAceptado) {
        completados += 1;
        if (viaje.unidad) {
          const u = viaje.unidad.toString();
          if (!unidadesStats[u]) unidadesStats[u] = { completados: 0, rechazos: 0 };
          unidadesStats[u].completados++;
        }
      } else if (esDisponible) {
        porApp += 1;
      } else if (esNoAtendido) {
        noAtendidos += 1;
      } else if (esCancelado) {
        cancelados += 1;
      }

      if (!operadora || operadora === '' || operadora === '-' || operadora === 'Sin operador' || viaje.cantidadConductoresCercanos !== undefined) {
        ubicacionesRecibidas += 1;
      }

      const tel = (viaje.telefono || viaje.celular || viaje.numero || '').toString().replace(/\D/g, '');
      if (tel.length === 7) {
        convencionales += 1;
      }

      // Analizar rechazos en conductoresCercanos
      if (viaje.conductoresCercanos && Array.isArray(viaje.conductoresCercanos)) {
        viaje.conductoresCercanos.forEach(c => {
          if (c.rechazado === true) {
            totalRechazos++;
            if (c.unidad) {
              const u = c.unidad.toString();
              if (!unidadesStats[u]) unidadesStats[u] = { completados: 0, rechazos: 0 };
              unidadesStats[u].rechazos++;
            }
          }
        });
      }
    });

    return {
      'Viajes Completados': completados,
      'Por App': porApp,
      'No Atendido': noAtendidos,
      'Ubicaciones Recibidas': ubicacionesRecibidas,
      'Teléfonos Convencionales': convencionales,
      'Cancelados': cancelados,
      'Total Rechazos': totalRechazos,
      'unidadesStats': unidadesStats
    };
  };

  // Función para calcular resumen por operadora
  const calcularResumenOperadores = (viajes) => {
    const resumen = {};
    viajes.forEach(viaje => {
      // Normalizar nombre de operadora
      let nombreOperadora = (viaje?.operador && (viaje.operador.nombre || viaje.operador.name)) ||
        viaje?.operadora ||
        viaje?.operador ||
        viaje?.operator ||
        viaje?.atendidoPor ||
        'Automático';

      if (!nombreOperadora || nombreOperadora === '' || nombreOperadora === '-') {
        nombreOperadora = 'Automático';
      }

      // Si dice "Sin Operador", cambiar a "Automático"
      if (nombreOperadora === 'Sin Operador') {
        nombreOperadora = 'Automático';
      }

      if (!resumen[nombreOperadora]) {
        resumen[nombreOperadora] = 0;
      }
      resumen[nombreOperadora]++;
    });
    return resumen;
  };



  // Función para cargar viajes por rango de fechas
  const cargarViajesPorRango = async (fechaInicio, fechaFin) => {
    setCargando(true);
    setError('');

    try {
      console.log('📊 Cargando viajes desde:', fechaInicio, 'hasta:', fechaFin, 'hora inicio:', horaInicio, 'hora fin:', horaFin);

      const todosLosViajesArray = [];

      // Convertir fechas de formato DD-MM-YYYY a objetos Date para comparación
      const [diaInicio, mesInicio, añoInicio] = fechaInicio.split('-').map(Number);
      const [diaFin, mesFin, añoFin] = fechaFin.split('-').map(Number);

      // Crear fechas de inicio y fin del rango
      const fechaInicioDate = new Date(añoInicio, mesInicio - 1, diaInicio, 0, 0, 0);
      const fechaFinDate = new Date(añoFin, mesFin - 1, diaFin, 23, 59, 59);

      console.log('📅 Rango de fechas:', fechaInicioDate, 'a', fechaFinDate);

      const viajesProcesadosIds = new Set();

      // Consultar directamente la colección todosLosViajes (Nueva Estructura)
      try {
        const viajesRef = collection(db, 'todosLosViajes');
        console.log('🔍 Consultando colección directa: todosLosViajes');

        const viajesSnapshot = await getDocs(viajesRef);
        console.log(`📊 Total de documentos en todosLosViajes (raíz): ${viajesSnapshot.size}`);


        /* Eliminado: No cargar toda la colección para evitar lentitud
        const todosLosDocumentos = viajesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const statsGlobales = calcularResumenEstados(todosLosDocumentos);
        statsGlobales.total = todosLosDocumentos.length;
        setEstadisticasGlobales(statsGlobales);
        */

        viajesSnapshot.forEach((docSnapshot) => {
          const viajeData = docSnapshot.data();
          viajesProcesadosIds.add(docSnapshot.id);

          // Obtener la fecha del documento
          let fechaViaje = null;
          if (viajeData.fecha) {
            if (viajeData.fecha.toDate && typeof viajeData.fecha.toDate === 'function') {
              fechaViaje = viajeData.fecha.toDate();
            } else if (viajeData.fecha instanceof Date) {
              fechaViaje = viajeData.fecha;
            } else if (typeof viajeData.fecha === 'string') {
              fechaViaje = new Date(viajeData.fecha);
            }
          }

          // Verificar si el viaje está dentro del rango de fechas
          let dentroDelRango = false;

          if (fechaViaje && !isNaN(fechaViaje.getTime())) {
            if (fechaViaje >= fechaInicioDate && fechaViaje <= fechaFinDate) {
              const horaViaje = fechaViaje.getHours().toString().padStart(2, '0') + ':' + fechaViaje.getMinutes().toString().padStart(2, '0');
              const fechaViajeFormateada = `${fechaViaje.getDate().toString().padStart(2, '0')}-${(fechaViaje.getMonth() + 1).toString().padStart(2, '0')}-${fechaViaje.getFullYear()}`;

              if (fechaInicio === fechaFin) {
                dentroDelRango = horaViaje >= horaInicio && horaViaje <= horaFin;
              } else {
                if (fechaViajeFormateada === fechaInicio) {
                  dentroDelRango = horaViaje >= horaInicio;
                } else if (fechaViajeFormateada === fechaFin) {
                  dentroDelRango = horaViaje <= horaFin;
                } else {
                  dentroDelRango = true;
                }
              }
            }
          }

          if (dentroDelRango) {
            const viaje = {
              id: docSnapshot.id,
              fecha: formatearFechaParaReporte(fechaViaje || viajeData.fecha),
              ...viajeData
            };
            todosLosViajesArray.push(viaje);
          }
        });
      } catch (error) {
        console.error('❌ Error al consultar todosLosViajes (raíz):', error);
      }

      // Consultar subcolecciones por fecha (Estructura Antigua - Retrocompatibilidad)
      try {
        const obtenerFechasEnRango = (fInicio, fFin) => {
          const fechas = [];
          let actual = new Date(añoInicio, mesInicio - 1, diaInicio);
          const finObj = new Date(añoFin, mesFin - 1, diaFin);
          while (actual <= finObj) {
            fechas.push(`${actual.getDate().toString().padStart(2, '0')}-${(actual.getMonth() + 1).toString().padStart(2, '0')}-${actual.getFullYear()}`);
            actual.setDate(actual.getDate() + 1);
          }
          return fechas;
        };

        const fechasABuscar = obtenerFechasEnRango(fechaInicio, fechaFin);
        // Dos estructuras por fecha: la antigua (todosLosViajes/{fecha}/viajes) y la actual (viajes/{fecha}/viajes)
        const raicesPorFecha = ['todosLosViajes', 'viajes'];
        console.log(`📂 Buscando en ${fechasABuscar.length} fechas x ${raicesPorFecha.length} subcolecciones...`);

        for (const fechaString of fechasABuscar) {
          for (const raiz of raicesPorFecha) {
            // eslint-disable-next-line no-await-in-loop
            const subColSnapshot = await getDocs(collection(db, raiz, fechaString, 'viajes'));

            if (subColSnapshot.size > 0) {
              console.log(`📍 Encontrados ${subColSnapshot.size} viajes en ${raiz}/${fechaString}/viajes`);

              subColSnapshot.forEach((docSnapshot) => {
                if (!viajesProcesadosIds.has(docSnapshot.id)) {
                  viajesProcesadosIds.add(docSnapshot.id);
                  const viajeData = docSnapshot.data();

                  let fechaViaje = null;
                  if (viajeData.fecha) {
                    fechaViaje = viajeData.fecha.toDate ? viajeData.fecha.toDate() : new Date(viajeData.fecha);
                  }

                  // Filtrar por hora si es necesario
                  let incluir = true;
                  if (fechaViaje && !isNaN(fechaViaje.getTime())) {
                    const horaViaje = fechaViaje.getHours().toString().padStart(2, '0') + ':' + fechaViaje.getMinutes().toString().padStart(2, '0');
                    if (fechaInicio === fechaFin) {
                      incluir = horaViaje >= horaInicio && horaViaje <= horaFin;
                    } else {
                      if (fechaString === fechaInicio) {
                        incluir = horaViaje >= horaInicio;
                      } else if (fechaString === fechaFin) {
                        incluir = horaViaje <= horaFin;
                      }
                    }
                  }

                  if (incluir) {
                    todosLosViajesArray.push({
                      id: docSnapshot.id,
                      fecha: formatearFechaParaReporte(fechaViaje || viajeData.fecha),
                      ...viajeData
                    });
                  }
                }
              });
            }
          }
        }
      } catch (error) {
        console.error('❌ Error al consultar subcolecciones antiguas:', error);
      }

      // Verificar si hay más de 10 pedidos activos para evitar duplicación
      try {
        const pedidosEnCursoRef = collection(db, 'pedidoEnCurso');
        const pedidosEnCursoSnapshot = await getDocs(pedidosEnCursoRef);
        const totalPedidosActivos = pedidosEnCursoSnapshot.size;

        console.log(`📊 Pedidos activos detectados: ${totalPedidosActivos}`);

        // Solo cargar desde otras colecciones si hay menos de 10 pedidos activos
        if (totalPedidosActivos < 10) {
          console.log('📄 Cargando datos adicionales desde otras colecciones...');

          // Cargar desde colecciones activas como respaldo
          try {
            const coleccionesActivas = ['pedidosDisponibles', 'pedidoSolicitado'];
            for (const colName of coleccionesActivas) {
              const colRef = collection(db, colName);
              const snap = await getDocs(colRef);

              snap.forEach((docSnapshot) => {
                const viaje = docSnapshot.data();
                let fechaViaje = null;

                if (viaje.fecha) {
                  if (viaje.fecha.toDate && typeof viaje.fecha.toDate === 'function') {
                    fechaViaje = viaje.fecha.toDate();
                  } else if (viaje.fecha instanceof Date) {
                    fechaViaje = viaje.fecha;
                  } else {
                    fechaViaje = new Date(viaje.fecha);
                  }
                }

                if (fechaViaje && !isNaN(fechaViaje.getTime())) {
                  if (fechaViaje >= fechaInicioDate && fechaViaje <= fechaFinDate) {
                    const horaViaje = fechaViaje.getHours().toString().padStart(2, '0') + ':' + fechaViaje.getMinutes().toString().padStart(2, '0');
                    const fechaViajeFormateada = `${fechaViaje.getDate().toString().padStart(2, '0')}-${(fechaViaje.getMonth() + 1).toString().padStart(2, '0')}-${fechaViaje.getFullYear()}`;

                    let dentroDelRango = false;
                    if (fechaInicio === fechaFin) {
                      dentroDelRango = horaViaje >= horaInicio && horaViaje <= horaFin;
                    } else {
                      if (fechaViajeFormateada === fechaInicio) {
                        dentroDelRango = horaViaje >= horaInicio;
                      } else if (fechaViajeFormateada === fechaFin) {
                        dentroDelRango = horaViaje <= horaFin;
                      } else {
                        dentroDelRango = true;
                      }
                    }

                    if (dentroDelRango) {
                      // Verificar que no exista ya
                      const yaExiste = todosLosViajesArray.some(v => v.id === docSnapshot.id);
                      if (!yaExiste) {
                        const viajeFormateado = {
                          id: docSnapshot.id,
                          fecha: formatearFechaParaReporte(fechaViaje),
                          ...viaje
                        };
                        todosLosViajesArray.push(viajeFormateado);
                        console.log(`📄 Viaje de ${colName} encontrado:`, docSnapshot.id);
                      }
                    }
                  }
                }
              });
            }
          } catch (error) {
            console.log('⚠️ Error al cargar colecciones de respaldo:', error.message);
          }

          // Cargar desde pedidoEnCurso como respaldo
          pedidosEnCursoSnapshot.forEach((docSnapshot) => {
            const viaje = docSnapshot.data();
            let fechaViaje = null;

            if (viaje.fecha) {
              if (viaje.fecha.toDate && typeof viaje.fecha.toDate === 'function') {
                fechaViaje = viaje.fecha.toDate();
              } else if (viaje.fecha instanceof Date) {
                fechaViaje = viaje.fecha;
              } else {
                fechaViaje = new Date(viaje.fecha);
              }
            }

            if (fechaViaje && !isNaN(fechaViaje.getTime())) {
              if (fechaViaje >= fechaInicioDate && fechaViaje <= fechaFinDate) {
                const horaViaje = fechaViaje.getHours().toString().padStart(2, '0') + ':' + fechaViaje.getMinutes().toString().padStart(2, '0');

                if (horaViaje >= horaInicio && horaViaje <= horaFin) {
                  // Verificar que no exista ya
                  const yaExiste = todosLosViajesArray.some(v => v.id === docSnapshot.id);
                  if (!yaExiste) {
                    const viajeFormateado = {
                      id: docSnapshot.id,
                      fecha: formatearFechaParaReporte(fechaViaje),
                      ...viaje
                    };
                    todosLosViajesArray.push(viajeFormateado);
                    console.log('📄 Viaje en curso encontrado:', docSnapshot.id);
                  }
                }
              }
            }
          });
        } else {
          console.log('⚠️ Se detectaron 10+ pedidos activos. Solo cargando desde todosLosViajes para evitar duplicación.');
        }
      } catch (error) {
        console.log('⚠️ Error al verificar pedidos activos:', error.message);
      }

      // Ordenar por fecha más reciente primero
      todosLosViajesArray.sort((a, b) => {
        try {
          let fechaA = new Date(0);
          let fechaB = new Date(0);

          if (a.fecha) {
            if (typeof a.fecha === 'string') {
              if (a.fecha.includes('/')) {
                // Formato DD/MM/YYYY HH:MM o DD/MM/YYYY
                const partes = a.fecha.split(' ');
                if (partes.length >= 1) {
                  const fechaParte = partes[0];
                  fechaA = new Date(fechaParte.split('/').reverse().join('-'));
                }
              } else if (a.fecha.includes('-')) {
                // Formato DD-MM-YYYY
                fechaA = new Date(a.fecha.split('-').reverse().join('-'));
              } else {
                // Intentar parsear como fecha directa
                fechaA = new Date(a.fecha);
              }
            } else if (a.fecha instanceof Date) {
              fechaA = a.fecha;
            } else if (a.fecha.toDate) {
              // Timestamp de Firestore
              fechaA = a.fecha.toDate();
            }
          }

          if (b.fecha) {
            if (typeof b.fecha === 'string') {
              if (b.fecha.includes('/')) {
                // Formato DD/MM/YYYY HH:MM o DD/MM/YYYY
                const partes = b.fecha.split(' ');
                if (partes.length >= 1) {
                  const fechaParte = partes[0];
                  fechaB = new Date(fechaParte.split('/').reverse().join('-'));
                }
              } else if (b.fecha.includes('-')) {
                // Formato DD-MM-YYYY
                fechaB = new Date(b.fecha.split('-').reverse().join('-'));
              } else {
                // Intentar parsear como fecha directa
                fechaB = new Date(b.fecha);
              }
            } else if (b.fecha instanceof Date) {
              fechaB = b.fecha;
            } else if (b.fecha.toDate) {
              // Timestamp de Firestore
              fechaB = b.fecha.toDate();
            }
          }

          return fechaB - fechaA;
        } catch (error) {
          console.error('Error al ordenar fechas:', error);
          return 0;
        }
      });

      console.log(`✅ Se encontraron ${todosLosViajesArray.length} viajes en total`);
      setViajes(todosLosViajesArray);

      // Calcular resumen de operadores siempre con TODOS los viajes cargados
      const resumenOperadoresCalculado = calcularResumenOperadores(todosLosViajesArray);
      setResumenOperadores(resumenOperadoresCalculado);
      console.log('📊 Resumen de operadores:', resumenOperadoresCalculado);

      // Nota: resumenEstados y resumenTipos ahora se calcularán dinámicamente según el filtro
      // pero inicializamos con todos
      // Nota: resumenEstados y resumenTipos ahora se calculan dinámicamente

      // Eliminado setResumenEstados y setResumenTipos previos
      // const resumenEstadosCalculado = calcularResumenEstados(todosLosViajesArray);
      // setResumenEstados(resumenEstadosCalculado);

      // const resumenTiposCalculado = calcularResumenTipos(todosLosViajesArray);
      // setResumenTipos(resumenTiposCalculado);

      // console.log('📊 Resumen de estados:', resumenEstadosCalculado);
      // console.log('📊 Resumen de estados:', resumenEstadosCalculado);
      // console.log('📊 Resumen de tipos:', resumenTiposCalculado);

    } catch (error) {
      console.error('❌ Error al cargar viajes:', error);

      // Mensajes de error más específicos
      if (error.code === 'permission-denied') {
        setError('No tienes permisos para acceder a los viajes.');
      } else {
        setError(`Error al cargar los viajes: ${error.message}`);
      }
    } finally {
      setCargando(false);
    }
  };



  // Función para manejar cambio de fecha inicio
  const handleFechaInicioChange = (e) => {
    const fechaInput = e.target.value; // Formato YYYY-MM-DD
    if (fechaInput) {
      const [año, mes, dia] = fechaInput.split('-');
      const fechaFormateada = `${dia}-${mes}-${año}`;
      setFechaInicio(fechaFormateada);
    }
  };

  // Función para manejar cambio de fecha fin
  const handleFechaFinChange = (e) => {
    const fechaInput = e.target.value; // Formato YYYY-MM-DD
    if (fechaInput) {
      const [año, mes, dia] = fechaInput.split('-');
      const fechaFormateada = `${dia}-${mes}-${año}`;
      setFechaFin(fechaFormateada);
    }
  };

  // Función para manejar cambio de hora inicio
  const handleHoraInicioChange = (e) => {
    setHoraInicio(e.target.value);
  };

  // Función para manejar cambio de hora fin
  const handleHoraFinChange = (e) => {
    setHoraFin(e.target.value);
  };

  // Función para aplicar filtros
  const aplicarFiltros = () => {
    if (fechaInicio && fechaFin) {
      console.log('🔍 Aplicando filtros:', { fechaInicio, fechaFin, horaInicio, horaFin });
      cargarViajesPorRango(fechaInicio, fechaFin);
    } else {
      console.warn('⚠️ Fechas no válidas para filtrar:', { fechaInicio, fechaFin });
      setError('Por favor, selecciona fechas de inicio y fin válidas.');
    }
  };

  // Función para formatear fecha para mostrar
  const formatearFechaMostrar = (fecha) => {
    if (!fecha) return 'N/A';

    // Si ya está en formato DD/MM/YYYY HH:MM, devolverlo tal como está
    if (typeof fecha === 'string' && fecha.includes('/') && fecha.includes(':')) {
      return fecha;
    }

    // Si ya está en formato DD/MM/YYYY, agregar hora
    if (typeof fecha === 'string' && fecha.includes('/') && !fecha.includes(':')) {
      return fecha + ' 00:00';
    }

    // Si está en formato DD-MM-YYYY
    if (typeof fecha === 'string' && fecha.includes('-')) {
      const partes = fecha.split('-');
      if (partes.length === 3) {
        const [dia, mes, año] = partes;
        return `${dia}/${mes}/${año} 00:00`;
      }
    }

    // Si es un timestamp de Firestore
    if (fecha.toDate && typeof fecha.toDate === 'function') {
      try {
        const fechaObj = fecha.toDate();
        const dia = String(fechaObj.getDate()).padStart(2, '0');
        const mes = String(fechaObj.getMonth() + 1).padStart(2, '0');
        const año = fechaObj.getFullYear();
        const hora = String(fechaObj.getHours()).padStart(2, '0');
        const minutos = String(fechaObj.getMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${año} ${hora}:${minutos}`;
      } catch (error) {
        console.error('Error al formatear timestamp de Firestore:', error);
      }
    }

    // Si es un objeto Date o timestamp
    try {
      const fechaObj = new Date(fecha);
      if (!isNaN(fechaObj.getTime())) {
        const dia = String(fechaObj.getDate()).padStart(2, '0');
        const mes = String(fechaObj.getMonth() + 1).padStart(2, '0');
        const año = fechaObj.getFullYear();
        const hora = String(fechaObj.getHours()).padStart(2, '0');
        const minutos = String(fechaObj.getMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${año} ${hora}:${minutos}`;
      }
    } catch (error) {
      console.error('Error al formatear fecha:', error);
    }

    return 'N/A';
  };

  // Función para formatear fecha para reporte
  const formatearFechaParaReporte = (fecha) => {
    if (!fecha) return 'N/A';

    try {
      let fechaObj;

      // Si es un timestamp de Firestore
      if (fecha.toDate && typeof fecha.toDate === 'function') {
        fechaObj = fecha.toDate();
      }
      // Si es un objeto Date
      else if (fecha instanceof Date) {
        fechaObj = fecha;
      }
      // Si es una cadena de fecha
      else if (typeof fecha === 'string') {
        fechaObj = new Date(fecha);
      }
      // Si es un timestamp (número)
      else if (typeof fecha === 'number') {
        fechaObj = new Date(fecha);
      }
      // Intentar parsear como fecha
      else {
        fechaObj = new Date(fecha);
      }

      if (isNaN(fechaObj.getTime())) {
        console.warn('Fecha inválida:', fecha);
        return 'N/A';
      }

      const dia = String(fechaObj.getDate()).padStart(2, '0');
      const mes = String(fechaObj.getMonth() + 1).padStart(2, '0');
      const año = fechaObj.getFullYear();
      const hora = String(fechaObj.getHours()).padStart(2, '0');
      const minutos = String(fechaObj.getMinutes()).padStart(2, '0');

      return `${dia}/${mes}/${año} ${hora}:${minutos}`;
    } catch (error) {
      console.error('Error al formatear fecha:', error, 'Fecha original:', fecha);
      return 'N/A';
    }
  };



  // Función para obtener estado con color
  const obtenerEstadoConColor = (estado) => {
    // const colores = ... // Eliminado por no uso directo y clave duplicada

    // Normalizar el estado para mejor comparación
    const estadoNormalizado = estado ? estado.toString().toLowerCase() : '';

    let color = '#6b7280'; // Color por defecto

    if (estadoNormalizado.includes('aceptado') || estadoNormalizado.includes('finalizado')) {
      color = '#10b981';
    } else if (estadoNormalizado.includes('voucher')) {
      color = '#7c3aed'; // Color púrpura para vouchers
    } else if (estadoNormalizado.includes('disponible')) {
      color = '#3b82f6';
    } else if (estadoNormalizado.includes('curso')) {
      color = '#f59e0b';
    } else if (estadoNormalizado.includes('cancelado')) {
      color = '#ef4444';
    } else if (estadoNormalizado.includes('pendiente')) {
      color = '#6b7280';
    }

    let textoMostrar = estado || 'N/A';
    if (estadoNormalizado.includes('disponible')) {
      textoMostrar = 'Por App';
    } else if (estadoNormalizado.includes('no atendido') || estadoNormalizado.includes('sin asignar')) {
      textoMostrar = 'NO ATENDIDO';
      color = '#ef4444';
    }

    return {
      texto: textoMostrar,
      color: color
    };
  };

  // Cargar viajes de la fecha actual al montar el componente
  useEffect(() => {
    const fechaActual = obtenerFechaActual();
    setFechaInicio(fechaActual);
    setFechaFin(fechaActual);
    cargarViajesPorRango(fechaActual, fechaActual);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Filtrar viajes y recalcular resúmenes dinámicamente
  const viajesFiltrados = React.useMemo(() => {
    if (operadorSeleccionado === 'Todos') {
      return viajes;
    }
    return viajes.filter(viaje => {
      const nombreOperadora = (viaje?.operador && (viaje.operador.nombre || viaje.operador.name)) ||
        viaje?.operadora ||
        viaje?.operador ||
        viaje?.operator ||
        viaje?.atendidoPor ||
        'Automático';

      let operadorNormalizado = (!nombreOperadora || nombreOperadora === '' || nombreOperadora === '-') ? 'Automático' : nombreOperadora;

      if (operadorNormalizado === 'Sin Operador') {
        operadorNormalizado = 'Automático';
      }

      return operadorNormalizado === operadorSeleccionado;
    });
  }, [viajes, operadorSeleccionado]);

  // Recalcular resumen de estados basado en la vista filtrada actual
  const resumenEstadosFiltrados = React.useMemo(() => {
    return calcularResumenEstados(viajesFiltrados);
  }, [viajesFiltrados]);

  // Contadores para badges del encabezado (usando datos filtrados)


  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 30 }}>
        <h2 style={{
          margin: '0 0 10px 0',
          color: '#1f2937',
          fontSize: '28px',
          fontWeight: 'bold'
        }}>
          📊 Reportes del Sistema
        </h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '6px 0 10px 0', alignItems: 'center' }}>
          <span style={{
            background: '#2563eb',
            color: 'white',
            borderRadius: '9999px',
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 'bold',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            Total: {viajesFiltrados.length}
          </span>
        </div>

        {/* Análisis del Periodo Seleccionado (Dinámico por Filtro) */}
        {viajesFiltrados.length > 0 && resumenEstadosFiltrados && (
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '30px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#1e293b', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              📈 Análisis del Periodo Seleccionado
              <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#64748b', background: '#f1f5f9', padding: '4px 10px', borderRadius: '6px' }}>
                Resumen de {viajesFiltrados.length} órdenes filtradas
              </span>
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>✅ Completados</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#059669' }}>{resumenEstadosFiltrados['Viajes Completados'] || 0}</div>
              </div>
              <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>❌ Rechazos Driver</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#e11d48' }}>{resumenEstadosFiltrados['Total Rechazos'] || 0}</div>
              </div>
              <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>📱 App Directa</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>{resumenEstadosFiltrados['Por App'] || 0}</div>
              </div>
              <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>⚠️ No Atendidos</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ea580c' }}>{resumenEstadosFiltrados['No Atendido'] || 0}</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {/* Ranking Rechazos */}
              <div style={{ background: 'white', border: '1px solid #fee2e2', borderRadius: '12px', padding: '16px' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#991b1b', fontSize: '15px' }}>🚨 Unidades que más Rechazan (Periodo)</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {resumenEstadosFiltrados.unidadesStats && Object.entries(resumenEstadosFiltrados.unidadesStats)
                    .sort(([, a], [, b]) => (b.rechazos || 0) - (a.rechazos || 0))
                    .slice(0, 5)
                    .map(([unidad, data]) => (
                      data.rechazos > 0 && (
                        <div key={unidad} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', background: '#fff5f5', borderRadius: '6px' }}>
                          <span style={{ fontWeight: 'bold', color: '#1e293b' }}>🚕 Unidad {unidad}</span>
                          <span style={{ background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>{data.rechazos} rechazos</span>
                        </div>
                      )
                    ))}
                </div>
              </div>

              {/* Ranking Completados */}
              <div style={{ background: 'white', border: '1px solid #d1fae5', borderRadius: '12px', padding: '16px' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#065f46', fontSize: '15px' }}>🏆 Unidades más Eficientes (Periodo)</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {resumenEstadosFiltrados.unidadesStats && Object.entries(resumenEstadosFiltrados.unidadesStats)
                    .sort(([, a], [, b]) => (b.completados || 0) - (a.completados || 0))
                    .slice(0, 5)
                    .map(([unidad, data]) => (
                      data.completados > 0 && (
                        <div key={unidad} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', background: '#f0fdf4', borderRadius: '6px' }}>
                          <span style={{ fontWeight: 'bold', color: '#1e293b' }}>🚕 Unidad {unidad}</span>
                          <span style={{ background: '#10b981', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>{data.completados} viajes</span>
                        </div>
                      )
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <p style={{
          margin: '0 0 20px 0',
          color: '#6b7280',
          fontSize: '16px'
        }}>
          Visualiza todos los viajes por rango de fechas. Selecciona fechas de inicio y fin para filtrar los registros.
        </p>

        {/* Filtros de fecha y hora */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 15,
          marginBottom: 20,
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{
              fontWeight: 'bold',
              color: '#374151',
              fontSize: '16px'
            }}>
              📅 Desde:
            </label>
            <input
              type="date"
              value={fechaInicio ? fechaInicio.split('-').reverse().join('-') : ''}
              onChange={handleFechaInicioChange}
              style={{
                padding: '10px 15px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: 'white',
                color: '#374151'
              }}
            />
            <input
              type="time"
              value={horaInicio}
              onChange={handleHoraInicioChange}
              style={{
                padding: '10px 15px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: 'white',
                color: '#374151'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{
              fontWeight: 'bold',
              color: '#374151',
              fontSize: '16px'
            }}>
              📅 Hasta:
            </label>
            <input
              type="date"
              value={fechaFin ? fechaFin.split('-').reverse().join('-') : ''}
              onChange={handleFechaFinChange}
              style={{
                padding: '10px 15px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: 'white',
                color: '#374151'
              }}
            />
            <input
              type="time"
              value={horaFin}
              onChange={handleHoraFinChange}
              style={{
                padding: '10px 15px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: 'white',
                color: '#374151'
              }}
            />
          </div>

          <button
            onClick={aplicarFiltros}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#2563eb'}
            onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
          >
            🔍 Buscar
          </button>

          {/* Filtro de Operador */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{
              fontWeight: 'bold',
              color: '#374151',
              fontSize: '16px'
            }}>
              👤 Operador:
            </label>
            <select
              value={operadorSeleccionado}
              onChange={(e) => setOperadorSeleccionado(e.target.value)}
              style={{
                padding: '10px 15px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: 'white',
                color: '#374151',
                cursor: 'pointer',
                minWidth: '200px'
              }}
            >
              <option value="Todos">Todos los Operadores</option>
              {Object.keys(resumenOperadores).sort().map(op => (
                <option key={op} value={op}>
                  {op} ({resumenOperadores[op]})
                </option>
              ))}
            </select>
          </div>

          {/* Exportar a Excel (Reporte de Viajes) */}
          <button
            onClick={() => {
              try {
                const headers = [
                  'Fecha',
                  'Teléfono',
                  'Dirección',
                  'Sector',
                  'Nombre',
                  'Cliente',
                  'Tipo Pedido',
                  'Unidad',
                  'Valor',
                  'Rating',
                  'Comentario',
                  'Estado'
                ];

                const rows = viajesFiltrados.map((viaje) => {
                  const estadoTexto = ((viaje.estado || viaje.pedido || '') + '').toLowerCase();
                  const esSinUnidad = /sin asignar|no hubo unidad/.test(estadoTexto);
                  const telefono = viaje.telefono || viaje.telefonoCompleto || '';
                  const direccion = viaje.direccion || '';
                  const sector = viaje.sector || viaje.direccion || '';
                  const nombre = viaje.nombre || '';
                  const nombreCliente = viaje.nombreCliente || viaje.codigo || '';
                  const tipoPedido = viaje.tipoPedido || viaje.tipopedido || viaje.tipoViaje || '';
                  const unidad = esSinUnidad ? '0' : (viaje.numeroUnidad || viaje.unidad || viaje.viajes || '');
                  const valorMostrar = (viaje.valor || viaje.montoTotalCalculado) ? (Number(viaje.valor || viaje.montoTotalCalculado) || 0).toFixed(2) : '0.00';
                  const fechaMostrar = (() => {
                    const fm = formatearFechaMostrar(viaje.fecha);
                    return (fm === 'N/A' && typeof viaje.fecha === 'string') ? viaje.fecha : fm;
                  })();

                  const ratingRaw = (viaje?.rating ?? viaje?.calificacion ?? viaje?.puntuacion ?? viaje?.valoracion ?? viaje?.evaluacion ?? (viaje?.feedback && (viaje.feedback.rating ?? viaje.feedback.calificacion ?? viaje.feedback.puntuacion)));
                  const ratingNum = Number(ratingRaw);
                  const ratingOut = !isNaN(ratingNum) && ratingNum > 0 ? `${Math.max(1, Math.min(5, Math.round(ratingNum)))}` : '';
                  const comentarioOut = (viaje?.comment ?? viaje?.comentario ?? viaje?.comentarios ?? viaje?.observacion ?? viaje?.observaciones ?? (viaje?.feedback && (viaje.feedback.comment ?? viaje.feedback.comentario ?? viaje.feedback.observacion))) || '';
                  const estadoOut = (viaje.estado || viaje.pedido || '') + '';

                  return [
                    fechaMostrar,
                    telefono,
                    direccion,
                    sector,
                    nombre,
                    nombreCliente,
                    tipoPedido,
                    unidad,
                    `$${valorMostrar}`,
                    ratingOut,
                    comentarioOut,
                    estadoOut
                  ];
                });

                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
                ws['!cols'] = [
                  { wch: 20 }, // Fecha
                  { wch: 16 }, // Teléfono
                  { wch: 35 }, // Dirección
                  { wch: 25 }, // Sector
                  { wch: 20 }, // Nombre
                  { wch: 20 }, // Cliente
                  { wch: 14 }, // Tipo Pedido
                  { wch: 8 },  // Unidad
                  { wch: 10 }, // Valor
                  { wch: 8 },  // Rating
                  { wch: 40 }, // Comentario
                  { wch: 14 }  // Estado
                ];
                XLSX.utils.book_append_sheet(wb, ws, 'Viajes');
                const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.href = url;
                const hoyISO = ymdEcuador();
                link.download = `reporte_viajes_${hoyISO}.xlsx`;
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                  if (link.parentNode) link.parentNode.removeChild(link);
                  try { URL.revokeObjectURL(url); } catch { }
                }, 0);
              } catch (err) {
                console.error('Error al exportar Excel:', err);
                alert('No se pudo exportar el Excel.');
              }
            }}
            style={{
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.target.style.background = '#059669'}
            onMouseLeave={(e) => e.target.style.background = '#10b981'}
          >
            📥 Exportar Excel
          </button>

          {fechaInicio && fechaFin && (
            <span style={{
              color: '#6b7280',
              fontSize: '14px'
            }}>
              Mostrando viajes del {fechaInicio ? fechaInicio.replace(/-/g, '/') : ''} {horaInicio} al {fechaFin ? fechaFin.replace(/-/g, '/') : ''} {horaFin}
              {viajesFiltrados.length > 0 && ` • ${viajesFiltrados.length} viajes encontrados`}
            </span>
          )}
        </div>
      </div>

      {/* Resumen de estados y tipos */}
      {!cargando && !error && (
        <div style={{
          display: 'flex',
          gap: '20px',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          {/* Resumen por Operadora (Nuevo) */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            padding: '20px',
            flex: '1',
            minWidth: '300px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h4 style={{
              margin: '0 0 15px 0',
              color: '#1f2937',
              fontSize: '18px',
              fontWeight: 'bold'
            }}>
              👥 Resumen por Operadora
            </h4>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              {Object.entries(resumenOperadores)
                // Ordenar por cantidad descendente
                .sort(([, a], [, b]) => b - a)
                .map(([operador, cantidad]) => (
                  <div
                    key={operador}
                    onClick={() => setOperadorSeleccionado(operador === operadorSeleccionado ? 'Todos' : operador)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background: operador === operadorSeleccionado ? '#eff6ff' : '#f8fafc',
                      borderRadius: '6px',
                      border: operador === operadorSeleccionado ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}>
                    <span style={{
                      fontWeight: '500',
                      color: operador === operadorSeleccionado ? '#1d4ed8' : '#374151'
                    }}>
                      {operador}
                    </span>
                    <span style={{
                      background: operador === operadorSeleccionado ? '#3b82f6' : '#64748b',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {cantidad}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* Resumen de Estados (filtrado) */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            padding: '20px',
            flex: '1',
            minWidth: '300px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h4 style={{
              margin: '0 0 15px 0',
              color: '#1f2937',
              fontSize: '18px',
              fontWeight: 'bold'
            }}>
              📊 Resumen por Estado {operadorSeleccionado !== 'Todos' && <span style={{ fontSize: '14px', color: '#6b7280' }}>(Filtro: {operadorSeleccionado})</span>}
            </h4>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {Object.entries(resumenEstadosFiltrados || {})
                .filter(([key]) => key !== 'unidadesStats')
                .map(([estado, cantidad]) => (
                  <div key={estado} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    background: '#f8fafc',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <span style={{
                      fontWeight: '500',
                      color: '#374151'
                    }}>
                      {estado}
                    </span>
                    <span style={{
                      background: '#3b82f6',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {cantidad}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Estado de carga */}
      {cargando && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#6b7280'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
          Cargando viajes...
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Tabla de viajes */}
      {!cargando && !error && (
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{
              margin: 0,
              color: '#1f2937',
              fontSize: '20px'
            }}>
              🚗 Viajes ({viajesFiltrados.length})
            </h3>
            {viajesFiltrados.length > 0 && (
              <div style={{
                color: '#6b7280',
                fontSize: '14px'
              }}>
                Total de registros: {viajesFiltrados.length}
              </div>
            )}
          </div>

          {viajesFiltrados.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#6b7280',
              background: '#f9fafb',
              borderRadius: '12px',
              border: '2px dashed #d1d5db'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>📭</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                No hay viajes registrados
              </div>
              <div style={{ fontSize: '14px' }}>
                No se encontraron viajes para el rango de fechas seleccionado
              </div>
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                overflowX: 'auto',
                maxWidth: '100%'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  minWidth: '1000px'
                }}>
                  <thead>
                    <tr style={{
                      background: '#f8fafc',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      {[
                        '📅 Fecha',
                        '📱 Teléfono',
                        '📍 Dirección',
                        '👨‍💼 Nombre',
                        '👤 Cliente',
                        '�‍💼 Operador',
                        '�🚖 Tipo Pedido',
                        '🔢 Unidad',
                        '🏷️ Estado',
                        '⭐ Rating',
                        '💬 Comentario'
                      ].map((titulo) => (
                        <th key={titulo} style={{
                          padding: '15px 12px',
                          textAlign: 'left',
                          fontWeight: 'bold',
                          color: '#374151',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          {titulo}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {viajesFiltrados.map((viaje) => {
                      const estadoInfo = obtenerEstadoConColor(viaje.estado || viaje.pedido);
                      const estadoTexto = ((viaje.estado || viaje.pedido || '') + '').toLowerCase();
                      const esSinUnidad = /sin asignar|no hubo unidad/.test(estadoTexto);
                      // Derivados con tolerancia a variantes de nombres de campo
                      const telefono = viaje.telefono || viaje.telefonoCompleto || 'N/A';
                      const direccion = viaje.direccion || 'N/A';
                      // const sector = viaje.sector || viaje.direccion || 'N/A'; // Eliminado de la tabla
                      const nombre = viaje.nombre || 'N/A';
                      const nombreCliente = viaje.nombreCliente || viaje.codigo || 'N/A';
                      const tipoPedido = viaje.tipoPedido || viaje.tipopedido || viaje.tipoViaje || 'Sin Tipo';
                      const unidad = esSinUnidad ? '0' : (viaje.numeroUnidad || viaje.unidad || viaje.viajes || 'N/A');
                      // const valorMostrar = formatearValor(viaje.valor || viaje.montoTotalCalculado || 0); // Eliminado de la tabla
                      // Feedback
                      const ratingRaw = (viaje?.rating ?? viaje?.calificacion ?? viaje?.puntuacion ?? viaje?.valoracion ?? viaje?.evaluacion ?? (viaje?.feedback && (viaje.feedback.rating ?? viaje.feedback.calificacion ?? viaje.feedback.puntuacion)));
                      const ratingNum = Number(ratingRaw);
                      const ratingOut = !isNaN(ratingNum) && ratingNum > 0 ? '★'.repeat(Math.max(1, Math.min(5, Math.round(ratingNum)))) : (typeof ratingRaw === 'string' ? ratingRaw : '');
                      const comentarioOut = (viaje?.comment ?? viaje?.comentario ?? viaje?.comentarios ?? viaje?.observacion ?? viaje?.observaciones ?? (viaje?.feedback && (viaje.feedback.comment ?? viaje.feedback.comentario ?? viaje.feedback.observacion))) || '';
                      // Operador/operadora
                      const operadorOut = (viaje?.operador && (viaje.operador.nombre || viaje.operador.name)) || viaje?.operadora || viaje?.operador || viaje?.operator || viaje?.atendidoPor || '';
                      const fechaMostrada = (() => {
                        const fm = formatearFechaMostrar(viaje.fecha);
                        return (fm === 'N/A' && typeof viaje.fecha === 'string') ? viaje.fecha : fm;
                      })();

                      return (
                        <tr key={viaje.id} style={{
                          borderBottom: '1px solid #f3f4f6',
                          transition: 'background 0.2s',
                          backgroundColor: viaje.esVoucher ? '#fef3c7' :
                            viaje.esViajeFinalizado ? '#dbeafe' : 'white' // Amarillo para vouchers, azul para finalizados
                        }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = viaje.esVoucher ? '#fde68a' :
                              viaje.esViajeFinalizado ? '#bfdbfe' : '#f9fafb';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = viaje.esVoucher ? '#fef3c7' :
                              viaje.esViajeFinalizado ? '#dbeafe' : 'white';
                          }}>
                          <td style={{ padding: '12px', color: '#6b7280', fontSize: '13px' }}>{fechaMostrada}</td>
                          <td style={{ padding: '12px', color: '#374151', fontFamily: 'monospace' }}>{telefono}</td>
                          <td style={{ padding: '12px', color: '#374151', maxWidth: '240px', wordWrap: 'break-word' }}>{direccion}</td>
                          {/* Sector eliminado */}
                          <td style={{ padding: '12px', color: '#374151' }}>{nombre}</td>
                          <td style={{ padding: '12px', color: '#1f2937', fontWeight: 500 }}>{nombreCliente}</td>
                          <td style={{ padding: '12px', color: '#374151' }}>{operadorOut || '—'}</td>
                          <td style={{ padding: '12px', color: '#374151' }}>{tipoPedido}</td>
                          <td style={{ padding: '12px', color: '#374151' }}>{unidad}</td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              background: estadoInfo.color,
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase'
                            }}>
                              {estadoInfo.texto}
                            </span>
                          </td>
                          <td style={{ padding: '12px', color: '#374151' }}>{ratingOut || '—'}</td>
                          <td style={{ padding: '12px', color: '#374151', maxWidth: '260px', wordWrap: 'break-word' }}>{(comentarioOut && `${comentarioOut}`.trim()) ? `${comentarioOut}` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Gestión de Operadores
function OperadoresContent() {
  const [operadores, setOperadores] = useState([]);
  const [cargandoOperadores, setCargandoOperadores] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [nuevoOperador, setNuevoOperador] = useState({ nombre: '', usuario: '', codigo: '' });
  const [errorFormulario, setErrorFormulario] = useState('');

  // Cargar operadores
  const cargarOperadores = async () => {
    setCargandoOperadores(true);
    try {
      const operadoresRef = collection(db, 'operadores');
      const snapshot = await getDocs(operadoresRef);
      const operadoresData = snapshot.docs.map(docu => ({ id: docu.id, ...docu.data() }));
      setOperadores(operadoresData);
    } catch (error) {
      console.error('❌ Error al cargar operadores:', error);
    } finally {
      setCargandoOperadores(false);
    }
  };

  // Crear nuevo operador
  const crearOperador = async () => {
    if (!nuevoOperador.nombre.trim() || !nuevoOperador.usuario.trim() || !nuevoOperador.codigo.trim()) {
      setErrorFormulario('Todos los campos son obligatorios');
      return;
    }
    if (nuevoOperador.codigo.length !== 4 || !/^\d{4}$/.test(nuevoOperador.codigo)) {
      setErrorFormulario('El código debe tener exactamente 4 dígitos numéricos');
      return;
    }
    try {
      // Verificar usuario único
      const operadoresRef = collection(db, 'operadores');
      const qUsuario = query(operadoresRef, where('usuario', '==', nuevoOperador.usuario));
      const snapUsuario = await getDocs(qUsuario);
      if (!snapUsuario.empty) {
        setErrorFormulario('El usuario ya existe');
        return;
      }
      // Verificar código único
      const qCodigo = query(operadoresRef, where('codigo', '==', nuevoOperador.codigo));
      const snapCodigo = await getDocs(qCodigo);
      if (!snapCodigo.empty) {
        setErrorFormulario('El código ya existe');
        return;
      }
      // Crear
      await addDoc(collection(db, 'operadores'), {
        nombre: nuevoOperador.nombre.trim(),
        usuario: nuevoOperador.usuario.trim(),
        codigo: nuevoOperador.codigo.trim(),
        fechaCreacion: new Date(),
        activo: true
      });
      setNuevoOperador({ nombre: '', usuario: '', codigo: '' });
      setErrorFormulario('');
      setMostrarFormulario(false);
      await cargarOperadores();
      alert('✅ Operador creado exitosamente');
    } catch (error) {
      console.error('❌ Error al crear operador:', error);
      setErrorFormulario('Error al crear el operador');
    }
  };

  // Eliminar operador
  const eliminarOperador = async (operadorId) => {
    if (window.confirm('¿Está seguro de que desea eliminar este operador?')) {
      try {
        await deleteDoc(doc(db, 'operadores', operadorId));
        cargarOperadores();
        alert('✅ Operador eliminado exitosamente');
      } catch (error) {
        console.error('❌ Error al eliminar operador:', error);
        alert('❌ Error al eliminar el operador');
      }
    }
  };

  useEffect(() => {
    cargarOperadores();
  }, []);

  return (
    <div style={{
      padding: '20px',
      backgroundColor: '#f9fafb',
      minHeight: '100vh'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{
          color: '#1f2937',
          fontSize: '24px',
          fontWeight: 'bold',
          margin: 0
        }}>
          👥 Gestión de Operadores
        </h2>
        <button
          onClick={() => setMostrarFormulario(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
        >
          ➕ Crear Operador
        </button>
      </div>

      {/* Formulario para crear operador */}
      {mostrarFormulario && (
        <div style={{
          backgroundColor: '#ffffff',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '20px'
        }}>
          <h3 style={{
            color: '#1f2937',
            marginBottom: '15px',
            fontSize: '18px',
            fontWeight: 'bold'
          }}>
            📝 Crear Nuevo Operador
          </h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: '#374151',
              fontWeight: '600'
            }}>
              Nombre Completo:
            </label>
            <input
              type="text"
              value={nuevoOperador.nombre}
              onChange={(e) => setNuevoOperador({ ...nuevoOperador, nombre: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              placeholder="Ingrese el nombre completo"
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: '#374151',
              fontWeight: '600'
            }}>
              Usuario:
            </label>
            <input
              type="text"
              value={nuevoOperador.usuario}
              onChange={(e) => setNuevoOperador({ ...nuevoOperador, usuario: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              placeholder="Ingrese el nombre de usuario"
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: '#374151',
              fontWeight: '600'
            }}>
              Código (4 dígitos):
            </label>
            <input
              type="password"
              value={nuevoOperador.codigo}
              onChange={(e) => setNuevoOperador({ ...nuevoOperador, codigo: e.target.value })}
              maxLength="4"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'monospace',
                letterSpacing: '4px'
              }}
              placeholder="0000"
            />
          </div>

          {errorFormulario && (
            <div style={{
              color: '#dc2626',
              fontSize: '14px',
              marginBottom: '15px',
              padding: '10px',
              backgroundColor: '#fef2f2',
              borderRadius: '6px',
              border: '1px solid #fecaca'
            }}>
              {errorFormulario}
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: '10px'
          }}>
            <button
              onClick={crearOperador}
              style={{
                padding: '10px 20px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
            >
              ✅ Crear Operador
            </button>
            <button
              onClick={() => {
                setMostrarFormulario(false);
                setNuevoOperador({ nombre: '', usuario: '', codigo: '' });
                setErrorFormulario('');
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
            >
              ❌ Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista de operadores */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{
          color: '#1f2937',
          marginBottom: '15px',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          📋 Lista de Operadores ({operadores.length})
        </h3>

        {cargandoOperadores ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ color: '#6b7280' }}>Cargando operadores...</p>
          </div>
        ) : operadores.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ color: '#6b7280' }}>No hay operadores registrados</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gap: '15px'
          }}>
            {operadores.map((operador) => (
              <div key={operador.id} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '15px',
                backgroundColor: '#f9fafb'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <h4 style={{
                      margin: '0 0 5px 0',
                      color: '#1f2937',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}>
                      {operador.nombre}
                    </h4>
                    <p style={{
                      margin: '0 0 5px 0',
                      color: '#6b7280',
                      fontSize: '14px'
                    }}>
                      Usuario: {operador.usuario}
                    </p>
                    <p style={{
                      margin: '0',
                      color: '#6b7280',
                      fontSize: '14px'
                    }}>
                      Código: {'•'.repeat(4)}
                    </p>
                  </div>
                  <button
                    onClick={() => eliminarOperador(operador.id)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                  >
                    🗑️ Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function VouchersContent({ operadorAutenticado }) {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    empresa: '',
    fechaInicio: '',
    fechaFin: '',
    numeroAutorizacion: ''
  });
  const [voucherSeleccionado, setVoucherSeleccionado] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [voucherEditado, setVoucherEditado] = useState(null);

  // Función auxiliar para consultar todas las colecciones y obtener números de autorización existentes
  const obtenerNumerosAutorizacionExistentes = async (logPrefix = '') => {
    const numerosExistentes = new Set();

    try {
      // 1. Consultar voucherCorporativos
      console.log(`${logPrefix}📋 Consultando voucherCorporativos`);
      const vouchersRef = collection(db, 'voucherCorporativos');
      const vouchersSnapshot = await getDocs(vouchersRef);

      vouchersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const numAuth = data.numeroAutorizacion;
        // Convertir a número si es string o ya es número
        const numAuthNumber = typeof numAuth === 'string' ? parseInt(numAuth) : numAuth;
        if (typeof numAuthNumber === 'number' && !isNaN(numAuthNumber) && numAuthNumber >= 100) {
          numerosExistentes.add(numAuthNumber);
          console.log(`${logPrefix}✅ Voucher encontrado: ${numAuthNumber} (tipo original: ${typeof numAuth})`);
        }
      });

      // 2. Consultar preregistroVouchers
      console.log(`${logPrefix}📋 Consultando preregistroVouchers`);
      try {
        const preregistroRef = collection(db, 'preregistroVouchers');
        const preregistroSnapshot = await getDocs(preregistroRef);

        preregistroSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const numAuth = data.numeroAutorizacion;
          // Convertir a número si es string o ya es número
          const numAuthNumber = typeof numAuth === 'string' ? parseInt(numAuth) : numAuth;
          if (typeof numAuthNumber === 'number' && !isNaN(numAuthNumber) && numAuthNumber >= 100) {
            numerosExistentes.add(numAuthNumber);
            console.log(`${logPrefix}✅ PreRegistro encontrado: ${numAuthNumber} (tipo original: ${typeof numAuth})`);
          }
        });
      } catch (preregistroError) {
        console.log(`${logPrefix}⚠️ No se pudo consultar preregistroVouchers`);
      }

      // 3. Consultar todosLosViajes (últimos 7 días)
      console.log(`${logPrefix}📋 Consultando todosLosViajes (últimos 7 días)`);
      const hoy = new Date();
      for (let i = 0; i < 7; i++) {
        const fecha = new Date(hoy);
        fecha.setDate(fecha.getDate() - i);
        const fechaStr = fecha.toLocaleDateString('es-EC', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).replace(/\//g, '-');

        try {
          const viajesRef = collection(db, 'todosLosViajes', fechaStr, 'viajes');
          const viajesSnapshot = await getDocs(viajesRef);

          viajesSnapshot.docs.forEach(doc => {
            const data = doc.data();

            // Verificar data.autorizacion
            if (data.autorizacion) {
              const authNumber = typeof data.autorizacion === 'string' ? parseInt(data.autorizacion) : data.autorizacion;
              if (typeof authNumber === 'number' && !isNaN(authNumber) && authNumber >= 100) {
                numerosExistentes.add(authNumber);
                console.log(`${logPrefix}✅ Autorización en viaje encontrada: ${authNumber}`);
              }
            }

            // Verificar data.numeroAutorizacion
            if (data.numeroAutorizacion) {
              const numAuthNumber = typeof data.numeroAutorizacion === 'string' ? parseInt(data.numeroAutorizacion) : data.numeroAutorizacion;
              if (typeof numAuthNumber === 'number' && !isNaN(numAuthNumber) && numAuthNumber >= 100) {
                numerosExistentes.add(numAuthNumber);
                console.log(`${logPrefix}✅ NumeroAutorizacion en viaje encontrado: ${numAuthNumber}`);
              }
            }

            // Verificar data.numeroAutorizacionVoucher
            if (data.numeroAutorizacionVoucher) {
              const voucherNumber = typeof data.numeroAutorizacionVoucher === 'string' ? parseInt(data.numeroAutorizacionVoucher) : data.numeroAutorizacionVoucher;
              if (typeof voucherNumber === 'number' && !isNaN(voucherNumber) && voucherNumber >= 100) {
                numerosExistentes.add(voucherNumber);
                console.log(`${logPrefix}✅ NumeroAutorizacionVoucher en viaje encontrado: ${voucherNumber}`);
              }
            }
          });
        } catch (viajesError) {
          console.log(`${logPrefix}⚠️ No se pudo consultar todosLosViajes para fecha ${fechaStr}`);
        }
      }

      // 4. Consultar pedidosDisponibles
      console.log(`${logPrefix}📋 Consultando pedidosDisponibles`);
      try {
        const pedidosRef = collection(db, 'pedidosDisponibles');
        const pedidosSnapshot = await getDocs(pedidosRef);

        pedidosSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.autorizacion) {
            const authNumber = typeof data.autorizacion === 'string' ? parseInt(data.autorizacion) : data.autorizacion;
            if (typeof authNumber === 'number' && !isNaN(authNumber) && authNumber >= 100) {
              numerosExistentes.add(authNumber);
              console.log(`${logPrefix}✅ Autorización en pedido encontrada: ${authNumber}`);
            }
          }
        });
      } catch (pedidosError) {
        console.log(`${logPrefix}⚠️ No se pudo consultar pedidosDisponibles`);
      }

      const numerosOrdenados = Array.from(numerosExistentes).sort((a, b) => a - b);
      console.log(`${logPrefix}🔍 Total números encontrados: ${numerosOrdenados.length}`);
      if (numerosOrdenados.length > 0) {
        console.log(`${logPrefix}🔍 Últimos 10 números: ${numerosOrdenados.slice(-10).join(', ')}`);
      }

      return numerosExistentes;
    } catch (error) {
      console.error(`${logPrefix}❌ Error al consultar colecciones:`, error);
      return numerosExistentes;
    }
  };

  // Lista de empresas para el voucher
  const empresasVoucher = EMPRESAS_MENU_PRINCIPAL;

  // Cargar vouchers
  const cargarVouchers = async () => {
    try {
      setLoading(true);
      const vouchersRef = collection(db, 'voucherCorporativos');
      const q = query(vouchersRef, orderBy('fechaCreacion', 'desc'));
      const querySnapshot = await getDocs(q);

      const vouchersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setVouchers(vouchersData);
    } catch (error) {
      console.error('Error al cargar vouchers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    return vouchers.filter(voucher => {
      const cumpleEmpresa = !filtros.empresa ||
        voucher.empresa?.toLowerCase().includes(filtros.empresa.toLowerCase());

      const cumpleNumeroAutorizacion = !filtros.numeroAutorizacion ||
        voucher.numeroAutorizacion?.toString().includes(filtros.numeroAutorizacion);

      let cumpleFecha = true;
      if (filtros.fechaInicio && voucher.fechaCreacion) {
        const fechaVoucher = voucher.fechaCreacion.toDate ?
          voucher.fechaCreacion.toDate() : new Date(voucher.fechaCreacion);
        const fechaInicio = new Date(filtros.fechaInicio);
        cumpleFecha = fechaVoucher >= fechaInicio;
      }

      if (filtros.fechaFin && voucher.fechaCreacion) {
        const fechaVoucher = voucher.fechaCreacion.toDate ?
          voucher.fechaCreacion.toDate() : new Date(voucher.fechaCreacion);
        const fechaFin = new Date(filtros.fechaFin);
        cumpleFecha = cumpleFecha && fechaVoucher <= fechaFin;
      }

      return cumpleEmpresa && cumpleNumeroAutorizacion && cumpleFecha;
    });
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    if (fecha.toDate) {
      return fecha.toDate().toLocaleString('es-EC');
    }
    return new Date(fecha).toLocaleString('es-EC');
  };

  // Obtener color del estado
  const obtenerColorEstado = (estado) => {
    switch (estado) {
      case 'Activo': return '#10b981';
      case 'Cancelado': return '#ef4444';
      case 'Finalizado': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  // Ver detalles del voucher
  const verDetalles = (voucher) => {
    setVoucherSeleccionado(voucher);
    setVoucherEditado({ ...voucher });
    setModoEdicion(false);
    setMostrarModal(true);
  };

  // Cerrar modal
  const cerrarModal = () => {
    setVoucherSeleccionado(null);
    setVoucherEditado(null);
    setModoEdicion(false);
    setMostrarModal(false);
  };

  // Activar modo edición
  const activarEdicion = () => {
    setModoEdicion(true);
  };

  // Cancelar edición
  const cancelarEdicion = () => {
    setVoucherEditado({ ...voucherSeleccionado });
    setModoEdicion(false);
  };

  // Guardar cambios
  const guardarCambios = async () => {
    try {
      // Actualizar en Firestore usando numeroAutorizacion como ID del documento
      const voucherRef = doc(db, 'voucherCorporativos', voucherSeleccionado.numeroAutorizacion.toString());
      await updateDoc(voucherRef, {
        nombreCliente: voucherEditado.nombreCliente,
        empresa: voucherEditado.empresa,
        destino: voucherEditado.destino,
        direccion: voucherEditado.direccion,
        fechaHoraInicio: voucherEditado.fechaHoraInicio,
        fechaHoraFinal: voucherEditado.fechaHoraFinal,
        valor: voucherEditado.valor
      });

      // Actualizar estado local
      setVoucherSeleccionado({ ...voucherEditado });
      setModoEdicion(false);

      // Recargar lista de vouchers
      await cargarVouchers();

      alert('Voucher actualizado correctamente');
    } catch (error) {
      console.error('Error al actualizar voucher:', error);
      alert('Error al actualizar el voucher');
    }
  };



  // Cargar datos al montar el componente
  useEffect(() => {
    cargarVouchers();
    obtenerSiguienteNumeroAutorizacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Función para obtener el siguiente número de autorización disponible (consulta múltiples colecciones)
  const obtenerSiguienteNumeroAutorizacion = async () => {
    try {
      console.log('🔍 [Vouchers] Consultando múltiples colecciones para obtener el siguiente número');
      const numerosExistentes = await obtenerNumerosAutorizacionExistentes('[Vouchers] ');

      // Buscar el primer número consecutivo que NO esté en uso
      let siguienteNumero = 100;
      while (numerosExistentes.has(siguienteNumero)) {
        siguienteNumero++;
      }

      console.log(`✅ [Vouchers] Siguiente número disponible: ${siguienteNumero} (verificado en múltiples colecciones)`);
    } catch (error) {
      console.error('Error al obtener número de autorización:', error);
    }
  };

  const vouchersFiltrados = aplicarFiltros();

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 10px 0', color: '#1f2937' }}>Gestión de Vouchers</h2>
        <p style={{ margin: 0, color: '#6b7280' }}>Administra los vouchers corporativos del sistema.</p>
        {operadorAutenticado && (
          <div style={{
            marginTop: '10px',
            padding: '8px 12px',
            background: '#f3f4f6',
            borderRadius: '6px',
            border: '1px solid #e5e7eb',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '14px', color: '#374151' }}>👤 Operador:</span>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              background: '#3b82f6',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px'
            }}>
              {operadorAutenticado.nombre}
            </span>
          </div>
        )}
      </div>

      {/* Resumen Superior */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        marginBottom: '20px'
      }}>
        {/* Total de Vouchers */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#3b82f6',
            marginBottom: '8px'
          }}>
            {vouchersFiltrados.length}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            Total de Vouchers
          </div>
        </div>

        {/* Valor Total */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#f59e0b',
            marginBottom: '8px'
          }}>
            ${vouchersFiltrados.reduce((total, voucher) => {
              const valor = parseFloat(voucher.valor) || 0;
              return total + valor;
            }, 0).toFixed(2)}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            Valor Total
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#374151' }}>Filtros</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#374151' }}>
              Empresa
            </label>
            <input
              type="text"
              value={filtros.empresa}
              onChange={(e) => setFiltros(prev => ({ ...prev, empresa: e.target.value }))}
              placeholder="Buscar por empresa..."
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#374151' }}>
              N° Autorización
            </label>
            <input
              type="text"
              value={filtros.numeroAutorizacion}
              onChange={(e) => setFiltros(prev => ({ ...prev, numeroAutorizacion: e.target.value }))}
              placeholder="Buscar por número..."
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#374151' }}>
              Fecha Inicio
            </label>
            <input
              type="date"
              value={filtros.fechaInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, fechaInicio: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#374151' }}>
              Fecha Fin
            </label>
            <input
              type="date"
              value={filtros.fechaFin}
              onChange={(e) => setFiltros(prev => ({ ...prev, fechaFin: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setFiltros({
              empresa: '',
              fechaInicio: '',
              fechaFin: '',
              numeroAutorizacion: ''
            })}
            style={{
              padding: '8px 16px',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Limpiar Filtros
          </button>

          <button
            onClick={cargarVouchers}
            style={{
              padding: '8px 16px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🔄 Actualizar
          </button>

          <button
            onClick={() => {
              const vouchersFiltrados = aplicarFiltros();

              // Crear datos para XLSX
              const headers = [
                'N° Autorización',
                'Cliente',
                'Teléfono',
                'Empresa',
                'Operador',
                'Dirección',
                'Destino',
                'Unidad',
                'Estado',
                'Valor',
                'Fecha'
              ];

              // Crear array de datos para XLSX
              const excelData = [
                headers,
                ...vouchersFiltrados.map(voucher => [
                  voucher.numeroAutorizacion,
                  voucher.nombreCliente,
                  voucher.telefono,
                  voucher.empresa,
                  voucher.operadora || 'Sin operador',
                  voucher.direccion,
                  voucher.destino,
                  voucher.numeroUnidad,
                  voucher.estado,
                  voucher.valor,
                  formatearFecha(voucher.fechaCreacion)
                ])
              ];

              // Crear workbook y worksheet
              const wb = XLSX.utils.book_new();
              const ws = XLSX.utils.aoa_to_sheet(excelData);

              // Ajustar ancho de columnas
              const colWidths = [
                { wch: 15 }, // N° Autorización
                { wch: 20 }, // Cliente
                { wch: 15 }, // Teléfono
                { wch: 20 }, // Empresa
                { wch: 30 }, // Dirección
                { wch: 25 }, // Destino
                { wch: 10 }, // Unidad
                { wch: 12 }, // Estado
                { wch: 12 }, // Valor
                { wch: 20 }  // Fecha
              ];
              ws['!cols'] = colWidths;

              // Agregar worksheet al workbook
              XLSX.utils.book_append_sheet(wb, ws, 'Vouchers');

              // Generar archivo XLSX
              const xlsxBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
              const blob = new Blob([xlsxBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

              // Descargar archivo
              const link = document.createElement('a');
              const url = URL.createObjectURL(blob);
              link.href = url;
              link.download = `vouchers_${ymdEcuador()}.xlsx`;
              link.style.visibility = 'hidden';
              document.body.appendChild(link);
              link.click();
              // Cleanup safely in next tick to avoid DOM removal race conditions
              setTimeout(() => {
                if (link.parentNode) {
                  link.parentNode.removeChild(link);
                }
                try { URL.revokeObjectURL(url); } catch { }
              }, 0);
            }}
            style={{
              padding: '8px 16px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            📊 Descargar Reporte
          </button>
        </div>
      </div>



      {/* Lista de Vouchers */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            Cargando vouchers...
          </div>
        ) : vouchersFiltrados.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            No se encontraron vouchers con los filtros aplicados.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    N° Autorización
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Cliente
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Teléfono
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Empresa
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Operador
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Dirección
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Destino
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Unidad
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Estado
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Valor
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Fecha
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {vouchersFiltrados.map((voucher) => (
                  <tr key={voucher.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td>
                      {voucher.numeroAutorizacion}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                      {voucher.nombreCliente}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#374151' }}>
                      {voucher.telefono}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#374151' }}>
                      {voucher.empresa}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#374151' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: '#3b82f6',
                        color: 'white'
                      }}>
                        {voucher.operadora || 'Sin operador'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#374151' }}>
                      {voucher.direccion}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#1f2937' }}>
                      {voucher.destino}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#374151' }}>
                      {voucher.numeroUnidad}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: obtenerColorEstado(voucher.estado) + '20',
                        color: obtenerColorEstado(voucher.estado)
                      }}>
                        {voucher.estado}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500', color: '#059669' }}>
                      {'$'}{voucher.valor}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                      {formatearFecha(voucher.fechaCreacion)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        onClick={() => verDetalles(voucher)}
                        style={{
                          padding: '6px 12px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        👁️ Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Detalles */}
      {mostrarModal && voucherSeleccionado && voucherEditado && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1f2937' }}>
                {modoEdicion ? 'Editar Voucher' : 'Detalles del Voucher'}
              </h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {!modoEdicion && (
                  <button
                    onClick={activarEdicion}
                    style={{
                      padding: '6px 12px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ✏️ Editar
                  </button>
                )}
                <button
                  onClick={cerrarModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gap: '15px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Cliente</label>
                  {modoEdicion ? (
                    <input
                      type="text"
                      value={voucherEditado.nombreCliente || ''}
                      onChange={(e) => setVoucherEditado({ ...voucherEditado, nombreCliente: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontWeight: '500'
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#1f2937' }}>
                      {voucherSeleccionado.nombreCliente}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Teléfono</label>
                  <div style={{ fontSize: '16px', color: '#374151' }}>
                    {voucherSeleccionado.telefono}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Empresa</label>
                  {modoEdicion ? (
                    <select
                      value={voucherEditado.empresa || ''}
                      onChange={(e) => setVoucherEditado({ ...voucherEditado, empresa: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '16px'
                      }}
                    >
                      <option value="">Seleccionar empresa...</option>
                      {empresasVoucher.map((empresa, index) => (
                        <option key={index} value={empresa}>{empresa}</option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ fontSize: '16px', color: '#374151' }}>
                      {voucherSeleccionado.empresa}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Número Autorización</label>
                  <div style={{ fontSize: '16px', color: '#374151' }}>
                    {voucherSeleccionado.numeroAutorizacion}
                  </div>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Destino</label>
                {modoEdicion ? (
                  <input
                    type="text"
                    value={voucherEditado.destino || ''}
                    onChange={(e) => setVoucherEditado({ ...voucherEditado, destino: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '16px', color: '#374151' }}>
                    {voucherSeleccionado.destino}
                  </div>
                )}
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Dirección</label>
                {modoEdicion ? (
                  <input
                    type="text"
                    value={voucherEditado.direccion || ''}
                    onChange={(e) => setVoucherEditado({ ...voucherEditado, direccion: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '16px', color: '#374151' }}>
                    {voucherSeleccionado.direccion}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Fecha/Hora Inicio</label>
                  {modoEdicion ? (
                    <input
                      type="datetime-local"
                      value={voucherEditado.fechaHoraInicio || ''}
                      onChange={(e) => setVoucherEditado({ ...voucherEditado, fechaHoraInicio: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '16px'
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: '16px', color: '#374151' }}>
                      {voucherSeleccionado.fechaHoraInicio || 'No definida'}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Fecha/Hora Final</label>
                  {modoEdicion ? (
                    <input
                      type="datetime-local"
                      value={voucherEditado.fechaHoraFinal || ''}
                      onChange={(e) => setVoucherEditado({ ...voucherEditado, fechaHoraFinal: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '16px'
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: '16px', color: '#374151' }}>
                      {voucherSeleccionado.fechaHoraFinal || 'No definida'}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Unidad</label>
                  <div style={{ fontSize: '16px', color: '#374151' }}>
                    {voucherSeleccionado.numeroUnidad}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Estado</label>
                  <div style={{ fontSize: '16px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '500',
                      background: obtenerColorEstado(voucherSeleccionado.estado) + '20',
                      color: obtenerColorEstado(voucherSeleccionado.estado)
                    }}>
                      {voucherSeleccionado.estado}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Valor</label>
                  {modoEdicion ? (
                    <input
                      type="number"
                      step="0.01"
                      value={voucherEditado.valor || ''}
                      onChange={(e) => setVoucherEditado({ ...voucherEditado, valor: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#059669'
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>
                      {'$'}{voucherSeleccionado.valor}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Fecha Creación</label>
                  <div style={{ fontSize: '16px', color: '#374151' }}>
                    {formatearFecha(voucherSeleccionado.fechaCreacion)}
                  </div>
                </div>
              </div>

              {voucherSeleccionado.informacionViaje && (
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Información del Viaje</label>
                  <div style={{ fontSize: '16px', color: '#374151' }}>
                    {voucherSeleccionado.informacionViaje}
                  </div>
                </div>
              )}

              {voucherSeleccionado.motivo && (
                <div>
                  <label style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>Motivo</label>
                  <div style={{ fontSize: '16px', color: '#374151' }}>
                    {voucherSeleccionado.motivo}
                  </div>
                </div>
              )}
            </div>

            {/* Botones de acción para modo edición */}
            {modoEdicion && (
              <div style={{
                marginTop: '20px',
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e5e7eb',
                paddingTop: '15px'
              }}>
                <button
                  onClick={cancelarEdicion}
                  style={{
                    padding: '8px 16px',
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={guardarCambios}
                  style={{
                    padding: '8px 16px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  💾 Guardar Cambios
                </button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// Componente de Ubicaciones en Tiempo Real
function UbicacionesContent() {
  const [ubicaciones, setUbicaciones] = useState([]);
  const [autoRefresh] = useState(true); // Mantener el valor si se usa, pero sin setter si no se usa
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState({});
  const [connectionStatus, setConnectionStatus] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [leafletLoaded, setLeafletLoaded] = useState(false);
  const [busquedaUnidad, setBusquedaUnidad] = useState(''); // Estado para búsqueda
  const mapRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const REFRESH_INTERVAL = 5000; // 5 segundos

  // Usar endpoint de Vercel que actúa como proxy (evita CORS)
  const API_URL = '/api/locacion-location-plaza';

  const getUbicacionKey = (loc) => {
    if (!loc) return '';
    return String(loc._id || loc.unidad || loc.email || '');
  };

  const sanitizeUbicacion = (loc) => {
    const lat = Number(loc?.latitud);
    const lng = Number(loc?.longitud);
    return {
      _id: loc?._id ?? null,
      unidad: loc?.unidad ?? null,
      estado: loc?.estado ?? null,
      hora: loc?.hora ?? null,
      fecha: loc?.fecha ?? null,
      timestamp: loc?.timestamp ?? null,
      createdAt: loc?.createdAt ?? null,
      updatedAt: loc?.updatedAt ?? null,
      latitud: Number.isFinite(lat) ? lat : null,
      longitud: Number.isFinite(lng) ? lng : null,
    };
  };


  // Cargar Leaflet desde CDN
  useEffect(() => {
    // Verificar si Leaflet ya está cargado
    if (window.L) {
      setLeafletLoaded(true);
      return;
    }

    // Cargar CSS de Leaflet
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    link.crossOrigin = '';
    document.head.appendChild(link);

    // Cargar JS de Leaflet
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
    script.crossOrigin = '';
    script.onload = () => {
      setLeafletLoaded(true);
    };
    script.onerror = () => {
      console.error('Error al cargar Leaflet');
      setError('Error al cargar la librería de mapas');
    };
    document.body.appendChild(script);

    return () => {
      // Limpiar al desmontar
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Función para obtener ubicaciones desde la API
  const fetchUbicaciones = async () => {
    try {
      setError(null);
      setConnectionStatus(true);
      console.log('🔄 Consultando API de ubicaciones:', API_URL);

      // Usar el proxy de Vercel (en producción) o setupProxy.js (en desarrollo)
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });


      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Respuesta de API recibida:', result);

      if (result.success && result.data) {
        setUbicaciones(Array.isArray(result.data) ? result.data.map(sanitizeUbicacion) : []);

        setLastUpdate(new Date());
        setConnectionStatus(true);
        setError(null); // Limpiar error si la respuesta es exitosa
        return Array.isArray(result.data) ? result.data.map(sanitizeUbicacion) : [];
      }

      setLastUpdate(new Date());
      setError(null); // Limpiar error incluso si no hay datos pero la respuesta fue exitosa
      return [];
    } catch (error) {
      console.error('❌ Error al obtener ubicaciones:', error);
      setConnectionStatus(false);

      // El error se establecerá, pero solo se mostrará si no hay ubicaciones
      // (esto se maneja en el render)
      setError(error.message);
      return [];
    }
  };

  // Función para formatear tiempo transcurrido
  const getTimeAgo = (date) => {
    if (!date) return 'Nunca';
    const seconds = Math.floor((new Date() - date) / 1000);

    if (seconds < 60) return 'hace unos segundos';
    if (seconds < 3600) return `hace ${Math.floor(seconds / 60)} minutos`;
    if (seconds < 86400) return `hace ${Math.floor(seconds / 3600)} horas`;
    return `hace ${Math.floor(seconds / 86400)} días`;
  };



  // Inicializar mapa con Leaflet
  useEffect(() => {
    if (!leafletLoaded || !mapRef.current || map) return;

    if (window.L) {
      const newMap = window.L.map(mapRef.current).setView(MAP_CENTER_SANGOLQUI, MAP_ZOOM_DEFAULT);

      // Agregar capa de OpenStreetMap
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(newMap);

      setMap(newMap);
    }
  }, [leafletLoaded, map]);

  // Cargar ubicaciones iniciales
  useEffect(() => {
    fetchUbicaciones();
  }, []);

  // Crear icono personalizado para Leaflet
  const createDriverIcon = (isActive, unidad) => {
    if (!window.L) return null;

    const taxiYellow = '#FFC107';
    const mainColor = isActive ? taxiYellow : '#9ca3af';
    const plateBg = isActive ? '#111827' : '#4b5563';
    const plateText = isActive ? taxiYellow : 'white';

    return window.L.divIcon({
      className: 'modern-driver-marker-full',
      html: `
        <div style="display: flex; flex-direction: column; align-items: center; position: relative; filter: drop-shadow(0 4px 10px rgba(0,0,0,0.3));">
          <!-- Badge CV -->
          <div style="
            position: absolute;
            top: -6px;
            right: -6px;
            width: 18px;
            height: 18px;
            background: #111827;
            color: ${taxiYellow};
            border-radius: 999px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            font-weight: 900;
            border: 2px solid white;
            box-shadow: 0 2px 6px rgba(0,0,0,0.35);
            z-index: 5;
          ">CV</div>
          <!-- Círculo principal -->
          <div style="
            width: 45px; 
            height: 45px; 
            background: ${mainColor}; 
            border: 3px solid white; 
            border-radius: 50%; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            font-size: 24px;
            z-index: 2;
          ">
            🚕
          </div>
          
          <!-- Badge de Unidad -->
          <div style="
            background: ${plateBg};
            color: ${plateText};
            padding: 2px 10px;
            border-radius: 5px;
            font-weight: 900;
            font-size: 16px;
            margin-top: -10px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.2);
            border: 2px solid white;
            z-index: 3;
            min-width: 40px;
            text-align: center;
          ">
            ${unidad || '--'}
          </div>
        </div>
      `,
      iconSize: [50, 65],
      iconAnchor: [25, 60],
      popupAnchor: [0, -60]
    });
  };

  const ubicacionesFiltradas = ubicaciones.filter(loc => {
    if (!busquedaUnidad) return true;
    return loc.unidad && loc.unidad.toString().toLowerCase().includes(busquedaUnidad.toLowerCase());
  });

  // Actualizar marcadores en el mapa usando ubicaciones FILTRADAS
  useEffect(() => {
    if (!map || !window.L) return;

    // Limpiar marcadores anteriores
    Object.values(markers).forEach(marker => {
      if (marker && map) {
        map.removeLayer(marker);
      }
    });

    const newMarkers = {};
    const bounds = [];

    // Usar ubicaciones filtradas para los marcadores
    const datosAMostrar = Array.isArray(ubicacionesFiltradas) ? ubicacionesFiltradas : [];

    datosAMostrar.forEach(location => {
      const { latitud, longitud, estado, unidad } = location;
      const ubicacionKey = getUbicacionKey(location);

      if (!latitud || !longitud || isNaN(latitud) || isNaN(longitud)) {
        return;
      }

      const isActive = estado === 'activo' || estado === 'true';
      const icon = createDriverIcon(isActive, unidad);

      const marker = window.L.marker([latitud, longitud], { icon })
        .addTo(map)
        .bindPopup(`
          <div style="min-width: 200px; padding: 10px;">
            <h3 style="margin: 0 0 10px 0; font-size: 16px;">Unidad: ${unidad || '--'}</h3>
            <div style="margin: 5px 0;">
              <strong>Estado:</strong> ${isActive ? 'Activo' : 'Inactivo'}
            </div>
            ${unidad ? `<div style="margin: 5px 0;"><strong>Unidad:</strong> ${unidad}</div>` : ''}
            <div style="margin: 5px 0; font-size: 12px; color: #666;">
              <strong>Coordenadas:</strong><br>
              Lat: ${latitud.toFixed(6)}<br>
              Lng: ${longitud.toFixed(6)}
            </div>
            ${location.hora ? `<div style="margin: 5px 0; font-size: 11px; color: #999;"><strong>Hora:</strong> ${location.hora}</div>` : ''}
          </div>
        `);

      if (ubicacionKey) newMarkers[ubicacionKey] = marker;
      bounds.push([latitud, longitud]);
    });

    setMarkers(newMarkers);

    // Ajustar vista para mostrar todos los marcadores
    if (bounds.length > 0) {
      if (bounds.length === 1) {
        // Si hay solo un marcador (búsqueda específica), hacer zoom directo
        map.setView(bounds[0], 18);
      } else {
        const group = new window.L.featureGroup(Object.values(newMarkers));
        map.fitBounds(group.getBounds().pad(0.1));
      }
    }
  }, [map, ubicaciones, busquedaUnidad, markers, ubicacionesFiltradas]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      refreshIntervalRef.current = setInterval(() => {
        fetchUbicaciones();
      }, REFRESH_INTERVAL);
    } else {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh]);





  const activeCount = ubicaciones.filter(loc => loc.estado === 'activo' || loc.estado === 'true').length;

  return (
    <div style={{ padding: 20, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        flexWrap: 'wrap',
        gap: 15
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>📍 Ubicaciones en Tiempo Real</h2>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 5,
            fontSize: 12,
            color: '#666'
          }}>
            <span style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: connectionStatus ? '#4ade80' : '#ef4444',
              animation: connectionStatus ? 'pulse 2s infinite' : 'none'
            }}></span>
            <span>{connectionStatus ? 'Conectado' : 'Desconectado'}</span>
            <span style={{ margin: '0 10px' }}>•</span>
            <span>Actualización cada {REFRESH_INTERVAL / 1000} segundos</span>
            {lastUpdate && (
              <>
                <span style={{ margin: '0 10px' }}>•</span>
                <span>Última actualización: {getTimeAgo(lastUpdate)}</span>
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{
            background: '#e8f5e9',
            padding: '8px 15px',
            borderRadius: 20,
            fontSize: 14
          }}>
            <div style={{ fontSize: 12, color: '#666' }}>Conductores Activos</div>
            <strong style={{ fontSize: 18, color: '#2e7d32' }}>{activeCount}</strong>
          </div>
          <div style={{
            background: '#e3f2fd',
            padding: '8px 15px',
            borderRadius: 20,
            fontSize: 14
          }}>
            <div style={{ fontSize: 12, color: '#666' }}>Total Ubicaciones</div>
            <strong style={{ fontSize: 18, color: '#1976d2' }}>{ubicaciones.length}</strong>
          </div>

          {/* Input de búsqueda */}
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>🔍</span>
            <input
              type="text"
              placeholder="Buscar unidad..."
              value={busquedaUnidad}
              onChange={(e) => setBusquedaUnidad(e.target.value)}
              style={{
                padding: '8px 15px 8px 35px',
                borderRadius: 20,
                border: '1px solid #ddd',
                fontSize: 14,
                outline: 'none',
                width: '180px'
              }}
            />
          </div>
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: 20,
        flex: 1,
        minHeight: 0,
        flexDirection: window.innerWidth < 768 ? 'column' : 'row'
      }}>
        {/* Lista de Conductores a la izquierda */}
        <div style={{
          width: window.innerWidth < 768 ? '100%' : 350,
          background: 'white',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: 20,
          overflowY: 'auto',
          maxHeight: window.innerWidth < 768 ? 300 : '100%'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: 15, fontSize: 18, borderBottom: '2px solid #667eea', paddingBottom: 10 }}>
            Conductores encontrados ({ubicacionesFiltradas.length})
          </h3>
          {ubicacionesFiltradas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              <p>No se encontraron conductores</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {ubicacionesFiltradas.map((location, index) => {
                const isActive = location.estado === 'activo' || location.estado === 'true';
                return (
                  <div
                    key={location._id || index}
                    onClick={() => {
                      if (map && location.latitud && location.longitud && window.L) {
                        map.setView([location.latitud, location.longitud], 18);
                        const locKey = getUbicacionKey(location);
                        if (locKey && markers[locKey]) {
                          markers[locKey].openPopup();
                          // Animación de rebote
                          const markerElement = markers[locKey].getElement();
                          if (markerElement) {
                            markerElement.style.animation = 'bounce 0.5s ease 4';
                            setTimeout(() => {
                              if (markerElement) {
                                markerElement.style.animation = '';
                              }
                            }, 2000);
                          }
                        }
                      }
                    }}
                    style={{
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: 8,
                      padding: 15,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e8eaf6';
                      e.currentTarget.style.borderColor = '#667eea';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8f9fa';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {/* UNIDAD GRANDE */}
                        <div style={{
                          fontSize: 28,
                          fontWeight: '800',
                          color: '#1f2937',
                          lineHeight: 1,
                          marginBottom: 5
                        }}>
                          {location.unidad || '---'}
                        </div>
                        {/* HORA VISIBLE */}
                        <div style={{
                          fontSize: 16,
                          color: '#4b5563',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5
                        }}>
                          🕒 {location.hora || 'Hora no disp.'}
                        </div>
                      </div>

                      {/* ESTADO */}
                      <span style={{
                        padding: '6px 12px',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 'bold',
                        background: isActive ? '#dcfce7' : '#fee2e2',
                        color: isActive ? '#166534' : '#991b1b',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        {isActive ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>

                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #eee', fontSize: 13, color: '#6b7280' }}>
                      {/* OTRA INFO */}
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>📅 {location.fecha || 'Fecha no disp.'}</span>
                        <span>📍 {location.latitud?.toFixed(4)}, {location.longitud?.toFixed(4)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mapa a la derecha */}
        <div style={{
          flex: 1,
          border: '2px solid #ddd',
          borderRadius: 8,
          overflow: 'hidden',
          minHeight: 400,
          position: 'relative'
        }}>
          {!leafletLoaded ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              fontSize: 16,
              color: '#666'
            }}>
              Cargando mapa...
            </div>
          ) : (
            <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
          )}
        </div>
      </div>

      {error && ubicaciones.length === 0 && (
        <div style={{
          marginTop: 15,
          padding: 12,
          background: '#fee2e2',
          border: '1px solid #fca5a5',
          borderRadius: 6,
          fontSize: 13,
          color: '#991b1b',
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <span>⚠️</span>
          <div>
            <strong>Error al conectar con la API:</strong> {error}
            <br />
            <span style={{ fontSize: 11 }}>Verifica que el servidor esté ejecutándose y que el proxy esté configurado correctamente.</span>
          </div>
        </div>
      )}

      {error && ubicaciones.length > 0 && (
        <div style={{
          marginTop: 15,
          padding: 10,
          background: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: 6,
          fontSize: 12,
          color: '#92400e',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <span>⚠️</span>
          <div>
            <strong>Advertencia:</strong> Hubo un problema al actualizar, pero se muestran los datos anteriores.
          </div>
        </div>
      )}

      <div style={{
        marginTop: 15,
        padding: 10,
        background: '#f0f0f0',
        borderRadius: 6,
        fontSize: 12,
        color: '#666',
        textAlign: 'center'
      }}>
        {autoRefresh ? (
          <span>🔄 Actualización automática cada {REFRESH_INTERVAL / 1000} segundos</span>
        ) : (
          <span>⏸️ Actualización automática pausada</span>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .custom-driver-marker {
          background: transparent !important;
          border: none !important;
        }
        .custom-driver-marker > div {
          transition: transform 0.3s ease, filter 0.3s ease;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));
        }
        .custom-driver-marker:hover > div {
          transform: scale(1.15);
          filter: drop-shadow(0 6px 12px rgba(0,0,0,0.5));
          z-index: 1000;
        }
      `}</style>
    </div>
  );
}

// WhatsApp Unidades - Página de Gestión WhatsApp y PM2 embebida
const WHATSAPP_GESTION_URL = 'http://147.93.130.33:3015/public/index.html';

function WhatsAppUnidadesContent() {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 'calc(100vh - 120px)' }}>
      <iframe
        src={WHATSAPP_GESTION_URL}
        title="Gestión WhatsApp y PM2"
        style={{
          width: '100%',
          flex: 1,
          minHeight: 600,
          border: 'none',
          borderRadius: 8
        }}
      />
    </div>
  );
}

// Componente principal
function MainContent({ activeSection, alertaActiva = null, operadorAutenticado, setOperadorAutenticado, reporteDiario, setReporteDiario, authTrigger, setAuthTrigger, setIsCollapsed }) {
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <TaxiForm
          alertaActiva={alertaActiva}
          operadorAutenticado={operadorAutenticado}
          setOperadorAutenticado={setOperadorAutenticado}
          reporteDiario={reporteDiario}
          setReporteDiario={setReporteDiario}
          authTrigger={authTrigger}
          setAuthTrigger={setAuthTrigger}
          setIsCollapsed={setIsCollapsed}
        />;

      case 'conductores':
        return <ConductoresContent />;
      case 'reportes':
        return <ReportesContent />;
      case 'reporte-unidades':
        return <ReporteUnidades />;
      case 'reporte-total-general':
        return <ReporteTotalGeneral />;
      case 'operadores':
        return <OperadoresContent />;
      case 'whatsapp-unidades':
        return <WhatsAppUnidadesContent />;
      case 'whatsapp-web':
        return <WhatsAppWeb />;
      case 'reservas':
        return <ReservasContent operadorAutenticado={operadorAutenticado} />;
      case 'ubicaciones':
        return <UbicacionesContent />;
      default:
        return <TaxiForm
          alertaActiva={alertaActiva}
          operadorAutenticado={operadorAutenticado}
          setOperadorAutenticado={setOperadorAutenticado}
          reporteDiario={reporteDiario}
          setReporteDiario={setReporteDiario}
          authTrigger={authTrigger}
          setIsCollapsed={setIsCollapsed}
        />;
    }
  };

  return (
    <main style={{
      flex: 1,
      padding: 0,
      background: '#f9fafb',
      overflow: activeSection === 'dashboard' ? 'hidden' : 'auto',
      minWidth: 0,
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {renderContent()}
    </main>
  );
}

export default MainContent;
